/*
 * https://github.com/5orenso
 *
 * Copyright (c) 2020 Øistein Sørensen
 * Licensed under the MIT license.
 */

import { h } from 'preact';
import util from 'preact-util';
import { Text, Localizer, withText } from 'preact-i18n';
import Markdown from 'preact-markdown';

import localUtil from './util';

import Feedback from './feedback';

const reloadApp = () => {
    window.location.reload(true);
}

const spiderPage = async (url) => {
    const response = await util.fetchApi(`/api/spider/${encodeURIComponent(url)}`, {}, this);
    switch (response.status) {
        case 200:
            return {
                ...response.data,
            }
            break;
        default:
            return response;
    }
}

const postnummerLookup = async (postnummer) => {
    const response = await util.fetchApi(`/api/postnummer/${postnummer}`, { method: 'GET' }, {});
    switch (response.status) {
        case 200:
            console.log('postnummerLookup', response.data);
            return response.data;
    }
}

const cellphoneLookup = async (cellphone) => {
    const response = await util.fetchApi(`/api/sms/lookup`, { method: 'GET' }, {
        cellphone,
    });
    switch (response.status) {
        case 200:
            console.log('cellphoneLookup', response.data);
            return response.data;
    }
}

function mapLetter(letter) {
    switch (letter) {
        case 'æ':
            return 'ae';
        case 'ø':
            return 'oe';
        case 'å':
            return 'aa';
        case 'Æ':
            return 'Ae';
        case 'Ø':
            return 'Oe';
        case 'Å':
            return 'Aa';
        default:
            return letter;
    }
}

function lineTotal(line, format = true, inputQty) {
    const qty = inputQty || line.qty;
    if (!qty) return 0;

    let aPrice = line.price;
    if (line.medicine || line.fee || line.equipment || line.other) {
        aPrice = parseFloat(line.medicine || 0) + parseFloat(line.fee || 0) + parseFloat(line.equipment || 0) + parseFloat(line.other || 0);
    }
    let price = qty * (aPrice || 0);
    if (line.discount) {
        price -= price * (line.discount / 100);
    }
    const taxAmount = price * (line.vat / 100);
    let totalPrice = price + taxAmount;
    if (line.discountAmount) {
        totalPrice -= (line.discountAmount * qty);
    }
    if (format) {
        return util.format(totalPrice, 2, ',', ' ');
    }
    return totalPrice;
}

/**
 * Returns an array of objects describing invalid characters and their positions.
 *
 * @param {string} email - The email string to validate
 * @returns {Array<{ char: string, index: number }>}
 */
function findInvalidEmailChars(email) {
    // A basic allowed character set for illustration:
    //   - alphanumerics
    //   - !#$%&'*+/=?^_`{|}~ (common RFC5322 punctuation)
    //   - @ and .
    // (This is not exhaustive of all possible valid emails, but typical.)
    const allowedRegex = /^[A-Za-z0-9!#$%&'*+\-/=?^_`{|}~.@]+$/;

    const invalids = [];
    // Check each character individually
    for (let i = 0; i < email.length; i++) {
        const c = email[i];
        // If this single character fails the allowed pattern, log it
        if (!c.match(/^[A-Za-z0-9!#$%&'*+\-/=?^_`{|}~.@]$/)) {
            invalids.push({ char: c, index: i + 1 });
        }
    }
    return invalids;
}

const apiServer = util.getApiServer();

const ACCOUNT_MAP = {
    '1200': 'Aktiverte kostn. gruppe d',
    '1201': 'Aktiverte kostn. gruppe a',
    '1202': 'Aktiverte kostn. leide lokaler',
    '1460': 'Lagerbeholdning salgsvarer',
    '1480': 'Forskudd til leverandører',
    '1510': 'Kunder',
    '1511': 'Avsetning for kundetap',
    '1525': 'Annen kundefordring',
    '1530': 'Annen kundefordring2',
    '1610': 'Fordring forsikring Agria',
    '1612': 'Fordring forsikring Codan',
    '1614': 'Fordring forsikring DnB NOR',
    '1616': 'Fordring forsikring Enter',
    '1618': 'Fordring forsikring Gjensidige',
    '1620': 'Fordring forsikring IF',
    '1622': 'Fordring forsikring Jernbanepersonalets',
    '1623': 'Fordring forsikring Nemi',
    '1624': 'Fordring forsikring Sparebank1',
    '1626': 'Fordring forsikring Storebrand',
    '1628': 'Fordring forsikring Tennant',
    '1630': 'Fordring forsikring Terra',
    '1632': 'Fordring forsikring Tryg Vesta',
    '1634': 'Fordring forsikring Frende',
    '1636': 'Fordring forsikring - annet selskap',
    '1910': 'Kasse',
    '1913': 'Bank kort (fordring)',
    '1915': 'Visa (fordring)',
    '1916': 'Eurocard (fordring)',
    '1917': 'Diners (fordring)',
    '1925': 'Fording på inkassoselskap kundeinnbetalinger',
    '1935': 'Bank plasseringskonto',
    '1939': 'Bank folio',
    '1940': 'Bank skattetrekk',
    '2000': 'Aksjekapital',
    '2020': 'Overkursfond',
    '2050': 'Egenkapital fri',
    '2114': 'Skyldig konsulent avg.fritt',
    '2410': 'Leverandører',
    '2412': 'Skyldig konsulent',
    '2500': 'Skyldig skatt utsatt',
    '2510': 'Skyldig skatt betalbar',
    '2610': 'Skyldig Skattetrekk',
    '2710': 'Utgående mva',
    '2720': 'Inngående mva',
    '2730': 'Investeringsavgift',
    '2750': 'Fylkesskattesjefen',
    '2780': 'Skyldig arbeidsgiveravgift',
    '2781': 'Skyldig arbeidsg.avg feriep',
    '2782': 'Gjeld bank',
    '2800': 'Skyldig utbytte',
    '2910': 'Skyldig lønn, feriepenger etc',
    '2920': 'Skyldig Feriepenger',
    '2930': 'Gjeld til ansatte og aksjonærer',
    '2960': 'Påløpne kostnader',
    '2965': 'Annen kortsiktig gjeld',
    '3001': 'Medisinsk utstyr',
    '3010': 'Poliklinikk',
    '3011': 'Fôrsalg',
    '3012': 'Rekvisitasalg',
    '3015': 'Varesalg avgiftsfritt',
    '3019': 'Ande salgsinntekter',
    '3030': 'Tenner',
    '3032': 'Røntgen/Ultralyd',
    '3034': 'Pelsstell',
    '3036': 'Vaksiner',
    '3038': 'Andre klinikkinntekter',
    '3040': 'Operasjon vanlig',
    '3042': 'Operasjon Spesialist',
    '3044': 'Lab eksternt',
    '3046': 'Lab internt',
    '3069': 'Fysio/kiro/rehab',
    '3620': 'Leieinntekter klinikk',
    '3900': 'Andre inntekter',
    '4006': 'Varekjøp - vaksiner',
    '4008': 'Varekjøp - medisin',
    '4010': 'Varekjøp - utstyr (vet) forbr.',
    '4012': 'Varekjøp - salgsrekvisita',
    '4013': 'Varekjøp - fôr',
    '4019': 'Varekjøp - annet',
    '4090': 'Beholdningsendring varer',
    '4110': 'Varekjøp - avgiftsfritt',
    '4510': 'Andre fremmede tjenester',
    '4511': 'Konsulent',
    '4512': 'Analyser, kremering',
    '5010': 'Lønn',
    '5130': 'Lønn u.feriep.gr.lag',
    '5190': 'Feriepenger',
    '5410': 'Arbeidsgiveravgift',
    '5411': 'Arbeidsgiveravgift på feriepenger',
    '5960': 'Sosiale kostnader',
    '5990': 'Andre godtgjørelser',
    '6010': 'Ordinære avskrivinger',
    '6290': 'Leiekostnader mva-fritt',
    '6300': 'Leiekostnader mva pliktig',
    '6340': 'Strøm',
    '6540': 'Utstyr, inventer (uaktivert)',
    '6700': 'Regnskap, revisjon mv',
    '6730': 'Inkassokostnader',
    '6800': 'Kontorrekvisita, trykksaker mv',
    '6840': 'Aviser og Tidsskrifter',
    '6850': 'Faglitteratur',
    '6860': 'Kurs (inkl. reise og diett)',
    '6900': 'Telefon',
    '6940': 'Porto',
    '7100': 'Bilgodtgjørelse',
    '7140': 'Andre reise og diettkostn.',
    '7320': 'Reklame Div',
    '7322': 'Reklame Telekatalog',
    '7323': 'Reklame Internett',
    '7410': 'Kontingenter og gaver',
    '7500': 'Forsikringer',
    '7600': 'Patentkostnader',
    '7790': 'Andre driftskostnader',
    '7830': 'Tap på fordringer',
    '7832': 'Ettergitt',
    '8050': 'Renteinntekter',
    '8150': 'Renteutgifter',
    '8190': 'Andre finanskostnader',
    '8192': 'Andre ekstraordinære kostnader',
    '8600': 'Skattekostnad betalbar',
    '8620': 'Skattekostnad utsatt',
    '8800': 'Årsoppgjør',
    '8920': 'Utbytte',
    '8931': 'Privatkonto uttak kontanter',
    '8939': 'Privatkonto uttak annet',
    '8950': 'Tilførte midler',
    '3013': 'Medisinering',
    '3050': 'Varer som takst',
    '1920': 'Vipps',
    '1950': 'Ansatt rabatt',
    '1700': 'Kreditt egenandel ansatte (lønn)',
};

const FIELDS = {
    user: [
        {
            name: <Text id='input.user-basics-title'>User basics</Text>,
            key: 'User basics',
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    help: <Text id='input.id-help'>This is the unique ID for this object. Do not change this unless you know what you are doing!</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: user => user.id || null,
                    showInList: true,
                    isReadOnly: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    help: <Text id='input.customer-help'>This is the customer.id for this object. Do not change this unless you know what you are doing!</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: user => user.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                // {
                //     title: <Text id='input.foobar'>foobar</Text>,
                //     placeholder: <Text id='input.foobar'>foobar</Text>,
                //     name: 'foobar',
                //     type: 'decimal',
                //     icon: 'fa-solid fa-input-numeric',
                //     getValue: user => user.foobar || null,
                //     showInList: true,
                //     isAdminField: true,
                // },
                {
                    title: <Text id='input.race'>race</Text>,
                    placeholder: <Text id='input.race'>race</Text>,
                    name: 'race',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: user => user.race || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'activities',
                    placeholder: 'activities. <14d, etc. ',
                    name: 'activities',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.activities,
                    isReadOnly: true,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'lastActivityEpoch',
                    placeholder: 'lastActivityEpoch. >1000 or <1000, etc.',
                    name: 'lastActivityEpoch',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.lastActivityEpoch ? util.isoDate(obj.lastActivityEpoch * 1000) : null,
                    isReadOnly: true,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'offline',
                    placeholder: 'offline',
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'delete',
                    placeholder: 'delete',
                    name: 'delete',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.delete,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'deleteDate',
                    placeholder: 'deleteDate',
                    name: 'deleteDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-earth-europe',
                    getValue: obj => obj.deleteDate,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.hprNumber'>HprNumber</Text>,
                    placeholder: <Text id='input.hprNumber'>HprNumber</Text>,
                    name: 'hprNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: user => user.hprNumber || null,
                    showInList: true,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.animaliaVeterinaryNumber'>Animalia Veterinary Number</Text>,
                    placeholder: <Text id='input.animaliaVeterinaryNumber'>Animalia Veterinary Number</Text>,
                    name: 'animaliaVeterinaryNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: user => user.animaliaVeterinaryNumber || null,
                    showInList: true,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.code'>Code</Text>,
                    placeholder: <Text id='input.code'>Code</Text>,
                    name: 'code',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.code || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.firstname'>Firstname</Text>,
                    placeholder: <Text id='input.firstname'>Firstname</Text>,
                    name: 'firstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.firstname || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.lastname'>Lastname</Text>,
                    placeholder: <Text id='input.lastname'>Lastname</Text>,
                    name: 'lastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.lastname,
                    showInList: true,
                },
                {
                    title: <Text id='input.birth'>Birth</Text>,
                    placeholder: <Text id='input.birth'>Birth</Text>,
                    name: 'birth',
                    type: 'date',
                    min: '1900-01-01',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.birth,
                    displayValue: obj => obj ? `${util.formatDate(obj, { locale: 'nb-NO' })}` : null,
                    showInList: false,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.email'>Email</Text>,
                    placeholder: <Text id='input.email'>Email</Text>,
                    name: 'email',
                    type: 'text',
                    icon: 'fa-solid fa-envelope',
                    getValue: user => user.email,
                    isReadOnly: true,
                    adminEditAllowed: true,
                    showInList: true,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.email && !util.validateEmail(obj.email)) {
                            const invalidChars = findInvalidEmailChars(obj.email);
                            let messages = [];
                            if (invalidChars.length > 0) {
                                messages = invalidChars.map(obj => {
                                    return `Character "${obj.char}" at position ${obj.index} is not allowed.`
                                });
                            }
                            holdingStore.setFeedbackError('email', `Invalid email. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('email', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('email', '');
                        holdingStore.setFeedbackButton('email', null, null);
                    },
                },
                {
                    title: <Text id='input.email'>Email</Text>,
                    placeholder: <Text id='input.email'>Email</Text>,
                    name: 'email',
                    type: 'text',
                    icon: 'fa-solid fa-envelope',
                    getValue: user => user.email,
                    isAdminField: true,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.email && !util.validateEmail(obj.email)) {
                            const invalidChars = findInvalidEmailChars(obj.email);
                            let messages = [];
                            if (invalidChars.length > 0) {
                                messages = invalidChars.map(obj => {
                                    return `Character "${obj.char}" at position ${obj.index} is not allowed.`
                                });
                            }
                            holdingStore.setFeedbackError('email', `Invalid email. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('email', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('email', '');
                        holdingStore.setFeedbackButton('email', null, null);
                    },
                },

                {
                    title: <Text id='input.cellphone'>Cellphone</Text>,
                    placeholder: <Text id='input.cellphone'>Cellphone</Text>,
                    name: 'cellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: user => user.cellphone,
                    isReadOnly: true,
                    adminEditAllowed: true,
                    showInList: true,
                },
                {
                    title: <Text id='input.cellphoneShadow'>cellphoneShadow</Text>,
                    placeholder: <Text id='input.cellphoneShadow'>cellphoneShadow</Text>,
                    name: 'cellphoneShadow',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: user => user.cellphoneShadow,
                    isReadOnly: true,
                    adminEditAllowed: true,
                    showInList: false,
                },
                {
                    title: <Text id='input.licenseNumber'>License Number</Text>,
                    placeholder: <Text id='input.licenseNumber'>License Number</Text>,
                    name: 'licenseNumber',
                    type: 'text',
                    icon: 'fa-duotone fa-id-card',
                    getValue: obj => obj.licenseNumber,
                    adminEditAllowed: true,
                    showInList: false,
                },
                {
                    title: <Text id='input.rubicId'>Rubic Id</Text>,
                    placeholder: <Text id='input.rubicId'>Rubic Id</Text>,
                    name: 'rubicId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.rubicId,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.nsfId'>NSF Id</Text>,
                    placeholder: <Text id='input.nsfId'>NSF Id</Text>,
                    name: 'nsfId',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.nsfId,
                    showInList: true,
                },
                {
                    title: <Text id='input.nsfClubNumber'>NSF Club Number</Text>,
                    placeholder: <Text id='input.nsfClubNumber'>NSF Club Number</Text>,
                    name: 'nsfClubNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.nsfClubNumber,
                    showInList: true,
                },
                {
                    title: <Text id='input.nsfClubId'>nsfClubId</Text>,
                    placeholder: <Text id='input.nsfClubId'>nsfClubId</Text>,
                    name: 'nsfClubId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.nsfClubId,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.sportsClub'>SportsClub</Text>,
                    placeholder: <Text id='input.sportsClub'>SportsClub</Text>,
                    name: 'sportsClub',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.sportsClub,
                    showInList: true,
                },
                {
                    title: <Text id='input.sportsAssociation'>sportsAssociation</Text>,
                    placeholder: <Text id='input.sportsAssociation'>sportsAssociation</Text>,
                    name: 'sportsAssociation',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.sportsAssociation,
                    showInList: true,
                },
                {
                    title: <Text id='input.kennel'>kennel</Text>,
                    placeholder: <Text id='input.kennel'>kennel</Text>,
                    name: 'kennel',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.kennel,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.user-webshop-title'>User webshop</Text>,
            key: 'User webshop',
            fields: [
                {
                    title: <Text id='input.firstname'>Firstname</Text>,
                    placeholder: <Text id='input.firstname'>Firstname</Text>,
                    name: 'firstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.firstname || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.lastname'>Lastname</Text>,
                    placeholder: <Text id='input.lastname'>Lastname</Text>,
                    name: 'lastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.lastname,
                    showInList: false,
                },
                {
                    title: <Text id='input.email'>Email</Text>,
                    placeholder: <Text id='input.email'>Email</Text>,
                    name: 'email',
                    type: 'text',
                    icon: 'fa-solid fa-envelope',
                    getValue: user => user.email,
                    adminEditAllowed: true,
                    showInList: false,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.email && !util.validateEmail(obj.email)) {
                            const invalidChars = findInvalidEmailChars(obj.email);
                            let messages = [];
                            if (invalidChars.length > 0) {
                                messages = invalidChars.map(obj => {
                                    return `Character "${obj.char}" at position ${obj.index} is not allowed.`
                                });
                            }
                            holdingStore.setFeedbackError('email', `Invalid email. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('email', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('email', '');
                        holdingStore.setFeedbackButton('email', null, null);
                    },
                },
                {
                    title: <Text id='input.cellphone'>Cellphone</Text>,
                    placeholder: <Text id='input.cellphone'>Cellphone</Text>,
                    name: 'cellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: user => user.cellphone,
                    isReadOnly: true,
                    adminEditAllowed: true,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.user-address-title'>User address</Text>,
            key: 'User address',
            fields: [
                {
                    title: <Text id='input.address'>Address</Text>,
                    placeholder: <Text id='input.address'>Address</Text>,
                    name: 'address',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.address,
                    showInList: false,
                },
                {
                    title: <Text id='input.postalcode'>Postalcode</Text>,
                    placeholder: <Text id='input.postalcode'>Postalcode</Text>,
                    name: 'postalcode',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.postalcode,
                    showInList: false,
                    callback: async (obj, updateValue) => {
                        if (obj.postalcode && obj.postalcode.length === 4) {
                            const response = await postnummerLookup(obj.postalcode);

                            if (response) {
                                const place = response[0]?.poststed;
                                updateValue({
                                    name: 'postalplace',
                                    value: place,
                                    id: obj.id,
                                });
                            }
                        }
                    },
                },
                {
                    title: <Text id='input.postalplace'>Postalplace</Text>,
                    placeholder: <Text id='input.postalplace'>Postalplace</Text>,
                    name: 'postalplace',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.postalplace,
                    showInList: false,
                },
                {
                    title: <Text id='input.country'>Country</Text>,
                    placeholder: <Text id='input.country'>Country</Text>,
                    name: 'country',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.country,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.user-settings-title'>User settings</Text>,
            help: <Text id='input.user-settings-help'>Core settings for your profile.</Text>,
            key: 'User settings',
            fields: [
                {
                    title: <Text id='input.language'>Language</Text>,
                    placeholder: <Text id='input.language'>Preferred language</Text>,
                    name: 'language',
                    type: 'select',
                    icon: 'fa-solid fa-language',
                    getValue: obj => obj.language,
                    datalist: [['en', 'English'], ['no', 'Norsk']],
                    // datalistFieldKey: e => e.id,
                    // datalistFieldVal: e => `${e.firstname} ${e.lastname}`,
                    showInList: true,
                },
                {
                    title: <Text id='input.darkmode'>Darkmode</Text>,
                    placeholder: <Text id='input.darkmode'>Darkmode</Text>,
                    name: 'settings.darkmode',
                    type: 'toggle',
                    icon: 'fa-solid fa-moon',
                    getValue: user => user.settings?.darkmode ? 1 : 0,
                    showInList: true,
                    callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.tightmode'>tightmode</Text>,
                    placeholder: <Text id='input.tightmode'>tightmode</Text>,
                    name: 'settings.tightmode',
                    type: 'toggle',
                    icon: 'fa-solid fa-distribute-spacing-vertical',
                    getValue: user => user.settings?.tightmode ? 1 : 0,
                    showInList: true,
                    callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.imperial'>Imperial</Text>,
                    placeholder: <Text id='input.imperial'>Imperial</Text>,
                    name: 'settings.imperial',
                    type: 'toggle',
                    icon: 'fa-solid fa-earth-americas',
                    getValue: user => user.settings?.imperial ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.twoFactorLogin'>TwoFactorLogin</Text>,
                    placeholder: <Text id='input.twoFactorLogin'>TwoFactorLogin</Text>,
                    name: 'settings.twoFactorLogin',
                    type: 'toggle',
                    icon: 'fa-solid fa-lock-a',
                    getValue: user => user.settings?.twoFactorLogin ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.backgroundColor'>backgroundColor</Text>,
                    placeholder: <Text id='input.backgroundColor'>backgroundColor</Text>,
                    name: 'settings.backgroundColor',
                    type: 'select',
                    icon: 'fa-solid fa-input-text',
                    datalist: [
                        ['#87CEEB', 'Sky Blue'],
                        ['#FF7F50', 'Coral'],
                        ['#90EE90', 'Light Green'],
                        ['#DDA0DD', 'Violet'],
                        ['#F4A460', 'Sandy Brown'],
                        ['#6495ED', 'Cornflower Blue'],
                        ['#AFEEEE', 'Pale Turquoise'],
                        ['#F0E68C', 'Khaki'],
                        ['#D8BFD8', 'Thistle'],
                        ['#FFA07A', 'Light Salmon'],
                        ['#DDA0DD', 'Plum'],
                        ['#5F9EA0', 'Cadet Blue'],
                        ['#FFDAB9', 'Peach Puff'],
                        ['#DA70D6', 'Orchid'],
                        ['#DEB887', 'Burlywood'],
                        ['#B0C4DE', 'Light Steel Blue'],
                        ['#7FFFD4', 'Aquamarine'],
                        ['#DAA520', 'Goldenrod'],
                        ['#7B68EE', 'Medium Slate Blue'],
                        ['#CD853F', 'Peru']
                    ],
                    useFirstValueAsBackgroundColorForOption: true,
                    getValue: obj => obj.settings?.backgroundColor,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.user-race-qualifying-title'>Race qualifying</Text>,
            key: 'Race qualifying',
            fields: [
                {
                    title: <Text id='input.veteranRookie'>VeteranRookie</Text>,
                    placeholder: <Text id='input.veteranRookie'>VeteranRookie</Text>,
                    name: 'veteranRookie',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.veteranRookie ? 1 : 0,
                    showInList: false,
                },
                {
                    title: <Text id='input.qualifying'>Qualifying</Text>,
                    placeholder: <Text id='input.qualifying'>Qualifying</Text>,
                    name: 'qualifying',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.qualifying,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.user-access-title'>Simple access code</Text>,
            key: 'Simple access code',
            fields: [
                {
                    title: <Text id='input.allowSimpleAccessCode'>allowSimpleAccessCode</Text>,
                    placeholder: <Text id='input.allowSimpleAccessCode'>allowSimpleAccessCode</Text>,
                    name: 'allowSimpleAccessCode',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.allowSimpleAccessCode ? 1 : 0,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.simpleAccessCode'>simpleAccessCode</Text>,
                    placeholder: <Text id='input.simpleAccessCode'>simpleAccessCode</Text>,
                    name: 'simpleAccessCode',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.simpleAccessCode,
                    showInList: false,
                    isAdminField: true,
                },
            ],
        },
        {
            name: <Text id='input.user-media-title'>Media</Text>,
            key: 'Media',
            fields: [
                {
                    title: <Text id='input.localNewsPaper'>LocalNewsPaper</Text>,
                    placeholder: <Text id='input.localNewsPaper'>LocalNewsPaper</Text>,
                    name: 'localNewsPaper',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.localNewsPaper,
                    showInList: false,
                },
                {
                    title: <Text id='input.okToShareInfoWithMedia'>OkToShareInfoWithMedia</Text>,
                    placeholder: <Text id='input.okToShareInfoWithMedia'>OkToShareInfoWithMedia</Text>,
                    name: 'okToShareInfoWithMedia',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.okToShareInfoWithMedia ? 1 : 0,
                    showInList: false,
                },
                {
                    title: <Text id='input.rnb'>Rnb</Text>,
                    placeholder: <Text id='input.rnb'>Rnb</Text>,
                    name: 'rnb',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.rnb ? 1 : 0,
                    showInList: false,
                },
                {
                    title: <Text id='input.sponsors'>Sponsors</Text>,
                    placeholder: <Text id='input.sponsors'>Sponsors</Text>,
                    name: 'sponsors',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.sponsors,
                    showInList: false,
                },
                {
                    title: <Text id='input.leadDogs'>LeadDogs</Text>,
                    placeholder: <Text id='input.leadDogs'>LeadDogs</Text>,
                    name: 'leadDogs',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.leadDogs,
                    showInList: false,
                },
                {
                    title: <Text id='input.oldMerits'>OldMerits</Text>,
                    placeholder: <Text id='input.oldMerits'>OldMerits</Text>,
                    name: 'oldMerits',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.oldMerits,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.user-about-title'>About yourself</Text>,
            key: 'About yourself',
            fields: [
                {
                    title: <Text id='input.occupation'>Occupation</Text>,
                    placeholder: <Text id='input.occupation'>Occupation</Text>,
                    name: 'occupation',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.occupation,
                    showInList: false,
                },

                {
                    title: <Text id='input.hobbies'>Hobbies</Text>,
                    placeholder: <Text id='input.hobbies'>Hobbies</Text>,
                    name: 'hobbies',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hobbies,
                    showInList: false,
                },
                {
                    title: <Text id='input.funFacts'>FunFacts</Text>,
                    placeholder: <Text id='input.funFacts'>FunFacts</Text>,
                    name: 'funFacts',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.funFacts,
                    showInList: false,
                },
                {
                    title: <Text id='input.ambitions'>Ambitions</Text>,
                    placeholder: <Text id='input.ambitions'>Ambitions</Text>,
                    name: 'ambitions',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.ambitions,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.user-admin-settings-title'>User admin settings</Text>,
            help: <Text id='input.user-admin-settings-help'>Admin settings for your profile.</Text>,
            key: 'User admin settings',
            fields: [
                {
                    title: <Text id='input.isApproved'>IsApproved</Text>,
                    placeholder: <Text id='input.isApproved'>IsApproved</Text>,
                    name: 'isApproved',
                    type: 'toggle',
                    icon: 'fa-solid fa-thumbs-up',
                    getValue: user => user.isApproved ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isInspector'>IsInspector</Text>,
                    placeholder: <Text id='input.isInspector'>IsInspector</Text>,
                    name: 'isInspector',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police',
                    getValue: user => user.isInspector ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isVeterinary'>IsVeterinary</Text>,
                    placeholder: <Text id='input.isVeterinary'>IsVeterinary</Text>,
                    name: 'isVeterinary',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-doctor',
                    getValue: user => user.isVeterinary ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isCustomerAdmin'>isCustomerAdmin</Text>,
                    placeholder: <Text id='input.isCustomerAdmin'>isCustomerAdmin</Text>,
                    name: 'isCustomerAdmin',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police-tie',
                    getValue: user => user.isCustomerAdmin ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isAdmin'>IsAdmin</Text>,
                    placeholder: <Text id='input.isAdmin'>IsAdmin</Text>,
                    name: 'isAdmin',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police',
                    getValue: user => user.isAdmin ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isRaceAdmin'>isRaceAdmin</Text>,
                    placeholder: <Text id='input.isRaceAdmin'>isRaceAdmin</Text>,
                    name: 'isRaceAdmin',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police',
                    getValue: user => user.isRaceAdmin ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isPaymentAdmin'>isPaymentAdmin</Text>,
                    placeholder: <Text id='input.isPaymentAdmin'>isPaymentAdmin</Text>,
                    name: 'isPaymentAdmin',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police',
                    getValue: user => user.isPaymentAdmin ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isTicketWorker'>isTicketWorker</Text>,
                    placeholder: <Text id='input.isTicketWorker'>isTicketWorker</Text>,
                    name: 'isTicketWorker',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police',
                    getValue: user => user.isTicketWorker ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isShopWorker'>isShopWorker</Text>,
                    placeholder: <Text id='input.isShopWorker'>isShopWorker</Text>,
                    name: 'isShopWorker',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police',
                    getValue: user => user.isShopWorker ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isMediaWorker'>isMediaWorker</Text>,
                    placeholder: <Text id='input.isMediaWorker'>isMediaWorker</Text>,
                    name: 'isMediaWorker',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police',
                    getValue: user => user.isMediaWorker ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isTimeTracker'>isTimeTracker</Text>,
                    placeholder: <Text id='input.isTimeTracker'>isTimeTracker</Text>,
                    name: 'isTimeTracker',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police',
                    getValue: user => user.isTimeTracker ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isRaceLeader'>isRaceLeader</Text>,
                    placeholder: <Text id='input.isRaceLeader'>isRaceLeader</Text>,
                    name: 'isRaceLeader',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police',
                    getValue: user => user.isRaceLeader ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isTdLeader'>isTdLeader</Text>,
                    placeholder: <Text id='input.isTdLeader'>isTdLeader</Text>,
                    name: 'isTdLeader',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police',
                    getValue: user => user.isTdLeader ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isSoknadsguideadmin'>isSoknadsguideadmin</Text>,
                    placeholder: <Text id='input.isSoknadsguideadmin'>isSoknadsguideadmin</Text>,
                    name: 'isSoknadsguideadmin',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police',
                    getValue: user => user.isSoknadsguideadmin ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isSoknadsguideSuperAdmin'>isSoknadsguideSuperAdmin</Text>,
                    placeholder: <Text id='input.isSoknadsguideSuperAdmin'>isSoknadsguideSuperAdmin</Text>,
                    name: 'isSoknadsguideSuperAdmin',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police',
                    getValue: user => user.isSoknadsguideSuperAdmin ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.hasValidSubscription'>hasValidSubscription</Text>,
                    placeholder: <Text id='input.hasValidSubscription'>hasValidSubscription</Text>,
                    name: 'hasValidSubscription',
                    type: 'toggle',
                    icon: 'fa-solid fa-passport',
                    getValue: user => user.hasValidSubscription ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isPremium'>isPremium</Text>,
                    placeholder: <Text id='input.isPremium'>isPremium</Text>,
                    name: 'isPremium',
                    type: 'toggle',
                    icon: 'fa-solid fa-passport',
                    getValue: user => user.isPremium ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
            ],
        },
        {
            name: <Text id='input.user-email-settings-title'>User email settings</Text>,
            help: <Text id='input.user-email-settings-help'>Email settings for your profile.</Text>,
            key: 'User email settings',
            fields: [
                {
                    title: <Text id='input.infoEmails'>InfoEmails</Text>,
                    placeholder: <Text id='input.infoEmails'>InfoEmails</Text>,
                    name: 'settings.infoEmails',
                    type: 'toggle',
                    icon: 'fa-solid fa-envelope',
                    getValue: user => user.settings?.infoEmails ? 1 : 0,
                },
                {
                    title: <Text id='input.notificationEmails'>NotificationEmails</Text>,
                    placeholder: <Text id='input.notificationEmails'>NotificationEmails</Text>,
                    name: 'settings.notificationEmails',
                    type: 'toggle',
                    icon: 'fa-solid fa-envelope-dot',
                    getValue: user => user.settings?.notificationEmails ? 1 : 0,
                },
                {
                    title: <Text id='input.inboxEmails'>InboxEmails</Text>,
                    placeholder: <Text id='input.inboxEmails'>InboxEmails</Text>,
                    name: 'settings.inboxEmails',
                    type: 'toggle',
                    icon: 'fa-solid fa-envelope-circle-check',
                    getValue: user => user.settings?.inboxEmails ? 1 : 0,
                },
                {
                    title: <Text id='input.partnerEmails'>PartnerEmails</Text>,
                    placeholder: <Text id='input.partnerEmails'>PartnerEmails</Text>,
                    name: 'settings.partnerEmails',
                    type: 'toggle',
                    icon: 'fa-solid fa-envelope-open-dollar',
                    getValue: user => user.settings?.partnerEmails ? 1 : 0,
                },
            ],
        },
        {
            name: <Text id='input.user-weapons-title'>User weapons</Text>,
            key: 'User weapons',
            fields: [
                {
                    title: <Text id='input.weapon1'>Standardpistol (25 m bane)</Text>,
                    placeholder: <Text id='input.weapon1'>Standardpistol (25 m bane)</Text>,
                    help: <Text id='input.weapon1-help'>Tillatte våpentyper: Pistol eller revolver i kaliber .22 Long Rifle (5,6 mm) som tilfredsstiller kravene for standardpistol-øvelsen. Våpenspesifikasjoner: Våpenet skal være i .22 LR med maksimum 5-skudds magasin. Konkurransereglene setter bl.a. krav om minimum avtrekksvekt 1000 g, kun åpne sikter (ikke optikk), og visse begrensninger på vekt og form (f.eks. maks ca. 1400 g for pistol).</Text>,
                    name: 'weapon1',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.weapon1 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.weapon2'>Finpistol, 25 m (sportpistol)</Text>,
                    placeholder: <Text id='input.weapon2'>Finpistol, 25 m (sportpistol)</Text>,
                    help: <Text id='input.weapon2-help'>Tillatte våpentyper: Pistol eller revolver i kaliber .22 LR (samme våpentype som i standardpistol-klassen). Våpenspesifikasjoner: Våpnene følger tilsvarende spesifikasjoner som for standardpistol (.22 LR, min. 1000 g avtrekk, kun åpne sikter, maks 5 skudd per magasin). Finpistol (også kalt sportpistol) er i praksis samme våpenkategori som standardpistol.</Text>,
                    name: 'weapon2',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.weapon2 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.weapon3'>Silhuettpistol, 25 m (hurtigpistol)</Text>,
                    placeholder: <Text id='input.weapon3'>Silhuettpistol, 25 m (hurtigpistol)</Text>,
                    help: <Text id='input.weapon3-help'>Tillatte våpentyper: Pistol (eller evt. revolver) i kaliber .22 LR som tilfredsstiller reglementet. De fleste benytter spesialpistoler i .22 LR, da dette er en hurtigskytingsøvelse. Våpenspesifikasjoner: .22 LR pistoler med 5-skudds magasin, ofte med lav vekt og kort løp for rask skyting. Minste avtrekksvekt er 1000 g, og kun mekaniske sikter er tillatt, i tråd med ISSF-regler.</Text>,
                    name: 'weapon3',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.weapon3 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.weapon4'>Fripistol, 50 m</Text>,
                    placeholder: <Text id='input.weapon4'>Fripistol, 50 m</Text>,
                    help: <Text id='input.weapon4-help'>Tillatte våpentyper: Enkelskudds pistol i .22 LR (5,6 mm) er typisk for fripistol. Man kan også bruke .22-våpen som i klassene over (magasinpistol/revolver), men konkurransen domineres av spesialdesignede fripistoler. Våpenspesifikasjoner: Fripistol (50 m pistol) har det mest åpne regelverket: kaliber .22 LR, maks én skudd per ladning (ingen magasin), ellers svært få begrensninger. Pistolen kan ha svært lang pipe og anatomisk grep, med svært lav avtrekksvekt (ofte 50–100 g). Det er ingen vekt- eller dimensjonsbegrensning bortsett fra at grep ikke kan støtte armen.</Text>,
                    name: 'weapon4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.weapon4 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.weapon5'>Grovpistol, 25 m</Text>,
                    placeholder: <Text id='input.weapon5'>Grovpistol, 25 m</Text>,
                    help: <Text id='input.weapon5-help'>Tillatte våpentyper: Pistol eller revolver i centerfire-kaliber 7,62 mm opp til 9,65 mm. Dette tilsvarer omtrent .30–.38 caliber (f.eks. .32 S&W Long, .38 Special WC, 9×19 mm). Våpenspesifikasjoner: Våpenet må følge reglementet for 25 m grovpistol (ISSF centerfire). Vanligvis brukes spesialpistoler i .32 S&W Long eller .38 wadcutter for presisjon, men 9 mm pistoler er også tillatt. Minste avtrekksvekt er 1360 g (ifølge ISSF-regler for grovpistol), og kun åpne sikter er tillatt.</Text>,
                    name: 'weapon5',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.weapon5 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.weapon6'>Hurtigpistol grov (25 m hurtigskyting)</Text>,
                    placeholder: <Text id='input.weapon6'>Hurtigpistol grov (25 m hurtigskyting)</Text>,
                    help: <Text id='input.weapon6-help'>Tillatte våpentyper: Pistol eller revolver i kaliber 7,62–9,65 mm (samme som for grovpistol). De fleste bruker 9×19 mm eller .38/.357 revolver i grov hurtigskyting. Våpenspesifikasjoner: Våpenkravene speiler grovpistol-klassen (typisk 5 skudds magasin/tønne, min. ~1360 g avtrekk, åpne sikter). På grunn av hurtigskytingens natur velger mange pistoler med kort løp og god balanse for rask omlading og siktebilder.</Text>,
                    name: 'weapon6',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.weapon6 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.weapon7'>Finfelt (feltpistol, fin)</Text>,
                    placeholder: <Text id='input.weapon7'>Finfelt (feltpistol, fin)</Text>,
                    help: <Text id='input.weapon7-help'>Tillatte våpentyper: Pistol eller revolver i .22 LR kaliber. Dette er samme våpenkategori som .22-baneøvelsene, men brukt til feltskyting. Våpenspesifikasjoner: .22 LR pistoler/revolvere i finfelt må tilfredsstille feltskytingens krav til sikkerhet og funksjon (f.eks. pålitelighet for variasjon i temperatur, robuste sikter). Det er ofte ingen strenge begrensninger på vekt/avtrekk ut over at våpenet må være trygg og håndterlig i feltløypa.</Text>,
                    name: 'weapon7',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.weapon7 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.weapon8'>Grovfelt</Text>,
                    placeholder: <Text id='input.weapon8'>Grovfelt</Text>,
                    help: <Text id='input.weapon8-help'>Tillatte våpentyper: Pistol eller revolver i kaliber 7,62 mm til 9,65 mm (centerfire, tilsvarende .32 til .38/9 mm). Våpenspesifikasjoner: Grovfelt-våpen er typisk robuste 9 mm pistoler eller .38/.357 revolvere som tåler feltbruk. Må oppfylle feltskytingens krav (f.eks. sikre funksjon ved rask omlading, ingen skarpe kanter som kan henge seg opp, etc.). Magasinkapasitet er vanligvis 5 skudd i feltløypa.</Text>,
                    name: 'weapon8',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.weapon8 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.weapon9'>Militærfelt</Text>,
                    placeholder: <Text id='input.weapon9'>Militærfelt</Text>,
                    help: <Text id='input.weapon9-help'>Tillatte våpentyper: Pistol (halvautomat) i kaliber 9 mm til 11,65 mm. Dette er grovkalibrede tjenestepistoler – typisk 9×19, .40 S&W, .45 ACP etc – som brukes i militærfelt. (Revolver hører til egen revolverklasse.) Våpenspesifikasjoner: Militærfelt-klassens våpen er ofte vanlige tjenestepistoler/krigsvåpen (f.eks. 9 mm Glock, SIG, CZ eller .45 1911). Reglementet kan kreve minst 9×19 mm kaliber, og kun åpne sikter. Våpnene må være sikre og egnet for hurtig feltløype-skyting (magasinbytte, enhåndsbruk osv.).</Text>,
                    name: 'weapon9',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.weapon9 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.weapon10'>Revolverfelt</Text>,
                    placeholder: <Text id='input.weapon10'>Revolverfelt</Text>,
                    help: <Text id='input.weapon10-help'>Tillatte våpentyper: Revolver i kaliber 9 mm til 11,65 mm. Dette omfatter typisk .38/.357 Magnum, .44 Special/.44 Magnum, .45 Colt etc., så lenge revolveren oppfyller reglene. Våpenspesifikasjoner: Revolverfelt-våpen er kraftige revolvere med tønnekapasitet 5–6 skudd. Det er ingen kaliberbegrensning opp til .45 (11,65 mm). Revolveren må brukes med double-action avtrekk i feltløypa (ifølge reglementet for revolverfelt). Kun åpne sikter er tillatt, og ofte brukes 4–6” løpslengde for balanse mellom presisjon og hurtighet.</Text>,
                    name: 'weapon10',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.weapon10 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.weapon11'>Magnum 1 (felt)</Text>,
                    placeholder: <Text id='input.weapon11'>Magnum 1 (felt)</Text>,
                    help: <Text id='input.weapon11-help'>Tillatte våpentyper: Pistol eller revolver i magnumkaliber fra .357 (9,07 mm) opp til .409” (≈10,4 mm). Dette inkluderer f.eks. .357 Magnum, .41 Magnum og .44 Special (.429”, som faller i Magnum 2 hvis ladet som Magnum). Våpenspesifikasjoner: Magnum 1-våpen er ofte revolvere kamret for .357 Magnum, eventuelt kraftige pistoler i .357 SIG, .40 S&W eller 10 mm Auto (10,2 mm). Rekyl og munningsvipp er betydelig, så våpnene bør ha robust konstruksjon. Maks kaliber .409” betyr .41 Magnum er øvre grense i denne klassen.</Text>,
                    name: 'weapon11',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.weapon11 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.weapon12'>Magnum 2 (felt)</Text>,
                    placeholder: <Text id='input.weapon12'>Magnum 2 (felt)</Text>,
                    help: <Text id='input.weapon12-help'>Tillatte våpentyper: Pistol eller revolver i stor magnumkaliber fra .410” (10,4 mm) opp til .505” (12,8 mm). Denne klassen dekker de virkelig kraftige håndvåpenkalibre, f.eks. .44 Magnum (.429”), .454 Casull (.45), .500 S&W (.50) og lignende. Våpenspesifikasjoner: Magnum 2-våpen er typisk tunge revolvere (f.eks. Smith & Wesson Model 29 i .44 Magnum, eller S&W .500) og enkelte store pistoler (f.eks. Desert Eagle i .50 AE). Våpnene har svært kraftig rekyl, så reglene tillater tohåndsgrep i Magnum-klassene. Ingen øvre grense på størrelse bortsett fra kaliber ≤ .505. Det presiseres at kalibre som .50 AE (12,7 mm) og .500 S&W (12,7 mm) er lovlige i Magnum 2.</Text>,
                    name: 'weapon12',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.weapon12 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.weapon13'>Spesialpistol (felt)</Text>,
                    placeholder: <Text id='input.weapon13'>Spesialpistol (felt)</Text>,
                    help: <Text id='input.weapon13-help'>Tillatte våpentyper: Pistol i valgfritt kaliber mellom .22 og .45 (5,6–11,5 mm), forutsatt at våpenet er lite nok til å gå i spesialpistolens målekasse. Våpenspesifikasjoner: Spesialpistol er en felt-klasse for små pistoler. Våpenet (uladd) må veie ≤ 800 gram og passe i en kasse på 185 × 125 × 35 mm. Dette tilsvarer typisk kompakte pistoler (“backup-guns”/skjulte bære-våpen). Kaliber kan være alt fra .22 LR til .45 ACP, men rekylen i de tyngre kalibrene er utfordrende i så lette våpen. Vanlige modeller i klassen er kompakte 9 mm pistoler og .380/.32 pistoler.</Text>,
                    name: 'weapon13',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.weapon13 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.weapon14'>Spesialrevolver (felt)</Text>,
                    placeholder: <Text id='input.weapon14'>Spesialrevolver (felt)</Text>,
                    help: <Text id='input.weapon14-help'>Tillatte våpentyper: Revolver i valgfritt kaliber .22 til .45, som får plass i spesialrevolver-målekassen. Våpenspesifikasjoner: Spesialrevolver er en klasse for små revolvere (f.eks. korte “snubnose”-revolvere). Vekten uladd kan være maks 1000 g, og maks ytre mål er 200 × 150 × 40 mm. Dette tilsvarer typisk 2–3” løpslengde revolvere i .38 Special eller .22 LR. Kaliber opp til .45 er tillatt, men en .44 Magnum i denne størrelsen vil være uvanlig pga. rekyl og størrelse. Oftest brukes 5-skudds .38/.357 revolvere i spesialrevolver-klassen.</Text>,
                    name: 'weapon14',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.weapon14 || null,
                    showInList: false,
                },
                // {
                //     title: <Text id='input.weapon15'></Text>,
                //     placeholder: <Text id='input.weapon15'></Text>,
                //     help: <Text id='input.weapon15-help'></Text>,
                //     name: 'weapon15',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: user => user.weapon15 || null,
                //     showInList: false,
                // },



                // {
                //     title: <Text id='input.reserveWeapon1'>Reserve Standardpistol (25 m bane)</Text>,
                //     placeholder: <Text id='input.reserveWeapon1'>Reserve Standardpistol (25 m bane)</Text>,
                //     help: <Text id='input.reserveWeapon1-help'>Tillatte våpentyper: Pistol eller revolver i kaliber .22 Long Rifle (5,6 mm) som tilfredsstiller kravene for standardpistol-øvelsen. Våpenspesifikasjoner: Våpenet skal være i .22 LR med maksimum 5-skudds magasin. Konkurransereglene setter bl.a. krav om minimum avtrekksvekt 1000 g, kun åpne sikter (ikke optikk), og visse begrensninger på vekt og form (f.eks. maks ca. 1400 g for pistol).</Text>,
                //     name: 'reserveWeapon1',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: user => user.reserveWeapon1 || null,
                //     showInList: false,
                // },
            ],
        },
        {
            name: <Text id='input.user-reserve-weapons-title'>User reserve weapons</Text>,
            key: 'User reserve weapons',
            isCollapsed: true,
            fields: [
                {
                    title: <Text id='input.reserveWeapon1'>Standardpistol (25 m bane)</Text>,
                    placeholder: <Text id='input.reserveWeapon1'>Standardpistol (25 m bane)</Text>,
                    help: <Text id='input.reserveWeapon1-help'>Tillatte våpentyper: Pistol eller revolver i kaliber .22 Long Rifle (5,6 mm) som tilfredsstiller kravene for standardpistol-øvelsen. Våpenspesifikasjoner: Våpenet skal være i .22 LR med maksimum 5-skudds magasin. Konkurransereglene setter bl.a. krav om minimum avtrekksvekt 1000 g, kun åpne sikter (ikke optikk), og visse begrensninger på vekt og form (f.eks. maks ca. 1400 g for pistol).</Text>,
                    name: 'reserveWeapon1',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.reserveWeapon1 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.reserveWeapon2'>Finpistol, 25 m (sportpistol)</Text>,
                    placeholder: <Text id='input.reserveWeapon2'>Finpistol, 25 m (sportpistol)</Text>,
                    help: <Text id='input.reserveWeapon2-help'>Tillatte våpentyper: Pistol eller revolver i kaliber .22 LR (samme våpentype som i standardpistol-klassen). Våpenspesifikasjoner: Våpnene følger tilsvarende spesifikasjoner som for standardpistol (.22 LR, min. 1000 g avtrekk, kun åpne sikter, maks 5 skudd per magasin). Finpistol (også kalt sportpistol) er i praksis samme våpenkategori som standardpistol.</Text>,
                    name: 'reserveWeapon2',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.reserveWeapon2 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.reserveWeapon3'>Silhuettpistol, 25 m (hurtigpistol)</Text>,
                    placeholder: <Text id='input.reserveWeapon3'>Silhuettpistol, 25 m (hurtigpistol)</Text>,
                    help: <Text id='input.reserveWeapon3-help'>Tillatte våpentyper: Pistol (eller evt. revolver) i kaliber .22 LR som tilfredsstiller reglementet. De fleste benytter spesialpistoler i .22 LR, da dette er en hurtigskytingsøvelse. Våpenspesifikasjoner: .22 LR pistoler med 5-skudds magasin, ofte med lav vekt og kort løp for rask skyting. Minste avtrekksvekt er 1000 g, og kun mekaniske sikter er tillatt, i tråd med ISSF-regler.</Text>,
                    name: 'reserveWeapon3',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.reserveWeapon3 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.reserveWeapon4'>Fripistol, 50 m</Text>,
                    placeholder: <Text id='input.reserveWeapon4'>Fripistol, 50 m</Text>,
                    help: <Text id='input.reserveWeapon4-help'>Tillatte våpentyper: Enkelskudds pistol i .22 LR (5,6 mm) er typisk for fripistol. Man kan også bruke .22-våpen som i klassene over (magasinpistol/revolver), men konkurransen domineres av spesialdesignede fripistoler. Våpenspesifikasjoner: Fripistol (50 m pistol) har det mest åpne regelverket: kaliber .22 LR, maks én skudd per ladning (ingen magasin), ellers svært få begrensninger. Pistolen kan ha svært lang pipe og anatomisk grep, med svært lav avtrekksvekt (ofte 50–100 g). Det er ingen vekt- eller dimensjonsbegrensning bortsett fra at grep ikke kan støtte armen.</Text>,
                    name: 'reserveWeapon4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.reserveWeapon4 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.reserveWeapon5'>Grovpistol, 25 m</Text>,
                    placeholder: <Text id='input.reserveWeapon5'>Grovpistol, 25 m</Text>,
                    help: <Text id='input.reserveWeapon5-help'>Tillatte våpentyper: Pistol eller revolver i centerfire-kaliber 7,62 mm opp til 9,65 mm. Dette tilsvarer omtrent .30–.38 caliber (f.eks. .32 S&W Long, .38 Special WC, 9×19 mm). Våpenspesifikasjoner: Våpenet må følge reglementet for 25 m grovpistol (ISSF centerfire). Vanligvis brukes spesialpistoler i .32 S&W Long eller .38 wadcutter for presisjon, men 9 mm pistoler er også tillatt. Minste avtrekksvekt er 1360 g (ifølge ISSF-regler for grovpistol), og kun åpne sikter er tillatt.</Text>,
                    name: 'reserveWeapon5',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.reserveWeapon5 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.reserveWeapon6'>Hurtigpistol grov (25 m hurtigskyting)</Text>,
                    placeholder: <Text id='input.reserveWeapon6'>Hurtigpistol grov (25 m hurtigskyting)</Text>,
                    help: <Text id='input.reserveWeapon6-help'>Tillatte våpentyper: Pistol eller revolver i kaliber 7,62–9,65 mm (samme som for grovpistol). De fleste bruker 9×19 mm eller .38/.357 revolver i grov hurtigskyting. Våpenspesifikasjoner: Våpenkravene speiler grovpistol-klassen (typisk 5 skudds magasin/tønne, min. ~1360 g avtrekk, åpne sikter). På grunn av hurtigskytingens natur velger mange pistoler med kort løp og god balanse for rask omlading og siktebilder.</Text>,
                    name: 'reserveWeapon6',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.reserveWeapon6 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.reserveWeapon7'>Finfelt (feltpistol, fin)</Text>,
                    placeholder: <Text id='input.reserveWeapon7'>Finfelt (feltpistol, fin)</Text>,
                    help: <Text id='input.reserveWeapon7-help'>Tillatte våpentyper: Pistol eller revolver i .22 LR kaliber. Dette er samme våpenkategori som .22-baneøvelsene, men brukt til feltskyting. Våpenspesifikasjoner: .22 LR pistoler/revolvere i finfelt må tilfredsstille feltskytingens krav til sikkerhet og funksjon (f.eks. pålitelighet for variasjon i temperatur, robuste sikter). Det er ofte ingen strenge begrensninger på vekt/avtrekk ut over at våpenet må være trygg og håndterlig i feltløypa.</Text>,
                    name: 'reserveWeapon7',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.reserveWeapon7 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.reserveWeapon8'>Grovfelt</Text>,
                    placeholder: <Text id='input.reserveWeapon8'>Grovfelt</Text>,
                    help: <Text id='input.reserveWeapon8-help'>Tillatte våpentyper: Pistol eller revolver i kaliber 7,62 mm til 9,65 mm (centerfire, tilsvarende .32 til .38/9 mm). Våpenspesifikasjoner: Grovfelt-våpen er typisk robuste 9 mm pistoler eller .38/.357 revolvere som tåler feltbruk. Må oppfylle feltskytingens krav (f.eks. sikre funksjon ved rask omlading, ingen skarpe kanter som kan henge seg opp, etc.). Magasinkapasitet er vanligvis 5 skudd i feltløypa.</Text>,
                    name: 'reserveWeapon8',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.reserveWeapon8 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.reserveWeapon9'>Militærfelt</Text>,
                    placeholder: <Text id='input.reserveWeapon9'>Militærfelt</Text>,
                    help: <Text id='input.reserveWeapon9-help'>Tillatte våpentyper: Pistol (halvautomat) i kaliber 9 mm til 11,65 mm. Dette er grovkalibrede tjenestepistoler – typisk 9×19, .40 S&W, .45 ACP etc – som brukes i militærfelt. (Revolver hører til egen revolverklasse.) Våpenspesifikasjoner: Militærfelt-klassens våpen er ofte vanlige tjenestepistoler/krigsvåpen (f.eks. 9 mm Glock, SIG, CZ eller .45 1911). Reglementet kan kreve minst 9×19 mm kaliber, og kun åpne sikter. Våpnene må være sikre og egnet for hurtig feltløype-skyting (magasinbytte, enhåndsbruk osv.).</Text>,
                    name: 'reserveWeapon9',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.reserveWeapon9 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.reserveWeapon10'>Revolverfelt</Text>,
                    placeholder: <Text id='input.reserveWeapon10'>Revolverfelt</Text>,
                    help: <Text id='input.reserveWeapon10-help'>Tillatte våpentyper: Revolver i kaliber 9 mm til 11,65 mm. Dette omfatter typisk .38/.357 Magnum, .44 Special/.44 Magnum, .45 Colt etc., så lenge revolveren oppfyller reglene. Våpenspesifikasjoner: Revolverfelt-våpen er kraftige revolvere med tønnekapasitet 5–6 skudd. Det er ingen kaliberbegrensning opp til .45 (11,65 mm). Revolveren må brukes med double-action avtrekk i feltløypa (ifølge reglementet for revolverfelt). Kun åpne sikter er tillatt, og ofte brukes 4–6” løpslengde for balanse mellom presisjon og hurtighet.</Text>,
                    name: 'reserveWeapon10',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.reserveWeapon10 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.reserveWeapon11'>Magnum 1 (felt)</Text>,
                    placeholder: <Text id='input.reserveWeapon11'>Magnum 1 (felt)</Text>,
                    help: <Text id='input.reserveWeapon11-help'>Tillatte våpentyper: Pistol eller revolver i magnumkaliber fra .357 (9,07 mm) opp til .409” (≈10,4 mm). Dette inkluderer f.eks. .357 Magnum, .41 Magnum og .44 Special (.429”, som faller i Magnum 2 hvis ladet som Magnum). Våpenspesifikasjoner: Magnum 1-våpen er ofte revolvere kamret for .357 Magnum, eventuelt kraftige pistoler i .357 SIG, .40 S&W eller 10 mm Auto (10,2 mm). Rekyl og munningsvipp er betydelig, så våpnene bør ha robust konstruksjon. Maks kaliber .409” betyr .41 Magnum er øvre grense i denne klassen.</Text>,
                    name: 'reserveWeapon11',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.reserveWeapon11 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.reserveWeapon12'>Magnum 2 (felt)</Text>,
                    placeholder: <Text id='input.reserveWeapon12'>Magnum 2 (felt)</Text>,
                    help: <Text id='input.reserveWeapon12-help'>Tillatte våpentyper: Pistol eller revolver i stor magnumkaliber fra .410” (10,4 mm) opp til .505” (12,8 mm). Denne klassen dekker de virkelig kraftige håndvåpenkalibre, f.eks. .44 Magnum (.429”), .454 Casull (.45), .500 S&W (.50) og lignende. Våpenspesifikasjoner: Magnum 2-våpen er typisk tunge revolvere (f.eks. Smith & Wesson Model 29 i .44 Magnum, eller S&W .500) og enkelte store pistoler (f.eks. Desert Eagle i .50 AE). Våpnene har svært kraftig rekyl, så reglene tillater tohåndsgrep i Magnum-klassene. Ingen øvre grense på størrelse bortsett fra kaliber ≤ .505. Det presiseres at kalibre som .50 AE (12,7 mm) og .500 S&W (12,7 mm) er lovlige i Magnum 2.</Text>,
                    name: 'reserveWeapon12',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.reserveWeapon12 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.reserveWeapon13'>Spesialpistol (felt)</Text>,
                    placeholder: <Text id='input.reserveWeapon13'>Spesialpistol (felt)</Text>,
                    help: <Text id='input.reserveWeapon13-help'>Tillatte våpentyper: Pistol i valgfritt kaliber mellom .22 og .45 (5,6–11,5 mm), forutsatt at våpenet er lite nok til å gå i spesialpistolens målekasse. Våpenspesifikasjoner: Spesialpistol er en felt-klasse for små pistoler. Våpenet (uladd) må veie ≤ 800 gram og passe i en kasse på 185 × 125 × 35 mm. Dette tilsvarer typisk kompakte pistoler (“backup-guns”/skjulte bære-våpen). Kaliber kan være alt fra .22 LR til .45 ACP, men rekylen i de tyngre kalibrene er utfordrende i så lette våpen. Vanlige modeller i klassen er kompakte 9 mm pistoler og .380/.32 pistoler.</Text>,
                    name: 'reserveWeapon13',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.reserveWeapon13 || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.reserveWeapon14'>Spesialrevolver (felt)</Text>,
                    placeholder: <Text id='input.reserveWeapon14'>Spesialrevolver (felt)</Text>,
                    help: <Text id='input.reserveWeapon14-help'>Tillatte våpentyper: Revolver i valgfritt kaliber .22 til .45, som får plass i spesialrevolver-målekassen. Våpenspesifikasjoner: Spesialrevolver er en klasse for små revolvere (f.eks. korte “snubnose”-revolvere). Vekten uladd kan være maks 1000 g, og maks ytre mål er 200 × 150 × 40 mm. Dette tilsvarer typisk 2–3” løpslengde revolvere i .38 Special eller .22 LR. Kaliber opp til .45 er tillatt, men en .44 Magnum i denne størrelsen vil være uvanlig pga. rekyl og størrelse. Oftest brukes 5-skudds .38/.357 revolvere i spesialrevolver-klassen.</Text>,
                    name: 'reserveWeapon14',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.reserveWeapon14 || null,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.user-images-file-links-title'>Images, files and links</Text>,
            key: 'Images, files and links',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
            ],
        },
    ],
    news: [
        {
            name: <Text id='input.news-basics-title'>News basics</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'News basics',
            fields: [
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                },
                {
                    title: <Text id='input.active'>Active</Text>,
                    placeholder: <Text id='input.active'>Active</Text>,
                    name: 'active',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: news => news.active || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.race'>Race</Text>,
                    placeholder: <Text id='input.race'>Race</Text>,
                    name: 'race',
                    type: 'select',
                    icon: 'fa-solid fa-list',
                    getValue: obj => obj.race,
                    datalistName: 'races',
                    // datalist: [['birthday', 'Birthday'], ['buying', 'Buying'], ['christmas', 'Christmas'], ['dreaming', 'Dreaming about'], ['wedding', 'Wedding'], ['other', 'Other']],
                    datalistFieldKey: e => e.id,
                    datalistFieldVal: e => `${e.title}`,
                    showInList: false,
                },
                {
                    title: <Text id='input.race'>race</Text>,
                    placeholder: <Text id='input.race'>race</Text>,
                    name: 'race',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: news => news.race,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.title'>Title</Text>,
                    placeholder: <Text id='input.title'>Title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: news => news.title,
                    showInList: true,
                },
                {
                    title: <Text id='input.sort'>Sort</Text>,
                    placeholder: <Text id='input.sort'>Sort</Text>,
                    name: 'sort',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: news => news.sort,
                    showInList: true,
                },
                {
                    title: <Text id='input.ingress'>Ingress</Text>,
                    placeholder: <Text id='input.ingress'>Ingress</Text>,
                    name: 'ingress',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: news => news.ingress,
                },
                {
                    title: <Text id='input.body'>Body</Text>,
                    placeholder: <Text id='input.body'>Body</Text>,
                    name: 'body',
                    type: 'textareaEditor',
                    viewHtml: false,
                    icon: 'fa-solid fa-pen-field',
                    getValue: news => news.body,
                },
                {
                    title: <Text id='input.inlineImages'>inlineImages</Text>,
                    placeholder: <Text id='input.inlineImages'>inlineImages</Text>,
                    name: 'inlineImages',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.inlineImages,
                    showInList: false,
                },
                {
                    title: <Text id='input.createdDate'>createdDate</Text>,
                    placeholder: <Text id='input.createdDate'>createdDate</Text>,
                    name: 'createdDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.createdDate,
                    showInList: true,
                    isReadOnly: true,
                },
                {
                    title: <Text id='input.publishDate'>publishDate</Text>,
                    placeholder: <Text id='input.publishDate'>publishDate</Text>,
                    name: 'publishDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.publishDate,
                    showInList: true,
                },
                {
                    title: <Text id='input.expireDate'>expireDate</Text>,
                    placeholder: <Text id='input.expireDate'>expireDate</Text>,
                    name: 'expireDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.expireDate,
                    showInList: true,
                },
                {
                    title: <Text id='input.tag'>Tag</Text>,
                    placeholder: <Text id='input.tag'>Tag</Text>,
                    help: <Text id='input.tag-help'>Tag is used to place news on the correct page. If unsure, do not change this..</Text>,
                    name: 'tag',
                    type: 'text',
                    icon: 'fa-solid fa-hashtag',
                    getValue: news => news.tag,
                    showInList: true,
                },
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    help: <Text id='input.tags-help'>Tags are used to find similar news. Add as many as you like.</Text>,
                    name: 'tags',
                    type: 'tags',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'tags',
                    foundListName: 'similarTags',
                    foundListKeysName: 'similarTagsKeys',
                    getValue: obj => obj.tags,
                    datalist: [
                        'name',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: news => news.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.customers'>customers</Text>,
                    placeholder: <Text id='input.customers'>customers</Text>,
                    name: 'customers',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'adminCustomers',
                    getValue: obj => obj.customers?.map(m => m),
                    datalist: [
                        'id', 'name'
                    ],
                    displayArrayObject: obj => `${obj.id}: ${obj.name}`,

                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                },
            ],
        },
    ],
    customer: [
        {
            name: <Text id='input.customer-basics-title'>Customer basics</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'Customer basics',
            fields: [
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                },
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: customer => customer.id || null,
                    showInList: true,
                    isReadOnly: true,
                },
                {
                    title: <Text id='input.hostname'>hostname</Text>,
                    placeholder: <Text id='input.hostname'>hostname</Text>,
                    name: 'hostname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.hostname || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.name || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.orgNumber'>orgNumber</Text>,
                    placeholder: <Text id='input.orgNumber'>orgNumber</Text>,
                    name: 'orgNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.orgNumber || null,
                    showInList: true,
                    showFieldInfo: ({ holdingStore, obj: object, field }) => {
                        const length = object[field] ? object[field].length : 0;
                        return (<>
                            <span class='font-weight-lighter'>Length:</span> {length}
                        </>);
                    },
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.orgNumber && !/^\d{9}$/.test(obj.orgNumber)) {
                            holdingStore.setFeedbackError('orgNumber', 'Invalid orgNumber. Must be 9 digits.');
                        } else {
                            holdingStore.setFeedbackError('orgNumber', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('orgNumber', '');
                        holdingStore.setFeedbackButton('orgNumber', null, null);
                    },
                },
                {
                    title: <Text id='input.name2'>Name 2</Text>,
                    placeholder: <Text id='input.name2'>Name 2</Text>,
                    name: 'name2',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.name2 || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.orgNumber2'>orgNumber 2</Text>,
                    placeholder: <Text id='input.orgNumber2'>orgNumber 2</Text>,
                    name: 'orgNumber2',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.orgNumber2 || null,
                    showInList: true,
                    showFieldInfo: ({ holdingStore, obj: object, field }) => {
                        const length = object[field] ? object[field].length : 0;
                        return (<>
                            <span class='font-weight-lighter'>Length:</span> {length}
                        </>);
                    },
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.orgNumber2 && !/^\d{9}$/.test(obj.orgNumber2)) {
                            holdingStore.setFeedbackError('orgNumber2', 'Invalid orgNumber2. Must be 9 digits.');
                        } else {
                            holdingStore.setFeedbackError('orgNumber2', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('orgNumber2', '');
                        holdingStore.setFeedbackButton('orgNumber2', null, null);
                    },
                },
                {
                    title: <Text id='input.customerNumber'>CustomerNumber</Text>,
                    placeholder: <Text id='input.customerNumber'>CustomerNumber</Text>,
                    name: 'customerNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: customer => customer.customerNumber || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.invoicingActive'>invoicingActive</Text>,
                    placeholder: <Text id='input.invoicingActive'>invoicingActive</Text>,
                    name: 'invoicingActive',
                    type: 'toggle',
                    icon: 'fa-solid fa-bag-shopping',
                    getValue: customer => customer.invoicingActive ? 1 : 0,
                    showInList: true,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: customer => customer.description,
                },
                {
                    title: <Text id='input.internalComment'>internalComment</Text>,
                    placeholder: <Text id='input.internalComment'>internalComment</Text>,
                    name: 'internalComment',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: customer => customer.internalComment,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.address'>Address</Text>,
                    placeholder: <Text id='input.address'>Address</Text>,
                    name: 'address',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.address,
                    showInList: true,
                },
                {
                    title: <Text id='input.postalcode'>Postalcode</Text>,
                    placeholder: <Text id='input.postalcode'>Postalcode</Text>,
                    name: 'postalcode',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.postalcode,
                    showInList: true,
                    callback: async (obj, updateValue) => {
                        if (obj.postalcode && obj.postalcode.length === 4) {
                            const response = await postnummerLookup(obj.postalcode);

                            if (response) {
                                const place = response[0]?.poststed;
                                updateValue({
                                    name: 'place',
                                    value: place,
                                    id: obj.id,
                                });
                            }
                        }
                    },
                },
                {
                    title: <Text id='input.place'>Place</Text>,
                    placeholder: <Text id='input.place'>Place</Text>,
                    name: 'place',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.place,
                    showInList: true,
                },
                {
                    title: <Text id='input.country'>Country</Text>,
                    placeholder: <Text id='input.country'>Country</Text>,
                    name: 'country',
                    type: 'text',
                    icon: 'fa-solid fa-earth-europe',
                    getValue: customer => customer.country,
                    showInList: true,
                },
                {
                    title: <Text id='input.url'>Url</Text>,
                    placeholder: <Text id='input.url'>Url</Text>,
                    name: 'url',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: customer => customer.url,
                },
                {
                    title: <Text id='input.email'>email</Text>,
                    placeholder: <Text id='input.email'>email</Text>,
                    name: 'email',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: obj => obj.email,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.email && !util.validateEmail(obj.email)) {
                            const invalidChars = findInvalidEmailChars(obj.email);
                            let messages = [];
                            if (invalidChars.length > 0) {
                                messages = invalidChars.map(obj => {
                                    return `Character "${obj.char}" at position ${obj.index} is not allowed.`
                                });
                            }
                            holdingStore.setFeedbackError('email', `Invalid email. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('email', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('email', '');
                        holdingStore.setFeedbackButton('email', null, null);
                    },
                },
                {
                    title: <Text id='input.phone'>phone</Text>,
                    placeholder: <Text id='input.phone'>phone</Text>,
                    name: 'phone',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: obj => obj.phone,
                },
                {
                    title: <Text id='input.fax'>fax</Text>,
                    placeholder: <Text id='input.fax'>fax</Text>,
                    name: 'fax',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: obj => obj.fax,
                },
                {
                    title: <Text id='input.facebook'>Facebook</Text>,
                    placeholder: <Text id='input.facebook'>Facebook</Text>,
                    name: 'facebook',
                    type: 'text',
                    icon: 'fa-brands fa-facebook',
                    getValue: customer => customer.facebook,
                },
                {
                    title: <Text id='input.instagram'>Instagram</Text>,
                    placeholder: <Text id='input.instagram'>Instagram</Text>,
                    name: 'instagram',
                    type: 'text',
                    icon: 'fa-brands fa-instagram',
                    getValue: customer => customer.instagram,
                },
                {
                    title: <Text id='input.emailSender'>emailSender</Text>,
                    placeholder: <Text id='input.emailSender'>emailSender</Text>,
                    help: <Text id='input.emailSender-help'>Must be an approved sender in the system. If you are unsure, please do NOT change this.</Text>,
                    showDanger: true,
                    name: 'emailSender',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.emailSender,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.emailSender && !util.validateEmail(obj.emailSender)) {
                            const invalidChars = findInvalidEmailChars(obj.emailSender);
                            let messages = [];
                            if (invalidChars.length > 0) {
                                messages = invalidChars.map(obj => {
                                    return `Character "${obj.char}" at position ${obj.index} is not allowed.`
                                });
                            }
                            holdingStore.setFeedbackError('emailSender', `Invalid emailSender. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('emailSender', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('emailSender', '');
                        holdingStore.setFeedbackButton('emailSender', null, null);
                    },
                },
                {
                    title: <Text id='input.emailSenderOnlyAsReplyTo'>emailSenderOnlyAsReplyTo</Text>,
                    placeholder: <Text id='input.emailSenderOnlyAsReplyTo'>emailSenderOnlyAsReplyTo</Text>,
                    name: 'emailSenderOnlyAsReplyTo',
                    type: 'toggle',
                    icon: 'fa-solid fa-bag-shopping',
                    getValue: customer => customer.emailSenderOnlyAsReplyTo ? 1 : 0,
                    showInList: true,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.smsBookingSmsDaysBefore'>Booking SMS Days before</Text>,
                    placeholder: <Text id='input.smsBookingSmsDaysBefore'>Send Booking SMS days before booking date.</Text>,
                    help: <Text id='input.smsBookingSmsDaysBefore-help'>Default is 1 calendar day before the booking date. </Text>,
                    name: 'smsBookingSmsDaysBefore',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: customer => customer.smsBookingSmsDaysBefore || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.smsSendingHourDaily'>SMS Send Booking SMS Daily at hour</Text>,
                    placeholder: <Text id='input.smsSendingHourDaily'>SMS Send Booking SMS Daily at hour</Text>,
                    name: 'smsSendingHourDaily',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: customer => customer.smsSendingHourDaily || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.smsSender'>smsSender</Text>,
                    placeholder: <Text id='input.smsSender'>smsSender</Text>,
                    help: <Text id='input.smsSender-help'>Must follow these rules: Alphanumeric. Maximum of 11 characters. No spaces. No special characters. The sender ID must be the brand name of the company sending the SMS. If you are unsure, please do NOT change this.</Text>,
                    maxLength: 11,
                    showDanger: true,
                    name: 'smsSender',
                    maxLength: 11,
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.smsSender,
                    isReadOnly: true,
                    adminEditAllowed: true,
                    showFieldInfo: ({ holdingStore, obj: object, field }) => {
                        const length = object[field] ? object[field].length : 0;
                        return (<>
                            <span class='font-weight-lighter'>Length:</span> {length}
                        </>);
                    },
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.smsSender && !/^[a-zA-Z0-9]+$/.test(obj.smsSender)) {
                            holdingStore.setFeedbackError('smsSender', 'Invalid smsSender. Must be 11 digits.');
                        } else {
                            holdingStore.setFeedbackError('smsSender', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('smsSender', '');
                        holdingStore.setFeedbackButton('smsSender', null, null);
                    },
                },
                {
                    title: <Text id='input.smsTextBooking'>smsTextBooking</Text>,
                    placeholder: <Text id='input.smsTextBooking'>smsTextBooking</Text>,
                    name: 'smsTextBooking',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.smsTextBooking,
                },
                {
                    title: <Text id='input.smsTextNotification'>smsTextNotification</Text>,
                    placeholder: <Text id='input.smsTextNotification'>smsTextNotification</Text>,
                    name: 'smsTextNotification',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.smsTextNotification,
                },
                {
                    title: <Text id='input.dyreIdClinicId'>dyreIdClinicId</Text>,
                    placeholder: <Text id='input.dyreIdClinicId'>dyreIdClinicId</Text>,
                    name: 'dyreIdClinicId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.dyreIdClinicId,
                },

                {
                    title: <Text id='input.members'>Members</Text>,
                    placeholder: <Text id='input.members'>Members</Text>,
                    name: 'members',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'adminMembers',
                    getValue: customer => customer.members?.map(m => m),
                    datalist: [
                        'id', 'firstname', 'lastname', 'email', 'cellphone'
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-left px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.firstname} {obj.lastname}<br />
                            <span class='font-weight-lighter'>Cellphone:</span> {obj.cellphone}<br />
                            <span class='font-weight-lighter'>Email:</span> {obj.email}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                },
            ],
        },
        {
            name: <Text id='input.customer-invoice-title'>Invoice details</Text>,
            key: 'Invoice details',
            fields: [
                {
                    title: <Text id='input.invoiceAccount'>invoiceAccount</Text>,
                    placeholder: <Text id='input.invoiceAccount'>invoiceAccount</Text>,
                    help: <Text id='input.invoiceAccount-help'>Bank account where invoice should be paid to.</Text>,
                    name: 'invoiceAccount',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.invoiceAccount,
                },
                {
                    title: <Text id='input.invoiceBic'>BIC</Text>,
                    placeholder: <Text id='input.invoiceBic'>BIC</Text>,
                    help: <Text id='input.invoiceBic-help'>BIC code for the bank account.</Text>,
                    name: 'invoiceBic',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.invoiceBic,
                },
                {
                    title: <Text id='input.invoiceIban'>IBAN</Text>,
                    placeholder: <Text id='input.invoiceIban'>IBAN</Text>,
                    help: <Text id='input.invoiceIban-help'>IBAN code for the bank account.</Text>,
                    name: 'invoiceIban',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.invoiceIban,
                },
                {
                    title: <Text id='input.invoiceInfo'>invoiceInfo</Text>,
                    placeholder: <Text id='input.invoiceInfo'>invoiceInfo</Text>,
                    help: <Text id='input.invoiceInfo-help'>Due date info on the invoice. Ie: "Due in 10 days".</Text>,
                    name: 'invoiceInfo',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: customer => customer.invoiceInfo,
                },
                {
                    title: <Text id='input.invoiceDueDays'>invoiceDueDays</Text>,
                    placeholder: <Text id='input.invoiceDueDays'>invoiceDueDays</Text>,
                    help: <Text id='input.invoiceDueDays-help'>Only a number to indicate number of days before due.</Text>,
                    name: 'invoiceDueDays',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: customer => customer.invoiceDueDays,
                },
            ],
        },
        {
            name: <Text id='input.customer-sender-settings-title'>Sender Settings</Text>,
            key: 'Sender Settings',
            fields: [
                {
                    title: <Text id='input.emailSender'>emailSender</Text>,
                    placeholder: <Text id='input.emailSender'>emailSender</Text>,
                    help: <Text id='input.emailSender-help'>Must be an approved sender in the system. If you are unsure, please do NOT change this.</Text>,
                    showDanger: true,
                    name: 'emailSender',
                    type: 'text',
                    icon: 'fa-duotone fa-input-text',
                    getValue: customer => customer.emailSender,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.emailSender && !util.validateEmail(obj.emailSender)) {
                            const invalidChars = findInvalidEmailChars(obj.emailSender);
                            let messages = [];
                            if (invalidChars.length > 0) {
                                messages = invalidChars.map(obj => {
                                    return `Character "${obj.char}" at position ${obj.index} is not allowed.`
                                });
                            }
                            holdingStore.setFeedbackError('emailSender', `Invalid emailSender. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('emailSender', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('emailSender', '');
                        holdingStore.setFeedbackButton('emailSender', null, null);
                    },
                },
                {
                    title: <Text id='input.smsBookingSmsDaysBefore'>Booking SMS Days before</Text>,
                    placeholder: <Text id='input.smsBookingSmsDaysBefore'>Send Booking SMS reminder x days before booking date.</Text>,
                    help: <Text id='input.smsBookingSmsDaysBefore-help'>Default is 1 calendar day before the booking date. </Text>,
                    name: 'smsBookingSmsDaysBefore',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: customer => customer.smsBookingSmsDaysBefore || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.smsSender'>smsSender</Text>,
                    placeholder: <Text id='input.smsSender'>smsSender</Text>,
                    help: <Text id='input.smsSender-help'>Must follow these rules: Alphanumeric. Maximum of 11 characters. No spaces. No special characters. The sender ID must be the brand name of the company sending the SMS. If you are unsure, please do NOT change this.</Text>,
                    maxLength: 11,
                    showDanger: true,
                    name: 'smsSender',
                    type: 'text',
                    icon: 'fa-duotone fa-input-text',
                    getValue: customer => customer.smsSender,
                    showFieldInfo: ({ holdingStore, obj: object, field }) => {
                        const length = object[field] ? object[field].length : 0;
                        return (<>
                            <span class='font-weight-lighter'>Length:</span> {length}
                        </>);
                    },
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.smsSender && !/^[a-zA-Z0-9]+$/.test(obj.smsSender)) {
                            holdingStore.setFeedbackError('smsSender', 'Invalid smsSender. Must be 11 digits.');
                        } else {
                            holdingStore.setFeedbackError('smsSender', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('smsSender', '');
                        holdingStore.setFeedbackButton('smsSender', null, null);
                    },
                },
                {
                    title: <Text id='input.smsTextBooking'>smsTextBooking</Text>,
                    placeholder: <Text id='input.smsTextBooking'>smsTextBooking</Text>,
                    help: <Text id='input.smsTextBooking-help'>SMS text template for booking sms. Fields: [animal] [name] [eventDate] [clinic] [clinicPhone]</Text>,
                    name: 'smsTextBooking',
                    type: 'textarea',
                    icon: 'fa-duotone fa-input-text',
                    getValue: customer => customer.smsTextBooking,
                },
                {
                    title: <Text id='input.smsTextNotification'>smsTextNotification</Text>,
                    placeholder: <Text id='input.smsTextNotification'>smsTextNotification</Text>,
                    help: <Text id='input.smsTextNotification-help'>SMS text template for booking sms. Fields: [animal] [name] [eventDate] [clinic] [clinicPhone]</Text>,
                    name: 'smsTextNotification',
                    type: 'textarea',
                    icon: 'fa-duotone fa-input-text',
                    getValue: customer => customer.smsTextNotification,
                },
            ],
        },
        {
            name: <Text id='input.customer-settings-title'>Settings</Text>,
            key: 'Settings',
            fields: [
                {
                    title: <Text id='input.calendarStartHour'>calendarStartHour</Text>,
                    placeholder: <Text id='input.calendarStartHour'>calendarStartHour</Text>,
                    help: <Text id='input.invoiceDueDays-help'>Only a number to indicate start hour of the calendar day. Do not start with 0.</Text>,
                    showWarning: true,
                    name: 'calendarStartHour',
                    type: 'number',
                    icon: 'fa-solid fa-clock',
                    getValue: obj => obj.calendarStartHour,
                    showInList: false,
                },
                {
                    title: <Text id='input.calendarEndHour'>calendarEndHour</Text>,
                    placeholder: <Text id='input.calendarEndHour'>calendarEndHour</Text>,
                    help: <Text id='input.invoiceDueDays-help'>Only a number to indicate end hour of the calendar day. Do not start with 0.</Text>,
                    showWarning: true,
                    name: 'calendarEndHour',
                    type: 'number',
                    icon: 'fa-solid fa-clock',
                    getValue: obj => obj.calendarEndHour,
                    showInList: false,
                },

                {
                    title: <Text id='input.calendarShowMonday'>Show monday</Text>,
                    placeholder: <Text id='input.calendarShowMonday'>Show monday</Text>,
                    name: 'calendarDays.monday',
                    type: 'toggle',
                    icon: 'fa-duotone fa-calendar',
                    getValue: obj => obj.calendarDays?.monday ? 1 : 0,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.calendarShowTuesday'>Show tuesday</Text>,
                    placeholder: <Text id='input.calendarShowTuesday'>Show tuesday</Text>,
                    name: 'calendarDays.tuesday',
                    type: 'toggle',
                    icon: 'fa-duotone fa-calendar',
                    getValue: obj => obj.calendarDays?.tuesday ? 1 : 0,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.calendarShowWednesday'>Show wednesday</Text>,
                    placeholder: <Text id='input.calendarShowWednesday'>Show wednesday</Text>,
                    name: 'calendarDays.wednesday',
                    type: 'toggle',
                    icon: 'fa-duotone fa-calendar',
                    getValue: obj => obj.calendarDays?.wednesday ? 1 : 0,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.calendarShowThursday'>Show thursday</Text>,
                    placeholder: <Text id='input.calendarShowThursday'>Show thursday</Text>,
                    name: 'calendarDays.thursday',
                    type: 'toggle',
                    icon: 'fa-duotone fa-calendar',
                    getValue: obj => obj.calendarDays?.thursday ? 1 : 0,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.calendarShowFriday'>Show friday</Text>,
                    placeholder: <Text id='input.calendarShowFriday'>Show friday</Text>,
                    name: 'calendarDays.friday',
                    type: 'toggle',
                    icon: 'fa-duotone fa-calendar',
                    getValue: obj => obj.calendarDays?.friday ? 1 : 0,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.calendarShowSaturday'>Show saturday</Text>,
                    placeholder: <Text id='input.calendarShowSaturday'>Show saturday</Text>,
                    name: 'calendarDays.saturday',
                    type: 'toggle',
                    icon: 'fa-duotone fa-calendar',
                    getValue: obj => obj.calendarDays?.saturday ? 1 : 0,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.calendarShowSunday'>Show sunday</Text>,
                    placeholder: <Text id='input.calendarShowSunday'>Show sunday</Text>,
                    name: 'calendarDays.sunday',
                    type: 'toggle',
                    icon: 'fa-duotone fa-calendar',
                    getValue: obj => obj.calendarDays?.sunday ? 1 : 0,
                    showInList: false,
                    // callback: val => reloadApp(),
                },

                {
                    title: <Text id='input.logoHeight'>logoHeight</Text>,
                    placeholder: <Text id='input.logoHeight'>logoHeight</Text>,
                    help: <Text id='input.logoHeight-help'>Height of the custom logo for customer domain view. Only applicaple if showCustomerLogo is enabled on domain.</Text>,
                    showDanger: true,
                    name: 'logoHeight',
                    type: 'text',
                    icon: 'fa-duotone fa-input-text',
                    getValue: customer => customer.logoHeight,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.backgroundColor'>backgroundColor</Text>,
                    placeholder: <Text id='input.backgroundColor'>backgroundColor</Text>,
                    help: <Text id='input.backgroundColor-help'>Background color of the page. Only applicaple if showCustomerLogo is enabled on domain.</Text>,
                    showDanger: true,
                    name: 'backgroundColor',
                    type: 'text',
                    icon: 'fa-duotone fa-input-text',
                    getValue: customer => customer.backgroundColor,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.backgroundColorHeader'>backgroundColorHeader</Text>,
                    placeholder: <Text id='input.backgroundColorHeader'>backgroundColorHeader</Text>,
                    help: <Text id='input.backgroundColorHeader-help'>Background color of the header. Only applicaple if showCustomerLogo is enabled on domain.</Text>,
                    showDanger: true,
                    name: 'backgroundColorHeader',
                    type: 'text',
                    icon: 'fa-duotone fa-input-text',
                    getValue: customer => customer.backgroundColorHeader,
                    isAdminField: true,
                },

                {
                    title: <Text id='input.articleContainerClasses'>articleContainerClasses</Text>,
                    placeholder: <Text id='input.articleContainerClasses'>articleContainerClasses</Text>,
                    help: <Text id='input.articleContainerClasses-help'>Default is "w-100", "w-50 px-2"...</Text>,
                    name: 'articleContainerClasses',
                    type: 'arrayObject',
                    icon: 'fa-brands fa-youtube',
                    getValue: part => part.articleContainerClasses?.map(yt => yt),
                    datalist: [
                        'class'
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayArrayObject: obj => <>
                        <div class='text-left px-2'>
                            <span class='font-weight-lighter'>class:</span> {obj.class}<br />
                        </div>
                    </>,
                    // hideOnCreate: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.articleListClasses'>articleListClasses</Text>,
                    placeholder: <Text id='input.articleListClasses'>articleListClasses</Text>,
                    help: <Text id='input.articleListClasses-help'>Default is "flex-column", "flex-row", "flex-row"...</Text>,
                    name: 'articleListClasses',
                    type: 'arrayObject',
                    icon: 'fa-brands fa-youtube',
                    getValue: part => part.articleListClasses?.map(yt => yt),
                    datalist: [
                        'class'
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayArrayObject: obj => <>
                        <div class='text-left px-2'>
                            <span class='font-weight-lighter'>class:</span> {obj.class}<br />
                        </div>
                    </>,
                    // hideOnCreate: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.articleListImageWidth'>articleListImageWidth</Text>,
                    placeholder: <Text id='input.articleListImageWidth'>articleListImageWidth</Text>,
                    help: <Text id='input.articleListImageWidth-help'>Default is "100%", "40%", "40%"...</Text>,
                    name: 'articleListImageWidth',
                    type: 'arrayObject',
                    icon: 'fa-brands fa-youtube',
                    getValue: part => part.articleListImageWidth?.map(yt => yt),
                    datalist: [
                        'width'
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayArrayObject: obj => <>
                        <div class='text-left px-2'>
                            <span class='font-weight-lighter'>width:</span> {obj.width}<br />
                        </div>
                    </>,
                    // hideOnCreate: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.articleListImageLocations'>articleListImageLocations</Text>,
                    placeholder: <Text id='input.articleListImageLocations'>articleListImageLocations</Text>,
                    help: <Text id='input.articleListImageLocations-help'>Default is "top", "left", "right"...</Text>,
                    name: 'articleListImageLocations',
                    type: 'arrayObject',
                    icon: 'fa-brands fa-youtube',
                    getValue: part => part.articleListImageLocations?.map(yt => yt),
                    datalist: [
                        'location'
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayArrayObject: obj => <>
                        <div class='text-left px-2'>
                            <span class='font-weight-lighter'>location:</span> {obj.location}<br />
                        </div>
                    </>,
                    // hideOnCreate: true,
                    isAdminField: true,
                },
            ],
        },
        {
            name: <Text id='input.customer-featureflags-title'>Customer Feature flags</Text>,
            help: <Text id='input.customer-featureflags-help'>Feature flags for customer. Some feature can be modules they order.</Text>,
            key: 'Customer featureFlags',
            fields: [
                {
                    title: <Text id='input.dyreid'>DyreId</Text>,
                    placeholder: <Text id='input.dyreid'>DyreId</Text>,
                    name: 'featureFlags.dyreid',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.dyreid, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.animalia'>Animalia DHP</Text>,
                    placeholder: <Text id='input.animalia'>Animalia DHP</Text>,
                    name: 'featureFlags.animalia',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.animalia, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.fiken'>Fiken</Text>,
                    placeholder: <Text id='input.fiken'>Fiken</Text>,
                    name: 'featureFlags.fiken',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.fiken, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.tripletex'>Tripletex</Text>,
                    placeholder: <Text id='input.tripletex'>Tripletex</Text>,
                    name: 'featureFlags.tripletex',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.tripletex, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.nets'>NETS</Text>,
                    placeholder: <Text id='input.nets'>NETS</Text>,
                    name: 'featureFlags.nets',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.nets, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.paymentHideCard'>paymentHideCard</Text>,
                    placeholder: <Text id='input.paymentHideCard'>paymentHideCard</Text>,
                    name: 'featureFlags.paymentHideCard',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.paymentHideCard, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.paymentHideCash'>paymentHideCash</Text>,
                    placeholder: <Text id='input.paymentHideCash'>paymentHideCash</Text>,
                    name: 'featureFlags.paymentHideCash',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.paymentHideCash, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.paymentHideVipps'>paymentHideVipps</Text>,
                    placeholder: <Text id='input.paymentHideVipps'>paymentHideVipps</Text>,
                    name: 'featureFlags.paymentHideVipps',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.paymentHideVipps, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.paymentHideInvoice'>paymentHideInvoice</Text>,
                    placeholder: <Text id='input.paymentHideInvoice'>paymentHideInvoice</Text>,
                    name: 'featureFlags.paymentHideInvoice',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.paymentHideInvoice, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.paymentHideAccount'>paymentHideAccount</Text>,
                    placeholder: <Text id='input.paymentHideAccount'>paymentHideAccount</Text>,
                    name: 'featureFlags.paymentHideAccount',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.paymentHideAccount, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },

                {
                    title: <Text id='input.hasVippsPayment'>hasVippsPayment</Text>,
                    placeholder: <Text id='input.hasVippsPayment'>hasVippsPayment</Text>,
                    name: 'featureFlags.hasVippsPayment',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.hasVippsPayment, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.hasPaypalPayment'>hasPaypalPayment</Text>,
                    placeholder: <Text id='input.hasPaypalPayment'>hasPaypalPayment</Text>,
                    name: 'featureFlags.hasPaypalPayment',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.hasPaypalPayment, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.hasBankPayment'>hasBankPayment</Text>,
                    placeholder: <Text id='input.hasBankPayment'>hasBankPayment</Text>,
                    name: 'featureFlags.hasBankPayment',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.hasBankPayment, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.hasInvoicePayment'>hasInvoicePayment</Text>,
                    placeholder: <Text id='input.hasInvoicePayment'>hasInvoicePayment</Text>,
                    name: 'featureFlags.hasInvoicePayment',
                    type: 'toggle',
                    icon: 'fa-duotone fa-trowel-bricks',
                    getValue: obj => parseInt(obj.featureFlags?.hasInvoicePayment, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
                {
                    title: <Text id='input.invoicePaymentInfo'>invoicePaymentInfo</Text>,
                    placeholder: <Text id='input.invoicePaymentInfo'>invoicePaymentInfo</Text>,
                    help: <Text id='input.invoicePaymentInfo-help'>Invoice payment info.</Text>,
                    name: 'invoicePaymentInfo',
                    type: 'textarea',
                    icon: 'fa-duotone fa-input-text',
                    getValue: obj => obj.invoicePaymentInfo,
                },
                {
                    title: <Text id='input.shareProductDatabase'>shareProductDatabase</Text>,
                    placeholder: <Text id='input.shareProductDatabase'>shareProductDatabase</Text>,
                    name: 'shareProductDatabase',
                    type: 'toggle',
                    icon: 'fa-duotone fa-square-share-nodes',
                    getValue: obj => parseInt(obj.shareProductDatabase, 10) === 1 ? 0 : 1,
                    showInList: false,
                    // callback: val => reloadApp(),
                },
            ],
        },
        {
            name: <Text id='input.customer-vipps-title'>Vipps integration</Text>,
            key: 'Vipps integration info',
            fields: [
                {
                    title: <Text id='input.vippsName'>vippsName</Text>,
                    placeholder: <Text id='input.vippsName'>vippsName</Text>,
                    help: <Text id='input.vippsName-help'>Vippsname has to be a-z and lowercase only.</Text>,
                    name: 'vippsName',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsName,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsVersion'>vippsVersion</Text>,
                    placeholder: <Text id='input.vippsVersion'>vippsVersion</Text>,
                    help: <Text id='input.vippsVersion-help'>vippsVersion has to 1.0.0</Text>,
                    name: 'vippsVersion',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsVersion,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsPluginName'>vippsPluginName</Text>,
                    placeholder: <Text id='input.vippsPluginName'>vippsPluginName</Text>,
                    help: <Text id='input.vippsPluginName-help'>vippsPluginName has to be a-z and lowercase only.</Text>,
                    name: 'vippsPluginName',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsPluginName,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsPluginVersion'>vippsPluginVersion</Text>,
                    placeholder: <Text id='input.vippsPluginVersion'>vippsPluginVersion</Text>,
                    help: <Text id='input.vippsPluginVersion-help'>vippsPluginVersion has to 1.0.0</Text>,
                    name: 'vippsPluginVersion',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsPluginVersion,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsMerchantSerialNumber'>vippsMerchantSerialNumber</Text>,
                    placeholder: <Text id='input.vippsMerchantSerialNumber'>vippsMerchantSerialNumber</Text>,
                    help: <Text id='input.vippsMerchantSerialNumber-help'>Vipps MSN number.</Text>,
                    name: 'vippsMerchantSerialNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsMerchantSerialNumber,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsClientId'>vippsClientId</Text>,
                    placeholder: <Text id='input.vippsClientId'>vippsClientId</Text>,
                    name: 'vippsClientId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsClientId,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsClientSecret'>vippsClientSecret</Text>,
                    placeholder: <Text id='input.vippsClientSecret'>vippsClientSecret</Text>,
                    name: 'vippsClientSecret',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsClientSecret,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsSubscriptionKey1'>vippsSubscriptionKey1</Text>,
                    placeholder: <Text id='input.vippsSubscriptionKey1'>vippsSubscriptionKey1</Text>,
                    name: 'vippsSubscriptionKey1',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsSubscriptionKey1,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsSubscriptionKey2'>vippsSubscriptionKey2</Text>,
                    placeholder: <Text id='input.vippsSubscriptionKey2'>vippsSubscriptionKey2</Text>,
                    name: 'vippsSubscriptionKey2',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsSubscriptionKey2,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsUseTestMode'>vippsUseTestMode</Text>,
                    placeholder: <Text id='input.vippsUseTestMode'>vippsUseTestMode</Text>,
                    name: 'vippsUseTestMode',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsUseTestMode,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsTestPhoneNumber'>vippsTestPhoneNumber</Text>,
                    placeholder: <Text id='input.vippsTestPhoneNumber'>vippsTestPhoneNumber</Text>,
                    name: 'vippsTestPhoneNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsTestPhoneNumber,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsTermsOfSale'>vippsTermsOfSale</Text>,
                    placeholder: <Text id='input.vippsTermsOfSale'>vippsTermsOfSale</Text>,
                    help: <Text id='input.vippsTermsOfSale-help'>Terms of sale in norwegian.</Text>,
                    name: 'vippsTermsOfSale',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsTermsOfSale,
                    showInList: false,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.vippsTermsOfSaleEn'>vippsTermsOfSaleEn</Text>,
                    placeholder: <Text id='input.vippsTermsOfSaleEn'>vippsTermsOfSaleEn</Text>,
                    help: <Text id='input.vippsTermsOfSaleEn-help'>Terms of sale in english.</Text>,
                    name: 'vippsTermsOfSaleEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsTermsOfSaleEn,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.customer-paypal-title'>Paypal integration</Text>,
            key: 'Paypal integration info',
            fields: [
                {
                    title: <Text id='input.paypalHostname'>Paypal hostname</Text>,
                    placeholder: <Text id='input.paypalHostname'>Paypal hostname</Text>,
                    help: <Text id='input.paypalHostname-help'>API hostname of PayPal integrations. Ie: "api-m.sandbox.paypal.com".</Text>,
                    name: 'paypalHostname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.paypalHostname,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.paypalClientId'>Paypal ClientId</Text>,
                    placeholder: <Text id='input.paypalClientId'>Paypal ClientId</Text>,
                    name: 'paypalClientId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.paypalClientId,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.paypalClientSecret'>Paypal ClientSecret</Text>,
                    placeholder: <Text id='input.paypalClientSecret'>Paypal ClientSecret</Text>,
                    name: 'paypalClientSecret',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.paypalClientSecret,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.paypalReturnUrl'>Paypal Return URL</Text>,
                    placeholder: <Text id='input.paypalReturnUrl'>Paypal Return URL</Text>,
                    name: 'paypalReturnUrl',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.paypalReturnUrl,
                    showInList: false,
                    isAdminField: true,
                },
            ],
        },
        {
            name: <Text id='input.customer-api-title'>API integration</Text>,
            key: 'API integration info',
            fields: [
                {
                    title: <Text id='input.fikenSlug'>Fiken.no - Slug</Text>,
                    placeholder: <Text id='input.fikenSlug'>Fiken.no - Slug</Text>,
                    help: <Text id='input.fikenSlug-help'>You will find this in the URL in Fiken. https://fiken.no/foretak/dyrejournal-as/forside/ = dyrejournal-as</Text>,
                    name: 'fikenSlug',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.fikenSlug,
                    showInList: false,
                },
                {
                    title: <Text id='input.fikenPaymentMethodInvoice'>Fiken.no - Payment Method: Invoice</Text>,
                    placeholder: <Text id='input.fikenPaymentMethodInvoice'>Fiken.no - Payment Method: Invoice</Text>,
                    help: <Text id='input.fikenPaymentMethodInvoice-help'>Check you Fiken.no installation to find the correct account. '1920:10001', might be your main bank account.</Text>,
                    name: 'fikenPaymentMethodInvoice',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.fikenPaymentMethodInvoice,
                    showInList: false,
                },
                {
                    title: <Text id='input.fikenPaymentMethodAccount'>Fiken.no - Payment Method: Account</Text>,
                    placeholder: <Text id='input.fikenPaymentMethodAccount'>Fiken.no - Payment Method: Account</Text>,
                    help: <Text id='input.fikenPaymentMethodAccount-help'>Check you Fiken.no installation to find the correct account. '1920:10001', might be your main bank account.</Text>,
                    name: 'fikenPaymentMethodAccount',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.fikenPaymentMethodAccount,
                    showInList: false,
                },
                {
                    title: <Text id='input.fikenPaymentMethodCard'>Fiken.no - Payment Method: Card</Text>,
                    placeholder: <Text id='input.fikenPaymentMethodCard'>Fiken.no - Payment Method: Card</Text>,
                    help: <Text id='input.fikenPaymentMethodCard-help'>Check you Fiken.no installation to find the correct account. '1920:10001', might be your main bank account.</Text>,
                    name: 'fikenPaymentMethodCard',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.fikenPaymentMethodCard,
                    showInList: false,
                },
                {
                    title: <Text id='input.fikenPaymentMethodCash'>Fiken.no - Payment Method: Cash</Text>,
                    placeholder: <Text id='input.fikenPaymentMethodCash'>Fiken.no - Payment Method: Cash</Text>,
                    help: <Text id='input.fikenPaymentMethodCash-help'>Check you Fiken.no installation to find the correct account. '1920:10001', might be your main bank account.</Text>,
                    name: 'fikenPaymentMethodCash',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.fikenPaymentMethodCash,
                    showInList: false,
                },
                {
                    title: <Text id='input.fikenPaymentMethodVipps'>Fiken.no - Payment Method: Vipps</Text>,
                    placeholder: <Text id='input.fikenPaymentMethodVipps'>Fiken.no - Payment Method: Vipps</Text>,
                    help: <Text id='input.fikenPaymentMethodVipps-help'>Check you Fiken.no installation to find the correct account. '1920:10001', might be your main bank account.</Text>,
                    name: 'fikenPaymentMethodVipps',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.fikenPaymentMethodVipps,
                    showInList: false,
                },
                // {
                //     title: <Text id='input.fikenPaymentMethodCreditNote'>Fiken.no - Payment Method: Creditnote</Text>,
                //     placeholder: <Text id='input.fikenPaymentMethodCreditNote'>Fiken.no - Payment Method: Creditnote</Text>,
                //     help: <Text id='input.fikenPaymentMethodCreditNote-help'>Check you Fiken.no installation to find the correct account. '1920:10001', might be your main bank account.</Text>,
                //     name: 'fikenPaymentMethodCreditNote',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj.fikenPaymentMethodCreditNote,
                //     showInList: false,
                // },
                {
                    title: <Text id='input.fikenAccessToken'>Fiken.no - Personal accessToken</Text>,
                    placeholder: <Text id='input.fikenAccessToken'>Fiken.no - Personal accessToken</Text>,
                    name: 'fikenAccessToken',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.fikenAccessToken,
                    showInList: false,
                },
                {
                    title: <Text id='input.tripletexEmployeeToken'>Tripletex.no - EmployeeToken</Text>,
                    placeholder: <Text id='input.tripletexEmployeeToken'>Tripletex.no - EmployeeToken</Text>,
                    name: 'tripletexEmployeeToken',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.tripletexEmployeeToken,
                    showInList: false,
                },
                {
                    title: <Text id='input.animalia-producerNumber'>Animalia producerNumber</Text>,
                    placeholder: <Text id='input.animalia-producerNumber'>Animalia producerNumber</Text>,
                    name: 'producerNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.producerNumber,
                },


                {
                    title: <Text id='input.nets-username'>NETS username</Text>,
                    placeholder: <Text id='input.nets-username'>NETS username</Text>,
                    name: 'netsUsername',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.netsUsername,
                },
                {
                    title: <Text id='input.nets-password'>NETS password</Text>,
                    placeholder: <Text id='input.nets-password'>NETS password</Text>,
                    name: 'netsPassword',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.netsUsername,
                },

                {
                    title: <Text id='input.netsTerminals'>NETS Terminals</Text>,
                    placeholder: <Text id='input.netsTerminals'>NETS Terminals</Text>,
                    name: 'netsTerminals',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    getValue: customer => customer.netsTerminals?.map(m => m),
                    datalist: [
                        'name', 'terminalId',
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-left px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.name}<br />
                            <span class='font-weight-lighter'>Terminal ID:</span> {obj.terminalId}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                },

                {
                    title: <Text id='input.rubicOrganizationId'>Rubic Organization ID</Text>,
                    placeholder: <Text id='input.rubicOrganizationId'>Rubic Organization ID</Text>,
                    name: 'rubicOrganizationId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.rubicOrganizationId,
                },
                {
                    title: <Text id='input.rubicOrganizationNumber'>Rubic Organization Number</Text>,
                    placeholder: <Text id='input.rubicOrganizationNumber'>Rubic Organization Number</Text>,
                    name: 'rubicOrganizationNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-number',
                    getValue: obj => obj.rubicOrganizationNumber,
                },

            ],
        },
        {
            name: <Text id='input.user-images-file-links-title'>Images, files and links</Text>,
            key: 'Images, files and links',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.icons'>icons</Text>,
                    placeholder: <Text id='input.icons'>icons</Text>,
                    name: 'icons',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.icons,
                    showInList: false,
                    isAdminField: true,
                },
            ],
        },
    ],
    installation: [
        {
            name: <Text id='input.installations-advanced-title'>Installation advanced</Text>,
            // help: <Text id='input.installation-basics-help'>Basic settings for your profile.</Text>,
            key: 'Installation advanced',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: installation => installation.id || null,
                    isReadOnly: true,
                    showInList: true,
                    isAdminField: true,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: installation => installation.customer || null,
                    showInList: true,
                    isAdminField: true,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: installation => installation.user || null,
                    showInList: true,
                    isAdminField: true,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.qr'>Qr</Text>,
                    placeholder: <Text id='input.qr'>Qr</Text>,
                    name: 'qr',
                    type: 'text',
                    icon: 'fa-solid fa-qrcode',
                    getValue: installation => installation.code || null,
                    showQR: true,
                    isReadOnly: true,
                    showInList: true,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.code'>Code</Text>,
                    placeholder: <Text id='input.code'>Code</Text>,
                    name: 'code',
                    type: 'text',
                    icon: 'fa-solid fa-key',
                    getValue: installation => installation.code || null,
                    showQR: false,
                    isReadOnly: true,
                    showInList: true,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.ordernumber'>OrderNumber</Text>,
                    placeholder: <Text id='input.ordernumber'>OrderNumber</Text>,
                    name: 'orderNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: installation => installation.orderNumber || null,
                    showInList: true,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.isWishList'>isWishList</Text>,
                    placeholder: <Text id='input.isWishList'>isWishList</Text>,
                    name: 'isWishList',
                    type: 'toggle',
                    icon: 'fa-solid fa-bag-shopping',
                    getValue: part => part.isWishList ? 1 : 0,
                    showInList: true,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.wishListType'>wishListType</Text>,
                    placeholder: <Text id='input.wishListType'>wishListType</Text>,
                    name: 'wishListType',
                    type: 'select',
                    icon: 'fa-solid fa-list',
                    getValue: obj => obj.wishListType,
                    datalist: [['birthday', 'Birthday'], ['buying', 'Buying'], ['christmas', 'Christmas'], ['dreaming', 'Dreaming about'], ['wedding', 'Wedding'], ['other', 'Other']],
                    // datalistFieldKey: e => e.id,
                    // datalistFieldVal: e => `${e.firstname} ${e.lastname}`,
                    showInList: true,
                },



            ],
        },
        {
            name: <Text id='input.installations-basics-title'>Installation basics</Text>,
            // help: <Text id='input.installation-basics-help'>Basic settings for your profile.</Text>,
            key: 'Installation basics',
            fields: [
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: installation => installation.name || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: installation => installation.description,
                    hideOnCreate: true,
                },
            ],
        },
        {
            name: <Text id='input.installations-location-title'>Installation location</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'Installation location',
            fields: [
                {
                    title: <Text id='input.address'>Address</Text>,
                    placeholder: <Text id='input.address'>Address</Text>,
                    name: 'address',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: installation => installation.address,
                },
                {
                    title: <Text id='input.postalcode'>Postalcode</Text>,
                    placeholder: <Text id='input.postalcode'>Postalcode</Text>,
                    name: 'postalcode',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: installation => installation.postalcode,
                    callback: async (obj, updateValue) => {
                        if (obj.postalcode && obj.postalcode.length === 4) {
                            const response = await postnummerLookup(obj.postalcode);

                            if (response) {
                                const place = response[0]?.poststed;
                                updateValue({
                                    name: 'place',
                                    value: place,
                                    id: obj.id,
                                });
                            }
                        }
                    },
                },
                {
                    title: <Text id='input.place'>Place</Text>,
                    placeholder: <Text id='input.place'>Place</Text>,
                    name: 'place',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: installation => installation.place,
                    showInList: true,
                },
                {
                    title: <Text id='input.country'>Country</Text>,
                    placeholder: <Text id='input.country'>Country</Text>,
                    name: 'country',
                    type: 'text',
                    icon: 'fa-solid fa-earth-europe',
                    getValue: installation => installation.country,
                    showInList: true,
                    hideOnCreate: true,
                },
            ],
        },
        {
            name: <Text id='input.installations-social-title'>Installation social</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'Installation social',
            fields: [
                {
                    title: <Text id='input.url'>Url</Text>,
                    placeholder: <Text id='input.url'>Url</Text>,
                    name: 'url',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: installation => installation.url,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.facebook'>Facebook</Text>,
                    placeholder: <Text id='input.facebook'>Facebook</Text>,
                    name: 'facebook',
                    type: 'text',
                    icon: 'fa-brands fa-facebook',
                    getValue: installation => installation.facebook,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.instagram'>Instagram</Text>,
                    placeholder: <Text id='input.instagram'>Instagram</Text>,
                    name: 'instagram',
                    type: 'text',
                    icon: 'fa-brands fa-instagram',
                    getValue: installation => installation.instagram,
                    hideOnCreate: true,
                },
            ],
        },
        {
            name: <Text id='input.installations-related-title'>Installation related</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'Installation related',
            fields: [
                {
                    title: <Text id='input.members'>Members</Text>,
                    placeholder: <Text id='input.members'>Members</Text>,
                    name: 'members',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'adminMembers',
                    getValue: installation => installation.members?.map(m => m),
                    datalist: [
                        'id', 'firstname', 'lastname', 'email', 'cellphone',
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-left px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.firstname} {obj.lastname}<br />
                            <span class='font-weight-lighter'>Cellphone:</span> {obj.cellphone}<br />
                            <span class='font-weight-lighter'>Email:</span> {obj.email}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.parts'>Parts</Text>,
                    placeholder: <Text id='input.parts'>Parts</Text>,
                    name: 'parts',
                    type: 'arrayObject',
                    lookupArray: 'adminParts',
                    icon: 'fa-solid fa-layer-group',
                    getValue: installation => installation.parts?.map(file => file),
                    datalist: [
                        'qty', 'id', 'name', 'partNumber',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.inspections'>Inspections</Text>,
                    placeholder: <Text id='input.inspections'>Inspections</Text>,
                    name: 'inspections',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-user-magnifying-glass',
                    getValue: installation => installation.inspections?.map(yt => yt),
                    datalist: [
                        'partNumber',
                        'check1', 'check2', 'check3', 'check4', 'check5',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '100%',
                    displayHeight: '200px',
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.inspectionDate'>InspectionDate</Text>,
                    placeholder: <Text id='input.inspectionDate'>InspectionDate</Text>,
                    name: 'inspectionDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: installation => installation.inspectionDate,
                    showInList: true,
                    hideOnCreate: true,
                },
            ],
        },
        {
            name: <Text id='input.installations-media-title'>Installation media</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'Installation media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: installation => installation.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: installation => installation.files,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.youtubeVideos'>YoutubeVideos</Text>,
                    placeholder: <Text id='input.youtubeVideos'>YoutubeVideos</Text>,
                    name: 'youtubeVideos',
                    type: 'arrayObject',
                    // displayValue: (el) => `${el.url}`,
                    icon: 'fa-brands fa-youtube',
                    getValue: installation => installation.youtubeVideos?.map(file => file),
                    datalist: [
                        'url', 'title', 'description',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    hideOnCreate: true,
                },
            ],
        },
    ],
    installationPart: [
        {
            name: <Text id='input.installations-part-basics-title'>InstallationPart basics</Text>,
            key: 'InstallationPart basics',
            fields: [
                {
                    title: <Text id='input.qty'>Qty</Text>,
                    placeholder: <Text id='input.qty'>Qty</Text>,
                    name: 'qty',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.qty || null,
                },
                {
                    title: <Text id='input.partNumber'>PartNumber</Text>,
                    placeholder: <Text id='input.partNumber'>PartNumber</Text>,
                    name: 'partNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.partNumber || null,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name || null,
                },
            ],
        },
    ],
    part: [
        {
            // name: 'Part related',
            name: <Text id='input.part-related-title'>Part Related</Text>,
            help: <Text id='input.part-related-help'>If this part is related to another part, you should add the part in this section.</Text>,
            key: 'Part related',
            lookupArray: 'parts',
            foundListName: 'foundParts',
            foundListText: 'Found parent parts',
            foundListDisplayKeys: ['id', 'name'],
            foundListReplaceKeys: [
                ['id', 'parent'],
                ['name', 'parentTitle'],
            ],
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            fields: [
                {
                    title: <Text id='input.part-parentTitle'>Parent Title</Text>,
                    placeholder: <Text id='input.part-parentTitle'>Parent Title</Text>,
                    name: 'parentTitle',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.parentTitle || null,
                    showInList: true,
                    // isAdminField: true,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.parent'>Parent</Text>,
                    placeholder: <Text id='input.parent'>Parent</Text>,
                    name: 'parent',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: part => part.parent || null,
                    showInList: true,
                    // isAdminField: true,
                    // hideOnCreate: true,
                },
            ],
        },
        {
            name:  <Text id='input.part-basics-title'>Part Basics</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'Part basics',
            fields: [
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: part => part.customer || null,
                    showInList: true,
                    isAdminField: true,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.qty'>Qty</Text>,
                    placeholder: <Text id='input.qty'>Qty</Text>,
                    name: 'qty',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: part => part.qty || null,
                    showInList: false,
                    isAdminField: true,
                    isReadOnly: true,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.sold'>sold</Text>,
                    placeholder: <Text id='input.sold'>sold</Text>,
                    name: 'sold',
                    type: 'toggle',
                    icon: 'fa-solid fa-house-user',
                    getValue: part => part.sold ? 1 : 0,
                    showInList: true,
                    displayValue: val => val ? <Text id='input.yes'>Yes</Text> : <Text id='input.no'>No</Text>,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.updatedDate'>Last updated</Text>,
                    placeholder: <Text id='input.updatedDate'>Last updated</Text>,
                    name: 'updatedDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: part => part.updatedDate || null,
                    showInList: false,
                    isAdminField: true,
                    isReadOnly: true,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    lookupArray: 'parts',
                    getValue: part => part.name || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.url'>Url</Text>,
                    placeholder: <Text id='input.url'>Url</Text>,
                    help: <Text id='input.url-help'>If you add a url to a part, the system will try to fetch the data from the url.</Text>,
                    name: 'url',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: part => part.url,
                    displayValue: val => val ? <><a href={val} rel='noopener noreferrer' target='_blank'>{val}</a></> : null,
                    callback: async (part, updateValue) => {
                        if (part.url && part.url.startsWith('http')) {
                            const response = await spiderPage(part.url);
                            //
                            if (response) {
                                updateValue({
                                    name: 'name',
                                    value: response.title,
                                });
                                updateValue({
                                    name: 'description',
                                    value: response.description,
                                });
                                updateValue({
                                    name: 'imageUrl',
                                    value: response.image || response.icon,
                                });
                                updateValue({
                                    name: 'brand',
                                    value: response.product?.brand?.name,
                                });
                                updateValue({
                                    name: 'category',
                                    value: response.product?.category,
                                });
                                updateValue({
                                    name: 'sku',
                                    value: response.product?.sku,
                                });
                                updateValue({
                                    name: 'mpn',
                                    value: response.product?.mpn,
                                });
                                updateValue({
                                    name: 'gtin13',
                                    value: response.product?.gtin13,
                                });
                                updateValue({
                                    name: 'price',
                                    value: response.product?.offers?.price,
                                });
                                updateValue({
                                    name: 'rating',
                                    value: response.product?.aggregateRating?.ratingValue,
                                });
                                updateValue({
                                    name: 'ratingCount',
                                    value: response.product?.aggregateRating?.reviewCount,
                                });
                            }
                        }
                    },
                    showInList: false,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.imageUrl'>ImageUrl</Text>,
                    placeholder: <Text id='input.imageUrl'>ImageUrl</Text>,
                    name: 'imageUrl',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.imageUrl || null,
                    showInList: true,
                },

                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: part => part.description,
                    showInList: false,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    name: 'tags',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'tags',
                    getValue: part => part.tags,
                    datalist: [
                        'name',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
            ],
        },
        {
            name: <Text id='input.part-media-title'>Part media</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'Part media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: part => part.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: part => part.files,
                    showInList: false,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.youtubeVideos'>Youtube Videos</Text>,
                    placeholder: <Text id='input.youtubeVideos'>Youtube Videos</Text>,
                    name: 'youtubeVideos',
                    type: 'arrayObject',
                    icon: 'fa-brands fa-youtube',
                    getValue: part => part.youtubeVideos?.map(yt => yt),
                    datalist: [
                        'url', 'title', 'description',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    // hideOnCreate: true,
                },
            ],
        },
        {
            name:  <Text id='input.part-extra-info'>Part Extra info</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'Part extra info',
            fields: [
                {
                    title: <Text id='input.url2'>Url 2</Text>,
                    placeholder: <Text id='input.url2'>Url 2</Text>,
                    help: <Text id='input.url2-help'>If you add an extra url to a part, you will be able to try to fetch data from this url.</Text>,
                    name: 'url2',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: part => part.url2,
                    displayValue: val => val ? <><a href={val} rel='noopener noreferrer' target='_blank'>{val}</a></> : null,
                    showInList: false,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.url3'>Url 3</Text>,
                    placeholder: <Text id='input.url3'>Url 3</Text>,
                    help: <Text id='input.url2-help'>If you add an extra url to a part, you will be able to try to fetch data from this url.</Text>,
                    name: 'url3',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: part => part.url3,
                    displayValue: val => val ? <><a href={val} rel='noopener noreferrer' target='_blank'>{val}</a></> : null,
                    showInList: false,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.url4'>Url 4</Text>,
                    placeholder: <Text id='input.url4'>Url 4</Text>,
                    help: <Text id='input.url2-help'>If you add an extra url to a part, you will be able to try to fetch data from this url.</Text>,
                    name: 'url4',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: part => part.url4,
                    displayValue: val => val ? <><a href={val} rel='noopener noreferrer' target='_blank'>{val}</a></> : null,
                    showInList: false,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.url5'>Url 5</Text>,
                    placeholder: <Text id='input.url5'>Url 5</Text>,
                    help: <Text id='input.url2-help'>If you add an extra url to a part, you will be able to try to fetch data from this url.</Text>,
                    name: 'url5',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: part => part.url5,
                    displayValue: val => val ? <><a href={val} rel='noopener noreferrer' target='_blank'>{val}</a></> : null,
                    showInList: false,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.name2'>name2</Text>,
                    placeholder: <Text id='input.name2'>name2</Text>,
                    help: <Text id='input.name2-help'>This name is fetched from the related URL.</Text>,
                    name: 'name2',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    lookupArray: 'parts',
                    getValue: part => part.name2 || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.name3'>name3</Text>,
                    placeholder: <Text id='input.name3'>name3</Text>,
                    help: <Text id='input.name2-help'>This name is fetched from the related URL.</Text>,
                    name: 'name3',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    lookupArray: 'parts',
                    getValue: part => part.name3 || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.name4'>name4</Text>,
                    placeholder: <Text id='input.name4'>name4</Text>,
                    help: <Text id='input.name2-help'>This name is fetched from the related URL.</Text>,
                    name: 'name4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    lookupArray: 'parts',
                    getValue: part => part.name4 || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.name5'>name5</Text>,
                    placeholder: <Text id='input.name5'>name5</Text>,
                    help: <Text id='input.name2-help'>This name is fetched from the related URL.</Text>,
                    name: 'name5',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    lookupArray: 'parts',
                    getValue: part => part.name5 || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.imageUrl2'>imageUrl2</Text>,
                    placeholder: <Text id='input.imageUrl2'>imageUrl2</Text>,
                    help: <Text id='input.imageUrl2-help'>This image URL is fetched from the related URL.</Text>,
                    name: 'imageUrl2',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.imageUrl2 || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.imageUrl3'>imageUrl3</Text>,
                    placeholder: <Text id='input.imageUrl3'>imageUrl3</Text>,
                    help: <Text id='input.imageUrl2-help'>This image URL is fetched from the related URL.</Text>,
                    name: 'imageUrl3',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.imageUrl3 || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.imageUrl4'>imageUrl4</Text>,
                    placeholder: <Text id='input.imageUrl4'>imageUrl4</Text>,
                    help: <Text id='input.imageUrl2-help'>This image URL is fetched from the related URL.</Text>,
                    name: 'imageUrl4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.imageUrl4 || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.imageUrl5'>imageUrl5</Text>,
                    placeholder: <Text id='input.imageUrl5'>imageUrl5</Text>,
                    help: <Text id='input.imageUrl2-help'>This image URL is fetched from the related URL.</Text>,
                    name: 'imageUrl5',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.imageUrl5 || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.description2'>description2</Text>,
                    placeholder: <Text id='input.description2'>description2</Text>,
                    help: <Text id='input.description2-help'>This description is fetched from the related URL.</Text>,
                    name: 'description2',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: part => part.description2,
                    showInList: false,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.description3'>description3</Text>,
                    placeholder: <Text id='input.description3'>description3</Text>,
                    help: <Text id='input.description2-help'>This description is fetched from the related URL.</Text>,
                    name: 'description3',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: part => part.description3,
                    showInList: false,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.description4'>description4</Text>,
                    placeholder: <Text id='input.description4'>description4</Text>,
                    help: <Text id='input.description2-help'>This description is fetched from the related URL.</Text>,
                    name: 'description4',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: part => part.description4,
                    showInList: false,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.description5'>description5</Text>,
                    placeholder: <Text id='input.description5'>description5</Text>,
                    help: <Text id='input.description2-help'>This description is fetched from the related URL.</Text>,
                    name: 'description5',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: part => part.description3,
                    showInList: false,
                    // hideOnCreate: true,
                },
            ],
        },
        {
            name: <Text id='input.part-details-title'>Part details</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'Part details',
            fields: [
                {
                    title: <Text id='input.category'>category</Text>,
                    placeholder: <Text id='input.category'>category</Text>,
                    help: <Text id='input.category-help'>The main product category.</Text>,
                    name: 'category',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.category || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.gtin13'>gtin13</Text>,
                    placeholder: <Text id='input.gtin13'>gtin13</Text>,
                    help: <Text id='input.gtin13-help'>Global Trade Item Number according to the GTIN13 standard.</Text>,
                    name: 'gtin13',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.gtin13 || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.sku'>sku</Text>,
                    placeholder: <Text id='input.sku'>sku</Text>,
                    help: <Text id='input.sku-help'>Internal product number for the shop you bought it from.</Text>,
                    name: 'sku',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.sku || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.mpn'>mpn</Text>,
                    placeholder: <Text id='input.mpn'>mpn</Text>,
                    help: <Text id='input.mpn-help'>MPN stands for Manufacturer Part Number, a unique identifier for a product issued by the manufacturer.</Text>,
                    name: 'mpn',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.mpn || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.commodityCode'>commodityCode</Text>,
                    placeholder: <Text id='input.commodityCode'>commodityCode</Text>,
                    help: <Text id='input.commodityCode-help'>Commodity Code is a numeric code for classifying goods in international trade. Used for customs and tariffs.</Text>,
                    name: 'commodityCode',
                    type: 'number',
                    icon: 'fa-solid fa-money-bill',
                    getValue: part => part.commodityCode || null,
                    displayValue: val => val ? val : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.brand'>Brand</Text>,
                    placeholder: <Text id='input.brand'>Brand</Text>,
                    name: 'brand',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.brand || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.model'>Model</Text>,
                    placeholder: <Text id='input.model'>Model</Text>,
                    name: 'model',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.model || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.isConsumable'>IsConsumable</Text>,
                    placeholder: <Text id='input.isConsumable'>IsConsumable</Text>,
                    help: <Text id='input.isConsumable-help'>Is this product something you consume in some way. Ie: food, one time batteries, etc.</Text>,
                    name: 'isConsumable',
                    type: 'toggle',
                    icon: 'fa-solid fa-bag-shopping',
                    getValue: part => part.isConsumable ? 1 : 0,
                    showInList: true,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.serialNumber'>SerialNumber</Text>,
                    placeholder: <Text id='input.serialNumber'>SerialNumber</Text>,
                    name: 'serialNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.serialNumber || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.regNumber'>RegNumber</Text>,
                    placeholder: <Text id='input.regNumber'>RegNumber</Text>,
                    name: 'regNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.regNumber || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.partNumber'>PartNumber</Text>,
                    placeholder: <Text id='input.partNumber'>PartNumber</Text>,
                    name: 'partNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.partNumber || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.purchaseDate'>PurchaseDate</Text>,
                    placeholder: <Text id='input.purchaseDate'>PurchaseDate</Text>,
                    name: 'purchaseDate',
                    type: 'date',
                    icon: 'fa-solid fa-calendar',
                    getValue: part => part.purchaseDate || null,
                    displayValue: val => val ? util.formatDate(val, { locale: 'nb-NO' }) : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.currency'>currency</Text>,
                    placeholder: <Text id='input.currency'>currency</Text>,
                    name: 'currency',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.currency || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.price'>Price</Text>,
                    placeholder: <Text id='input.price'>Price</Text>,
                    name: 'price',
                    type: 'number',
                    icon: 'fa-solid fa-money-bill',
                    getValue: part => part.price || null,
                    displayValue: val => val ? util.format(val, 0, ',') : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.latestPrice'>latestPrice</Text>,
                    placeholder: <Text id='input.latestPrice'>latestPrice</Text>,
                    name: 'latestPrice',
                    type: 'number',
                    icon: 'fa-solid fa-money-bill',
                    getValue: part => part.latestPrice || null,
                    displayValue: val => val ? util.format(val, 0, ',') : null,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.part-rating-title'>Part rating</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'Part rating',
            fields: [
                {
                    title: <Text id='input.rating'>rating</Text>,
                    placeholder: <Text id='input.rating'>rating</Text>,
                    name: 'rating',
                    type: 'number',
                    icon: 'fa-solid fa-star',
                    getValue: part => part.rating || null,
                    displayValue: val => val ? util.format(val, 1, ',') : null,
                    showInList: true,
                    isReadOnly: true,
                },
                {
                    title: <Text id='input.ratingCount'>ratingCount</Text>,
                    placeholder: <Text id='input.ratingCount'>ratingCount</Text>,
                    name: 'ratingCount',
                    type: 'number',
                    icon: 'fa-solid fa-star-exclamation',
                    getValue: part => part.ratingCount || null,
                    displayValue: val => val ? util.format(val, 0, ',') : null,
                    showInList: true,
                    isReadOnly: true,
                },
            ],
        },
        {
            name: <Text id='input.part-usage-title'>Part usage</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'Part usage',
            fields: [
                {
                    title: <Text id='input.requireInspection'>RequireInspection</Text>,
                    placeholder: <Text id='input.requireInspection'>RequireInspection</Text>,
                    name: 'requireInspection',
                    type: 'toggle',
                    icon: 'fa-solid fa-user-police',
                    getValue: part => part.requireInspection ? 1 : 0,
                    showInList: true,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.serviceDate'>ServiceDate</Text>,
                    placeholder: <Text id='input.serviceDate'>ServiceDate</Text>,
                    name: 'serviceDate',
                    type: 'date',
                    icon: 'fa-solid fa-calendar',
                    getValue: part => part.serviceDate || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.totalDistance'>TotalDistance</Text>,
                    placeholder: <Text id='input.totalDistance'>TotalDistance</Text>,
                    name: 'totalDistance',
                    type: 'number',
                    icon: 'fa-solid fa-road',
                    getValue: part => part.totalDistance || null,
                    showInList: true,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.totalHours'>TotalHours</Text>,
                    placeholder: <Text id='input.totalHours'>TotalHours</Text>,
                    name: 'totalHours',
                    type: 'number',
                    icon: 'fa-solid fa-clock',
                    getValue: part => part.totalHours || null,
                    showInList: true,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.inspections'>Inspections</Text>,
                    placeholder: <Text id='input.inspections'>Inspections</Text>,
                    name: 'inspections',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-user-magnifying-glass',
                    getValue: part => part.inspections?.map(yt => yt),
                    datalist: [
                        'image', 'check1', 'check2', 'check3', 'check4', 'check5'
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '100%',
                    displayHeight: '200px',
                    // hideOnCreate: true,
                },
            ],
        },
        {
            name: <Text id='input.part-owner-title'>Part owner</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'Part owner',
            lookupArray: 'loaners',
            foundListName: 'foundLoaners',
            foundListText: 'Found similar owners',
            foundListDisplayKeys: ['firstname', 'lastname', 'cellphone', 'email'],
            foundListReplaceKeys: [
                ['firstname', 'ownerFirstname'],
                ['lastname', 'ownerLastname'],
                ['cellphone', 'ownerCellphone'],
                ['email', 'ownerEmail'],
            ],
            // foundListKeysName: 'foundLoanersKeys',
            fields: [
                {
                    title: <Text id='input.ownedByOther'>OwnedByOther</Text>,
                    placeholder: <Text id='input.ownedByOther'>OwnedByOther</Text>,
                    name: 'ownedByOther',
                    type: 'toggle',
                    icon: 'fa-solid fa-house-user',
                    getValue: part => part.ownedByOther ? 1 : 0,
                    showInList: true,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.ownerFirstname'>OwnerFirstname</Text>,
                    placeholder: <Text id='input.ownerFirstname'>OwnerFirstname</Text>,
                    name: 'ownerFirstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.ownerFirstname || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.ownerLastname'>OwnerLastname</Text>,
                    placeholder: <Text id='input.ownerLastname'>OwnerLastname</Text>,
                    name: 'ownerLastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: part => part.ownerLastname || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.ownerCellphone'>OwnerCellphone</Text>,
                    placeholder: <Text id='input.ownerCellphone'>OwnerCellphone</Text>,
                    name: 'ownerCellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: part => part.ownerCellphone || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.ownerEmail'>OwnerEmail</Text>,
                    placeholder: <Text id='input.ownerEmail'>OwnerEmail</Text>,
                    name: 'ownerEmail',
                    type: 'text',
                    icon: 'fa-solid fa-envelope',
                    getValue: part => part.ownerEmail || null,
                    showInList: true,
                },
            ],
        },
    ],
    inspection: [
        {
            name: <Text id='input.inspection-basics-title'>Inspection basics</Text>,
            // help: <Text id='input.inspection-basics-help'>Basic settings for your profile.</Text>,
            key: 'Inspection basics',
            fields: [
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: inspection => inspection.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: inspection => inspection.name || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: inspection => inspection.description,
                    showInList: false,
                },
                {
                    title: <Text id='input.installation'>Installation</Text>,
                    placeholder: <Text id='input.installation'>Installation</Text>,
                    name: 'installation',
                    type: 'number',
                    lookupArray: 'adminInstallations',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: inspection => inspection.installation,
                    showInList: true,
                },
                {
                    title: <Text id='input.score'>Score</Text>,
                    placeholder: <Text id='input.score'>Score</Text>,
                    name: 'score',
                    type: 'number',
                    min: 1,
                    max: 6,
                    icon: 'fa-solid fa-input-numeric',
                    getValue: inspection => inspection.score,
                    showInList: true,
                },
                {
                    title: <Text id='input.url'>Url</Text>,
                    placeholder: <Text id='input.url'>Url</Text>,
                    name: 'url',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: inspection => inspection.url,
                    showInList: true,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: inspection => inspection.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: inspection => inspection.files,
                    showInList: false,
                },
                {
                    title: <Text id='input.inspections'>Inspections</Text>,
                    placeholder: <Text id='input.inspections'>Inspections</Text>,
                    name: 'inspections',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-user-magnifying-glass',
                    getValue: inspection => inspection.inspections?.map(yt => yt),
                    datalist: [
                        'name', 'value', 'description', 'date',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '100%',
                    displayHeight: '100px',
                },
            ],
        },
    ],

    partLoan: [
        {
            name: <Text id='input.part-loan-basics-title'>Friend basics</Text>,
            help: <Text id='input.part-loan-basics-help'>The loaner is the person who is borrowing the part. Enter all the details of the person borrowing the part.</Text>,
            // help: <Text id='input.inspection-basics-help'>Basic settings for your profile.</Text>,
            key: 'Friend basics',
            fields: [
                {
                    title: <Text id='input.qty'>Qty</Text>,
                    placeholder: <Text id='input.qty'>Qty</Text>,
                    name: 'qty',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: loan => loan.qty || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.firstname'>Firstname</Text>,
                    placeholder: <Text id='input.firstname'>Firstname</Text>,
                    name: 'firstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: loan => loan.firstname || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.lastname'>Lastname</Text>,
                    placeholder: <Text id='input.lastname'>Lastname</Text>,
                    name: 'lastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: loan => loan.lastname,
                    showInList: true,
                },
                {
                    title: <Text id='input.cellphone'>Cellphone</Text>,
                    placeholder: <Text id='input.cellphone'>Cellphone</Text>,
                    name: 'cellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: loan => loan.cellphone,
                    isReadOnly: true,
                    adminEditAllowed: true,
                    showInList: true,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: loan => loan.images,
                    showInList: false,
                },
            ],
        },
    ],

    loan: [
        {
            name: <Text id='input.loan-basics-title'>Loan basics</Text>,
            // help: <Text id='input.inspection-basics-help'>Basic settings for your profile.</Text>,
            key: 'Loan basics',
            fields: [
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.loaner'>Loaner</Text>,
                    placeholder: <Text id='input.loaner'>Loaner</Text>,
                    name: 'loaner',
                    type: 'number',
                    lookupArray: 'adminLoaners',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.loaner || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.part'>Part</Text>,
                    placeholder: <Text id='input.part'>Part</Text>,
                    name: 'part',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    lookupArray: 'adminParts',
                    getValue: obj => obj.part || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.installation'>Installation</Text>,
                    placeholder: <Text id='input.installation'>Installation</Text>,
                    name: 'installation',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.installation || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.qty'>Qty</Text>,
                    placeholder: <Text id='input.qty'>Qty</Text>,
                    name: 'qty',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.qty || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.description,
                    showInList: false,
                },
                {
                    title: <Text id='input.comment'>Comment</Text>,
                    placeholder: <Text id='input.comment'>Comment</Text>,
                    name: 'comment',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.comment,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.loan-loaner-title'>Loan loaner</Text>,
            // help: <Text id='input.inspection-basics-help'>Basic settings for your profile.</Text>,
            key: 'Loan loaner',
            fields: [
                {
                    title: <Text id='input.firstname'>Firstname</Text>,
                    placeholder: <Text id='input.firstname'>Firstname</Text>,
                    name: 'firstname',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: obj => obj.firstname,
                    showInList: true,
                },
                {
                    title: <Text id='input.lastname'>Lastname</Text>,
                    placeholder: <Text id='input.lastname'>Lastname</Text>,
                    name: 'lastname',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: obj => obj.lastname,
                    showInList: true,
                },
                {
                    title: <Text id='input.cellphone'>Cellphone</Text>,
                    placeholder: <Text id='input.cellphone'>Cellphone</Text>,
                    name: 'cellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-link',
                    getValue: obj => obj.cellphone,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.loan-info-title'>Loan info</Text>,
            // help: <Text id='input.inspection-basics-help'>Basic settings for your profile.</Text>,
            key: 'Loan info',
            fields: [
                {
                    title: <Text id='input.requiredReturnDate'>RequiredReturnDate</Text>,
                    placeholder: <Text id='input.requiredReturnDate'>RequiredReturnDate</Text>,
                    name: 'requiredReturnDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: installation => installation.requiredReturnDate,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.startDate'>StartDate</Text>,
                    placeholder: <Text id='input.startDate'>StartDate</Text>,
                    name: 'startDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: installation => installation.startDate,
                    showInList: true,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.endDate'>EndDate</Text>,
                    placeholder: <Text id='input.endDate'>EndDate</Text>,
                    name: 'endDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: installation => installation.endDate,
                    minFunc: obj => obj.startDate ? util.isoDate(obj.startDate, { dateTimeSep: 'T' }) : null,
                    showInList: false,
                    hideOnCreate: true,
                },
            ],
        },
        {
            name: <Text id='input.loan-media-title'>Loan media</Text>,
            // help: <Text id='input.inspection-basics-help'>Basic settings for your profile.</Text>,
            key: 'Loan media',
            fields: [
                {
                    title: <Text id='input.url'>Url</Text>,
                    placeholder: <Text id='input.url'>Url</Text>,
                    name: 'url',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: obj => obj.url,
                    showInList: false,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                }
            ],
        },
    ],

    usage: [
        {
            name: <Text id='input.usage-basics-title'>Usage basics</Text>,
            // help: <Text id='input.inspection-basics-help'>Basic settings for your profile.</Text>,
            key: 'Usage basics',
            fields: [
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.part'>Part</Text>,
                    placeholder: <Text id='input.part'>Part</Text>,
                    name: 'part',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    lookupArray: 'adminParts',
                    getValue: obj => obj.part || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.title'>Title</Text>,
                    placeholder: <Text id='input.title'>Title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.title,
                    showInList: false,
                },
                {
                    title: <Text id='input.comment'>Comment</Text>,
                    placeholder: <Text id='input.comment'>Comment</Text>,
                    name: 'comment',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.comment,
                    showInList: false,
                },
                {
                    title: <Text id='input.date'>Date</Text>,
                    placeholder: <Text id='input.date'>Date</Text>,
                    name: 'date',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.date,
                    showInList: true,
                },
                {
                    title: <Text id='input.hours'>Hours</Text>,
                    placeholder: <Text id='input.hours'>Hours</Text>,
                    name: 'hours',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.hours || null,
                    displayValue: val => val ? `${val} h` : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.shotCount'>shotCount</Text>,
                    help: <Text id='input.shotCount-help'>Shot count in this session.</Text>,
                    placeholder: <Text id='input.shotCount'>shotCount</Text>,
                    name: 'shotCount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.shotCount || null,
                    displayValue: val => val ? `${val}` : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.comment'>Comment</Text>,
                    placeholder: <Text id='input.comment'>Comment</Text>,
                    name: 'comment',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.comment,
                    showInList: false,
                },
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    name: 'tags',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'tags',
                    getValue: obj => obj.tags,
                    datalist: [
                        'name',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
            ],
        },
        {
            name: <Text id='input.usage-media-title'>Usage media</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'Usage media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                }
            ],
        },
        {
            name: <Text id='input.usage-shooting-details-title'>Usage shooting details</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'Usage shooting details',
            fields: [
                {
                    title: <Text id='input.shotAnalysis'>shotAnalysis</Text>,
                    placeholder: <Text id='input.shotAnalysis'>shotAnalysis</Text>,
                    name: 'shotAnalysis',
                    type: 'arrayObject',
                    drawerSize: 'drawerHeightMediumSmall',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.shotAnalysis,
                    datalist: [
                        'imageMd5', 'round', 'roundType', 'groupPlacement', 'centerScore', 'totalScore',
                    ],
                    datalistHelp: [
                        'Reference to the image.', 'Which number shot string is this?', 'Type: Prøve 150s, 1:150s, 2:150s, 1:5x3s, 2:5x3s, 20s, 10s.', '0-360 degrees', 'Score. 10.10 center target.', 'Total score for the round.',
                    ],
                    datalistValues: [
                        [], [1, 2, 3, 4], ['Prøve 150s', '1:150s', '2:150s', '1:5x3s', '2:5x3s', '20s', '10s', '8s', '6s'], [], [],
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-left px-2'>
                            <span class='font-weight-lighter'>Round:</span> {obj.round}<br />
                            <span class='font-weight-lighter'>Type:</span> {obj.roundType}<br />
                            <span class='font-weight-lighter'>Placement:</span> {obj.groupPlacement}°<br />
                            <span class='font-weight-lighter'>Center group:</span> {obj.centerScore}<br />
                            <span class='font-weight-lighter'>Total Score:</span> {obj.totalScore}<br />
                        </div>
                    </>,
                    showInList: false,
                    displayHeight: '140px',
                },
                {
                    title: <Text id='input.shotStrings'>shotStrings</Text>,
                    placeholder: <Text id='input.shotStrings'>shotStrings</Text>,
                    name: 'shotStrings',
                    type: 'arrayObject',
                    drawerSize: 'drawerHeightMediumSmall',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.shotStrings,
                    datalist: [
                        'round', 'roundType', 'score', 'shotCount',
                        // 'speedHigh', 'speedLow', 'speedAvg', 'spread', 'stdDev', 'groupSize',
                    ],
                    datalistValues: [
                        [1, 2, 3, 4], ['NAIS', 'Hurtig'], [], [35, 65],
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-left px-2'>
                            <span class='font-weight-lighter'>Round:</span> {obj.round}<br />
                            <span class='font-weight-lighter'>Type:</span> {obj.roundType}<br />
                            <span class='font-weight-lighter'>score:</span> {obj.score}<br />
                            <span class='font-weight-lighter'>shotCount:</span> {obj.shotCount}<br />
                            {/* <span class='font-weight-lighter'>speedHigh:</span> {obj.speedHigh}<br />
                            <span class='font-weight-lighter'>speedLow:</span> {obj.speedLow}<br />
                            <span class='font-weight-lighter'>speedAvg:</span> {obj.speedAvg}<br />
                            <span class='font-weight-lighter'>spread:</span> {obj.spread}<br />
                            <span class='font-weight-lighter'>stdDev:</span> {obj.stdDev}<br /> */}
                            {/* <span class='font-weight-lighter'>groupSize:</span> {obj.groupSize}<br /> */}
                        </div>
                    </>,
                    showInList: false,
                    displayHeight: '120px',
                },

                {
                    title: <Text id='input.distanceToTarget'>DistanceToTarget</Text>,
                    help: <Text id='input.distanceToTarget-help'>Distance to target in meters.</Text>,
                    placeholder: <Text id='input.distanceToTarget'>DistanceToTarget</Text>,
                    name: 'distanceToTarget',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.distanceToTarget || null,
                    displayValue: val => val ? `${val} m` : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.totalRounds'>TotalRounds</Text>,
                    help: <Text id='input.totalRounds-help'>Total rounds in this session.</Text>,
                    placeholder: <Text id='input.totalRounds'>TotalRounds</Text>,
                    name: 'totalRounds',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.totalRounds || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.shootingPosition'>ShootingPosition</Text>,
                    help: <Text id='input.shootingPosition-help'>Shooting position.</Text>,
                    placeholder: <Text id='input.shootingPosition'>ShootingPosition</Text>,
                    name: 'shootingPosition',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.shootingPosition || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.ammunition'>Ammunition</Text>,
                    help: <Text id='input.ammunition-help'>Ammunition brand and type.</Text>,
                    placeholder: <Text id='input.ammunition'>Ammunition</Text>,
                    name: 'ammunition',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.ammunition || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.ammunitionLot'>AmmunitionLot</Text>,
                    help: <Text id='input.ammunitionLot-help'>Ammunition lot.</Text>,
                    placeholder: <Text id='input.ammunitionLot'>AmmunitionLot</Text>,
                    name: 'ammunitionLot',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.ammunitionLot || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.gunAirPressure'>GunAirPressure</Text>,
                    help: <Text id='input.gunAirPressure-help'>Air pressure inside the airtank of the gun in bars.</Text>,
                    placeholder: <Text id='input.gunAirPressure'>GunAirPressure</Text>,
                    name: 'gunAirPressure',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.gunAirPressure || null,
                    displayValue: val => val ? `${val} bar` : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.ammunitionWeight'>AmmunitionWeight</Text>,
                    help: <Text id='input.ammunitionWeight-help'>Ammunition weight in milligrams.</Text>,
                    placeholder: <Text id='input.ammunitionWeight'>AmmunitionWeight</Text>,
                    name: 'ammunitionWeight',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.ammunitionWeight || null,
                    displayValue: val => val ? `${val} mg` : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.distanceMeters'>DistanceMeters</Text>,
                    placeholder: <Text id='input.distanceMeters'>DistanceMeters</Text>,
                    name: 'distanceMeters',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.distanceMeters || null,
                    displayValue: val => val ? `${val} m` : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.shotCount'>shotCount</Text>,
                    help: <Text id='input.shotCount-help'>Shot count in this session.</Text>,
                    placeholder: <Text id='input.shotCount'>shotCount</Text>,
                    name: 'shotCount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.shotCount || null,
                    displayValue: val => val ? `${val}` : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.shotVelocityLow'>shotVelocityLow</Text>,
                    help: <Text id='input.shotVelocityLow-help'>Shot shotVelocityLow in m/s.</Text>,
                    placeholder: <Text id='input.shotVelocityLow'>shotVelocityLow</Text>,
                    name: 'shotVelocityLow',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.shotVelocityLow || null,
                    displayValue: val => val ? `${val} m/s` : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.shotVelocityHi'>shotVelocityHi</Text>,
                    help: <Text id='input.shotVelocityHi-help'>Shot shotVelocityHi in m/s.</Text>,
                    placeholder: <Text id='input.shotVelocityHi'>shotVelocityHi</Text>,
                    name: 'shotVelocityHi',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.shotVelocityHi || null,
                    displayValue: val => val ? `${val} m/s` : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.shotVelocityAvg'>shotVelocityAvg</Text>,
                    help: <Text id='input.shotVelocityAvg-help'>Shot shotVelocityAvg in m/s.</Text>,
                    placeholder: <Text id='input.shotVelocityAvg'>shotVelocityAvg</Text>,
                    name: 'shotVelocityAvg',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.shotVelocityAvg || null,
                    displayValue: val => val ? `${val} m/s` : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.shotVelocitySpread'>shotVelocitySpread</Text>,
                    help: <Text id='input.shotVelocitySpread-help'>Shot velocity spread.</Text>,
                    placeholder: <Text id='input.shotVelocitySpread'>shotVelocitySpread</Text>,
                    name: 'shotVelocitySpread',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.shotVelocitySpread || null,
                    displayValue: val => val ? `${val}` : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.shotVelocityStdDev'>shotVelocityStdDev</Text>,
                    help: <Text id='input.shotVelocityStdDev-help'>Shot velocity standard deveation.</Text>,
                    placeholder: <Text id='input.shotVelocityStdDev'>shotVelocityStdDev</Text>,
                    name: 'shotVelocityStdDev',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.shotVelocityStdDev || null,
                    displayValue: val => val ? `${val}` : null,
                    showInList: true,
                },

                {
                    title: <Text id='input.shotPlacementX'>ShotPlacementX</Text>,
                    help: <Text id='input.shotPlacementX-help'>Shot placement X in mm. 0 is center.</Text>,
                    placeholder: <Text id='input.shotPlacementX'>ShotPlacementX</Text>,
                    name: 'shotPlacementX',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.shotPlacementX || null,
                    displayValue: val => val ? `${val} cm` : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.shotPlacementY'>ShotPlacementY</Text>,
                    help: <Text id='input.shotPlacementY-help'>Shot placement Y in mm. 0 is center.</Text>,
                    placeholder: <Text id='input.shotPlacementY'>ShotPlacementY</Text>,
                    name: 'shotPlacementY',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.shotPlacementY || null,
                    displayValue: val => val ? `${val} cm` : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.shotGroup'>ShotGroup</Text>,
                    help: <Text id='input.shotGroup-help'>Shot group diameter in mm.</Text>,
                    placeholder: <Text id='input.shotGroup'>ShotGroup</Text>,
                    name: 'shotGroup',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.shotGroup || null,
                    showInList: true,
                },

                {
                    title: <Text id='input.windDirection'>WindDirection</Text>,
                    help: <Text id='input.windDirection-help'>Wind direction N, E, S, W.</Text>,
                    placeholder: <Text id='input.windDirection'>WindDirection</Text>,
                    name: 'windDirection',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.windDirection || null,
                    displayValue: val => val ? `${val}` : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.windSpeed'>WindSpeed</Text>,
                    help: <Text id='input.windSpeed-help'>Wind speed in m/s.</Text>,
                    placeholder: <Text id='input.windSpeed'>WindSpeed</Text>,
                    name: 'windSpeed',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.windSpeed || null,
                    displayValue: val => val ? `${val} m/s` : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.airTemp'>AirTemp</Text>,
                    help: <Text id='input.airTemp-help'>Air temp in C.</Text>,
                    placeholder: <Text id='input.airTemp'>AirTemp</Text>,
                    name: 'airTemp',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.airTemp || null,
                    displayValue: val => val ? `${val} °C` : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.airPressure'>AirPressure</Text>,
                    help: <Text id='input.airPressure-help'>Air pressure in mbar.</Text>,
                    placeholder: <Text id='input.airPressure'>AirPressure</Text>,
                    name: 'airPressure',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.airPressure || null,
                    displayValue: val => val ? `${val} mbar` : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.airHumidity'>AirHumidity</Text>,
                    help: <Text id='input.airHumidity-help'>Air humidity in %.</Text>,
                    placeholder: <Text id='input.airHumidity'>AirHumidity</Text>,
                    name: 'airHumidity',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.airHumidity || null,
                    displayValue: val => val ? `${val} %` : null,
                    showInList: true,
                },
            ],
        },
    ],

    journal: [
        {
            name: <Text id='input.journal-basics-title'>Journal basics</Text>,
            key: 'Journal basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.sale'>Sale</Text>,
                    placeholder: <Text id='input.sale'>Sale</Text>,
                    name: 'sale',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.sale,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.visitor'>Visitor</Text>,
                    placeholder: <Text id='input.visitor'>Visitor</Text>,
                    name: 'visitor',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.visitor,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.animal'>Animal</Text>,
                    placeholder: <Text id='input.animal'>Animal</Text>,
                    name: 'animal',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.animal,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.journalNumber'>JournalNumber</Text>,
                    placeholder: <Text id='input.journalNumber'>JournalNumber</Text>,
                    name: 'journalNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.journalNumber,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.animalNumber'>animalNumber</Text>,
                    placeholder: <Text id='input.animalNumber'>animalNumber</Text>,
                    name: 'animalNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.animalNumber,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.saleNumber'>saleNumber</Text>,
                    placeholder: <Text id='input.saleNumber'>saleNumber</Text>,
                    name: 'saleNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.saleNumber,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.veterinaryNumber'>veterinaryNumber</Text>,
                    placeholder: <Text id='input.veterinaryNumber'>veterinaryNumber</Text>,
                    name: 'veterinaryNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.veterinaryNumber,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.assistantNumber'>assistantNumber</Text>,
                    placeholder: <Text id='input.assistantNumber'>assistantNumber</Text>,
                    name: 'assistantNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.assistantNumber,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.title'>Title</Text>,
                    placeholder: <Text id='input.title'>Title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                },
                {
                    title: <Text id='input.veterinary'>Veterinary</Text>,
                    placeholder: <Text id='input.veterinary'>Veterinary</Text>,
                    name: 'veterinary',
                    type: 'select',
                    icon: 'fa-solid fa-user-doctor',
                    getValue: obj => obj?.veterinary,
                    // displayValue: (val, obj) => {
                    //     return `${obj.firstname} ${obj.lastname}`;
                    // },
                    datalistName: 'veterinaries',
                    datalistFieldKey: e => e.id,
                    datalistFieldVal: e => `${e.firstname} ${e.lastname}`,
                    showInList: true,
                },
                {
                    title: <Text id='input.veterinaryName'>Veterinary name from old system</Text>,
                    placeholder: <Text id='input.veterinaryName'>Veterinary name from old system</Text>,
                    name: 'veterinaryName',
                    type: 'text',
                    icon: 'fa-solid fa-user-doctor',
                    getValue: obj => obj.veterinaryName,
                    showInList: true,
                    hideOnCreate: false,
                    isReadOnly: true,
                },
                {
                    title: <Text id='input.date'>Date</Text>,
                    placeholder: <Text id='input.date'>Date</Text>,
                    name: 'date',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.date,
                    min: '2000-01-01',
                    maxFunc: obj => util.isoDate(new Date(), { dateTimeSep: 'T' }),
                    showInList: true,
                },
                {
                    title: <Text id='input.closed'>Closed</Text>,
                    placeholder: <Text id='input.closed'>Closed</Text>,
                    name: 'closed',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.closed,
                    min: '2000-01-01',
                    maxFunc: obj => util.isoDate(new Date(), { dateTimeSep: 'T' }),
                    showInList: true,
                },
                {
                    title: <Text id='input.comment'>Comment</Text>,
                    placeholder: <Text id='input.comment'>Comment</Text>,
                    name: 'comment',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.comment,
                    showInList: false
                },
                // {
                //     title: <Text id='input.description'>Description</Text>,
                //     placeholder: <Text id='input.description'>Description</Text>,
                //     name: 'description',
                //     type: 'textarea',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj.description,
                //     showInList: false
                // },
            ],
        },
        {
            name: <Text id='input.journal-medical-title'>Journal medical</Text>,
            key: 'Journal medical',
            fields: [
                {
                    title: <Text id='input.chiefComplaint'>ChiefComplaint</Text>,
                    placeholder: <Text id='input.chiefComplaint'>ChiefComplaint</Text>,
                    // help: <Text id='input.chiefComplaint-help'>Chief Complaint: The primary reason for the veterinary visit, describing the main concern or symptoms observed by the owner or veterinarian.</Text>,
                    name: 'chiefComplaint',
                    type: 'textareaEditor',
                    viewHtml: false,
                    icon: 'fa-solid fa-exclamation-circle',
                    getValue: obj => obj?.chiefComplaint,
                    showInList: false
                },
                {
                    title: <Text id='input.generalCondition'>general Condition</Text>,
                    placeholder: <Text id='input.generalCondition'>general Condition</Text>,
                    // help: <Text id='input.generalCondition-help'>General condition of the animal.</Text>,
                    name: 'generalCondition',
                    type: 'textarea',
                    icon: 'fa-solid fa-exclamation-circle',
                    getValue: obj => obj?.generalCondition,
                    showInList: false
                },
                {
                    title: <Text id='input.physicalExaminationFindings'>PhysicalExaminationFindings</Text>,
                    placeholder: <Text id='input.physicalExaminationFindings'>PhysicalExaminationFindings</Text>,
                    // help: <Text id='input.physicalExaminationFindings-help'>Physical Examination Findings: Document the veterinarians findings from the physical examination, including vital signs, body condition, general appearance, organ systems assessment, and any abnormalities or notable observations.</Text>,
                    name: 'physicalExaminationFindings',
                    type: 'textarea',
                    icon: 'fa-solid fa-stethoscope',
                    getValue: obj => obj?.physicalExaminationFindings,
                    showInList: false
                },
                {
                    title: <Text id='input.diagnosticTests'>DiagnosticTests</Text>,
                    placeholder: <Text id='input.diagnosticTests'>DiagnosticTests</Text>,
                    // help: <Text id='input.diagnosticTests-help'>Diagnostic Tests: List any diagnostic tests performed during the visit, such as blood tests, imaging (X-rays, ultrasounds), fecal analysis, or other laboratory evaluations. Include the results if available.</Text>,
                    name: 'diagnosticTests',
                    type: 'textarea',
                    icon: 'fa-solid fa-flask',
                    getValue: obj => obj?.diagnosticTests,
                    showInList: false
                },
                {
                    title: <Text id='input.diagnosis'>Diagnosis</Text>,
                    placeholder: <Text id='input.diagnosis'>Diagnosis</Text>,
                    // help: <Text id='input.diagnosis-help'>Diagnosis: Record the veterinarians assessment and diagnosis based on the physical examination, medical history, and diagnostic test results. This may involve identifying the underlying condition, disease, or injury causing the presenting symptoms.</Text>,
                    name: 'diagnosis',
                    type: 'textarea',
                    icon: 'fa-solid fa-file-medical',
                    getValue: obj => obj?.diagnosis,
                    showInList: false
                },
                {
                    title: <Text id='input.treatmentPlan'>TreatmentPlan</Text>,
                    placeholder: <Text id='input.treatmentPlan'>TreatmentPlan</Text>,
                    // help: <Text id='input.treatmentPlan-help'>Treatment Plan: Outline the treatment plan recommended by the veterinarian, including medications, surgeries, therapies, or other interventions. Specify dosages, frequency, and duration of treatments as applicable.</Text>,
                    name: 'treatmentPlan',
                    type: 'textarea',
                    icon: 'fa-solid fa-medkit',
                    getValue: obj => obj?.treatmentPlan,
                    showInList: false
                },
                {
                    title: <Text id='input.medicines'>medicines</Text>,
                    placeholder: <Text id='input.medicines'>medicines</Text>,
                    // help: <Text id='input.medicines-help'>Treatment Plan: Outline the treatment plan recommended by the veterinarian, including medications, surgeries, therapies, or other interventions. Specify dosages, frequency, and duration of treatments as applicable.</Text>,
                    name: 'medicines',
                    type: 'textarea',
                    icon: 'fa-solid fa-medkit',
                    getValue: obj => obj?.medicines,
                    showInList: false
                },
                {
                    title: <Text id='input.medicine'>Medicine</Text>,
                    placeholder: <Text id='input.medicine'>Medicine</Text>,
                    name: 'medicine',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-prescription-bottle',
                    getValue: obj => obj?.medicine,
                    datalist: [
                        // 'id', 'name', 'amountEachAnimal', 'unit', 'Note', 'Last treatment', 'Usage', 'AddedBy', 'StopDate', 'ProductNumber',
                        'id', 'name', 'navnFormStyrke', 'amountPerAnimal', 'unit',
                    ],
                    displayArrayObject: obj => <>
                        <div class='d-flex flex-column text-left flex-fill overflow-auto px-3 py-2 mt-1'>
                            <div class='w-100'><span class='font-weight-lighter'>Name:</span> {obj.name}<br /></div>
                            <div class='w-100'><span class='font-weight-lighter'>Fullname:</span> {obj.fullname}<br /></div>
                            <div class='w-100'><span class='font-weight-lighter'>amountPerAnimal:</span> {obj.amountPerAnimal} {obj.unit}<br /></div>
                            <div class='w-100'><span class='font-weight-lighter'>no:</span> {obj.no}<br /></div>
                            <div class='w-100'><span class='font-weight-lighter'>lastThreatment:</span> {obj.lastThreatment}<br /></div>
                            <div class='w-100'><span class='font-weight-lighter'>usageReason:</span> {obj.usageReason }<br /></div>
                            <div class='w-100'><span class='font-weight-lighter'>threatmentBy:</span> {obj.threatmentBy }<br /></div>
                            <div class='w-100'><span class='font-weight-lighter'>productNumber:</span> {obj.productNumber }<br /></div>
                            <div class='w-100'><span class='font-weight-lighter'>retentionPeriods:</span> </div>
                            <div class='w-100'>
                                {obj.retentionPeriods && obj.retentionPeriods.map(rp => {
                                    return (<span>
                                        <span class='font-weight-lighter ml-5'>type</span> {rp.type}<br />
                                        <span class='d-flex flex-column ml-5'>{rp.dyreslag && rp.dyreslag.map(ds => {
                                            return (<span class='ml-5'>
                                                <span class='font-weight-lighter'>dyreslagkode</span> {ds.dyreslagkode}
                                                <span class='font-weight-lighter ml-2'>antall-dager</span> {ds['antall-dager']}
                                            </span>);
                                        })}<br /></span>

                                        {/* {JSON.stringify(rp, null, 2)} */}
                                    </span>);
                                })}<br />
                            </div>
                        </div>
                    </>,
                    drawerSize: 'drawerHeightMediumLarge',
                    drawerName: 'addMedicine',
                    showInList: false,
                    displayWidth: '600px',
                    displayHeight: '330px',
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.diagnoses'>Diagnose</Text>,
                    placeholder: <Text id='input.diagnoses'>Diagnose</Text>,
                    name: 'diagnoses',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-stethoscope',
                    getValue: obj => obj?.diagnoses,
                    datalist: [
                        // 'id', 'name', 'amountEachAnimal', 'unit', 'Note', 'Last treatment', 'Usage', 'AddedBy', 'StopDate', 'ProductNumber',
                        'id', 'name', 'diagnoseCode',
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-left px-3 py-2 mt-1'>
                            <span class='font-weight-lighter'>Code:</span> {obj.diagnoseCode}<br />
                            <span class='font-weight-lighter'>Diagnose:</span> {obj.name}<br />
                        </div>
                    </>,
                    drawerSize: 'drawerHeightMediumLarge',
                    drawerName: 'addDiagnose',
                    showInList: false,
                    displayWidth: '300px',
                    displayHeight: '100px',
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.description'>description</Text>,
                    placeholder: <Text id='input.description'>description</Text>,
                    // help: <Text id='input.description-help'>Client Communication: Summarize the discussions and instructions provided by the veterinarian to the owner, including any additional information about the condition, prognosis, follow-up care, or home care instructions.</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-comments',
                    getValue: obj => obj.description,
                    showInList: false
                },
                {
                    title: <Text id='input.clientCommunication'>ClientCommunication</Text>,
                    placeholder: <Text id='input.clientCommunication'>ClientCommunication</Text>,
                    // help: <Text id='input.clientCommunication-help'>Client Communication: Summarize the discussions and instructions provided by the veterinarian to the owner, including any additional information about the condition, prognosis, follow-up care, or home care instructions.</Text>,
                    name: 'clientCommunication',
                    type: 'textarea',
                    icon: 'fa-solid fa-comments',
                    getValue: obj => obj.clientCommunication,
                    showInList: false
                },
                {
                    title: <Text id='input.recommendations'>Recommendations</Text>,
                    placeholder: <Text id='input.recommendations'>Recommendations</Text>,
                    // help: <Text id='input.recommendations-help'>Recommendations: Include any recommendations for preventive care, vaccinations, dietary adjustments, lifestyle changes, or future monitoring or examinations.</Text>,
                    name: 'recommendations',
                    type: 'textarea',
                    icon: 'fa-solid fa-check-square',
                    getValue: obj => obj.recommendations,
                    showInList: false
                },
                {
                    title: <Text id='input.followUp'>FollowUp</Text>,
                    placeholder: <Text id='input.followUp'>FollowUp</Text>,
                    // help: <Text id='input.followUp-help'>Follow-up: Note any scheduled follow-up appointments, rechecks, or additional tests planned as part of the ongoing care for the patient.</Text>,
                    name: 'followUp',
                    type: 'textarea',
                    icon: 'fa-solid fa-clock',
                    getValue: obj => obj.followUp,
                    showInList: false
                },
            ],
        },
        {
            name: <Text id='input.journal-clinical-title'>Journal clinical</Text>,
            key: 'Journal clinical',
            fields: [
                {
                    title: <Text id='input.weight'>Weight</Text>,
                    placeholder: <Text id='input.weight'>Weight</Text>,
                    name: 'weight',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.weight,
                    showInList: true,
                },
                {
                    title: <Text id='input.puls'>Puls</Text>,
                    placeholder: <Text id='input.puls'>Puls</Text>,
                    name: 'puls',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.puls,
                    showInList: true,
                },
                {
                    title: <Text id='input.respiration'>respiration</Text>,
                    placeholder: <Text id='input.respiration'>respiration</Text>,
                    name: 'respiration',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.respiration,
                    showInList: true,
                },
                {
                    title: <Text id='input.temperature'>Temperature</Text>,
                    placeholder: <Text id='input.temperature'>Temperature</Text>,
                    name: 'temperature',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.temperature,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.journal-passport-title'>Journal passport</Text>,
            key: 'Journal passport',
            fields: [
                {
                    title: <Text id='input.passportNumber'>PassportNumber</Text>,
                    placeholder: <Text id='input.passportNumber'>PassportNumber</Text>,
                    name: 'passportNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.passportNumber,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.journal-extra-title'>Journal extra</Text>,
            key: 'Journal extra',
            fields: [
                {
                    title: <Text id='input.isDentalCheck'>isDentalCheck</Text>,
                    placeholder: <Text id='input.isDentalCheck'>isDentalCheck</Text>,
                    name: 'isDentalCheck',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isDentalCheck ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.isEmergencyResponse'>isEmergencyResponse</Text>,
                    placeholder: <Text id='input.isEmergencyResponse'>isEmergencyResponse</Text>,
                    name: 'isEmergencyResponse',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isEmergencyResponse ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.isInProgress'>isInProgress</Text>,
                    placeholder: <Text id='input.isInProgress'>isInProgress</Text>,
                    name: 'isInProgress',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isInProgress ? 1 : 0,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.journal-attachments-title'>Journal attatchments</Text>,
            key: 'Journal attatchments',
            fields: [
                {
                    title: <Text id='input.prescriptions'>Prescriptions</Text>,
                    placeholder: <Text id='input.prescriptions'>Prescriptions</Text>,
                    name: 'prescriptions',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-prescription-bottle',
                    getValue: obj => obj.prescriptions,
                    datalist: [
                        'id', 'name', 'description',
                    ],
                    drawerSize: 'drawerHeightMediumLarge',
                    drawerName: 'addPrescription',
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.diagnoses'>Diagnoses</Text>,
                    placeholder: <Text id='input.diagnoses'>Diagnoses</Text>,
                    name: 'diagnoses',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-file-medical',
                    getValue: obj => obj.diagnoses,
                    datalist: [
                        'id', 'name', 'description',
                    ],
                    drawerSize: 'drawerHeightMediumLarge',
                    drawerName: 'addDiagnose',
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.referrals'>Referrals</Text>,
                    placeholder: <Text id='input.referrals'>Referrals</Text>,
                    name: 'referrals',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-hand-point-right',
                    getValue: obj => obj.referrals,
                    datalist: [
                        'id', 'name', 'description',
                    ],
                    drawerSize: 'drawerHeightMediumLarge',
                    drawerName: 'addReferral',
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
            ],
        },
        {
            name: <Text id='input.journal-media-title'>Journal media</Text>,
            key: 'Journal media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.emails'>emails</Text>,
                    placeholder: <Text id='input.emails'>emails</Text>,
                    name: 'emails',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.emails,
                    datalist: [
                        'subject', 'from', 'date', 'filename',
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-left px-3'>
                            <span class='font-weight-lighter'>Date:</span> {obj.date}<br />
                            <span class='font-weight-lighter'>From:</span> {obj.from}<br />
                            <span class='font-weight-lighter'>Subject:</span> {obj.subject}<br />
                            <br />
                            <span style='font-size: 1.5em;'>
                                <a href={`https://dyrejournal.app/original/${obj.fileClinic}/${obj.fileLocation}`} target='_blank' native><i class='fa-solid fa-link' /> {obj.filename}</a><br />
                            </span>
                        </div>
                    </>,
                    showInList: false,
                    displayWidth: '450px',
                    displayHeight: '250px',
                },
            ],
        },
    ],
    prescription: [
        {
            name: <Text id='input.prescription-basics-title'>Prescription basics</Text>,
            key: 'Prescription basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.journal'>Journal</Text>,
                    placeholder: <Text id='input.journal'>Journal</Text>,
                    name: 'journal',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.journal,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.animal'>Animal</Text>,
                    placeholder: <Text id='input.animal'>Animal</Text>,
                    name: 'animal',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.animal,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.sale'>Sale</Text>,
                    placeholder: <Text id='input.sale'>Sale</Text>,
                    name: 'sale',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.sale,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.veterinary'>Veterinary</Text>,
                    placeholder: <Text id='input.veterinary'>Veterinary</Text>,
                    name: 'veterinary',
                    type: 'select',
                    icon: 'fa-solid fa-user-doctor',
                    getValue: obj => obj.veterinary,
                    datalistName: 'veterinaries',
                    datalistFieldKey: e => e.id,
                    datalistFieldVal: e => `${e.firstname} ${e.lastname}`,
                    showInList: true,
                },
                {
                    title: <Text id='input.date'>Date</Text>,
                    placeholder: <Text id='input.date'>Date</Text>,
                    name: 'date',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.date,
                    showInList: true,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.endDate'>EndDate</Text>,
                    placeholder: <Text id='input.endDate'>EndDate</Text>,
                    name: 'endDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.endDate,
                    showInList: true,
                    hideOnCreate: false,
                },
                // {
                //     title: <Text id='input.description'>Description</Text>,
                //     placeholder: <Text id='input.description'>Description</Text>,
                //     name: 'description',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj.description,
                //     showInList: true,
                // },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.drugs'>Drugs</Text>,
                    placeholder: <Text id='input.drugs'>Drugs</Text>,
                    name: 'drugs',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.drugs,
                    datalist: [
                        'id', 'name', 'description', 'dosage', 'strength', 'sideEffects', 'units', 'reit',
                    ],
                    showInList: false,
                    displayHeight: '150px',
                    hideOnCreate: true,
                },
            ],
        },
    ],
    prescriptionDrugs: [
        {
            name: <Text id='input.prescription-drugs-basics-title'>Prescription drugs basics</Text>,
            key: 'Prescription drugs basics',
            fields: [
                // {
                //     title: <Text id='input.description'>Description</Text>,
                //     placeholder: <Text id='input.description'>Description</Text>,
                //     name: 'description',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj.description,
                //     showInList: true,
                // },

                {
                    title: <Text id='input.qty'>Qty</Text>,
                    placeholder: <Text id='input.qty'>Qty</Text>,
                    name: 'qty',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.qty,
                    showInList: true,
                },

                // AND - Andre sykdommer av vesentlig omfang/betydning
                // BEV - skjelett, ledd, bånd, sener og muskulatur
                // BFD - Behandling av friske dyr
                // BRY - Brysthuleorganer og bloddannende organer
                // FRD - fordøyelsesorganer med lever, bukspyttkjertel og endokrine organer
                // HUD - Hud og organer, inkl. klauv
                // IOT - ikke organrelaterte tilstander
                // JUR - Jur og spener
                // KOB - kjønnsorganer og obstetrikk
                // KRU - kjønnsorganer - reproduksjon - Urinveier
                // NRE - Nervesystem og sanseorganer inkl. øye og øre

                // I tillegg til disse diagnosekodene skal det registreres
                // en kode for bruksområde for legemiddelet. Dette
                // kodeverket består av tre koder/områder:
                // A: Andre
                // F: Forebyggende behandling inkl. vaksiner
                // S: Sykdom
                {
                    title: <Text id='input.diagnoseText'>diagnoseText</Text>,
                    placeholder: <Text id='input.diagnoseText'>diagnoseText</Text>,
                    name: 'diagnoseText',
                    type: 'select',
                    icon: 'fa-solid fa-language',
                    getValue: obj => obj.diagnoseText,
                    datalist: [
                        ['AND - Andre sykdommer av vesentlig omfang/betydning', 'AND - Andre sykdommer av vesentlig omfang/betydning'],
                        ['BEV - skjelett, ledd, bånd, sener og muskulatur', 'BEV - skjelett, ledd, bånd, sener og muskulatur'],
                        ['BFD - Behandling av friske dyr', 'BFD - Behandling av friske dyr'],
                        ['BRY - Brysthuleorganer og bloddannende organer', 'BRY - Brysthuleorganer og bloddannende organer'],
                        ['FRD - fordøyelsesorganer med lever, bukspyttkjertel og endokrine organer', 'FRD - fordøyelsesorganer med lever, bukspyttkjertel og endokrine organer'],
                        ['HUD - Hud og organer, inkl. klauv', 'HUD - Hud og organer, inkl. klauv'],
                        ['IOT - ikke organrelaterte tilstander', 'IOT - ikke organrelaterte tilstander'],
                        ['JUR - Jur og spener', 'JUR - Jur og spener'],
                        ['KOB - kjønnsorganer og obstetrikk', 'KOB - kjønnsorganer og obstetrikk'],
                        ['KRU - kjønnsorganer - reproduksjon - Urinveier', 'KRU - kjønnsorganer - reproduksjon - Urinveier'],
                        ['NRE - Nervesystem og sanseorganer inkl. øye og øre', 'NRE - Nervesystem og sanseorganer inkl. øye og øre'],
                    ],
                    // datalistFieldKey: e => e.id,
                    // datalistFieldVal: e => `${e.firstname} ${e.lastname}`,
                    showInList: true,
                },
                {
                    title: <Text id='input.usageText'>usageText</Text>,
                    placeholder: <Text id='input.usageText'>usageText</Text>,
                    name: 'usageText',
                    type: 'select',
                    icon: 'fa-solid fa-language',
                    getValue: obj => obj.usageText,
                    datalist: [
                        ['A: Andre', 'A: Andre'],
                        ['F: Forebyggende behandling inkl. vaksiner', 'F: Forebyggende behandling inkl. vaksiner'],
                        ['S: Sykdom', 'S: Sykdom'],
                    ],
                    // datalistFieldKey: e => e.id,
                    // datalistFieldVal: e => `${e.firstname} ${e.lastname}`,
                    showInList: true,
                },
                {
                    title: <Text id='input.dosage'>Dosage</Text>,
                    placeholder: <Text id='input.dosage'>Dosage</Text>,
                    help: <Text id='input.dosage-help'>Dosage: The prescribed dosage or strength of the medication.</Text>,
                    name: 'dosage',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.dosage,
                    showInList: true,
                },
                // {
                //     title: <Text id='input.frequency'>Frequency</Text>,
                //     placeholder: <Text id='input.frequency'>Frequency</Text>,
                //     help: 'Frequency: How often the medication should be taken (e.g., once daily, twice daily).',
                //     name: 'frequency',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj.frequency,
                //     showInList: true,
                // },
                // {
                //     title: <Text id='input.routeOfAdministration'>RouteOfAdministration</Text>,
                //     placeholder: <Text id='input.routeOfAdministration'>RouteOfAdministration</Text>,
                //     help: 'Route of Administration: How the medication should be administered (e.g., oral, topical, injection).',
                //     name: 'routeOfAdministration',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj.routeOfAdministration,
                //     showInList: true,
                // },
                // {
                //     title: <Text id='input.instructionForUse'>InstructionForUse</Text>,
                //     placeholder: <Text id='input.instructionForUse'>InstructionForUse</Text>,
                //     help: 'Instructions for Use: Provide clear and specific instructions on how to take or use the medication, including any special considerations or precautions.',
                //     name: 'instructionForUse',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj.instructionForUse,
                //     showInList: true,
                // },
                {
                    title: <Text id='input.additionalNotes'>AdditionalNotes</Text>,
                    placeholder: <Text id='input.additionalNotes'>AdditionalNotes</Text>,
                    help: <Text id='input.additionalNotes-help'>Additional Notes: Any additional information or special instructions that may be relevant to the prescription, such as dietary restrictions, storage requirements, or possible side effects.</Text>,
                    name: 'additionalNotes',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.additionalNotes,
                    showInList: true,
                },

                // {
                //     title: <Text id='input.sideEffects'>SideEffects</Text>,
                //     placeholder: <Text id='input.sideEffects'>SideEffects</Text>,
                //     name: 'sideEffects',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj.sideEffects,
                //     showInList: true,
                // },
                // {
                //     title: <Text id='input.units'>Units</Text>,
                //     placeholder: <Text id='input.units'>Units</Text>,
                //     name: 'units',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj.units,
                //     showInList: true,
                // },
                {
                    title: <Text id='input.reit'>Reit</Text>,
                    placeholder: <Text id='input.reit'>Reit</Text>,
                    name: 'reit',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.reit,
                    showInList: true,
                },

            ],
        },
    ],
    drug: [
        {
            name: <Text id='input.drugs-basics-title'>Drug basics</Text>,
            key: 'Drug basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.varenavn'>varenavn</Text>,
                    placeholder: <Text id='input.varenavn'>varenavn</Text>,
                    help: <Text id='input.varenavn-help'>Ie: Synulox vet</Text>,
                    name: 'varenavn',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.varenavn,
                    showInList: true,
                },
                {
                    title: <Text id='input.navnFormStyrke'>NavnFormStyrke</Text>,
                    placeholder: <Text id='input.navnFormStyrke'>NavnFormStyrke</Text>,
                    help: <Text id='input.navnFormStyrke-help'>NavnFormStyrke: The name, form, and strength of the medication. Ie: Synulox vet tab hund,katt 200 mg/50 mg</Text>,
                    name: 'navnFormStyrke',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.navnFormStyrke,
                    showInList: true,
                },
                {
                    title: <Text id='input.atcDn'>atcDn</Text>,
                    placeholder: <Text id='input.atcDn'>atcDn</Text>,
                    help: <Text id='input.atcDn-help'>Ie: Amoksicillin og enzymhemmer</Text>,
                    name: 'atcDn',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.atcDn,
                    showInList: true,
                },
                {
                    title: <Text id='input.administrasjonsveiDn'>administrasjonsveiDn</Text>,
                    placeholder: <Text id='input.administrasjonsveiDn'>administrasjonsveiDn</Text>,
                    help: <Text id='input.administrasjonsveiDn-help'>Ie: Oral bruk</Text>,
                    name: 'administrasjonsveiDn',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.administrasjonsveiDn,
                    showInList: true,
                },
                {
                    title: <Text id='input.enhetDoseringDn'>enhetDoseringDn</Text>,
                    placeholder: <Text id='input.enhetDoseringDn'>enhetDoseringDn</Text>,
                    help: <Text id='input.enhetDoseringDn-help'>Ie: tablett</Text>,
                    name: 'enhetDoseringDn',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.enhetDoseringDn,
                    showInList: true,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.description,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.dosage'>Dosage</Text>,
                    placeholder: <Text id='input.dosage'>Dosage</Text>,
                    name: 'dosage',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.dosage,
                    showInList: true,
                },
                {
                    title: <Text id='input.strength'>Strength</Text>,
                    placeholder: <Text id='input.strength'>Strength</Text>,
                    name: 'strength',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.strength,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.sideEffects'>SideEffects</Text>,
                    placeholder: <Text id='input.sideEffects'>SideEffects</Text>,
                    name: 'sideEffects',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.sideEffects,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.units'>Units</Text>,
                    placeholder: <Text id='input.units'>Units</Text>,
                    name: 'units',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.units,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                    isAdminField: true,
                },
            ],
        },
    ],
    animal: [
        {
            name: <Text id='input.animal-basics-title'>Animal basics</Text>,
            key: 'Animal basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.veterinary'>Veterinary</Text>,
                    placeholder: <Text id='input.veterinary'>Veterinary</Text>,
                    name: 'veterinary',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.veterinary,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.owner'>Owner</Text>,
                    placeholder: <Text id='input.owner'>Owner</Text>,
                    name: 'owner',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.owner,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.animalNumber'>AnimalNumber</Text>,
                    placeholder: <Text id='input.animalNumber'>AnimalNumber</Text>,
                    name: 'animalNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.animalNumber,
                    showInList: true,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.markOfOrigin'>markOfOrigin</Text>,
                    placeholder: <Text id='input.markOfOrigin'>markOfOrigin</Text>,
                    help: <Text id='input.markOfOrigin-help'>Mark of origin is always the longest number. Usually 7-8 digits.</Text>,
                    name: 'markOfOrigin',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.markOfOrigin,
                    showInList: false,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.earMark'>earMark</Text>,
                    placeholder: <Text id='input.earMark'>earMark</Text>,
                    help: <Text id='input.earMark-help'>Ear mark is always the shortest number. Usually 4-5 digits.</Text>,
                    name: 'earMark',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.earMark,
                    showInList: false,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.visitorNumber'>visitorNumber</Text>,
                    placeholder: <Text id='input.visitorNumber'>visitorNumber</Text>,
                    name: 'visitorNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.visitorNumber,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.cave'>cave</Text>,
                    placeholder: <Text id='input.cave'>cave</Text>,
                    name: 'cave',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.cave,
                    showInList: true,
                },
                {
                    title: <Text id='input.isAggressive'>Animal is aggressive</Text>,
                    placeholder: <Text id='input.isAggressive'>Animal is aggressive</Text>,
                    name: 'isAggressive',
                    type: 'toggle',
                    icon: 'fa-duotone fa-bell-slash',
                    getValue: obj => obj?.isAggressive ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.isGroup'>Group of animals</Text>,
                    placeholder: <Text id='input.isGroup'>Group of animals</Text>,
                    name: 'isGroup',
                    type: 'toggle',
                    icon: 'fa-duotone fa-info-circle',
                    getValue: obj => obj?.isGroup ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                },
                {
                    title: <Text id='input.color'>color</Text>,
                    placeholder: <Text id='input.color'>color</Text>,
                    name: 'color',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.color,
                    showInList: true,
                },
                {
                    title: <Text id='input.groupSize'>Group size</Text>,
                    placeholder: <Text id='input.groupSize'>Group size</Text>,
                    name: 'groupSize',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.groupSize,
                    showInList: true,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.comment'>comment</Text>,
                    placeholder: <Text id='input.comment'>comment</Text>,
                    name: 'comment',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.comment,
                },
                {
                    title: <Text id='input.commentInternal'>comment Internal</Text>,
                    placeholder: <Text id='input.commentInternal'>comment Internal</Text>,
                    name: 'commentInternal',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.commentInternal,
                },
                {
                    title: <Text id='input.gender'>Gender</Text>,
                    placeholder: <Text id='input.gender'>Gender</Text>,
                    name: 'gender',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.gender,
                    checkValue: (obj, holdingStore) => {
                        console.log('checkValue', obj, holdingStore.name);
                        if (!['Hann', 'Hunn', 'Kastrert hann', 'Kastrert hunn'].includes(obj?.gender)) {
                            holdingStore.setFeedbackError('gender', 'Invalid gender. Must be one of: Hann, Hunn, Kastrert hann, Kastrert hunn');
                        } else {
                            holdingStore.setFeedbackError('gender', '');
                        }
                    },
                    showInList: true,
                    usefulValues: ['Hann', 'Hunn', 'Kastrert hann', 'Kastrert hunn'],
                },
                {
                    title: <Text id='input.species'>Species</Text>,
                    placeholder: <Text id='input.species'>Species</Text>,
                    name: 'speciesId',
                    type: 'selectAdvanced',
                    icon: 'fa-solid fa-user-doctor',
                    getValue: obj => obj.speciesId,
                    datalistName: 'species',
                    datalistFieldKeyName: 'speciesId',
                    datalistFieldKey: e => e?.speciesId,
                    datalistFieldVal: e => `${e?.name} ${e?.isFromDyreid ? `(DyreID)` : ''}`,
                    showInList: true,
                    callback: (obj, updateValue, holdingStore) => {
                        // console.log('speciesId callback', obj);
                        if (obj?.speciesId) {
                            holdingStore.loadBreeds(obj.speciesId);
                        }
                    },
                },
                {
                    title: <Text id='input.breed'>Breed</Text>,
                    placeholder: <Text id='input.breed'>Breed</Text>,
                    name: 'breedId',
                    type: 'selectAdvanced',
                    icon: 'fa-solid fa-user-doctor',
                    getValue: obj => obj.breedId,
                    datalistName: 'breeds',
                    datalistFieldKeyName: 'breedId',
                    datalistFieldKey: e => e.breedId,
                    datalistFieldVal: e => `${e?.name}  ${e?.isFromDyreid ? `(DyreID)` : ''}`,
                    showInList: true,
                },
                {
                    title: <Text id='input.species'>Species</Text>,
                    placeholder: <Text id='input.species'>Species</Text>,
                    name: 'species',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.species,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.breed'>Breed</Text>,
                    placeholder: <Text id='input.breed'>Breed</Text>,
                    name: 'breed',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.breed,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.passportId'>passportId</Text>,
                    placeholder: <Text id='input.passportId'>passportId</Text>,
                    name: 'passportId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.passportId,
                    showInList: true,
                },
                {
                    title: <Text id='input.nkkId'>nkkId</Text>,
                    placeholder: <Text id='input.nkkId'>nkkId</Text>,
                    name: 'nkkId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.nkkId,
                    showInList: true,
                },
                {
                    title: <Text id='input.birth'>Birth</Text>,
                    placeholder: <Text id='input.birth'>Birth</Text>,
                    name: 'birth',
                    min: '2000-01-01',
                    max: 'today',
                    type: 'date',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.birth,
                    displayValue: obj => obj ? util.formatDate(obj, { locale: 'nb-NO' }) : null,
                    showInList: false,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.age'>Age</Text>,
                    placeholder: <Text id='input.age'>Age</Text>,
                    name: 'age',
                    type: 'text',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => {
                        if (!obj.birth) return null;
                        return util.age(obj.birth);
                    },
                    displayValue: (val, obj) => {
                        if (!obj.birth) return null;
                        return util.age(obj.birth);
                    },
                    showInList: true,
                    hideOnCreate: false,
                    isReadOnly: true,
                },
                {
                    title: <Text id='input.dececed'>Dececed</Text>,
                    placeholder: <Text id='input.dececed'>Dececed</Text>,
                    name: 'dececed',
                    type: 'date',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.dececed,
                    showInList: true,
                    hideOnCreate: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isOkDyreId'>isOkDyreId</Text>,
                    placeholder: <Text id='input.isOkDyreId'>isOkDyreId</Text>,
                    name: 'isOkDyreId',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isOkDyreId ? 1 : 0,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.chipIdDate'>chipIdDate</Text>,
                    placeholder: <Text id='input.chipIdDate'>chipIdDate</Text>,
                    name: 'chipIdDate',
                    type: 'date',
                    icon: 'fa-solid fa-calendar',
                    min: '2000-01-01',
                    max: 'today',
                    getValue: obj => obj.chipIdDate,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (new Date(obj.birth) > new Date(obj.chipIdDate)) {
                            holdingStore.setFeedback('chipIdDate', 'Date can not be before birth date.');
                        } else if (new Date() < new Date(obj.chipIdDate)) {
                            holdingStore.setFeedback('chipIdDate', 'Date can not be in the future.');
                        } else {
                            holdingStore.setFeedback('chipIdDate', '');
                        }
                    },
                },
                // {
                //     title: <Text id='input.chipIdBarcode'>ChipID</Text>,
                //     placeholder: <Text id='input.chipIdBarcode'>ChipID</Text>,
                //     name: 'chipIdBarcode',
                //     type: 'text',
                //     icon: 'fa-solid fa-barcode',
                //     getValue: obj => obj.chipId || null,
                //     showBarcode: true,
                //     getBarcodeValue: obj => `${obj.chipId}`,
                //     isReadOnly: true,
                //     showInList: true,
                //     hideOnCreate: true,
                // },
                {
                    title: <Text id='input.chipId'>ChipId</Text>,
                    placeholder: <Text id='input.chipId'>ChipId</Text>,
                    name: 'chipId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.chipId,
                    showInList: true,
                    // dyreIdPetId
                    showFieldInfo: ({ holdingStore, obj: object, field }) => {
                        const length = object[field] ? object[field].length : 0;
                        return (<>
                            <span class='font-weight-lighter'>Length:</span> {length}
                        </>);
                    },
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.chipId && !/^\d{15}$/.test(obj.chipId)) {
                            holdingStore.setFeedbackError('chipId', 'Invalid chipId. Must be 15 digits.');
                        } else {
                            holdingStore.setFeedbackError('chipId', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('chipId', '');
                        holdingStore.setFeedbackButton('chipId', null, null);
                    },
                    loadCallback: async (obj, updateValue, holdingStore) => {
                        // console.log('loadCallback', obj, holdingStore.name);
                        holdingStore.setFeedback('chipId', '');
                        holdingStore.setFeedbackButton('chipId', null, null);
                        // This is beeing called when the object is loaded after component is mounted.
                        //
                        // console.log('loadCallback', obj, updateValue, holdingStore.name);
                        if (!holdingStore.dyreIdApproved) return;
                        if (!obj.id) {
                            holdingStore.setFeedback('chipId', 'You need to save the animal before you can register it with DyreID.');
                            return;
                        }
                        if (!obj.speciesId || !obj.breedId) {
                            holdingStore.setFeedback('chipId', 'You need to add species and breed before you can register this animal with DyreID.');
                        }
                        if (!obj.gender) {
                            holdingStore.setFeedback('chipId', 'You need to add gender before you can register this animal with DyreID.');
                        }
                        if (!obj.birth) {
                            holdingStore.setFeedback('chipId', 'You need to add birth date before you can register this animal with DyreID.');
                        }
                        if (!obj.chipIdDate) {
                            holdingStore.setFeedback('chipId', 'You need to add chip date before you can register this animal with DyreID.');
                        }
                        if (new Date(obj.birth) > new Date(obj.chipIdDate)) {
                            holdingStore.setFeedback('chipId', 'Chip date can not be before birth date.');
                            return;
                        }
                        if (!obj.chipId || !obj.speciesId || !obj.breedId || !obj.birth || !obj.chipIdDate) return;
                        // Must be a number and 15 digits
                        if (!/^\d{15}$/.test(obj.chipId)) {
                            holdingStore.setFeedback('chipId', 'Invalid chipId. Has to be 15 digits.');
                        } else if (obj.dyreIdPetId && obj.chipId === obj.dyreIdPetChipId) {
                            console.log('obj.dyreIdPetId', obj.dyreIdPetId);
                            console.log('obj.dyreIdPetChipId', obj.dyreIdPetChipId);
                            holdingStore.setFeedback('chipId', 'DyreID: ChipId is already registered with DyreID.');
                        } else {
                            holdingStore.setFeedback('chipId', 'Checking with DyreID...');
                            const response = await holdingStore.dyreIdChipValidationStatus(obj.chipId);
                            if (response?.data?.status) {
                                switch (response.data.status) {
                                    case 1:
                                        holdingStore.setFeedback('chipId', 'DyreID: Chip is already another pet. Is the number you are registering correct?');
                                        holdingStore.setFeedbackButtonInfo('chipId', 'Customer has been informed that data is shared with DyreID.')
                                        holdingStore.setFeedbackButton('chipId', async () => {
                                            holdingStore.setFeedbackButtonProgress('chipId', true);
                                            await holdingStore.dyreIdRegisterPetOwnerInfoV1(obj.id);
                                            holdingStore.setFeedbackButtonProgress('chipId', false);
                                            holdingStore.setFeedbackButtonDone('chipId', true);
                                            holdingStore.setFeedbackButtonInfo('chipId', '');
                                        }, 'Send to DyreId');
                                        break;
                                    case 2:
                                        holdingStore.setFeedback('chipId', 'DyreID: Valid chip & available for new registration.');
                                        holdingStore.setFeedbackButtonInfo('chipId', 'Customer has been informed that data is shared with DyreID.')
                                        holdingStore.setFeedbackButton('chipId', async () => {
                                            holdingStore.setFeedbackButtonProgress('chipId', true);
                                            await holdingStore.dyreIdRegisterPetOwnerInfoV1(obj.id);
                                            holdingStore.setFeedbackButtonProgress('chipId', false);
                                            holdingStore.setFeedbackButtonDone('chipId', true);
                                            holdingStore.setFeedbackButtonInfo('chipId', '');
                                        }, 'Send to DyreId');
                                        break;
                                    case 3:
                                        holdingStore.setFeedback('chipId', 'DyreID: Chip is not in database, but you can still register it with DyreId');
                                        holdingStore.setFeedbackButtonInfo('chipId', 'Customer has been informed that data is shared with DyreID.')
                                        holdingStore.setFeedbackButton('chipId', async () => {
                                            holdingStore.setFeedbackButtonProgress('chipId', true);
                                            await holdingStore.dyreIdRegisterPetOwnerInfoV1(obj.id);
                                            holdingStore.setFeedbackButtonProgress('chipId', false);
                                            holdingStore.setFeedbackButtonDone('chipId', true);
                                            holdingStore.setFeedbackButtonInfo('chipId', '');
                                        }, 'Send to DyreId');
                                        break;
                                    default:
                                        holdingStore.setFeedback('chipId', 'DyreID: Unknown status from DyreId.');
                                        break;
                                }
                            }
                            // console.log('response', response);
                        }
                    },
                    callback: async (obj, updateValue, holdingStore) => {
                        // console.log('callback', holdingStore.name, holdingStore.dyreIdApproved, obj.speciesId, obj.breedId, obj.birth, obj.chipId, obj.dyreIdPetId);
                        if (!holdingStore.dyreIdApproved) return;
                        if (!obj.id) {
                            holdingStore.setFeedback('chipId', 'You need to save the animal before you can register it with DyreID.');
                            return;
                        }
                        if (!obj.speciesId || !obj.breedId) {
                            holdingStore.setFeedback('chipId', 'You need to add species and breed before you can register this animal with DyreID.');
                        }
                        if (!obj.gender) {
                            holdingStore.setFeedback('chipId', 'You need to add gender before you can register this animal with DyreID.');
                        }
                        if (!obj.birth) {
                            holdingStore.setFeedback('chipId', 'You need to add birth date before you can register this animal with DyreID.');
                        }
                        if (!obj.chipIdDate) {
                            holdingStore.setFeedback('chipId', 'You need to add chip date before you can register this animal with DyreID.');
                        }
                        if (new Date(obj.birth) > new Date(obj.chipIdDate)) {
                            holdingStore.setFeedback('chipId', 'Chip date can not be before birth date.');
                            return;
                        }
                        if (!obj.chipId || !obj.speciesId || !obj.breedId || !obj.birth || !obj.chipIdDate) return;
                        // Must be a number and 15 digits
                        if (!/^\d{15}$/.test(obj.chipId)) {
                            holdingStore.setFeedback('chipId', 'Invalid chipId. Has to be 15 digits.');
                        } else if (obj.dyreIdPetId && obj.chipId === obj.dyreIdPetChipId) {
                            console.log('obj.dyreIdPetId', obj.dyreIdPetId);
                            console.log('obj.dyreIdPetChipId', obj.dyreIdPetChipId);
                            holdingStore.setFeedback('chipId', 'DyreID: ChipId is already registered with DyreId.');
                        } else {
                            holdingStore.setFeedback('chipId', 'Checking with DyreID...');
                            const response = await holdingStore.dyreIdChipValidationStatus(obj.chipId);
                            if (response?.data?.status) {
                                switch (response.data.status) {
                                    case 1:
                                        holdingStore.setFeedback('chipId', 'DyreID: Chip is already another pet. Is the number you are registering correct?');
                                        holdingStore.setFeedbackButton('chipId', async () => {
                                            holdingStore.setFeedbackButtonProgress('chipId', true);
                                            await holdingStore.dyreIdRegisterPetOwnerInfoV1(obj.id);
                                            holdingStore.setFeedbackButtonProgress('chipId', false);
                                            holdingStore.setFeedbackButtonDone('chipId', true);
                                        }, 'Send to DyreId');
                                        break;
                                    case 2:
                                        holdingStore.setFeedback('chipId', 'DyreID: Valid chip & available for new registration.');
                                        holdingStore.setFeedbackButton('chipId', async () => {
                                            holdingStore.setFeedbackButtonProgress('chipId', true);
                                            await holdingStore.dyreIdRegisterPetOwnerInfoV1(obj.id);
                                            holdingStore.setFeedbackButtonProgress('chipId', false);
                                            holdingStore.setFeedbackButtonDone('chipId', true);
                                        }, 'Send to DyreId');
                                        break;
                                    case 3:
                                        holdingStore.setFeedback('chipId', 'DyreID: Chip is not in database, but you can still register it with DyreId');
                                        holdingStore.setFeedbackButton('chipId', async () => {
                                            holdingStore.setFeedbackButtonProgress('chipId', true);
                                            await holdingStore.dyreIdRegisterPetOwnerInfoV1(obj.id);
                                            holdingStore.setFeedbackButtonProgress('chipId', false);
                                            holdingStore.setFeedbackButtonDone('chipId', true);
                                        }, 'Send to DyreId');
                                        break;
                                    default:
                                        holdingStore.setFeedback('chipId', 'DyreID: Unknown status from DyreId.');
                                        break;
                                }
                            }
                            // console.log('response', response);
                            // holdingStore.setFeedback('chipId', JSON.stringify(response));
                        }
                    },
                },
                {
                    title: <Text id='input.chipIdChecked'>ChipId Checked</Text>,
                    placeholder: <Text id='input.chipIdChecked'>ChipId Checked</Text>,
                    name: 'chipIdChecked',
                    type: 'date',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.chipIdChecked,
                    min: '2010-01-01',
                    max: 'today',
                    showInList: true,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.isDececed'>isDececed</Text>,
                    placeholder: <Text id='input.isDececed'>isDececed</Text>,
                    name: 'isDececed',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isDececed ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.isRelocated'>isRelocated</Text>,
                    placeholder: <Text id='input.isRelocated'>isRelocated</Text>,
                    name: 'isRelocated',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isRelocated ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.isOverweight'>isOverweight</Text>,
                    placeholder: <Text id='input.isOverweight'>isOverweight</Text>,
                    name: 'isOverweight',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isOverweight ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.isSterialized'>isSterialized</Text>,
                    placeholder: <Text id='input.isSterialized'>isSterialized</Text>,
                    name: 'isSterialized',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isSterialized ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.foodType'>food Type</Text>,
                    placeholder: <Text id='input.foodType'>food Type</Text>,
                    name: 'foodType',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.foodType,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.animal-admission-title'>Animal admission</Text>,
            key: 'Animal admission',
            fields: [
                {
                    title: <Text id='input.isAdmittedToClinic'>isAdmittedToClinic</Text>,
                    placeholder: <Text id='input.isAdmittedToClinic'>isAdmittedToClinic</Text>,
                    name: 'isAdmittedToClinic',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isAdmittedToClinic ? 1 : 0,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.animal-insurance-title'>Animal insurance</Text>,
            key: 'Animal insurance',
            fields: [
                {
                    title: <Text id='input.insuranceId'>InsuranceId</Text>,
                    placeholder: <Text id='input.insuranceId'>InsuranceId</Text>,
                    name: 'insuranceId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.insuranceId,
                    showInList: true,
                },
                {
                    title: <Text id='input.insuranceCompany'>InsuranceCompany</Text>,
                    placeholder: <Text id='input.insuranceCompany'>InsuranceCompany</Text>,
                    name: 'insuranceCompany',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.insuranceCompany,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.animal-weight-title'>Animal weight</Text>,
            key: 'Animal weight',
            fields: [
                {
                    title: <Text id='input.weight'>weight</Text>,
                    placeholder: <Text id='input.weight'>weight</Text>,
                    name: 'weight',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.weight,
                    showInList: true,
                },
                {
                    title: <Text id='input.allWeights'>allWeights</Text>,
                    placeholder: <Text id='input.allWeights'>allWeights</Text>,
                    name: 'allWeights',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.allWeights,
                    datalist: [
                        'date', 'weight',
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-left px-2'>
                            <span class='font-weight-lighter'>date:</span> {util.isoDate(obj.date)}<br />
                            <span class='font-weight-lighter'>weight:</span> {obj.weight} kg<br />
                        </div>
                    </>,
                    showInList: false,
                    displayHeight: '60px',
                    displayWidth: '200px',
                },
            ],
        },
        {
            name: <Text id='input.animal-media-title'>Animal media</Text>,
            key: 'Animal media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },

            ],
        },
        {
            name: <Text id='input.animal-advanced-title'>Animal advanced</Text>,
            key: 'Animal advanced',
            fields: [
                {
                    title: <Text id='input.members'>Members</Text>,
                    placeholder: <Text id='input.members'>Members</Text>,
                    name: 'members',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'adminMembers',
                    getValue: obj => obj.members,
                    datalist: [
                        'id', 'firstname', 'lastname', 'email', 'cellphone',
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-left px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.firstname} {obj.lastname}<br />
                            <span class='font-weight-lighter'>Cellphone:</span> {obj.cellphone}<br />
                            <span class='font-weight-lighter'>Email:</span> {obj.email}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                },
            ],
        },
    ],
    animalSpecie: [
        {
            name: <Text id='input.animalSpecie-basics-title'>animalSpecie basics</Text>,
            key: 'animalSpecie basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.speciesId'>speciesId</Text>,
                    placeholder: <Text id='input.speciesId'>speciesId</Text>,
                    name: 'speciesId',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.speciesId,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.speciesId'>speciesId</Text>,
                    placeholder: <Text id='input.speciesId'>speciesId</Text>,
                    name: 'speciesId',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.speciesId,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                },
                {
                    title: <Text id='input.shortName'>shortName</Text>,
                    placeholder: <Text id='input.shortName'>shortName</Text>,
                    name: 'shortName',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.shortName,
                    showInList: true,
                },
                {
                    title: <Text id='input.speciesIdNkk'>speciesIdNkk</Text>,
                    placeholder: <Text id='input.speciesIdNkk'>speciesIdNkk</Text>,
                    name: 'speciesIdNkk',
                    type: 'number',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.speciesIdNkk,
                    showInList: true,
                },
                {
                    title: <Text id='input.speciesIdMats'>speciesIdMats</Text>,
                    placeholder: <Text id='input.speciesIdMats'>speciesIdMats</Text>,
                    name: 'speciesIdMats',
                    type: 'number',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.speciesIdMats,
                    showInList: true,
                },
                {
                    title: <Text id='input.speciesIdDyreid'>speciesIdDyreid</Text>,
                    placeholder: <Text id='input.speciesIdDyreid'>speciesIdDyreid</Text>,
                    name: 'speciesIdDyreid',
                    type: 'number',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.speciesIdDyreid,
                    showInList: true,
                },
                {
                    title: <Text id='input.speciesIdDhp'>speciesIdDhp</Text>,
                    placeholder: <Text id='input.speciesIdDhp'>speciesIdDhp</Text>,
                    name: 'speciesIdDhp',
                    type: 'number',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.speciesIdDhp,
                    showInList: true,
                },
                {
                    title: <Text id='input.isFromDyreid'>isFromDyreid</Text>,
                    placeholder: <Text id='input.isFromDyreid'>isFromDyreid</Text>,
                    name: 'isFromDyreid',
                    type: 'number',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isFromDyreid,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.animalSpecie-media-title'>animalSpecie media</Text>,
            key: 'animalSpecie media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },

            ],
        },
    ],
    animalBreed: [
        {
            name: <Text id='input.animalBreed-basics-title'>animalBreed basics</Text>,
            key: 'animalBreed basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.speciesId'>speciesId</Text>,
                    placeholder: <Text id='input.speciesId'>speciesId</Text>,
                    name: 'speciesId',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.speciesId,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.breedId'>breedId</Text>,
                    placeholder: <Text id='input.breedId'>breedId</Text>,
                    name: 'breedId',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.breedId,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                },
                {
                    title: <Text id='input.speciesIdNkk'>speciesIdNkk</Text>,
                    placeholder: <Text id='input.speciesIdNkk'>speciesIdNkk</Text>,
                    name: 'speciesIdNkk',
                    type: 'number',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.speciesIdNkk,
                    showInList: true,
                },
                {
                    title: <Text id='input.breedIdNkk'>breedIdNkk</Text>,
                    placeholder: <Text id='input.breedIdNkk'>breedIdNkk</Text>,
                    name: 'breedIdNkk',
                    type: 'number',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.breedIdNkk,
                    showInList: true,
                },
                {
                    title: <Text id='input.speciesIdDyreid'>speciesIdDyreid</Text>,
                    placeholder: <Text id='input.speciesIdDyreid'>speciesIdDyreid</Text>,
                    name: 'speciesIdDyreid',
                    type: 'number',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.speciesIdDyreid,
                    showInList: true,
                },
                {
                    title: <Text id='input.breedIdDyreid'>breedIdDyreid</Text>,
                    placeholder: <Text id='input.breedIdDyreid'>breedIdDyreid</Text>,
                    name: 'breedIdDyreid',
                    type: 'number',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.breedIdDyreid,
                    showInList: true,
                },
                {
                    title: <Text id='input.breedIdOther'>breedIdOther</Text>,
                    placeholder: <Text id='input.breedIdOther'>breedIdOther</Text>,
                    name: 'breedIdOther',
                    type: 'number',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.breedIdOther,
                    showInList: true,
                },
                {
                    title: <Text id='input.isFromDyreid'>isFromDyreid</Text>,
                    placeholder: <Text id='input.isFromDyreid'>isFromDyreid</Text>,
                    name: 'isFromDyreid',
                    type: 'number',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isFromDyreid,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.animalBreed-media-title'>animalBreed media</Text>,
            key: 'animalBreed media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },

            ],
        },
    ],
    visitor: [
        {
            name: <Text id='input.visitor-search-title'>Visitor search</Text>,
            key: 'Visitor search',
            fields: [
                {
                    title: <Text id='input.cellphone-lookup'>Cellphone lookup</Text>,
                    placeholder: <Text id='input.cellphone-lookup'>Cellphone lookup</Text>,
                    name: 'cellphoneLookup',
                    type: 'phone',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.cellphoneLookup,
                    callback: async (obj, updateValue) => {
                        // console.log('obj.cellphone', obj?.cellphone);
                        if (!obj.id && obj.cellphoneLookup && obj.cellphoneLookup.length >= 8) {
                            const response = await cellphoneLookup(obj.cellphoneLookup);
                            console.log('response', response);
                            if (response && response?.firstName) {
                                // accountId: 0
                                // age: 52
                                // city: "Nord-Torpa"
                                // created: "2025-01-15T14:53:46.6059858+00:00"
                                // dateOfBirth: "1972-10-31"
                                // firstName: "Øistein"
                                // gender: "M"
                                // lastName: "Sørensen"
                                // msisdn: "+4791520191"
                                // streetName: "Gamlebyvegen"
                                // streetNumber: "85"
                                // success: false
                                // zipCode: "2880"
                                updateValue({ name: 'cellphone', value: response?.msisdn });
                                updateValue({ name: 'firstname', value: response?.firstName });
                                updateValue({ name: 'lastname', value: response?.lastName });
                                updateValue({ name: 'gender', value: response?.gender });
                                updateValue({ name: 'birth', value: response?.dateOfBirth });
                                updateValue({ name: 'address', value: response?.streetName + ' ' + response?.streetNumber });
                                updateValue({ name: 'postalcode', value: response?.zipCode });
                                updateValue({ name: 'place', value: response?.city });
                            }

                            // if (response) {
                            //     const place = response[0]?.poststed;
                            //     updateValue({
                            //         name: 'postalplace',
                            //         value: place,
                            //         id: obj.id,
                            //     });
                            // }
                        }
                    },
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.visitor-basics-title'>Visitor basics</Text>,
            key: 'Visitor basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    help: <Text id='input.id-help'>This is the primary ID for this object. Do not change this!</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    help: <Text id='input.uuidv4-help'>This is the uuidv4 for this object. Do not change this!</Text>,
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    help: <Text id='input.user-help'>This is the user.id for this object. Do not change this unless you know what you are doing!</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    help: <Text id='input.customer-help'>This is the customer.id for this object. Do not change this unless you know what you are doing!</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.visitorNumber'>VisitorNumber</Text>,
                    placeholder: <Text id='input.visitorNumber'>VisitorNumber</Text>,
                    name: 'visitorNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.visitorNumber,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.producerNumber'>producerNumber</Text>,
                    placeholder: <Text id='input.producerNumber'>producerNumber</Text>,
                    name: 'producerNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.producerNumber,
                },
                {
                    title: <Text id='input.productionCategory'>Production Category</Text>,
                    placeholder: <Text id='input.productionCategory'>Production Category</Text>,
                    name: 'productionCategory',
                    type: 'select',
                    datalist: [['ANNEN', 'Annen'], ['KJOETT', 'Kjøtt'], ['MELK', 'Melk'], ['EGG', 'Egg']],
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.productionCategory,
                    showInList: true,
                },
                {
                    title: <Text id='input.name'>name</Text>,
                    placeholder: <Text id='input.name'>name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.firstname'>Firstname</Text>,
                    placeholder: <Text id='input.firstname'>Firstname</Text>,
                    name: 'firstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.firstname,
                    showInList: true,
                    isRequired: true,
                },
                {
                    title: <Text id='input.lastname'>Lastname</Text>,
                    placeholder: <Text id='input.lastname'>Lastname</Text>,
                    name: 'lastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.lastname,
                    showInList: true,
                    isRequired: true,
                },
                {
                    title: <Text id='input.orgNumber'>orgNumber</Text>,
                    placeholder: <Text id='input.orgNumber'>orgNumber</Text>,
                    name: 'orgNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.orgNumber || null,
                    showInList: true,
                    showFieldInfo: ({ holdingStore, obj: object, field }) => {
                        const length = object[field] ? object[field].length : 0;
                        return (<>
                            <span class='font-weight-lighter'>Length:</span> {length}
                        </>);
                    },
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.orgNumber && !/^\d{9}$/.test(obj.orgNumber)) {
                            holdingStore.setFeedbackError('orgNumber', 'Invalid orgNumber. Must be 9 digits.');
                        } else {
                            holdingStore.setFeedbackError('orgNumber', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('orgNumber', '');
                        holdingStore.setFeedbackButton('orgNumber', null, null);
                    },
                },
                {
                    title: <Text id='input.invoiceType'>invoiceType</Text>,
                    placeholder: <Text id='input.invoiceType'>invoiceType</Text>,
                    name: 'invoiceType',
                    type: 'select',
                    datalist: [['EHF', 'EHF'], ['EMAIL', 'EMAIL']],
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.invoiceType,
                },
            ],
        },
        {
            name: <Text id='input.visitor-about'>About</Text>,
            key: 'Visitor about',
            fields: [
                {
                    title: <Text id='input.comment'>Comment</Text>,
                    placeholder: <Text id='input.comment'>Comment</Text>,
                    name: 'comment',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.comment,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.visitor-contact-title'>Visitor contact</Text>,
            key: 'Visitor contact',
            fields: [

                {
                    title: <Text id='input.email'>Email</Text>,
                    placeholder: <Text id='input.email'>Email</Text>,
                    name: 'email',
                    type: 'text',
                    icon: 'fa-solid fa-envelope',
                    getValue: obj => obj.email,
                    showInList: true,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.email && !util.validateEmail(obj.email)) {
                            const invalidChars = findInvalidEmailChars(obj.email);
                            let messages = [];
                            if (invalidChars.length > 0) {
                                messages = invalidChars.map(obj => {
                                    return `Character "${obj.char}" at position ${obj.index} is not allowed.`
                                });
                            }
                            holdingStore.setFeedbackError('email', `Invalid email. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('email', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('email', '');
                        holdingStore.setFeedbackButton('email', null, null);
                    },
                },
                {
                    title: <Text id='input.cellphone'>Cellphone</Text>,
                    placeholder: <Text id='input.cellphone'>Cellphone</Text>,
                    name: 'cellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.cellphone,
                    showInList: true,
                },
                {
                    title: <Text id='input.cellphone2'>Cellphone 2</Text>,
                    placeholder: <Text id='input.cellphone2'>Cellphone 2</Text>,
                    name: 'cellphone2',
                    type: 'phone',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.cellphone2,
                    showInList: true,
                },
                {
                    title: <Text id='input.cellphone3'>Cellphone 3</Text>,
                    placeholder: <Text id='input.cellphone3'>Cellphone 3</Text>,
                    name: 'cellphone3',
                    type: 'phone',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.cellphone3,
                    showInList: true,
                },

            ],
        },
        {
            name: <Text id='input.visitor-other-title'>Visitor other</Text>,
            key: 'Visitor other',
            fields: [
                {
                    title: <Text id='input.birth'>Birth</Text>,
                    placeholder: <Text id='input.birth'>Birth</Text>,
                    name: 'birth',
                    type: 'date',
                    min: '1900-01-01',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.birth,
                    displayValue: obj => obj ? util.formatDate(obj, { locale: 'nb-NO' }) : null,
                    showInList: false,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.socialSecurityNumber'>Social Security Number</Text>,
                    placeholder: <Text id='input.socialSecurityNumber'>Social Security Number</Text>,
                    name: 'socialSecurityNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.socialSecurityNumber,
                },
                {
                    title: <Text id='input.dyreIdOwnerId'>dyreIdOwnerId</Text>,
                    placeholder: <Text id='input.dyreIdOwnerId'>dyreIdOwnerId</Text>,
                    name: 'dyreIdOwnerId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.dyreIdOwnerId,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.description,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.visitor-location-title'>Visitor location</Text>,
            key: 'Visitor location',
            fields: [
                {
                    title: <Text id='input.address'>Address</Text>,
                    placeholder: <Text id='input.address'>Address</Text>,
                    name: 'address',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.address,
                    showInList: true,
                },
                {
                    title: <Text id='input.postalcode'>Postalcode</Text>,
                    placeholder: <Text id='input.postalcode'>Postalcode</Text>,
                    name: 'postalcode',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.postalcode,
                    showInList: true,
                    callback: async (obj, updateValue) => {
                        if (obj.postalcode && obj.postalcode.length === 4) {
                            const response = await postnummerLookup(obj.postalcode);

                            if (response) {
                                const place = response[0]?.poststed;
                                updateValue({
                                    name: 'place',
                                    value: place,
                                    id: obj.id,
                                });
                            }
                        }
                    },
                },
                {
                    title: <Text id='input.place'>Place</Text>,
                    placeholder: <Text id='input.place'>Place</Text>,
                    name: 'place',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.place,
                    showInList: true,
                },
                {
                    title: <Text id='input.country'>Country</Text>,
                    placeholder: <Text id='input.country'>Country</Text>,
                    name: 'country',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.country,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.visitor-social-title'>Visitor social</Text>,
            key: 'Visitor social',
            fields: [
                {
                    title: <Text id='input.url'>Url</Text>,
                    placeholder: <Text id='input.url'>Url</Text>,
                    name: 'url',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.url,
                    showInList: true,
                },
                {
                    title: <Text id='input.facebook'>Facebook</Text>,
                    placeholder: <Text id='input.facebook'>Facebook</Text>,
                    name: 'facebook',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.facebook,
                    showInList: true,
                },
                {
                    title: <Text id='input.instagram'>Instagram</Text>,
                    placeholder: <Text id='input.instagram'>Instagram</Text>,
                    name: 'instagram',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.instagram,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.visitor-media-title'>Visitor media</Text>,
            key: 'Visitor media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },
            ],
        },
        {
            name: <Text id='input.visitor-relations-title'>Visitor relations</Text>,
            key: 'Visitor relations',
            fields: [
                {
                    title: <Text id='input.animals'>Animals</Text>,
                    placeholder: <Text id='input.animals'>Animals</Text>,
                    name: 'animals',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.animals,
                    datalist: [
                        'id', 'name', 'breed'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.members'>Members</Text>,
                    placeholder: <Text id='input.members'>Members</Text>,
                    name: 'members',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'adminMembers',
                    getValue: obj => obj.members,
                    datalist: [
                        'id', 'firstname', 'lastname', 'email', 'cellphone',
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-left px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.firstname} {obj.lastname}<br />
                            <span class='font-weight-lighter'>Cellphone:</span> {obj.cellphone}<br />
                            <span class='font-weight-lighter'>Email:</span> {obj.email}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                },
            ],
        },
    ],
    sale: [
        {
            name: <Text id='input.sale-basics-title'>Sale basics</Text>,
            key: 'Sale basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.status'>Status</Text>,
                    placeholder: <Text id='input.status'>Status</Text>,
                    name: 'status',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.status,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>user</Text>,
                    placeholder: <Text id='input.user'>user</Text>,
                    name: 'user',
                    type: 'select',
                    icon: 'fa-solid fa-user-doctor',
                    getValue: obj => obj.user,
                    datalistName: 'employees',
                    datalistFieldKey: e => e.id,
                    datalistFieldVal: e => `${e.firstname} ${e.lastname} (${e.isApproved ? 'active' : 'inactive'})`,
                    showInList: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.visitor'>visitor</Text>,
                    placeholder: <Text id='input.visitor'>visitor</Text>,
                    name: 'visitor',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.visitor,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.journal'>journal</Text>,
                    placeholder: <Text id='input.journal'>journal</Text>,
                    name: 'journal',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.journal,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.calendarEvent'>calendarEvent</Text>,
                    placeholder: <Text id='input.calendarEvent'>calendarEvent</Text>,
                    name: 'calendarEvent',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.calendarEvent,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.isProductionAnimal'>isProductionAnimal</Text>,
                    placeholder: <Text id='input.isProductionAnimal'>isProductionAnimal</Text>,
                    name: 'isProductionAnimal',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.isProductionAnimal || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.isWeborder'>isWeborder</Text>,
                    placeholder: <Text id='input.isWeborder'>isWeborder</Text>,
                    name: 'isWeborder',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.isWeborder || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.invoiceNumber'>invoiceNumber</Text>,
                    placeholder: <Text id='input.invoiceNumber'>invoiceNumber</Text>,
                    name: 'invoiceNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.invoiceNumber,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.visitorNumber'>visitorNumber</Text>,
                    placeholder: <Text id='input.visitorNumber'>visitorNumber</Text>,
                    name: 'visitorNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.visitorNumber,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.animalNumber'>animalNumber</Text>,
                    placeholder: <Text id='input.animalNumber'>animalNumber</Text>,
                    name: 'animalNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.animalNumber,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.saleNumber'>SaleNumber</Text>,
                    placeholder: <Text id='input.saleNumber'>SaleNumber</Text>,
                    name: 'saleNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.saleNumber,
                    showInList: true,
                },
                {
                    title: <Text id='input.title'>Title</Text>,
                    placeholder: <Text id='input.title'>Title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                },
                {
                    title: <Text id='input.saleDate'>SaleDate</Text>,
                    placeholder: <Text id='input.saleDate'>SaleDate</Text>,
                    name: 'saleDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.saleDate,
                    maxFunc: obj => util.isoDate(new Date(), { dateTimeSep: 'T' }),
                    showInList: true,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.paidDate'>PaidDate</Text>,
                    placeholder: <Text id='input.paidDate'>PaidDate</Text>,
                    name: 'paidDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.paidDate,
                    maxFunc: obj => util.isoDate(new Date(), { dateTimeSep: 'T' }),
                    showInList: true,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.dueDate'>dueDate</Text>,
                    placeholder: <Text id='input.dueDate'>dueDate</Text>,
                    name: 'dueDate',
                    type: 'date',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.dueDate,
                    showInList: true,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.description,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.sale-price-payment-title'>Sale price and payment</Text>,
            key: 'Sale price and payment',
            fields: [
                {
                    title: <Text id='input.totalPrice'>TotalPrice</Text>,
                    placeholder: <Text id='input.totalPrice'>TotalPrice</Text>,
                    name: 'totalPrice',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.totalPrice,
                    showInList: true,
                },
                {
                    title: <Text id='input.vat'>Vat</Text>,
                    placeholder: <Text id='input.vat'>Vat</Text>,
                    name: 'vat',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.vat,
                    showInList: true,
                },
                {
                    title: <Text id='input.discount'>Discount</Text>,
                    placeholder: <Text id='input.discount'>Discount</Text>,
                    name: 'discount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.discount,
                    showInList: true,
                },
                {
                    title: <Text id='input.paymentMethod'>PaymentMethod</Text>,
                    placeholder: <Text id='input.paymentMethod'>PaymentMethod</Text>,
                    name: 'paymentMethod',
                    type: 'select',
                    datalist: [['cash', 'Kontant'], ['card', 'Kort'], ['invoice', 'Faktura'], ['account', 'Konto'], ['creditNote', 'Kreditnota']],
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.paymentMethod,

                    showInList: true,
                },
                {
                    title: <Text id='input.paymentAmount'>PaymentAmount</Text>,
                    placeholder: <Text id='input.paymentAmount'>PaymentAmount</Text>,
                    name: 'paymentAmount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.paymentAmount,
                    showInList: true,
                },
                {
                    title: <Text id='input.contactMethod'>ContactMethod</Text>,
                    placeholder: <Text id='input.contactMethod'>ContactMethod</Text>,
                    name: 'contactMethod',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.contactMethod,
                    showInList: true,
                },
                {
                    title: <Text id='input.fikenLocation'>fikenLocation</Text>,
                    placeholder: <Text id='input.fikenLocation'>fikenLocation</Text>,
                    name: 'fikenLocation',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.fikenLocation,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.animaliaId'>animaliaId</Text>,
                    placeholder: <Text id='input.animaliaId'>animaliaId</Text>,
                    name: 'animaliaId',
                    type: 'number',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.animaliaId,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.reminders'>reminders</Text>,
                    placeholder: <Text id='input.reminders'>reminders</Text>,
                    name: 'reminders',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-paper-plane',
                    getValue: obj => obj.reminders,
                    datalist: [
                        'date', 'type'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                },
                {
                    title: <Text id='input.comments'>comments</Text>,
                    placeholder: <Text id='input.comments'>comments</Text>,
                    name: 'comments',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-paper-plane',
                    getValue: obj => obj.comments,
                    datalist: [
                        'date', 'comment'
                    ],
                    showInList: false,
                    displayHeight: '120px',
                },
            ],
        },
        {
            name: <Text id='input.sale-content-title'>Sale content</Text>,
            key: 'Sale content',
            fields: [
                {
                    title: <Text id='input.products'>products</Text>,
                    placeholder: <Text id='input.products'>products</Text>,
                    name: 'products',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.products?.map(e => e),
                    displayArrayObject: obj => `${obj.qty} x ${obj.id} ${obj.name}, ${util.format(obj.price, 2)}`,
                    datalist: [
                        'id', 'name', 'qty', 'price',
                    ],
                    showInList: false,
                    displayHeight: '120px',
                },
                {
                    title: <Text id='input.journals'>journals</Text>,
                    placeholder: <Text id='input.journals'>journals</Text>,
                    name: 'journals',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.journals?.map(e => e),
                    displayArrayObject: obj => `id: ${obj.id}, jnr: ${obj.journalNumber}, ${util.isoDate(obj.date)}`,
                    datalist: [
                        'id', 'journalNumber', 'date',
                    ],
                    showInList: false,
                    displayHeight: '120px',
                },
                {
                    title: <Text id='input.visitors'>visitors</Text>,
                    placeholder: <Text id='input.visitors'>visitors</Text>,
                    name: 'visitors',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.visitors?.map(e => e),
                    displayArrayObject: obj => `${obj.id} ${obj.firstname} ${obj.lastname}, ${obj.cellphone}`,
                    datalist: [
                        'id', 'firstname', 'lastname', 'cellphone',
                    ],
                    showInList: false,
                    displayHeight: '120px',
                },
            ],
        },
        {
            name: <Text id='input.sale-shipping-title'>Sale shipping</Text>,
            key: 'Sale shipping',
            fields: [
                {
                    title: <Text id='input.noPackages'>noPackages</Text>,
                    placeholder: <Text id='input.noPackages'>noPackages</Text>,
                    name: 'noPackages',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.noPackages,
                    showInList: false,
                },
                {
                    title: <Text id='input.packedDate'>packedDate</Text>,
                    placeholder: <Text id='input.packedDate'>packedDate</Text>,
                    name: 'packedDate',
                    type: 'date',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.packedDate,
                    showInList: false,
                },
                {
                    title: <Text id='input.packedBy'>packedBy</Text>,
                    placeholder: <Text id='input.packedBy'>packedBy</Text>,
                    name: 'packedBy',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.packedBy,
                    showInList: false,
                },
                {
                    title: <Text id='input.shippingDate'>shippingDate</Text>,
                    placeholder: <Text id='input.shippingDate'>shippingDate</Text>,
                    name: 'shippingDate',
                    type: 'date',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.shippingDate,
                    showInList: false,
                },
                {
                    title: <Text id='input.shippedBy'>shippedBy</Text>,
                    placeholder: <Text id='input.shippedBy'>shippedBy</Text>,
                    name: 'shippedBy',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.shippedBy,
                    showInList: false,
                },
                {
                    title: <Text id='input.shippingMethod'>shippingMethod</Text>,
                    placeholder: <Text id='input.shippingMethod'>shippingMethod</Text>,
                    name: 'shippingMethod',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.shippingMethod,
                    showInList: false,
                },
                {
                    title: <Text id='input.trackingNumber'>trackingNumber</Text>,
                    placeholder: <Text id='input.trackingNumber'>trackingNumber</Text>,
                    name: 'trackingNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.trackingNumber,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.sale-media-title'>Sale media</Text>,
            key: 'Sale media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                },
            ],
        },
    ],
    salePayment: [
        {
            name: <Text id='input.salePayment-basics-title'>salePayment basics</Text>,
            key: 'salePayment basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.sale'>sale</Text>,
                    placeholder: <Text id='input.sale'>sale</Text>,
                    name: 'sale',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.sale,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.paidDate'>PaidDate</Text>,
                    placeholder: <Text id='input.paidDate'>PaidDate</Text>,
                    name: 'paidDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.paidDate,
                    showInList: true,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.description,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.salePayment-price-payment-title'>salePayment price and payment</Text>,
            key: 'salePayment price and payment',
            fields: [
                {
                    title: <Text id='input.amount'>amount</Text>,
                    placeholder: <Text id='input.amount'>amount</Text>,
                    name: 'amount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.amount,
                    showInList: true,
                },
                {
                    title: <Text id='input.vat'>Vat</Text>,
                    placeholder: <Text id='input.vat'>Vat</Text>,
                    name: 'vat',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.vat,
                    showInList: true,
                },
                {
                    title: <Text id='input.paymentMethod'>PaymentMethod</Text>,
                    placeholder: <Text id='input.paymentMethod'>PaymentMethod</Text>,
                    name: 'paymentMethod',
                    type: 'select',
                    datalist: [['cash', 'Kontant'], ['card', 'Kort'], ['invoice', 'Faktura'], ['account', 'Konto'], ['creditNote', 'Kreditnota']],
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.paymentMethod,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.salePayment-media-title'>salePayment media</Text>,
            key: 'salePayment media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                },
            ],
        },
    ],
    saleReport: [
        {
            name: <Text id='input.saleReport-basics-title'>saleReport basics</Text>,
            key: 'saleReport basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.reportDate'>reportDate</Text>,
                    placeholder: <Text id='input.reportDate'>reportDate</Text>,
                    name: 'reportDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.reportDate,
                    showInList: true,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.reportType'>reportType</Text>,
                    placeholder: <Text id='input.reportType'>reportType</Text>,
                    name: 'reportType',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.reportType,
                    showInList: true,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.description,
                    showInList: true,
                },
                {
                    title: <Text id='input.comment'>comment</Text>,
                    placeholder: <Text id='input.comment'>comment</Text>,
                    name: 'comment',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.comment,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.saleReport-price-payment-title'>saleReport amounts</Text>,
            key: 'saleReport amounts',
            fields: [
                {
                    title: <Text id='input.amountCash'>amountCash</Text>,
                    placeholder: <Text id='input.amountCash'>amountCash</Text>,
                    name: 'amountCash',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.amountCash,
                    showInList: true,
                },
                {
                    title: <Text id='input.amountCard'>amountCard</Text>,
                    placeholder: <Text id='input.amountCard'>amountCard</Text>,
                    name: 'amountCard',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.amountCard,
                    showInList: true,
                },
                {
                    title: <Text id='input.amountInvoice'>amountInvoice</Text>,
                    placeholder: <Text id='input.amountInvoice'>amountInvoice</Text>,
                    name: 'amountInvoice',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.amountInvoice,
                    showInList: true,
                },
                {
                    title: <Text id='input.amountAccount'>amountAccount</Text>,
                    placeholder: <Text id='input.amountAccount'>amountAccount</Text>,
                    name: 'amountAccount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.amountAccount,
                    showInList: true,
                },
                {
                    title: <Text id='input.amountCreditNote'>amountCreditNote</Text>,
                    placeholder: <Text id='input.amountCreditNote'>amountCreditNote</Text>,
                    name: 'amountCreditNote',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.amountCreditNote,
                    showInList: true,
                },
                {
                    title: <Text id='input.amountUnpaid'>amountUnpaid</Text>,
                    placeholder: <Text id='input.amountUnpaid'>amountUnpaid</Text>,
                    name: 'amountUnpaid',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.amountUnpaid,
                    showInList: true,
                },
                {
                    title: <Text id='input.amountTotal'>amountTotal</Text>,
                    placeholder: <Text id='input.amountTotal'>amountTotal</Text>,
                    name: 'amountTotal',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.amountTotal,
                    showInList: true,
                },
                {
                    title: <Text id='input.amountTax'>amountTax</Text>,
                    placeholder: <Text id='input.amountTax'>amountTax</Text>,
                    name: 'amountTax',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.amountTax,
                    showInList: true,
                },

                {
                    title: <Text id='input.deviationCash'>deviationCash</Text>,
                    placeholder: <Text id='input.deviationCash'>deviationCash</Text>,
                    name: 'deviationCash',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.deviationCash,
                    showInList: true,
                },
                {
                    title: <Text id='input.deviationCard'>deviationCard</Text>,
                    placeholder: <Text id='input.deviationCard'>deviationCard</Text>,
                    name: 'deviationCard',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.deviationCard,
                    showInList: true,
                },
                {
                    title: <Text id='input.deviationVipps'>deviationVipps</Text>,
                    placeholder: <Text id='input.deviationVipps'>deviationVipps</Text>,
                    name: 'deviationVipps',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.deviationVipps,
                    showInList: true,
                },
                {
                    title: <Text id='input.deviationInvoice'>deviationInvoice</Text>,
                    placeholder: <Text id='input.deviationInvoice'>deviationInvoice</Text>,
                    name: 'deviationInvoice',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.deviationInvoice,
                    showInList: true,
                },
                {
                    title: <Text id='input.deviationAccount'>deviationAccount</Text>,
                    placeholder: <Text id='input.deviationAccount'>deviationAccount</Text>,
                    name: 'deviationAccount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.deviationAccount,
                    showInList: true,
                },
                {
                    title: <Text id='input.deviationCreditNote'>deviationCreditNote</Text>,
                    placeholder: <Text id='input.deviationCreditNote'>deviationCreditNote</Text>,
                    name: 'deviationCreditNote',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.deviationCreditNote,
                    showInList: true,
                },
                {
                    title: <Text id='input.deviationUnpaid'>deviationUnpaid</Text>,
                    placeholder: <Text id='input.deviationCreditNote'>deviationUnpaid</Text>,
                    name: 'deviationUnpaid',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.deviationUnpaid,
                    showInList: true,
                },
                {
                    title: <Text id='input.deviationTotal'>deviationTotal</Text>,
                    placeholder: <Text id='input.deviationTotal'>deviationTotal</Text>,
                    name: 'deviationTotal',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.deviationTotal,
                    showInList: true,
                },
                {
                    title: <Text id='input.deviationTax'>deviationTax</Text>,
                    placeholder: <Text id='input.deviationTax'>deviationTax</Text>,
                    name: 'deviationTax',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.deviationTax,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.saleReport-media-title'>saleReport media</Text>,
            key: 'saleReport media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                },
            ],
        },
    ],
    product: [
        {
            name: <Text id='input.product-important-title'>Product important</Text>,
            key: 'Product important',
            isCollapsed: false,
            backgroundColor: '#f6fff6 !important',
            borderColor: '#33dd33 !important',
            fields: [
                {
                    title: <Text id='input.webshop'>webshop</Text>,
                    placeholder: <Text id='input.webshop'>webshop</Text>,
                    name: 'webshop',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.webshop || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.productNumber'>Product Number</Text>,
                    placeholder: <Text id='input.productNumber'>Product Number</Text>,
                    help: <Text id='input.productNumber-help'>Product number. Your own product number. THIS IS REQUIRED FOR TRIPLETEX AND FIKEN.</Text>,
                    name: 'productNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.productNumber,
                    before: async (obj, holdingStore) => {
                        const { allProducts = [] } = holdingStore;
                        holdingStore.setFeedback('productNumber', `Siste: \n${allProducts.filter(e => e.productNumber).splice(0, 5).map(e => `💡 __${e.productNumber}__: ${e.name}`).join('\n')}`);
                        console.log('before', allProducts.map(e => e.productNumber).join(', '));
                        // holdingStore.setFeedback('productNumber', 'FOOBAR');
                        holdingStore.setFeedbackButton('productNumber', null, null);
                    },
                    showInList: true,
                    isRequired: true,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                    isRequired: true,
                },
                {
                    title: <Text id='input.inStock'>inStock</Text>,
                    placeholder: <Text id='input.inStock'>inStock</Text>,
                    help: <Text id='input.qty-help'>Quantity in stock</Text>,
                    name: 'inStock',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.inStock,
                    showInList: true,
                },
                {
                    title: <Text id='input.priceIncVat'>Price inc VAT</Text>,
                    placeholder: <Text id='input.priceIncVat'>Price inc VAT</Text>,
                    help: <Text id='input.price-help'>Selling price with VAT</Text>,
                    name: 'priceIncVat',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.priceIncVat,
                    callback: (obj, updateValue, holdingStore, value) => {
                        // console.log('callback for priceIncVat', { obj, updateValue, holdingStore, value });
                        const finalPrice = obj.priceIncVat / (1 + (obj.vat || 0) / 100);
                        updateValue({
                            id: obj.id,
                            name: 'price',
                            value: finalPrice,
                        });
                    },
                    // checkValue: (obj, holdingStore, value) => {
                    //     console.log('checkValue for priceIncVat', { obj, holdingStore, value });
                    // },
                    showInList: true,
                },
                {
                    title: <Text id='input.price'>Price</Text>,
                    placeholder: <Text id='input.price'>Price</Text>,
                    help: <Text id='input.price-help'>Selling price without VAT</Text>,
                    name: 'price',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.price,
                    callback: (obj, updateValue, holdingStore, value) => {
                        console.log('callback for price', { obj, updateValue, holdingStore, value });
                        const finalPrice = obj.price * (1 + (obj.vat || 0) / 100);
                        updateValue({
                            id: obj.id,
                            name: 'priceIncVat',
                            value: finalPrice,
                        });
                    },
                    displayPublicValue: (price, obj) => `${obj?.currency || 'kr.'} ${lineTotal(obj, true, 1)}`,
                    showInList: true,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    help: <Text id='input.product-images-help'>Images of product</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.colors'>Colors</Text>,
                    placeholder: <Text id='input.colors'>Colors</Text>,
                    help: <Text id='input.colors-help'>Color variants of this product.</Text>,
                    name: 'colors',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.colors,
                    datalist: [
                        'productNumer', 'qty', 'name', 'description'
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-left px-2'>
                            <span class='font-weight-lighter'>productNumer:</span> {obj.productNumer}<br />
                            <span class='font-weight-lighter'>qty:</span> {obj.qty}<br />
                            <span class='font-weight-lighter'>name:</span> {obj.name}<br />
                            <span class='font-weight-lighter'>description:</span> {obj.description}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '250px',
                    displayHeight: '100px',
                },
                {
                    title: <Text id='input.sizes'>Sizes</Text>,
                    placeholder: <Text id='input.sizes'>Sizes</Text>,
                    help: <Text id='input.sizes-help'>Sizes variants of this product.</Text>,
                    name: 'sizes',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.sizes,
                    datalist: [
                        'productNumer', 'qty', 'name', 'description'
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-left px-2'>
                            <span class='font-weight-lighter'>productNumer:</span> {obj.productNumer}<br />
                            <span class='font-weight-lighter'>qty:</span> {obj.qty}<br />
                            <span class='font-weight-lighter'>name:</span> {obj.name}<br />
                            <span class='font-weight-lighter'>description:</span> {obj.description}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '250px',
                    displayHeight: '100px',
                },

            ],
        },
        {
            name: <Text id='input.product-basics-title'>Product basics</Text>,
            key: 'Product basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.race'>race</Text>,
                    placeholder: <Text id='input.race'>race</Text>,
                    name: 'race',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.race,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.webshop'>webshop</Text>,
                    placeholder: <Text id='input.webshop'>webshop</Text>,
                    name: 'webshop',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.webshop || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.isGlobalSubscription'>isGlobalSubscription</Text>,
                    placeholder: <Text id='input.webshop'>isGlobalSubscription</Text>,
                    name: 'isGlobalSubscription',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.isGlobalSubscription || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.account'>account</Text>,
                    placeholder: <Text id='input.account'>account</Text>,
                    help: <Text id='input.account-help'>Accounting account number. Use real series.</Text>,
                    name: 'account',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.account,
                    // ACCOUNT_MAP
                    showFieldInfo: ({ holdingStore, obj: object, field }) => {
                        return <>
                            {ACCOUNT_MAP[object.account]}
                        </>;
                    },
                    showInList: true,
                },
                {
                    title: <Text id='input.productNumber'>Product Number</Text>,
                    placeholder: <Text id='input.productNumber'>Product Number</Text>,
                    help: <Text id='input.productNumber-help'>Product number. Your own product number. THIS IS REQUIRED FOR TRIPLETEX AND FIKEN.</Text>,
                    name: 'productNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.productNumber,
                    showInList: true,
                    isRequired: true,
                },
                {
                    title: <Text id='input.category'>category</Text>,
                    placeholder: <Text id='input.category'>category</Text>,
                    help: <Text id='input.category-help'>Category of product</Text>,
                    name: 'category',
                    type: 'select',
                    icon: 'fa-solid fa-folder',
                    getValue: obj => obj.category,
                    datalist: [
                        ['produkt', 'Produkt'],
                        ['honorar', 'Honorar'],
                        ['medisin', 'Medisin'],
                        ['annet', 'Annet'],
                    ],
                    // datalistFieldKey: e => e.id,
                    // datalistFieldVal: e => `${e.firstname} ${e.lastname}`,
                    showInList: true,
                },
                {
                    title: <Text id='input.type'>type</Text>,
                    placeholder: <Text id='input.type'>type</Text>,
                    help: <Text id='input.type-help'>Type of product</Text>,
                    name: 'type',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.type,
                    showInList: true,
                },
                {
                    title: <Text id='input.gtin'>gtin</Text>,
                    placeholder: <Text id='input.gtin'>gtin</Text>,
                    help: <Text id='input.gtin-help'>Global Trade Item Number for product. This is used for barcodes.</Text>,
                    name: 'gtin',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.gtin,
                    showInList: true,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                    isRequired: true,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    help: <Text id='input.description-help'>Description of product</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.description,
                    showInList: false,
                },
                {
                    title: <Text id='input.technicalInfo'>Technical Info</Text>,
                    placeholder: <Text id='input.technicalInfo'>Technical Info</Text>,
                    help: <Text id='input.technicalInfo-help'>Technical Info of product</Text>,
                    name: 'technicalInfo',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.technicalInfo,
                    showInList: false,
                },
                {
                    title: <Text id='input.properties'>Properties</Text>,
                    placeholder: <Text id='input.properties'>Properties</Text>,
                    help: <Text id='input.properties-help'>Properties of product</Text>,
                    name: 'properties',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.properties,
                    showInList: false,
                },
                {
                    title: <Text id='input.journal'>journal</Text>,
                    placeholder: <Text id='input.journal'>journal</Text>,
                    name: 'journal',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.journal,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.reminderDays'>reminderDays</Text>,
                    placeholder: <Text id='input.reminderDays'>reminderDays</Text>,
                    help: <Text id='input.reminderDays-help'>Days in the future you want to schedule an reminder sms to the animal owner.</Text>,
                    name: 'reminderDays',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.reminderDays,
                    showInList: true,
                    isAdminField: false,
                },
            ],
        },
        {
            name: <Text id='input.product-online-booking-title'>Products for online booking</Text>,
            key: 'Products for online booking',
            fields: [
                {
                    title: <Text id='input.isOnlineBooking'>isOnlineBooking</Text>,
                    placeholder: <Text id='input.isOnlineBooking'>isOnlineBooking</Text>,
                    name: 'isOnlineBooking',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.isOnlineBooking || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.isBookingExclusive'>Booking is exclusive</Text>,
                    placeholder: <Text id='input.isBookingExclusive'>Booking is exclusive</Text>,
                    help: <Text id='input.isBookingExclusive-help'>Booking is exclusive for this customer.</Text>,
                    name: 'isBookingExclusive',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.isBookingExclusive || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.estimatedTime'>Estimated Time</Text>,
                    placeholder: <Text id='input.estimatedTime'>Estimated Time</Text>,
                    help: <Text id='input.estimatedTime-help'>Estimated Time in minutes</Text>,
                    name: 'estimatedTime',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.estimatedTime,
                    showInList: true,
                },
                {
                    title: <Text id='input.minutesBeforeEndOfDay'>Minutes Before End Of Day</Text>,
                    placeholder: <Text id='input.minutesBeforeEndOfDay'>Minutes Before End Of Day</Text>,
                    help: <Text id='input.minutesBeforeEndOfDay-help'>Not later than x minutes before end of day.</Text>,
                    name: 'minutesBeforeEndOfDay',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.minutesBeforeEndOfDay,
                    showInList: true,
                },
                {
                    title: <Text id='input.veterinaries'>veterinaries</Text>,
                    placeholder: <Text id='input.veterinaries'>veterinaries</Text>,
                    name: 'veterinaries',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'veterinaries',
                    getValue: obj => obj.veterinaries?.map(m => m),
                    datalist: [
                        'id', 'firstname', 'lastname', 'email', 'cellphone',
                    ],
                    datalistHelp: [
                        'User id', 'Firstname of veterinary.', 'Lastname of veterinary.',
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-left px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.firstname} {obj.lastname}<br />
                            <span class='font-weight-lighter'>Cellphone:</span> {obj.cellphone}<br />
                            <span class='font-weight-lighter'>Email:</span> {obj.email}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                },
            ],
        },
        {
            name: <Text id='input.product-price-stock-title'>Product price and stock</Text>,
            key: 'Product price and stock',
            fields: [
                {
                    title: <Text id='input.qty'>Qty</Text>,
                    placeholder: <Text id='input.qty'>Qty</Text>,
                    help: <Text id='input.qty-help'>Quantity added when you add product to sale.</Text>,
                    name: 'qty',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.qty,
                    showInList: true,
                },
                {
                    title: <Text id='input.idealStock'>idealStock</Text>,
                    placeholder: <Text id='input.idealStock'>idealStock</Text>,
                    help: <Text id='input.idealStock-help'>Quantity should always be in stock.</Text>,
                    name: 'idealStock',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.idealStock,
                    showInList: true,
                },
                {
                    title: <Text id='input.inStock'>inStock</Text>,
                    placeholder: <Text id='input.inStock'>inStock</Text>,
                    help: <Text id='input.qty-help'>Quantity in stock</Text>,
                    name: 'inStock',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.inStock,
                    showInList: true,
                },
                {
                    title: <Text id='input.inPrice'>inPrice</Text>,
                    placeholder: <Text id='input.inPrice'>inPrice</Text>,
                    help: <Text id='input.inPrice-help'>In price without VAT</Text>,
                    name: 'inPrice',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.inPrice,
                    showInList: true,
                },
                {
                    title: <Text id='input.inPriceDiscount'>inPriceDiscount</Text>,
                    placeholder: <Text id='input.inPriceDiscount'>inPriceDiscount</Text>,
                    help: <Text id='input.inPriceDiscount-help'>In price discount in %</Text>,
                    name: 'inPriceDiscount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.inPriceDiscount,
                    showInList: true,
                    showFieldInfo: ({ holdingStore, obj: object, field }) => {
                        const priceWithDiscount = object.inPrice * (1 - object.inPriceDiscount / 100);
                        return (<>
                            <span class='font-weight-lighter'><Text id='input.inPrice-width-discount'>inPrice w/discount</Text>:</span> {util.format(priceWithDiscount, 2, ',', ' ')}
                        </>);
                    },
                    // checkValue: (obj, holdingStore) => {
                    //     // console.log('checkValue', obj, holdingStore.name);
                    //     if (obj.talentApplication.length > 500) {
                    //         holdingStore.setFeedbackError('talentApplication', 'Too many characters');
                    //     } else {
                    //         holdingStore.setFeedbackError('talentApplication', '');
                    //     }
                    // },
                },
                {
                    title: <Text id='input.desiredMargin'>desiredMargin</Text>,
                    placeholder: <Text id='input.desiredMargin'>desiredMargin</Text>,
                    help: <Text id='input.desiredMargin-help'>Desired margin in percent</Text>,
                    name: 'desiredMargin',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.desiredMargin,
                    callback: (obj, updateValue) => {
                        const inPrice = obj.inPrice;
                        const desiredMargin = obj.desiredMargin;
                        const price = Math.round(inPrice * (1 + desiredMargin / 100));
                        updateValue({
                            id: obj.id,
                            name: 'price',
                            value: price,
                        });
                    },
                    showInList: true,
                },
                {
                    title: <Text id='input.price'>Price</Text>,
                    placeholder: <Text id='input.price'>Price</Text>,
                    help: <Text id='input.price-help'>Selling price without VAT</Text>,
                    name: 'price',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.price,
                    displayPublicValue: (price, obj) => `${obj?.currency || 'kr.'} ${lineTotal(obj, true, 1)}`,
                    showInList: true,
                },
                {
                    title: <Text id='input.medicine'>Medicine</Text>,
                    placeholder: <Text id='input.medicine'>Medicine</Text>,
                    help: <Text id='input.medicine-help'>Part of price that is medicine cost.</Text>,
                    name: 'medicine',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.medicine,
                    showInList: true,
                },
                {
                    title: <Text id='input.fee'>Fee</Text>,
                    placeholder: <Text id='input.fee'>Fee</Text>,
                    help: <Text id='input.fee-help'>Part of price that is fee cost.</Text>,
                    name: 'fee',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.fee,
                    showInList: true,
                },
                {
                    title: <Text id='input.equipment'>Equipment</Text>,
                    placeholder: <Text id='input.equipment'>Equipment</Text>,
                    help: <Text id='input.equipment-help'>Part of price that is equipment.</Text>,
                    name: 'equipment',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.equipment,
                    showInList: true,
                },
                {
                    title: <Text id='input.other'>Other</Text>,
                    placeholder: <Text id='input.other'>Other</Text>,
                    help: <Text id='input.other-help'>Part of price that is other cost.</Text>,
                    name: 'other',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.other,
                    showInList: true,
                },
                {
                    title: <Text id='input.vat'>Vat</Text>,
                    placeholder: <Text id='input.vat'>Vat</Text>,
                    help: <Text id='input.vat-help'>VAT in percent. Normally 25%.</Text>,
                    name: 'vat',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.vat,
                    showInList: true,
                    defaultValue: 25,
                },
                {
                    title: <Text id='input.discount'>Discount</Text>,
                    placeholder: <Text id='input.discount'>Discount</Text>,
                    help: <Text id='input.discount-help'>Discount in percent</Text>,
                    name: 'discount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.discount,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.product-sub-title'>Sub Products</Text>,
            key: 'Sub Products',
            fields: [
                {
                    title: <Text id='input.subProducts'>subProducts</Text>,
                    placeholder: <Text id='input.subProducts'>subProducts</Text>,
                    help: <Text id='input.subProducts-help'>Sub products of this product. Here you can attach other products that will be added automatically when you choose this product.</Text>,
                    name: 'subProducts',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',

                    lookupArray: 'adminAllProducts',
                    getValue: obj => obj.subProducts?.map(e => e),
                    displayArrayObject: obj => `${obj.qty} stk. ${obj.name} (${obj.externalId}/${obj.id})`,

                    datalist: [
                        'md5', 'qty', 'name', 'id', 'externalId',
                    ],
                    showInList: false,
                    displayHeight: '150px',
                    isAdminField: true,
                },
                {
                    title: <Text id='input.subProducts'>subProducts</Text>,
                    placeholder: <Text id='input.subProducts'>subProducts</Text>,
                    help: <Text id='input.subProducts-help'>Sub products of this product. Here you can attach other products that will be added automatically when you choose this product.</Text>,
                    name: 'subProducts',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',

                    lookupArray: 'allProducts',
                    getValue: obj => obj.subProducts?.map(e => e),
                    displayArrayObject: obj => obj ? `${obj.qty} stk. ${obj.name} (${obj.externalId}/${obj.id})` : '',

                    datalist: [
                        'qty', 'name', 'id', 'externalId',
                    ],
                    showInList: false,
                    displayHeight: '150px',
                },
            ],
        },
        {
            name: <Text id='input.product-media-title'>Product media</Text>,
            key: 'Product media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    help: <Text id='input.product-images-help'>Images of product</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    help: <Text id='input.files-help'>Files related to this product. Can be product sheets, usage manuals and such.</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    help: <Text id='input.links-help'>Links to product sheets, product pages or such.</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                },
            ],
        },
    ],
    diagnose: [
        {
            name: <Text id='input.diagnose-basics-title'>Diagnose basics</Text>,
            key: 'Diagnose basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.diagnoseCode'>DiagnoseCode</Text>,
                    placeholder: <Text id='input.diagnoseCode'>DiagnoseCode</Text>,
                    name: 'diagnoseCode',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.diagnoseCode,
                    showInList: true,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.description,
                    showInList: true,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.drugs'>Drugs</Text>,
                    placeholder: <Text id='input.drugs'>Drugs</Text>,
                    name: 'drugs',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.drugs,
                    datalist: [
                        'drug', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },
            ],
        },
    ],
    file: [
        {
            name: <Text id='input.file-basics-title'>File basics</Text>,
            key: 'File basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.date'>date</Text>,
                    placeholder: <Text id='input.date'>date</Text>,
                    name: 'date',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.date,
                    showInList: true,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.isPublic'>isPublic</Text>,
                    placeholder: <Text id='input.isPublic'>isPublic</Text>,
                    name: 'isPublic',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.isPublic || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.showOnFrontpage'>showOnFrontpage</Text>,
                    placeholder: <Text id='input.showOnFrontpage'>showOnFrontpage</Text>,
                    name: 'showOnFrontpage',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.showOnFrontpage || null,
                    showInList: true,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.isFavorite'>isFavorite</Text>,
                    placeholder: <Text id='input.isFavorite'>isFavorite</Text>,
                    name: 'isFavorite',
                    type: 'toggle',
                    icon: 'fa-solid fa-star',
                    getValue: obj => obj.isFavorite || null,
                    showInList: true,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.fileCode'>FileCode</Text>,
                    placeholder: <Text id='input.fileCode'>FileCode</Text>,
                    name: 'fileCode',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.fileCode,
                    showInList: true,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.description,
                    showInList: true,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
            ],
        },
    ],
    media: [
        {
            name: <Text id='input.media-content-title'>Media content</Text>,
            key: 'Media content',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                    callback: async (obj, updateValue) => {
                        // console.log('fields.callback', { obj, updateValue });
                        if (obj.images?.length > 0) {
                            const img = obj.images[0];
                            if (img?.exif?.image?.Artist) {
                                updateValue({
                                    name: 'attribution',
                                    value: img?.exif?.image?.Artist,
                                });
                            }
                            if (img?.exif?.exif?.DateTimeOriginal) {
                                updateValue({
                                    name: 'whenIsTheMediaRecorded',
                                    value: img?.exif?.exif?.DateTimeOriginal,
                                });
                            }
                        }
                    },
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: false,
                },
                // {
                //     title: <Text id='input.links'>Links</Text>,
                //     placeholder: <Text id='input.links'>Links</Text>,
                //     name: 'links',
                //     type: 'arrayObject',
                //     icon: 'fa-solid fa-tag',
                //     getValue: obj => obj.links,
                //     datalist: [
                //         'url', 'name', 'description'
                //     ],
                //     showInList: false,
                //     displayHeight: '60px',
                //     hideOnCreate: false,
                // },
            ],
        },
        {
            name: <Text id='input.media-basics-title'>Media basics</Text>,
            key: 'Media basics',
            fields: [
                {
                    lang: { en: 'Id', no: 'Id' },
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    lang: { en: 'Offline', no: 'Avpublisert' },
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    help: <Text id='input.offline-help'>Set to 1 to delete the media</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                },
                {
                    title: <Text id='input.sort'>Sort</Text>,
                    placeholder: <Text id='input.sort'>Sort</Text>,
                    name: 'sort',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.sort,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                // {
                //     title: <Text id='input.race'>Race</Text>,
                //     placeholder: <Text id='input.race'>Race</Text>,
                //     name: 'race',
                //     type: 'number',
                //     icon: 'fa-solid fa-input-numeric',
                //     getValue: obj => obj.race,
                //     showInList: true,
                //     isAdminField: true,
                // },

                {
                    title: <Text id='input.race'>Race</Text>,
                    placeholder: <Text id='input.race'>Race</Text>,
                    name: 'race',
                    type: 'select',
                    icon: 'fa-solid fa-list',
                    getValue: obj => obj.race,
                    datalistName: 'races',
                    // datalist: [['birthday', 'Birthday'], ['buying', 'Buying'], ['christmas', 'Christmas'], ['dreaming', 'Dreaming about'], ['wedding', 'Wedding'], ['other', 'Other']],
                    datalistFieldKey: e => e.id,
                    datalistFieldVal: e => `${e.title}`,
                    showInList: false,
                },

                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.title'>Title</Text>,
                    placeholder: <Text id='input.title'>Title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                },


                {
                    title: <Text id='input.attribution'>attribution</Text>,
                    placeholder: <Text id='input.attribution'>attribution</Text>,
                    help: <Text id='input.attribution-help'>Include an optional field for users to enter how they want the image to be attributed, especially important for Creative Commons licenses that require it.</Text>,
                    name: 'attribution',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.attribution,
                    showInList: true,
                },


                // Copyright Licenses

                // - All Rights Reserved: The creator retains all the rights provided by copyright law. Others can only use the work in ways that are directly allowed by law.
                // - Royalty-Free: A one-time fee is paid for non-exclusive, multiple usages of the asset. Note that "free" here doesn't mean no cost, but rather that you are free from paying royalties every time the asset is used.
                // - Rights Managed: A license where the user pays for the use of a copyrighted asset based on factors like duration, geographical location, type of media, etc.

                // Open Source Licenses (mostly for software)
                // - MIT License: A permissive license that only requires including the original copyright and license.
                // - GNU General Public License (GPL): Requires any derivative work to also be open-source under the same license.
                // - Apache License: A permissive free software license written by the Apache Software Foundation.

                // Creative Commons Licenses
                // CC BY (Attribution): Others can distribute, remix, adapt, and build upon your work, even commercially, as long as they credit you for the original creation.
                // CC BY-SA (Attribution-ShareAlike): Same as CC BY, but derivative works must be licensed under identical terms.
                // CC BY-ND (Attribution-NoDerivs): Allows for redistribution, commercial and non-commercial, as long as the work is passed along unchanged and in whole, with credit to you.
                // CC BY-NC (Attribution-NonCommercial): Others can remix, adapt, and build upon your work non-commercially, and although their new works must also acknowledge you and be non-commercial, they don’t have to license their derivative works on the same terms.
                // CC BY-NC-SA (Attribution-NonCommercial-ShareAlike): Others can remix, adapt, and build upon your work non-commercially, as long as they credit you and license their new creations under the identical terms.
                // CC BY-NC-ND (Attribution-NonCommercial-NoDerivs): Others can download your works and share them with others as long as they credit you, but they can’t change them in any way or use them commercially.

                // Public Domain
                // Public Domain (CC0): The creator waives all their copyright and related rights, effectively placing their work in the public domain.

                // These licenses provide various levels of permissions, requirements, and limitations. Make sure to consult with a legal advisor to ensure that you choose the license that fits your specific needs.
                {
                    title: <Text id='input.licenseType'>licenseType</Text>,
                    placeholder: <Text id='input.licenseType'>licenseType</Text>,
                    name: 'licenseType',
                    type: 'select',
                    icon: 'fa-solid fa-file-certificate',
                    getValue: obj => obj.licenseType,
                    datalist: [
                        ['All Rights Reserved', 'All Rights Reserved'],
                        ['Royalty-Free', 'Royalty-Free'],
                        ['Rights Managed', 'Rights Managed'],
                        ['Creative Commons Licenses CC BY-NC', 'Creative Commons Licenses CC BY-NC'],
                        ['Public Domain (CC0)', 'Public Domain (CC0)'],
                    ],
                    // datalistFieldKey: e => e.id,
                    // datalistFieldVal: e => `${e.firstname} ${e.lastname}`,
                    showInList: true,
                },
                {
                    title: <Text id='input.userAgreement'>userAgreement</Text>,
                    placeholder: <Text id='input.userAgreement'>userAgreement</Text>,
                    help: <Text id='input.userAgreement-help'>I confirm that I own or have the right to use these images, and I grant permission to use them as described in the license above.</Text>,
                    name: 'userAgreement',
                    type: 'toggle',
                    icon: 'fa-solid fa-handshake',
                    getValue: obj => obj.userAgreement ? 1 : 0,
                    showInList: true,
                },


            ],
        },
        {
            name: <Text id='input.media-meta-title'>Media meta</Text>,
            key: 'Media meta',
            fields: [
                // {
                //     title: <Text id='input.location'>location</Text>,
                //     placeholder: <Text id='input.location'>location</Text>,
                //     name: 'location',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj.location,
                //     showInList: true
                // },
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    name: 'tags',
                    type: 'tags',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'tags',
                    foundListName: 'similarTags',
                    foundListKeysName: 'similarTagsKeys',
                    getValue: obj => obj.tags,
                    datalist: [
                        'name',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.bib'>bib</Text>,
                    placeholder: <Text id='input.bib'>bib</Text>,
                    name: 'bib',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.bib || null,
                    showInList: false,
                },
                {
                    title: <Text id='input.whoIsInTheMedia'>WhoIsInTheMedia</Text>,
                    placeholder: <Text id='input.whoIsInTheMedia'>Who is in the media</Text>,
                    name: 'whoIsInTheMedia',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.whoIsInTheMedia,
                    showInList: true,
                },
                {
                    title: <Text id='input.whatIsInTheMedia'>WhatIsInTheMedia</Text>,
                    placeholder: <Text id='input.whatIsInTheMedia'>What is in the media</Text>,
                    name: 'whatIsInTheMedia',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.whatIsInTheMedia,
                    showInList: true,
                },
                {
                    title: <Text id='input.whatIsTheStory'>WhatIsTheStory</Text>,
                    placeholder: <Text id='input.whatIsTheStory'>What is the story</Text>,
                    name: 'whatIsTheStory',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.whatIsTheStory,
                    showInList: true,
                },
                {
                    title: <Text id='input.whereIsTheMediaRecorded'>WhereIsTheMediaRecorded</Text>,
                    placeholder: <Text id='input.whereIsTheMediaRecorded'>Where is the media recorded</Text>,
                    name: 'whereIsTheMediaRecorded',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.whereIsTheMediaRecorded,
                    showInList: true,
                },
                {
                    title: <Text id='input.whenIsTheMediaRecorded'>WhenIsTheMediaRecorded</Text>,
                    placeholder: <Text id='input.whenIsTheMediaRecorded'>When is the media recorded</Text>,
                    name: 'whenIsTheMediaRecorded',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.whenIsTheMediaRecorded,
                    showInList: true,
                },
                {
                    title: <Text id='input.preferredUse'>PreferredUse</Text>,
                    placeholder: <Text id='input.preferredUse'>Preferred use</Text>,
                    name: 'preferredUse',
                    type: 'select',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.preferredUse,
                    datalist: [['web', 'Web'], ['instagram', 'Instagram'], ['facebook', 'Facebook'], ['magazine', 'Magazine']],
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.media-text-title'>Media text</Text>,
            key: 'Media text',
            fields: [
                {
                    title: <Text id='input.body'>Body</Text>,
                    placeholder: <Text id='input.body'>Media text</Text>,
                    name: 'body',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.body,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.media-publish-info-title'>Media publish info</Text>,
            key: 'Media publish info',
            fields: [
                {
                    title: <Text id='input.publishedToWeb'>publishedToWeb</Text>,
                    placeholder: <Text id='input.publishedToWeb'>publishedToWeb</Text>,
                    name: 'publishedToWeb',
                    type: 'number',
                    icon: 'fa-solid fa-check',
                    getValue: obj => obj.publishedToWeb || null,
                    showInList: true,
                    isAdminField: false,
                    // isReadOnly: true,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.publishedToNews'>publishedToNews</Text>,
                    placeholder: <Text id='input.publishedToNews'>publishedToNews</Text>,
                    name: 'publishedToNews',
                    type: 'number',
                    icon: 'fa-solid fa-check',
                    getValue: obj => obj.publishedToNews || null,
                    showInList: true,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.publishedToFacebook'>publishedToFacebook</Text>,
                    placeholder: <Text id='input.publishedToFacebook'>publishedToFacebook</Text>,
                    name: 'publishedToFacebook',
                    type: 'number',
                    icon: 'fa-solid fa-check',
                    getValue: obj => obj.publishedToFacebook || null,
                    showInList: true,
                    isAdminField: false,
                    // isReadOnly: true,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.publishedToInstagram'>publishedToInstagram</Text>,
                    placeholder: <Text id='input.publishedToInstagram'>publishedToInstagram</Text>,
                    name: 'publishedToInstagram',
                    type: 'number',
                    icon: 'fa-solid fa-check',
                    getValue: obj => obj.publishedToInstagram || null,
                    showInList: true,
                    isAdminField: false,
                    // isReadOnly: true,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.publishedToOther'>publishedToOther</Text>,
                    placeholder: <Text id='input.publishedToOther'>publishedToOther</Text>,
                    name: 'publishedToOther',
                    type: 'number',
                    icon: 'fa-solid fa-check',
                    getValue: obj => obj.publishedToOther || null,
                    showInList: true,
                    isAdminField: false,
                    // isReadOnly: true,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.done'>done</Text>,
                    placeholder: <Text id='input.done'>done</Text>,
                    name: 'done',
                    type: 'number',
                    icon: 'fa-solid fa-check',
                    getValue: obj => obj.done || null,
                    showInList: true,
                    isAdminField: false,
                    isReadOnly: true,
                    // hideOnCreate: true,
                },


            ],
        },
    ],
    article: [
        {
            name: <Text id='input.article-basics-title'>Article basics</Text>,
            key: 'Article basics',
            fields: [
                {
                    lang: { en: 'Id', no: 'Id' },
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    lang: { en: 'Offline', no: 'Avpublisert' },
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    // help: <Text id='input.offline-help'>Set to 1 to delete the article</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                },
                {
                    title: <Text id='input.sort'>Sort</Text>,
                    placeholder: <Text id='input.sort'>Sort</Text>,
                    name: 'sort',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.sort,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                // {
                //     title: <Text id='input.race'>Race</Text>,
                //     placeholder: <Text id='input.race'>Race</Text>,
                //     name: 'race',
                //     type: 'number',
                //     icon: 'fa-solid fa-input-numeric',
                //     getValue: obj => obj.race,
                //     showInList: true,
                //     isAdminField: true,
                // },

                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.active'>Active</Text>,
                    placeholder: <Text id='input.active'>Active</Text>,
                    name: 'active',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.active || null,
                    showInList: true,
                    // isSimpleEdit: true,
                },
                {
                    title: <Text id='input.category'>category</Text>,
                    placeholder: <Text id='input.category'>category</Text>,
                    help: <Text id='input.category-help'>Category is used to group articles together</Text>,
                    name: 'category',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.category,
                    showInList: true,
                },
                {
                    title: <Text id='input.headingIcon'>headingIcon</Text>,
                    placeholder: <Text id='input.headingIcon'>headingIcon</Text>,
                    name: 'headingIcon',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.headingIcon,
                    showInList: true,
                },
                {
                    title: <Text id='input.heading'>heading</Text>,
                    placeholder: <Text id='input.heading'>heading</Text>,
                    name: 'heading',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.heading,
                    showInList: true,
                },
                {
                    title: <Text id='input.title'>Title</Text>,
                    placeholder: <Text id='input.title'>Title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                    // isSimpleEdit: true,
                },
                {
                    title: <Text id='input.ingress'>Ingress</Text>,
                    placeholder: <Text id='input.ingress'>Article ingress</Text>,
                    name: 'ingress',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.ingress,
                    showInList: false,
                    // isSimpleEdit: true,
                },
                {
                    title: <Text id='input.body'>Body</Text>,
                    placeholder: <Text id='input.body'>Article text</Text>,
                    name: 'body',
                    type: 'textareaEditor',
                    viewHtml: false,
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.body,
                    displayValue: obj => obj.body,
                    showInList: false,
                },
                {
                    title: <Text id='input.inlineImages'>inlineImages</Text>,
                    placeholder: <Text id='input.inlineImages'>inlineImages</Text>,
                    name: 'inlineImages',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.inlineImages,
                    showInList: false,
                },
                {
                    title: <Text id='input.youtube'>youtube</Text>,
                    placeholder: <Text id='input.youtube'>youtube</Text>,
                    name: 'youtube',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.youtube,
                    showInList: true,
                },
                {
                    title: <Text id='input.spotify'>spotify</Text>,
                    placeholder: <Text id='input.spotify'>spotify</Text>,
                    name: 'spotify',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.spotify,
                    showInList: true,
                },
                {
                    title: <Text id='input.googleSheetId'>googleSheetId</Text>,
                    placeholder: <Text id='input.googleSheetId'>googleSheetId</Text>,
                    name: 'googleSheetId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.googleSheetId,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.article-meta-title'>Article meta</Text>,
            key: 'Article meta',
            fields: [
                // {
                //     title: <Text id='input.location'>location</Text>,
                //     placeholder: <Text id='input.location'>location</Text>,
                //     name: 'location',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj.location,
                //     showInList: true
                // },
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    name: 'tags',
                    type: 'tags',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'tags',
                    foundListName: 'similarTags',
                    foundListKeysName: 'similarTagsKeys',
                    getValue: obj => obj.tags,
                    datalist: [
                        'name',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.publishDate'>publishDate</Text>,
                    placeholder: <Text id='input.publishDate'>publishDate</Text>,
                    name: 'publishDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.publishDate,
                    showInList: true,
                },
                {
                    title: <Text id='input.expireDate'>expireDate</Text>,
                    placeholder: <Text id='input.expireDate'>expireDate</Text>,
                    name: 'expireDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.expireDate,
                    showInList: true,
                },
                {
                    title: <Text id='input.styles'>styles</Text>,
                    placeholder: <Text id='input.styles'>Article styles</Text>,
                    name: 'styles',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.styles,
                    showInList: false,
                },
                {
                    title: <Text id='input.price'>price</Text>,
                    placeholder: <Text id='input.price'>price</Text>,
                    name: 'price',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.price || null,
                    showInList: true,
                    // isMarketPlace: true,
                },
                {
                    title: <Text id='input.isSold'>isSold</Text>,
                    placeholder: <Text id='input.isSold'>isSold</Text>,
                    name: 'isSold',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.isSold || null,
                    showInList: true,
                    // isMarketPlace: true,
                },

            ],
        },
        {
            name: <Text id='input.article-content-title'>Article content</Text>,
            key: 'Article content',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    help: <Text id='input.images-upload-help'>Add one or more images. Images takes some seconds to prepare for web view. Please wait until it is ready.</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                    callback: async (obj, updateValue) => {
                        // console.log('fields.callback', { obj, updateValue });
                        if (obj.images?.length > 0) {
                            const img = obj.images[0];
                            if (img?.exif?.image?.Artist) {
                                updateValue({
                                    name: 'attribution',
                                    value: img?.exif?.image?.Artist,
                                });
                            }
                            if (img?.exif?.exif?.DateTimeOriginal) {
                                updateValue({
                                    name: 'whenIsTheMediaRecorded',
                                    value: img?.exif?.exif?.DateTimeOriginal,
                                });
                            }
                        }
                    },
                    // isSimpleEdit: true,
                },
                {
                    title: <Text id='input.videos'>Videos</Text>,
                    placeholder: <Text id='input.videos'>Videos</Text>,
                    help: <Text id='input.videos-help'>Add a video to the article. Videos will take a couple of minutes to process.</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: false,
                    // isSimpleEdit: true,
                },
                // {
                //     title: <Text id='input.links'>Links</Text>,
                //     placeholder: <Text id='input.links'>Links</Text>,
                //     name: 'links',
                //     type: 'arrayObject',
                //     icon: 'fa-solid fa-tag',
                //     getValue: obj => obj.links,
                //     datalist: [
                //         'url', 'name', 'description'
                //     ],
                //     showInList: false,
                //     displayHeight: '60px',
                //     hideOnCreate: false,
                // },
            ],
        },
        {
            name: <Text id='input.article-simple-title'>Your content</Text>,
            key: 'Article simple',
            showSectionName: false,
            marginTop: 'mt-1',
            fields: [
                {
                    title: <Text id='input.active'>Active</Text>,
                    placeholder: <Text id='input.active'>Active</Text>,
                    name: 'active',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.active || null,
                    showInList: true,
                    isSimpleEdit: true,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.ingress'>Article ingress</Text>,
                    placeholder: <Text id='input.ingress-what-is-on-your-mind'>What is on your mind,</Text>,
                    name: 'ingress',
                    type: 'textarea',
                    // icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.ingress,
                    showInList: false,
                    isSimpleEdit: true,
                    addNameAfterFieldname: true,
                    showEmojis: true,
                },
                {
                    title: <Text id='input.background'>background</Text>,
                    placeholder: <Text id='input.background'>background</Text>,
                    name: 'background',
                    type: 'select',
                    datalist: [['#0000ff,#ffffff', 'blue'], ['#ff0000,#ffffff', 'red'], ['#00ff00,#ffffff', 'green'], ['#000000,#ffffff', 'black'], ['#ffffff,#000000', 'white']],
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.background,
                    // isSimpleEdit: true,
                },
                {
                    title: <Text id='input.backgroundImage'>backgroundImage</Text>,
                    placeholder: <Text id='input.backgroundImage'>backgroundImage</Text>,
                    name: 'backgroundImage',
                    type: 'select',
                    datalist: [
                        ['linear-gradient(45deg, #f093fb 0%, #f5576c 100%);#ffffff', 'SunsetGlow'],
                        ['linear-gradient(45deg, #f6d365 0%, #fda085 100%);#ffffff', 'WarmSunrise'],
                        ['linear-gradient(45deg, #5ee7df 0%, #b490ca 100%);#ffffff', 'AquaMarine'],
                        ['linear-gradient(45deg, #7579ff 0%, #b224ef 100%);#ffffff', 'SteelPurple'],
                        ['linear-gradient(45deg, #3ca55c 0%, #b5ac49 100%);#ffffff', 'Seabreeze'],
                        ['linear-gradient(45deg, #ee9ca7 0%, #ffdde1 100%);#ffffff', 'OrchidHeaven'],
                        ['linear-gradient(45deg, #141e30 0%, #243b55 100%);#ffffff', 'EveningNight'],

                        // New hard-edged (abrupt stop) gradients
                        ['linear-gradient(15deg, #f12711 0%, #f5af19 100%);#ffffff', 'TangerineBlaze'],
                        ['linear-gradient(120deg, #84fab0 0%, #8fd3f4 100%);#000000', 'MintSky'],
                        ['linear-gradient(200deg, #fceabb 0%, #f8b500 50%, #c21500 50%, #c21500 100%);#ffffff', 'DesertEdge'],
                        ['linear-gradient(70deg, #00d2ff 0%, #3a7bd5 100%);#ffffff', 'BlueShift'],
                        ['linear-gradient(60deg, #dd3e54 0%, #6be585 100%);#ffffff', 'WatermelonFlash'],
                        ['linear-gradient(35deg, #bdc3c7 0%, #2c3e50 100%);#ffffff', 'SteelFade'],
                        ['linear-gradient(90deg, #009fff 0%, #ec2f4b 100%);#ffffff', 'FireAndIce'],
                        ['linear-gradient(135deg, #ef32d9 0%, #89fffd 100%);#000000', 'CottonCandy'],
                        ['linear-gradient(25deg, #fffc00 0%, #ffffff 25%, #00fffc 25%, #00fffc 75%, #ff00b4 75%, #ff00b4 100%);#000000', 'TechnoStripes'],
                        ['linear-gradient(45deg, #ff9966 0%, #ff5e62 100%);#ffffff', 'PeachMelba'],
                        ['linear-gradient(145deg, #667eea 0%, #764ba2 40%, #ffafd2 40%, #ffafd2 80%, #50c9c3 80%, #50c9c3 100%);#ffffff', 'LavenderPop'],
                        ['linear-gradient(0deg, #3a1c71 0%, #d76d77 50%, #ffaf7b 100%);#ffffff', 'WarmTrinity'],
                        ['linear-gradient(45deg, #f5515f 0%, #9f041b 100%);#ffffff', 'CrimsonNight'],
                        ['linear-gradient(90deg, #8e2de2 0%, #4a00e0 25%, #4a00e0 75%, #8e2de2 75%, #8e2de2 100%);#ffffff', 'RoyalViolet'],
                        ['linear-gradient(135deg, #fffcdc 0%, #d9a7c7 100%);#000000', 'LilyFields'],
                        ['linear-gradient(75deg, #2193b0 0%, #6dd5ed 30%, #cc2b5e 30%, #cc2b5e 70%, #753a88 70%, #753a88 100%);#ffffff', 'DuskStreaks'],
                        ['linear-gradient(135deg, #11998e 0%, #38ef7d 100%);#000000', 'EmeraldLane'],
                        ['linear-gradient(110deg, #ee0979 0%, #ff6a00 50%, #00c9ff 50%, #00c9ff 100%);#ffffff', 'FlamingSunset'],
                        ['linear-gradient(180deg, #09203f 0%, #537895 100%);#ffffff', 'DeepOcean'],
                        ['linear-gradient(45deg, #fbda61 0%, #ff5acd 100%);#000000', 'PinkLemonade'],

                        // France: blue - white - red (vertical stripes)
                        ['linear-gradient(90deg, #0055A4 0%, #0055A4 33%, #FFFFFF 33%, #FFFFFF 67%, #EF4135 67%, #EF4135 100%);#000000', 'FranceFlag'],
                        // Germany: black - red - gold (horizontal stripes)
                        ['linear-gradient(180deg, #000000 0%, #000000 33%, #DD0000 33%, #DD0000 67%, #FFCE00 67%, #FFCE00 100%);#ffffff', 'GermanyFlag'],
                        // Italy: green - white - red (vertical stripes)
                        ['linear-gradient(90deg, #008C45 0%, #008C45 33%, #FFFFFF 33%, #FFFFFF 67%, #CD212A 67%, #CD212A 100%);#000000', 'ItalyFlag'],
                        // Netherlands: red - white - blue (horizontal stripes)
                        ['linear-gradient(180deg, #AE1C28 0%, #AE1C28 33%, #FFFFFF 33%, #FFFFFF 67%, #21468B 67%, #21468B 100%);#000000', 'NetherlandsFlag'],
                        // Ukraine: blue on top, yellow on bottom (horizontal stripes)
                        ['linear-gradient(180deg, #0057B7 0%, #0057B7 50%, #FFDD00 50%, #FFDD00 100%);#000000', 'UkraineFlag'],
                        // Belgium: black - yellow - red (vertical stripes)
                        ['linear-gradient(90deg, #000000 0%, #000000 33%, #FDDA24 33%, #FDDA24 67%, #EF3340 67%, #EF3340 100%);#ffffff', 'BelgiumFlag'],
                        // Ireland: green - white - orange (vertical stripes)
                        ['linear-gradient(90deg, #169B62 0%, #169B62 33%, #FFFFFF 33%, #FFFFFF 67%, #FF883E 67%, #FF883E 100%);#000000', 'IrelandFlag'],
                        // Romania: blue - yellow - red (vertical stripes)
                        ['linear-gradient(90deg, #002B7F 0%, #002B7F 33%, #FCD116 33%, #FCD116 67%, #CE1126 67%, #CE1126 100%);#000000', 'RomaniaFlag'],
                        // Estonia: blue - black - white (horizontal stripes)
                        ['linear-gradient(180deg, #0072CE 0%, #0072CE 33%, #000000 33%, #000000 67%, #FFFFFF 67%, #FFFFFF 100%);#000000', 'EstoniaFlag'],

                        // 1) Norway (Red, white, blue) - extremely approximate
                        ['linear-gradient(180deg, #EF2B2D 0%, #EF2B2D 60%, #FFFFFF 60%, #FFFFFF 70%, #00205B 70%, #00205B 100%);#000000', 'NorwayFlag'],
                        // 2) Sweden (Blue, yellow) - stripes approximation
                        ['linear-gradient(180deg, #005293 0%, #005293 50%, #FECB00 50%, #FECB00 100%);#000000', 'SwedenFlag'],
                        // 3) Denmark (Red, white) - cross in real life, but simplified
                        ['linear-gradient(180deg, #C60C30 0%, #C60C30 70%, #FFFFFF 70%, #FFFFFF 100%);#000000', 'DenmarkFlag'],
                        // 4) Finland (White, blue) - cross in real life, but simplified
                        ['linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 60%, #003580 60%, #003580 100%);#000000', 'FinlandFlag'],
                        // 5) Iceland (Blue, white, red) - cross in real life, but simplified
                        ['linear-gradient(180deg, #003897 0%, #003897 50%, #FFFFFF 50%, #FFFFFF 65%, #DC1E35 65%, #DC1E35 100%);#000000', 'IcelandFlag'],
                        // 6) Great Britain (Union Jack is complex; just color blocks)
                        ['linear-gradient(180deg, #012169 0%, #012169 40%, #FFFFFF 40%, #FFFFFF 50%, #C8102E 50%, #C8102E 60%, #FFFFFF 60%, #FFFFFF 70%, #012169 70%, #012169 100%);#000000', 'GreatBritainFlag'],
                        // 7) Scotland (Diagonal white cross on blue; simplified as stripes)
                        ['linear-gradient(180deg, #0065BF 0%, #0065BF 50%, #FFFFFF 50%, #FFFFFF 100%);#000000', 'ScotlandFlag'],
                        // 8) Wales (Green, white, red dragon in reality; just approximate)
                        ['linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 50%, #D00C33 50%, #D00C33 65%, #00A04D 65%, #00A04D 100%);#000000', 'WalesFlag'],
                        // 9) Northern Ireland (White field with red cross + emblem; simplified stripes)
                        ['linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 60%, #CF142B 60%, #CF142B 70%, #FFFFFF 70%, #FFFFFF 100%);#000000', 'NorthernIrelandFlag'],
                        // 10) Faroe Islands (White field, offset Nordic cross in blue/red; simplified stripes)
                        ['linear-gradient(90deg, #FFFFFF 0%, #FFFFFF 45%, #0065BF 45%, #0065BF 55%, #ED2939 55%, #ED2939 65%, #FFFFFF 65%, #FFFFFF 100%);#000000', 'FaroeIslandsFlag']

                    ],
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.backgroundImage,
                    isSimpleEdit: true,
                    showBackgroundImage: true,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    help: <Text id='input.images-upload-help'>Add one or more images. Images takes some seconds to prepare for web view. Please wait until it is ready.</Text>,
                    name: 'images',
                    type: 'image',
                    displayLargeImages: true,
                    displayWidth: '180px',
                    displayHeight: 'auto',
                    // icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                    callback: async (obj, updateValue) => {
                        // console.log('fields.callback', { obj, updateValue });
                        if (obj.images?.length > 0) {
                            const img = obj.images[0];
                            if (img?.exif?.image?.Artist) {
                                updateValue({
                                    name: 'attribution',
                                    value: img?.exif?.image?.Artist,
                                });
                            }
                            if (img?.exif?.exif?.DateTimeOriginal) {
                                updateValue({
                                    name: 'whenIsTheMediaRecorded',
                                    value: img?.exif?.exif?.DateTimeOriginal,
                                });
                            }
                        }
                    },
                    isSimpleEdit: true,
                },
            ],
        },
        {
            name: <Text id='input.article-simple-marketplace-title'>Marketplace content</Text>,
            key: 'Article simple marketplace',
            showSectionName: false,
            marginTop: 'mt-1',
            fields: [
                {
                    title: <Text id='input.active'>Active</Text>,
                    placeholder: <Text id='input.active'>Active</Text>,
                    name: 'active',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.active || null,
                    showInList: true,
                    isSimpleEdit: true,
                    isMarketPlace: true,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.ingress'>Article ingress</Text>,
                    placeholder: <Text id='input.ingress-describe-the-item'>Describe the item you are selling</Text>,
                    name: 'ingress',
                    type: 'textarea',
                    // icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.ingress,
                    showInList: false,
                    isSimpleEdit: true,
                    isMarketPlace: true,
                    // addNameAfterFieldname: true,
                },
                {
                    title: <Text id='input.ingressTechDetails'>Article ingress tech details</Text>,
                    placeholder: <Text id='input.ingressTechDetails-describe-the-item'>Technical details about the item you are selling</Text>,
                    name: 'ingressTechDetails',
                    type: 'textarea',
                    // icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.ingressTechDetails,
                    showInList: false,
                    isSimpleEdit: true,
                    isMarketPlace: true,
                    // addNameAfterFieldname: true,
                },
                {
                    title: <Text id='input.price'>price</Text>,
                    placeholder: <Text id='input.price'>price</Text>,
                    name: 'price',
                    type: 'number',
                    // icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.price || null,
                    showInList: true,
                    isMarketPlace: true,
                },
                {
                    title: <Text id='input.isSold'>isSold</Text>,
                    placeholder: <Text id='input.isSold'>isSold</Text>,
                    name: 'isSold',
                    type: 'toggle',
                    // icon: 'fa-solid fa-eye',
                    getValue: obj => obj.isSold || null,
                    showInList: true,
                    isMarketPlace: true,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    help: <Text id='input.images-upload-help'>Add one or more images. Images takes some seconds to prepare for web view. Please wait until it is ready.</Text>,
                    name: 'images',
                    type: 'image',
                    displayLargeImages: true,
                    displayWidth: '180px',
                    displayHeight: 'auto',
                    // icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                    callback: async (obj, updateValue) => {
                        // console.log('fields.callback', { obj, updateValue });
                        if (obj.images?.length > 0) {
                            const img = obj.images[0];
                            if (img?.exif?.image?.Artist) {
                                updateValue({
                                    name: 'attribution',
                                    value: img?.exif?.image?.Artist,
                                });
                            }
                            if (img?.exif?.exif?.DateTimeOriginal) {
                                updateValue({
                                    name: 'whenIsTheMediaRecorded',
                                    value: img?.exif?.exif?.DateTimeOriginal,
                                });
                            }
                        }
                    },
                    isSimpleEdit: true,
                    isMarketPlace: true,
                },
            ],
        },
        {
            name: <Text id='input.article-simple-video-title'>Video content</Text>,
            key: 'Article simple video',
            isCollapsed: true,
            fields: [
                {
                    title: <Text id='input.videos'>Videos</Text>,
                    placeholder: <Text id='input.videos'>Videos</Text>,
                    help: <Text id='input.videos-help'>Add a video to the article. Videos will take a couple of minutes to process.</Text>,
                    addFileText: <Text id='input.add-video'>Add video</Text>,
                    name: 'files',
                    type: 'file',
                    displayLargeFiles: true,
                    // icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: false,
                    isSimpleEdit: true,
                },
            ],
        },
    ],
    race: [
        {
            name: <Text id='input.race-config-title'>Race config</Text>,
            key: 'Race config',
            fields: [
                {
                    title: <Text id='input.logo'>logo</Text>,
                    placeholder: <Text id='input.logo'>logo</Text>,
                    name: 'logo',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.logo,
                    showInList: true,
                },
                {
                    title: <Text id='input.logoHeight'>logoHeight</Text>,
                    placeholder: <Text id='input.logoHeight'>logoHeight</Text>,
                    name: 'logoHeight',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.logoHeight,
                    showInList: true,
                },
                {
                    title: <Text id='input.logoUrl'>logoUrl</Text>,
                    placeholder: <Text id='input.logoUrl'>logoUrl</Text>,
                    name: 'logoUrl',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.logoUrl,
                    showInList: true,
                },
                {
                    title: <Text id='input.css'>css</Text>,
                    placeholder: <Text id='input.css'>css</Text>,
                    name: 'css',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.css,
                    showInList: true,
                },
                {
                    title: <Text id='input.qr'>Qr</Text>,
                    placeholder: <Text id='input.qr'>Qr</Text>,
                    name: 'qr',
                    type: 'text',
                    icon: 'fa-solid fa-qrcode',
                    getValue: obj => obj.id || null,
                    showQR: true,
                    getQrValue: obj => `${apiServer}/#/race/${obj.id}`,
                    isReadOnly: true,
                    showInList: true,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.hasTracking'>hasTracking</Text>,
                    placeholder: <Text id='input.hasTracking'>hasTracking</Text>,
                    name: 'hasTracking',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hasTracking,
                    showInList: true,
                },
                {
                    title: <Text id='input.hasResults'>hasResults</Text>,
                    placeholder: <Text id='input.hasResults'>hasResults</Text>,
                    name: 'hasResults',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hasResults,
                    showInList: true,
                },
                {
                    title: <Text id='input.hasNews'>hasNews</Text>,
                    placeholder: <Text id='input.hasNews'>hasNews</Text>,
                    name: 'hasNews',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hasNews,
                    showInList: true,
                },
                {
                    title: <Text id='input.isCancelled'>isCancelled</Text>,
                    placeholder: <Text id='input.isCancelled'>isCancelled</Text>,
                    name: 'isCancelled',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isCancelled,
                    showInList: true,
                },
                {
                    title: <Text id='input.mergeNewsWithRace'>Merge news with race</Text>,
                    placeholder: <Text id='input.mergeNewsWithRace'>Merge news with race</Text>,
                    name: 'mergeNewsWithRace',
                    type: 'number',
                    icon: 'fa-solid fa-list',
                    getValue: obj => obj.mergeNewsWithRace,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.race-basics-title'>Race basics</Text>,
            key: 'Race basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: user => user.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.title'>Title</Text>,
                    placeholder: <Text id='input.title'>Title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                    styles: 'min-width: 180px;',
                },
                {
                    title: <Text id='input.tag'>Tag</Text>,
                    placeholder: <Text id='input.tag'>Tag</Text>,
                    name: 'tag',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.tag,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-dates-title'>Race dates</Text>,
            key: 'Race dates',
            fields: [
                {
                    title: <Text id='input.startDate'>StartDate</Text>,
                    placeholder: <Text id='input.startDate'>StartDate</Text>,
                    name: 'startDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.startDate,
                    showInList: true,
                },
                {
                    title: <Text id='input.endDate'>EndDate</Text>,
                    placeholder: <Text id='input.endDate'>EndDate</Text>,
                    name: 'endDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.endDate,
                    showInList: true,
                },
                {
                    title: <Text id='input.signupStartDate'>SignupStartDate</Text>,
                    placeholder: <Text id='input.signupStartDate'>SignupStartDate</Text>,
                    name: 'signupStartDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.signupStartDate,
                    showInList: true,
                },
                {
                    title: <Text id='input.signupEndDate'>SignupEndDate</Text>,
                    placeholder: <Text id='input.signupEndDate'>SignupEndDate</Text>,
                    name: 'signupEndDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.signupEndDate,
                    showInList: true,
                },
                {
                    title: <Text id='input.signupPaymentDueDate'>Signup Payment Due Date</Text>,
                    placeholder: <Text id='input.signupPaymentDueDate'>Signup Payment Due Date</Text>,
                    name: 'signupPaymentDueDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.signupPaymentDueDate,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.race-info-title'>Race Info</Text>,
            key: 'Race Info',
            fields: [
                {
                    title: <Text id='input.tagFacebookUsers'>TagFacebookUsers</Text>,
                    placeholder: <Text id='input.tagFacebookUsers'>TagFacebookUsers</Text>,
                    name: 'tagFacebookUsers',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.tagFacebookUsers,
                    showInList: false,
                },
                {
                    title: <Text id='input.tagInstagramUsers'>TagInstagramUsers</Text>,
                    placeholder: <Text id='input.tagInstagramUsers'>TagInstagramUsers</Text>,
                    name: 'tagInstagramUsers',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.tagInstagramUsers,
                    showInList: false,
                },
                {
                    title: <Text id='input.raceTrackerRace'>RaceTrackerRace</Text>,
                    placeholder: <Text id='input.raceTrackerRace'>RaceTrackerRace</Text>,
                    name: 'raceTrackerRace',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.raceTrackerRace,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.ingress'>Ingress</Text>,
                    placeholder: <Text id='input.ingress'>Ingress</Text>,
                    help: <Text id='input.ingress-help'>Intro text for this race.</Text>,
                    name: 'ingress',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.ingress,
                    showInList: false,
                },
                {
                    title: <Text id='input.body'>Body</Text>,
                    placeholder: <Text id='input.body'>Body</Text>,
                    help: <Text id='input.body-help'>Main text for this race.</Text>,
                    name: 'body',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.body,
                    showInList: false,
                },
                {
                    title: <Text id='input.info'>Info</Text>,
                    placeholder: <Text id='input.info'>Info</Text>,
                    help: <Text id='input.info-help'>General info about the race. Can be info about signup, payment, etc.</Text>,
                    name: 'info',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.info,
                    showInList: false,
                },
                {
                    title: <Text id='input.terms'>Terms</Text>,
                    placeholder: <Text id='input.terms'>Terms</Text>,
                    help: <Text id='input.terms-help'>Race terms. Describe the terms for the race and all classes.</Text>,
                    name: 'terms',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.terms,
                    showInList: false,
                },
                {
                    title: <Text id='input.trackInfo'>TrackInfo</Text>,
                    placeholder: <Text id='input.trackInfo'>TrackInfo</Text>,
                    help: <Text id='input.trackInfo-help'>Information about the race track. General info and worthy mentions.</Text>,
                    name: 'trackInfo',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.trackInfo,
                    showInList: false,
                },
                {
                    title: <Text id='input.qualifying'>Qualifying</Text>,
                    placeholder: <Text id='input.qualifying'>Qualifying</Text>,
                    help: <Text id='input.qualifying-help'>Describe the qualifying for this race.</Text>,
                    name: 'qualifying',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.qualifying,
                    showInList: false,
                },
                {
                    title: <Text id='input.emergencyInfo'>EmergencyInfo</Text>,
                    placeholder: <Text id='input.emergencyInfo'>EmergencyInfo</Text>,
                    help: <Text id='input.emergencyInfo-help'>Emergency plan and information. Include contact info at the top.</Text>,
                    name: 'emergencyInfo',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.emergencyInfo,
                    showInList: false,
                },
                {
                    title: <Text id='input.rules'>Rules</Text>,
                    placeholder: <Text id='input.rules'>Rules</Text>,
                    help: <Text id='input.rules-help'>Rules for this race.</Text>,
                    name: 'rules',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.rules,
                    showInList: false,
                },
                {
                    title: <Text id='input.declaration'>Declaration</Text>,
                    placeholder: <Text id='input.declaration'>Declaration</Text>,
                    help: <Text id='input.declaration-help'>Musher declaration form.</Text>,
                    name: 'declaration',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.declaration,
                    showInList: false,
                },
                {
                    title: <Text id='input.veterinaryInfo'>VeterinaryInfo</Text>,
                    placeholder: <Text id='input.veterinaryInfo'>VeterinaryInfo</Text>,
                    help: <Text id='input.info-help'>Veterinary information for this race.</Text>,
                    name: 'veterinaryInfo',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.veterinaryInfo,
                    showInList: false,
                },
                {
                    title: <Text id='input.checkpointsInfo'>CheckpointsInfo</Text>,
                    placeholder: <Text id='input.checkpointsInfo'>CheckpointsInfo</Text>,
                    help: <Text id='input.checkpointsInfo-help'>Information about all checkpoints.</Text>,
                    name: 'checkpointsInfo',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.checkpointsInfo,
                    showInList: false,
                },
                {
                    title: <Text id='input.trailDescription'>TrailDescription</Text>,
                    placeholder: <Text id='input.trailDescription'>TrailDescription</Text>,
                    help: <Text id='input.trailDescription-help'>Not used.</Text>,
                    name: 'trailDescription',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.trailDescription,
                    showInList: false,
                },
                {
                    title: <Text id='input.program'>Program</Text>,
                    placeholder: <Text id='input.program'>Program</Text>,
                    help: <Text id='input.program-help'>The program for this race.</Text>,
                    name: 'program',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.program,
                    showInList: false,
                },
                {
                    title: <Text id='input.prices'>Prices</Text>,
                    placeholder: <Text id='input.prices'>Prices</Text>,
                    help: <Text id='input.prices-help'>List the prices and diplomas for this race.</Text>,
                    name: 'prices',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.prices,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-info-englist-title'>Race Info English</Text>,
            key: 'Race Info English',
            fields: [
                {
                    title: <Text id='input.bodyEn'>bodyEn</Text>,
                    placeholder: <Text id='input.bodyEn'>bodyEn</Text>,
                    name: 'bodyEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.bodyEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.programEn'>programEn</Text>,
                    placeholder: <Text id='input.programEn'>programEn</Text>,
                    name: 'programEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.programEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.checkpointsInfoEn'>checkpointsInfoEn</Text>,
                    placeholder: <Text id='input.checkpointsInfoEn'>checkpointsInfoEn</Text>,
                    name: 'checkpointsInfoEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.checkpointsInfoEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.termsEn'>termsEn</Text>,
                    placeholder: <Text id='input.termsEn'>termsEn</Text>,
                    name: 'termsEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.termsEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.rulesEn'>rulesEn</Text>,
                    placeholder: <Text id='input.rulesEn'>rulesEn</Text>,
                    name: 'rulesEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.rulesEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.veterinaryInfoEn'>veterinaryInfoEn</Text>,
                    placeholder: <Text id='input.veterinaryInfoEn'>veterinaryInfoEn</Text>,
                    name: 'veterinaryInfoEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.veterinaryInfoEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.declarationEn'>declarationEn</Text>,
                    placeholder: <Text id='input.declarationEn'>declarationEn</Text>,
                    name: 'declarationEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.declarationEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.emergencyInfoEn'>emergencyInfoEn</Text>,
                    placeholder: <Text id='input.emergencyInfoEn'>emergencyInfoEn</Text>,
                    name: 'emergencyInfoEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.emergencyInfoEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.trackInfoEn'>trackInfoEn</Text>,
                    placeholder: <Text id='input.trackInfoEn'>trackInfoEn</Text>,
                    help: <Text id='input.trackInfoEn-help'>Information about the race track. General info and worthy mentions.</Text>,
                    name: 'trackInfoEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.trackInfoEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.qualifyingEn'>qualifyingEn</Text>,
                    placeholder: <Text id='input.qualifyingEn'>qualifyingEn</Text>,
                    name: 'qualifyingEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.qualifyingEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.pricesEn'>pricesEn</Text>,
                    placeholder: <Text id='input.pricesEn'>pricesEn</Text>,
                    help: <Text id='input.pricesEn-help'>List the prices and diplomas for this race.</Text>,
                    name: 'pricesEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.pricesEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.vippsTermsOfSaleEn'>vippsTermsOfSaleEn</Text>,
                    placeholder: <Text id='input.vippsTermsOfSaleEn'>vippsTermsOfSaleEn</Text>,
                    help: <Text id='input.vippsTermsOfSaleEn-help'>Terms of sale in english.</Text>,
                    name: 'vippsTermsOfSaleEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsTermsOfSaleEn,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-results-title'>Race results info</Text>,
            key: 'Race results info',
            fields: [
                {
                    title: <Text id='input.showResults'>showResults</Text>,
                    placeholder: <Text id='input.showResults'>showResults</Text>,
                    name: 'showResults',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.showResults,
                    showInList: true,
                },
                {
                    title: <Text id='input.showStreaming'>showStreaming</Text>,
                    placeholder: <Text id='input.showStreaming'>showStreaming</Text>,
                    name: 'showStreaming',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.showStreaming,
                    showInList: false,
                },
                {
                    title: <Text id='input.resultsText'>ResultsText</Text>,
                    placeholder: <Text id='input.resultsText'>ResultsText</Text>,
                    name: 'resultsText',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.resultsText,
                    showInList: false,
                },
                {
                    title: <Text id='input.resultsUrl'>ResultsUrl</Text>,
                    placeholder: <Text id='input.resultsUrl'>ResultsUrl</Text>,
                    name: 'resultsUrl',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.resultsUrl,
                    showInList: false,
                },
                {
                    title: <Text id='input.resultsHide'>ResultsHide</Text>,
                    placeholder: <Text id='input.resultsHide'>ResultsHide</Text>,
                    name: 'resultsHide',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.resultsHide,
                    showInList: false,
                },
                {
                    title: <Text id='input.contestantsText'>ContestantsText</Text>,
                    placeholder: <Text id='input.contestantsText'>ContestantsText</Text>,
                    name: 'contestantsText',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.contestantsText,
                    showInList: false,
                },
                {
                    title: <Text id='input.contestantsUrl'>ContestantsUrl</Text>,
                    placeholder: <Text id='input.contestantsUrl'>ContestantsUrl</Text>,
                    name: 'contestantsUrl',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.contestantsUrl,
                    showInList: false,
                },
                {
                    title: <Text id='input.resultDanger'>ResultDanger</Text>,
                    placeholder: <Text id='input.resultDanger'>ResultDanger</Text>,
                    name: 'resultDanger',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.resultDanger,
                    showInList: false,
                },
                {
                    title: <Text id='input.resultWarning'>ResultWarning</Text>,
                    placeholder: <Text id='input.resultWarning'>ResultWarning</Text>,
                    name: 'resultWarning',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.resultWarning,
                    showInList: true,
                },
                {
                    title: <Text id='input.resultSuccess'>ResultSuccess</Text>,
                    placeholder: <Text id='input.resultSuccess'>ResultSuccess</Text>,
                    name: 'resultSuccess',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.resultSuccess,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-subscription-title'>Race subscription info</Text>,
            key: 'Race subscription info',
            fields: [
                {
                    title: <Text id='input.isSubscriptionsTracking'>isSubscriptionsTracking</Text>,
                    placeholder: <Text id='input.isSubscriptionsTracking'>isSubscriptionsTracking</Text>,
                    name: 'isSubscriptionsTracking',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isSubscriptionsTracking,
                    showInList: false,
                },
                {
                    title: <Text id='input.isSubscriptionsStreaming'>isSubscriptionsStreaming</Text>,
                    placeholder: <Text id='input.isSubscriptionsStreaming'>isSubscriptionsStreaming</Text>,
                    name: 'isSubscriptionsStreaming',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isSubscriptionsStreaming,
                    showInList: false,
                },
                {
                    title: <Text id='input.isSubscriptionsNews'>isSubscriptionsNews</Text>,
                    placeholder: <Text id='input.isSubscriptionsNews'>isSubscriptionsNews</Text>,
                    name: 'isSubscriptionsNews',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isSubscriptionsNews,
                    showInList: false,
                },
                {
                    title: <Text id='input.isSubscriptionsResults'>isSubscriptionsResults</Text>,
                    placeholder: <Text id='input.isSubscriptionsResults'>isSubscriptionsResults</Text>,
                    name: 'isSubscriptionsResults',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isSubscriptionsResults,
                    showInList: false,
                },

                {
                    title: <Text id='input.subscriptionTitle'>subscriptionTitle</Text>,
                    placeholder: <Text id='input.subscriptionTitle'>subscriptionTitle</Text>,
                    help: <Text id='input.subscriptionTitle-help'>Title for the top of the subscription page.</Text>,
                    name: 'subscriptionTitle',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.subscriptionTitle,
                    showInList: false,
                },
                {
                    title: <Text id='input.subscriptionBody'>subscriptionBody</Text>,
                    placeholder: <Text id='input.subscriptionBody'>subscriptionBody</Text>,
                    help: <Text id='input.subscriptionBody-help'>Body text for the subscription page.</Text>,
                    name: 'subscriptionBody',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.subscriptionBody,
                    showInList: false,
                },

                {
                    title: <Text id='input.subscriptionBox1Title'>subscriptionBox1Title</Text>,
                    placeholder: <Text id='input.subscriptionBox1Title'>subscriptionBox1Title</Text>,
                    help: <Text id='input.subscriptionBox1Title-help'>Title for 1. subscription box.</Text>,
                    name: 'subscriptionBox1Title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.subscriptionBox1Title,
                    showInList: false,
                },
                {
                    title: <Text id='input.subscriptionBox1Body'>subscriptionBox1Body</Text>,
                    placeholder: <Text id='input.subscriptionBox1Body'>subscriptionBox1Body</Text>,
                    help: <Text id='input.subscriptionBox1Body-help'>Body text for the 1. subscription box.</Text>,
                    name: 'subscriptionBox1Body',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.subscriptionBox1Body,
                    showInList: false,
                },
                {
                    title: <Text id='input.subscriptionBox1Button'>subscriptionBox1Button</Text>,
                    placeholder: <Text id='input.subscriptionBox1Button'>subscriptionBox1Button</Text>,
                    help: <Text id='input.subscriptionBox1Button-help'>Button text for 1. subscription box.</Text>,
                    name: 'subscriptionBox1Button',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.subscriptionBox1Button,
                    showInList: false,
                },
                {
                    title: <Text id='input.subscriptionBox1Price'>subscriptionBox1Price</Text>,
                    placeholder: <Text id='input.subscriptionBox1Price'>subscriptionBox1Price</Text>,
                    help: <Text id='input.subscriptionBox1Price-help'>Price for 1. subscription box.</Text>,
                    name: 'subscriptionBox1Price',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.subscriptionBox1Price,
                    showInList: false,
                },

                {
                    title: <Text id='input.subscriptionBox2Title'>subscriptionBox2Title</Text>,
                    placeholder: <Text id='input.subscriptionBox2Title'>subscriptionBox2Title</Text>,
                    help: <Text id='input.subscriptionBox2Title-help'>Title for 2. subscription box.</Text>,
                    name: 'subscriptionBox2Title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.subscriptionBox2Title,
                    showInList: false,
                },
                {
                    title: <Text id='input.subscriptionBox2Body'>subscriptionBox2Body</Text>,
                    placeholder: <Text id='input.subscriptionBox2Body'>subscriptionBox2Body</Text>,
                    help: <Text id='input.subscriptionBox2Body-help'>Body text for the 2. subscription box.</Text>,
                    name: 'subscriptionBox2Body',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.subscriptionBox2Body,
                    showInList: false,
                },
                {
                    title: <Text id='input.subscriptionBox2Button'>subscriptionBox2Button</Text>,
                    placeholder: <Text id='input.subscriptionBox2Button'>subscriptionBox2Button</Text>,
                    help: <Text id='input.subscriptionBox2Button-help'>Button text for 2. subscription box.</Text>,
                    name: 'subscriptionBox2Button',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.subscriptionBox2Button,
                    showInList: false,
                },
                {
                    title: <Text id='input.subscriptionBox2Price'>subscriptionBox2Price</Text>,
                    placeholder: <Text id='input.subscriptionBox2Price'>subscriptionBox2Price</Text>,
                    help: <Text id='input.subscriptionBox2Price-help'>Price for 2. subscription box.</Text>,
                    name: 'subscriptionBox2Price',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.subscriptionBox2Price,
                    showInList: false,
                },

                {
                    title: <Text id='input.subscriptionBox3Title'>subscriptionBox3Title</Text>,
                    placeholder: <Text id='input.subscriptionBox3Title'>subscriptionBox3Title</Text>,
                    help: <Text id='input.subscriptionBox3Title-help'>Title for 3. subscription box.</Text>,
                    name: 'subscriptionBox3Title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.subscriptionBox3Title,
                    showInList: false,
                },
                {
                    title: <Text id='input.subscriptionBox3Body'>subscriptionBox3Body</Text>,
                    placeholder: <Text id='input.subscriptionBox3Body'>subscriptionBox3Body</Text>,
                    help: <Text id='input.subscriptionBox3Body-help'>Body text for the 3. subscription box.</Text>,
                    name: 'subscriptionBox3Body',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.subscriptionBox3Body,
                    showInList: false,
                },
                {
                    title: <Text id='input.subscriptionBox3Button'>subscriptionBox3Button</Text>,
                    placeholder: <Text id='input.subscriptionBox3Button'>subscriptionBox3Button</Text>,
                    help: <Text id='input.subscriptionBox3Button-help'>Button text for 3. subscription box.</Text>,
                    name: 'subscriptionBox3Button',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.subscriptionBox3Button,
                    showInList: false,
                },
                {
                    title: <Text id='input.subscriptionBox3Price'>subscriptionBox3Price</Text>,
                    placeholder: <Text id='input.subscriptionBox3Price'>subscriptionBox3Price</Text>,
                    help: <Text id='input.subscriptionBox3Price-help'>Price for 3. subscription box.</Text>,
                    name: 'subscriptionBox3Price',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.subscriptionBox3Price,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-location-title'>Race location</Text>,
            key: 'Race location',
            fields: [
                {
                    title: <Text id='input.startLatlng'>StartLatlng</Text>,
                    placeholder: <Text id='input.startLatlng'>StartLatlng</Text>,
                    name: 'startLatlng',
                    type: 'arrayElement',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.startLatlng,
                    showInList: false
                },
                {
                    title: <Text id='input.endLatlng'>EndLatlng</Text>,
                    placeholder: <Text id='input.endLatlng'>EndLatlng</Text>,
                    name: 'endLatlng',
                    type: 'arrayElement',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.endLatlng,
                    showInList: false
                },
                {
                    title: <Text id='input.locationCity'>LocationCity</Text>,
                    placeholder: <Text id='input.locationCity'>LocationCity</Text>,
                    name: 'locationCity',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.locationCity,
                    showInList: false,
                },
                {
                    title: <Text id='input.locationState'>LocationState</Text>,
                    placeholder: <Text id='input.locationState'>LocationState</Text>,
                    name: 'locationState',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.locationState,
                    showInList: false,
                },
                {
                    title: <Text id='input.locationCountry'>LocationCountry</Text>,
                    placeholder: <Text id='input.locationCountry'>LocationCountry</Text>,
                    name: 'locationCountry',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.locationCountry,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-contact-title'>Race contact info</Text>,
            key: 'Race contact info',
            fields: [
                {
                    title: <Text id='input.emailSender'>emailSender</Text>,
                    placeholder: <Text id='input.emailSender'>emailSender</Text>,
                    help: <Text id='input.emailSender-help'>Must be an approved sender in the system. If you are unsure, please do NOT change this.</Text>,
                    name: 'emailSender',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.emailSender,
                    showInList: false,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.emailSender && !util.validateEmail(obj.emailSender)) {
                            const invalidChars = findInvalidEmailChars(obj.emailSender);
                            let messages = [];
                            if (invalidChars.length > 0) {
                                messages = invalidChars.map(obj => {
                                    return `Character "${obj.char}" at position ${obj.index} is not allowed.`
                                });
                            }
                            holdingStore.setFeedbackError('emailSender', `Invalid emailSender. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('emailSender', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('emailSender', '');
                        holdingStore.setFeedbackButton('emailSender', null, null);
                    },
                },
                {
                    title: <Text id='input.smsSender'>smsSender</Text>,
                    placeholder: <Text id='input.smsSender'>smsSender</Text>,
                    help: <Text id='input.smsSender-help'>Must follow these rules: Alphanumeric. Maximum of 11 characters. No spaces. No special characters. The sender ID must be the brand name of the company sending the SMS. If you are unsure, please do NOT change this.</Text>,
                    name: 'smsSender',
                    maxLength: 11,
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.smsSender,
                    showInList: false,
                    showFieldInfo: ({ holdingStore, obj: object, field }) => {
                        const length = object[field] ? object[field].length : 0;
                        return (<>
                            <span class='font-weight-lighter'>Length:</span> {length}
                        </>);
                    },
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.smsSender && !/^[a-zA-Z0-9]+$/.test(obj.smsSender)) {
                            holdingStore.setFeedbackError('smsSender', 'Invalid smsSender. Must be 11 digits.');
                        } else {
                            holdingStore.setFeedbackError('smsSender', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('smsSender', '');
                        holdingStore.setFeedbackButton('smsSender', null, null);
                    },
                },
                {
                    title: <Text id='input.contactEmail'>ContactEmail</Text>,
                    placeholder: <Text id='input.contactEmail'>ContactEmail</Text>,
                    name: 'contactEmail',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.contactEmail,
                    showInList: false,
                },
                {
                    title: <Text id='input.contactCellphone'>ContactCellphone</Text>,
                    placeholder: <Text id='input.contactCellphone'>ContactCellphone</Text>,
                    name: 'contactCellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.contactCellphone,
                    showInList: false,
                },
                {
                    title: <Text id='input.facebook'>Facebook</Text>,
                    placeholder: <Text id='input.facebook'>Facebook</Text>,
                    name: 'facebook',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.facebook,
                    showInList: false,
                },
                {
                    title: <Text id='input.instagram'>Instagram</Text>,
                    placeholder: <Text id='input.instagram'>Instagram</Text>,
                    name: 'instagram',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.instagram,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-signup-text-title'>Race signup text</Text>,
            key: 'Race signup texts',
            fields: [
                {
                    title: <Text id='input.declarationText'>declarationText</Text>,
                    placeholder: <Text id='input.declarationText'>declarationText</Text>,
                    name: 'declarationText',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.declarationText,
                    showInList: false,
                },
                {
                    title: <Text id='input.termsAndAgreementText'>termsAndAgreementText</Text>,
                    placeholder: <Text id='input.termsAndAgreementText'>termsAndAgreementText</Text>,
                    name: 'termsAndAgreementText',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.termsAndAgreementText,
                    showInList: false,
                },
                {
                    title: <Text id='input.licenseText'>licenseText</Text>,
                    placeholder: <Text id='input.licenseText'>licenseText</Text>,
                    name: 'licenseText',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.licenseText,
                    showInList: false,
                },
                {
                    title: <Text id='input.preCheckText'>preCheckText</Text>,
                    placeholder: <Text id='input.preCheckText'>preCheckText</Text>,
                    name: 'preCheckText',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.preCheckText,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-media-title'>Race media</Text>,
            key: 'Race media',
            fields: [
                {
                    title: <Text id='input.sponsors'>sponsors</Text>,
                    placeholder: <Text id='input.sponsors'>sponsors</Text>,
                    help: <Text id='input.race-sponsors-help'>Add sponsor images to promote your sponsors.</Text>,
                    name: 'sponsors',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.sponsors,
                    showInList: false,
                    addToImages: false,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },

            ],
        },
        {
            name: <Text id='input.race-settings-title'>Race settings</Text>,
            key: 'Race settings',
            fields: [
                {
                    title: <Text id='input.nonstop'>Nonstop</Text>,
                    placeholder: <Text id='input.nonstop'>Nonstop</Text>,
                    name: 'nonstop',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.nonstop,
                    showInList: true,
                },
                {
                    title: <Text id='input.active'>Active</Text>,
                    placeholder: <Text id='input.active'>Active</Text>,
                    name: 'active',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.active || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.hideFromList'>hide From List</Text>,
                    placeholder: <Text id='input.hideFromList'>hide From List</Text>,
                    name: 'hideFromList',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.hideFromList || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.raceAdmins'>RaceAdmins</Text>,
                    placeholder: <Text id='input.raceAdmins'>RaceAdmins</Text>,
                    name: 'raceAdmins',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'adminRaceAdmins',
                    getValue: obj => obj.raceAdmins?.map(e => e),
                    displayArrayObject: obj => `${obj.firstname} ${obj.lastname}, ${obj.cellphone}`,
                    datalist: [
                        'firstname', 'lastname', 'cellphone', 'email',
                    ],
                    showInList: false,
                    displayHeight: '60px',
                },
                {
                    title: <Text id='input.mediaWorkers'>mediaWorkers</Text>,
                    placeholder: <Text id='input.mediaWorkers'>mediaWorkers</Text>,
                    name: 'mediaWorkers',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.mediaWorkers?.map(e => e),
                    displayArrayObject: obj => `${obj.firstname} ${obj.lastname}, ${obj.cellphone}`,
                    datalist: [
                        'firstname', 'lastname', 'cellphone', 'email',
                    ],
                    showInList: false,
                    displayHeight: '60px',
                },
                {
                    title: <Text id='input.timeTrackers'>timeTrackers</Text>,
                    placeholder: <Text id='input.timeTrackers'>timeTrackers</Text>,
                    name: 'timeTrackers',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.timeTrackers?.map(e => e),
                    displayArrayObject: obj => `${obj.firstname} ${obj.lastname}, ${obj.cellphone}`,
                    datalist: [
                        'firstname', 'lastname', 'cellphone', 'email',
                    ],
                    showInList: false,
                    displayHeight: '60px',
                },
                {
                    title: <Text id='input.raceLeaders'>raceLeaders</Text>,
                    placeholder: <Text id='input.raceLeaders'>raceLeaders</Text>,
                    name: 'raceLeaders',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.raceLeaders?.map(e => e),
                    displayArrayObject: obj => `${obj.firstname} ${obj.lastname}, ${obj.cellphone}`,
                    datalist: [
                        'firstname', 'lastname', 'cellphone', 'email',
                    ],
                    showInList: false,
                    displayHeight: '60px',
                },
                {
                    title: <Text id='input.tdLeaders'>tdLeaders</Text>,
                    placeholder: <Text id='input.tdLeaders'>tdLeaders</Text>,
                    name: 'tdLeaders',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.tdLeaders?.map(e => e),
                    displayArrayObject: obj => `${obj.firstname} ${obj.lastname}, ${obj.cellphone}`,
                    datalist: [
                        'firstname', 'lastname', 'cellphone', 'email',
                    ],
                    showInList: false,
                    displayHeight: '60px',
                },
                {
                    title: <Text id='input.scooterBibs'>ScooterBibs</Text>,
                    placeholder: <Text id='input.scooterBibs'>ScooterBibs</Text>,
                    name: 'scooterBibs',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.scooterBibs,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                },
                {
                    title: <Text id='input.mapCredits'>MapCredits</Text>,
                    placeholder: <Text id='input.mapCredits'>MapCredits</Text>,
                    name: 'mapCredits',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.mapCredits,
                    showInList: false,
                },
                {
                    title: <Text id='input.mapType'>mapType</Text>,
                    placeholder: <Text id='input.mapType'>mapType</Text>,
                    name: 'mapType',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.mapType,
                    showInList: false,
                },
                {
                    title: <Text id='input.checkpointOuterFence'>CheckpointOuterFence</Text>,
                    placeholder: <Text id='input.checkpointOuterFence'>CheckpointOuterFence</Text>,
                    name: 'checkpointOuterFence',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.checkpointOuterFence,
                    showInList: true,
                },
                {
                    title: <Text id='input.checkpointArrivalFence'>CheckpointArrivalFence</Text>,
                    placeholder: <Text id='input.checkpointArrivalFence'>CheckpointArrivalFence</Text>,
                    name: 'checkpointArrivalFence',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.checkpointArrivalFence,
                    showInList: true,
                },
                {
                    title: <Text id='input.checkpointDepartureFence'>CheckpointDepartureFence</Text>,
                    placeholder: <Text id='input.checkpointDepartureFence'>CheckpointDepartureFence</Text>,
                    name: 'checkpointDepartureFence',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.checkpointDepartureFence,
                    showInList: true,
                },
                {
                    title: <Text id='input.cutOffSpeedMeterSec'>CutOffSpeedMeterSec</Text>,
                    placeholder: <Text id='input.cutOffSpeedMeterSec'>CutOffSpeedMeterSec</Text>,
                    name: 'cutOffSpeedMeterSec',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.cutOffSpeedMeterSec,
                    showInList: true,
                },
                {
                    title: <Text id='input.cutOffDurationSec'>CutOffDurationSec</Text>,
                    placeholder: <Text id='input.cutOffDurationSec'>CutOffDurationSec</Text>,
                    name: 'cutOffDurationSec',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.cutOffDurationSec,
                    showInList: true,
                },
                {
                    title: <Text id='input.secondsToShowFinishMushers'>SecondsToShowFinishMushers</Text>,
                    placeholder: <Text id='input.secondsToShowFinishMushers'>SecondsToShowFinishMushers</Text>,
                    name: 'secondsToShowFinishMushers',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.secondsToShowFinishMushers,
                    showInList: true,
                },
                {
                    title: <Text id='input.speedGroupSize'>SpeedGroupSize</Text>,
                    placeholder: <Text id='input.speedGroupSize'>SpeedGroupSize</Text>,
                    name: 'speedGroupSize',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.speedGroupSize,
                    showInList: true,
                },
                {
                    title: <Text id='input.numberOfLastTrackPoints'>NumberOfLastTrackPoints</Text>,
                    placeholder: <Text id='input.numberOfLastTrackPoints'>NumberOfLastTrackPoints</Text>,
                    name: 'numberOfLastTrackPoints',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.numberOfLastTrackPoints,
                    showInList: true,
                },
                {
                    title: <Text id='input.useGpsDataToCalcPercentDone'>UseGpsDataToCalcPercentDone</Text>,
                    placeholder: <Text id='input.useGpsDataToCalcPercentDone'>UseGpsDataToCalcPercentDone</Text>,
                    name: 'useGpsDataToCalcPercentDone',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.useGpsDataToCalcPercentDone,
                    showInList: true,
                },
                {
                    title: <Text id='input.showTheoreticalPosition'>ShowTheoreticalPosition</Text>,
                    placeholder: <Text id='input.showTheoreticalPosition'>ShowTheoreticalPosition</Text>,
                    name: 'showTheoreticalPosition',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.showTheoreticalPosition,
                    showInList: true,
                },
                {
                    title: <Text id='input.showDistanceMarkers'>ShowDistanceMarkers</Text>,
                    placeholder: <Text id='input.showDistanceMarkers'>ShowDistanceMarkers</Text>,
                    name: 'showDistanceMarkers',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.showDistanceMarkers,
                    showInList: true,
                },
                {
                    title: <Text id='input.maxTrackIdxToSkip'>MaxTrackIdxToSkip</Text>,
                    placeholder: <Text id='input.maxTrackIdxToSkip'>MaxTrackIdxToSkip</Text>,
                    name: 'maxTrackIdxToSkip',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.maxTrackIdxToSkip,
                    showInList: true,
                },
                {
                    title: <Text id='input.minTimeToNextCpSec'>MinTimeToNextCpSec</Text>,
                    placeholder: <Text id='input.minTimeToNextCpSec'>MinTimeToNextCpSec</Text>,
                    name: 'minTimeToNextCpSec',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.minTimeToNextCpSec,
                    showInList: true,
                },
                {
                    title: <Text id='input.countryRegexp'>CountryRegexp</Text>,
                    placeholder: <Text id='input.countryRegexp'>CountryRegexp</Text>,
                    help: <Text id='input.countryRegexp-help'>Only show tracking for countries matching this regexp.</Text>,
                    name: 'countryRegexp',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.countryRegexp,
                    showInList: false,
                },
                {
                    title: <Text id='input.countryRegexpMessage'>CountryRegexpMessage</Text>,
                    placeholder: <Text id='input.countryRegexpMessage'>CountryRegexpMessage</Text>,
                    name: 'countryRegexpMessage',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.countryRegexpMessage,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-link-title'>Race link buttons</Text>,
            key: 'Race link buttons',
            fields: [
                {
                    title: <Text id='input.link1'>Link 1</Text>,
                    placeholder: <Text id='input.link1'>Link 1</Text>,
                    help: <Text id='input.link1-help'>Valid link 1.</Text>,
                    name: 'link1',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.link1,
                    showInList: false,
                },
                {
                    title: <Text id='input.link1Text'>Link Text 1</Text>,
                    placeholder: <Text id='input.link1Text'>Link Text 1</Text>,
                    help: <Text id='input.link1Text-help'>Link description 1.</Text>,
                    name: 'link1Text',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.link1Text,
                    showInList: false,
                },
                {
                    title: <Text id='input.link2'>Link 2</Text>,
                    placeholder: <Text id='input.link2'>Link 2</Text>,
                    help: <Text id='input.link2-help'>Valid link 2.</Text>,
                    name: 'link2',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.link2,
                    showInList: false,
                },
                {
                    title: <Text id='input.link2Text'>Link Text 2</Text>,
                    placeholder: <Text id='input.link2Text'>Link Text 2</Text>,
                    help: <Text id='input.link2Text-help'>Link description 2.</Text>,
                    name: 'link2Text',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.link2Text,
                    showInList: false,
                },
                {
                    title: <Text id='input.link3'>Link 3</Text>,
                    placeholder: <Text id='input.link3'>Link 3</Text>,
                    help: <Text id='input.link3-help'>Valid link 3.</Text>,
                    name: 'link3',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.link3,
                    showInList: false,
                },
                {
                    title: <Text id='input.link3Text'>Link Text 3</Text>,
                    placeholder: <Text id='input.link3Text'>Link Text 3</Text>,
                    help: <Text id='input.link3Text-help'>Link description 3.</Text>,
                    name: 'link3Text',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.link3Text,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-map-title'>Race Map Details</Text>,
            key: 'Race map details',
            fields: [
                {
                    title: <Text id='input.waypoints'>waypoints</Text>,
                    placeholder: <Text id='input.waypoints'>waypoints</Text>,
                    name: 'waypoints',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-map-marker',
                    getValue: obj => obj.waypoints,
                    displayArrayObject: obj => <>
                        {obj.name}:<br /> {obj.lat},{obj.lng}`;
                    </>,
                    datalist: [
                        'name', 'lat', 'lng', 'rotateDeg', 'icon',
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },
            ],
        },
        {
            name: <Text id='input.race-vipps-payment-title'>Vipps payment settings</Text>,
            key: 'Race vipps settings',
            fields: [
                {
                    title: <Text id='input.vippsName'>vippsName</Text>,
                    placeholder: <Text id='input.vippsName'>vippsName</Text>,
                    help: <Text id='input.vippsName-help'>Vippsname has to be a-z and lowercase only.</Text>,
                    name: 'vippsName',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsName,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsVersion'>vippsVersion</Text>,
                    placeholder: <Text id='input.vippsVersion'>vippsVersion</Text>,
                    help: <Text id='input.vippsVersion-help'>vippsVersion has to 1.0.0</Text>,
                    name: 'vippsVersion',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsVersion,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsPluginName'>vippsPluginName</Text>,
                    placeholder: <Text id='input.vippsPluginName'>vippsPluginName</Text>,
                    help: <Text id='input.vippsPluginName-help'>vippsPluginName has to be a-z and lowercase only.</Text>,
                    name: 'vippsPluginName',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsPluginName,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsPluginVersion'>vippsPluginVersion</Text>,
                    placeholder: <Text id='input.vippsPluginVersion'>vippsPluginVersion</Text>,
                    help: <Text id='input.vippsPluginVersion-help'>vippsPluginVersion has to 1.0.0</Text>,
                    name: 'vippsPluginVersion',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsPluginVersion,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsMerchantSerialNumber'>vippsMerchantSerialNumber</Text>,
                    placeholder: <Text id='input.vippsMerchantSerialNumber'>vippsMerchantSerialNumber</Text>,
                    help: <Text id='input.vippsMerchantSerialNumber-help'>Vipps MSN number.</Text>,
                    name: 'vippsMerchantSerialNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsMerchantSerialNumber,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsClientId'>vippsClientId</Text>,
                    placeholder: <Text id='input.vippsClientId'>vippsClientId</Text>,
                    name: 'vippsClientId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsClientId,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsClientSecret'>vippsClientSecret</Text>,
                    placeholder: <Text id='input.vippsClientSecret'>vippsClientSecret</Text>,
                    name: 'vippsClientSecret',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsClientSecret,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsSubscriptionKey1'>vippsSubscriptionKey1</Text>,
                    placeholder: <Text id='input.vippsSubscriptionKey1'>vippsSubscriptionKey1</Text>,
                    name: 'vippsSubscriptionKey1',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsSubscriptionKey1,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsSubscriptionKey2'>vippsSubscriptionKey2</Text>,
                    placeholder: <Text id='input.vippsSubscriptionKey2'>vippsSubscriptionKey2</Text>,
                    name: 'vippsSubscriptionKey2',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsSubscriptionKey2,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsUseTestMode'>vippsUseTestMode</Text>,
                    placeholder: <Text id='input.vippsUseTestMode'>vippsUseTestMode</Text>,
                    name: 'vippsUseTestMode',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsUseTestMode,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsTestPhoneNumber'>vippsTestPhoneNumber</Text>,
                    placeholder: <Text id='input.vippsTestPhoneNumber'>vippsTestPhoneNumber</Text>,
                    name: 'vippsTestPhoneNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsTestPhoneNumber,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.vippsTermsOfSale'>vippsTermsOfSale</Text>,
                    placeholder: <Text id='input.vippsTermsOfSale'>vippsTermsOfSale</Text>,
                    name: 'vippsTermsOfSale',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.vippsTermsOfSale,
                    showInList: false,
                    isAdminField: false,
                },
            ],
        },
        {
            name: <Text id='input.race-bank-payment-title'>Bank payment settings</Text>,
            key: 'Race bank payment settings',
            fields: [
                {
                    title: <Text id='input.bankCompany'>bankCompany</Text>,
                    placeholder: <Text id='input.bankCompany'>bankCompany</Text>,
                    name: 'bankCompany',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.bankCompany,
                    showInList: false,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.bankAddress'>bankAddress</Text>,
                    placeholder: <Text id='input.bankAddress'>bankAddress</Text>,
                    name: 'bankAddress',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.bankAddress,
                    showInList: false,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.bankPostalcode'>bankPostalcode</Text>,
                    placeholder: <Text id='input.bankPostalcode'>bankPostalcode</Text>,
                    name: 'bankPostalcode',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.bankPostalcode,
                    showInList: false,
                    isAdminField: false,
                    // callback: async (obj, updateValue) => {
                    //     if (obj.postalcode && obj.postalcode.length === 4) {
                    //         const response = await postnummerLookup(obj.postalcode);
                    //
                    //         if (response) {
                    //             const place = response[0]?.poststed;
                    //             updateValue({
                    //                 name: 'postalplace',
                    //                 value: place,
                    //                 id: obj.id,
                    //             });
                    //         }
                    //     }
                    // },
                },
                {
                    title: <Text id='input.bankPostalplace'>bankPostalplace</Text>,
                    placeholder: <Text id='input.bankPostalplace'>bankPostalplace</Text>,
                    name: 'bankPostalplace',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.bankPostalplace,
                    showInList: false,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.bankAccountNumber'>bankAccountNumber</Text>,
                    placeholder: <Text id='input.bankAccountNumber'>bankAccountNumber</Text>,
                    name: 'bankAccountNumber',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.bankAccountNumber,
                    showInList: false,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.bankIBAN'>bankIBAN</Text>,
                    placeholder: <Text id='input.bankIBAN'>bankIBAN</Text>,
                    name: 'bankIBAN',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.bankIBAN,
                    showInList: false,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.bankSWIFT'>bankSWIFT</Text>,
                    placeholder: <Text id='input.bankSWIFT'>bankSWIFT</Text>,
                    name: 'bankSWIFT',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.bankSWIFT,
                    showInList: false,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.bankMarkPayment'>bankMarkPayment</Text>,
                    placeholder: <Text id='input.bankMarkPayment'>bankMarkPayment</Text>,
                    name: 'bankMarkPayment',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.bankMarkPayment,
                    showInList: false,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.bankDueDate'>bankDueDate</Text>,
                    placeholder: <Text id='input.bankDueDate'>bankDueDate</Text>,
                    name: 'bankDueDate',
                    type: 'date',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.bankDueDate,
                    displayValue: val => val ? util.formatDate(val, {
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    showInList: false,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.bankOtherInfo'>bankOtherInfo</Text>,
                    placeholder: <Text id='input.bankOtherInfo'>bankOtherInfo</Text>,
                    help: <Text id='input.bankOtherInfo-help'>Alternative manual payment like Vipps number or other.</Text>,
                    name: 'bankOtherInfo',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.bankOtherInfo,
                    showInList: false,
                    isAdminField: false,
                },
            ],
        },
    ],
    raceClass: [
        {
            name: <Text id='input.race-class-basics-title'>Race Class basics</Text>,
            key: 'Race Class basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: user => user.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.race'>Race</Text>,
                    placeholder: <Text id='input.race'>Race</Text>,
                    name: 'race',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.race,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.race'>Race</Text>,
                    placeholder: <Text id='input.race'>Race</Text>,
                    name: 'race',
                    type: 'select',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj?.race,
                    // displayValue: (val, obj) => {
                    //     return `${obj.firstname} ${obj.lastname}`;
                    // },
                    datalistName: 'races',
                    datalistFieldKey: e => e.id,
                    datalistFieldVal: e => `${e.title}`,
                    showInList: false,
                    hideIfAdmin: true,
                },
                {
                    title: <Text id='input.race'>Race</Text>,
                    placeholder: <Text id='input.race'>Race</Text>,
                    name: 'race',
                    type: 'select',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj?.race,
                    // displayValue: (val, obj) => {
                    //     return `${obj.firstname} ${obj.lastname}`;
                    // },
                    datalistName: 'adminRaces',
                    datalistFieldKey: e => e.id,
                    datalistFieldVal: e => `${e.title}`,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.year'>Year</Text>,
                    placeholder: <Text id='input.year'>Year</Text>,
                    name: 'year',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.year,
                    showInList: true,
                    styles: 'min-width: 50px;',
                },
                {
                    title: <Text id='input.title'>Title</Text>,
                    placeholder: <Text id='input.title'>Title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                    styles: 'min-width: 120px;',
                },
                {
                    title: <Text id='input.tag'>Tag</Text>,
                    placeholder: <Text id='input.tag'>Tag</Text>,
                    name: 'tag',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.tag,
                    showInList: false,
                },
                {
                    title: <Text id='input.warning'>warning</Text>,
                    placeholder: <Text id='input.warning'>warning</Text>,
                    name: 'warning',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.warning,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.race-class-info-title'>Race Class Info</Text>,
            key: 'Race Class Info',
            fields: [
                {
                    title: <Text id='input.ingress'>Ingress</Text>,
                    placeholder: <Text id='input.ingress'>Ingress</Text>,
                    name: 'ingress',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.ingress,
                    showInList: false,
                },
                {
                    title: <Text id='input.body'>Body</Text>,
                    placeholder: <Text id='input.body'>Body</Text>,
                    name: 'body',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.body,
                    showInList: false,
                },
                {
                    title: <Text id='input.info'>Info</Text>,
                    placeholder: <Text id='input.info'>Info</Text>,
                    name: 'info',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.info,
                    showInList: false,
                },
                {
                    title: <Text id='input.terms'>Terms</Text>,
                    placeholder: <Text id='input.terms'>Terms</Text>,
                    name: 'terms',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.terms,
                    showInList: false,
                },
                {
                    title: <Text id='input.trackInfo'>TrackInfo</Text>,
                    placeholder: <Text id='input.trackInfo'>TrackInfo</Text>,
                    name: 'trackInfo',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.trackInfo,
                    showInList: false,
                },
                {
                    title: <Text id='input.prices'>Prices</Text>,
                    placeholder: <Text id='input.prices'>Prices</Text>,
                    name: 'prices',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.prices,
                    showInList: false,
                },
                {
                    title: <Text id='input.qualifying'>Qualifying</Text>,
                    placeholder: <Text id='input.qualifying'>Qualifying</Text>,
                    name: 'qualifying',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.qualifying,
                    showInList: false,
                },
                {
                    title: <Text id='input.rules'>Rules</Text>,
                    placeholder: <Text id='input.rules'>Rules</Text>,
                    name: 'rules',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.rules,
                    showInList: false,
                },
                {
                    title: <Text id='input.readyToSignup'>ReadyToSignup</Text>,
                    placeholder: <Text id='input.readyToSignup'>ReadyToSignup</Text>,
                    name: 'readyToSignup',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.readyToSignup,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-class-info-english-title'>Race Class Info English</Text>,
            key: 'Race Class Info English',
            fields: [
                {
                    title: <Text id='input.trackInfoEn'>trackInfoEn</Text>,
                    placeholder: <Text id='input.trackInfoEn'>trackInfoEn</Text>,
                    name: 'trackInfoEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.trackInfoEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.infoEn'>infoEn</Text>,
                    placeholder: <Text id='input.infoEn'>infoEn</Text>,
                    name: 'infoEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.infoEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.termsEn'>termsEn</Text>,
                    placeholder: <Text id='input.termsEn'>termsEn</Text>,
                    name: 'termsEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.termsEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.pricesEn'>pricesEn</Text>,
                    placeholder: <Text id='input.pricesEn'>pricesEn</Text>,
                    name: 'pricesEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.pricesEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.qualifyingEn'>qualifyingEn</Text>,
                    placeholder: <Text id='input.qualifyingEn'>qualifyingEn</Text>,
                    name: 'qualifyingEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.qualifyingEn,
                    showInList: false,
                },
                {
                    title: <Text id='input.rulesEn'>rulesEn</Text>,
                    placeholder: <Text id='input.rulesEn'>rulesEn</Text>,
                    name: 'rulesEn',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.rulesEn,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-class-dates-title'>Race Class dates</Text>,
            key: 'Race Class dates',
            fields: [
                {
                    title: <Text id='input.startDate'>StartDate</Text>,
                    placeholder: <Text id='input.startDate'>StartDate</Text>,
                    name: 'startDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.startDate,
                    showInList: true,
                },
                {
                    title: <Text id='input.endDate'>EndDate</Text>,
                    placeholder: <Text id='input.endDate'>EndDate</Text>,
                    name: 'endDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.endDate,
                    showInList: true,
                },
                {
                    title: <Text id='input.signupStartDate'>SignupStartDate</Text>,
                    placeholder: <Text id='input.signupStartDate'>SignupStartDate</Text>,
                    name: 'signupStartDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.signupStartDate,
                    showInList: true,
                },
                {
                    title: <Text id='input.signupEndDate'>SignupEndDate</Text>,
                    placeholder: <Text id='input.signupEndDate'>SignupEndDate</Text>,
                    name: 'signupEndDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.signupEndDate,
                    showInList: true,
                },
                {
                    title: <Text id='input.signupDelayedEndDate'>SignupDelayedEndDate</Text>,
                    placeholder: <Text id='input.signupDelayedEndDate'>SignupDelayedEndDate</Text>,
                    name: 'signupDelayedEndDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.signupDelayedEndDate,
                    showInList: true,
                },
                {
                    title: <Text id='input.musherMeetingStartDate'>Musher Meeting startDate</Text>,
                    placeholder: <Text id='input.musherMeetingStartDate'>Musher Meeting startDate</Text>,
                    name: 'musherMeetingStartDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.musherMeetingStartDate,
                    showInList: true,
                },
                {
                    title: <Text id='input.musherMeetingEndDate'>Musher Meeting endDate</Text>,
                    placeholder: <Text id='input.musherMeetingEndDate'>Musher Meeting endDate</Text>,
                    name: 'musherMeetingEndDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.musherMeetingEndDate,
                    showInList: true,
                },
                {
                    title: <Text id='input.checkInStartDate'>CheckIn StartDate</Text>,
                    placeholder: <Text id='input.checkInStartDate'>CheckIn StartDate</Text>,
                    name: 'checkInStartDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.checkInStartDate,
                    showInList: true,
                },
                {
                    title: <Text id='input.checkInEndDate'>CheckIn EndDate</Text>,
                    placeholder: <Text id='input.checkInEndDate'>CheckIn EndDate</Text>,
                    name: 'checkInEndDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.checkInEndDate,
                    showInList: true,
                },
            ],
        },


        {
            name: <Text id='input.race-class-settings-title'>Race Class settings</Text>,
            key: 'Race Class settings',
            fields: [
                {
                    title: <Text id='input.dogs'>Dogs</Text>,
                    placeholder: <Text id='input.dogs'>Dogs</Text>,
                    name: 'dogs',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.dogs,
                    showInList: true,
                },
                {
                    title: <Text id='input.dogsInfo'>dogsInfo</Text>,
                    placeholder: <Text id='input.dogsInfo'>dogsInfo</Text>,
                    name: 'dogsInfo',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.dogsInfo,
                    showInList: false,
                },
                {
                    title: <Text id='input.doNotCountRestTime'>Do not count rest time</Text>,
                    placeholder: <Text id='input.doNotCountRestTime'>Do not count rest time</Text>,
                    help: <Text id='input.doNotCountRestTime-help'>Toggle if you do not want to calculate rest time. Typically used for stage races with one stage every day.</Text>,
                    name: 'doNotCountRestTime',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.doNotCountRestTime ? 1 : 0,
                    showInList: true,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.sortOrderResults'>SortOrderResults</Text>,
                    placeholder: <Text id='input.sortOrderResults'>SortOrderResults</Text>,
                    help: <Text id='input.sortOrderResults-help'>Sort order for results. Nothing for normal races, "timeused" for stage races with rest and "raceTimeused" for stage races new start every day.</Text>,
                    name: 'sortOrderResults',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.sortOrderResults,
                    showInList: false,
                },
                {
                    title: <Text id='input.distance'>Distance</Text>,
                    placeholder: <Text id='input.distance'>Distance</Text>,
                    name: 'distance',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.distance,
                    showInList: true,
                },
                {
                    title: <Text id='input.minDistance'>MinDistance</Text>,
                    placeholder: <Text id='input.minDistance'>MinDistance</Text>,
                    name: 'minDistance',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.minDistance,
                    showInList: true,
                },
                {
                    title: <Text id='input.requiredRestMinutes'>RequiredRestMinutes</Text>,
                    placeholder: <Text id='input.requiredRestMinutes'>RequiredRestMinutes</Text>,
                    name: 'requiredRestMinutes',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.requiredRestMinutes,
                    showInList: true,
                },
                {
                    title: <Text id='input.elevation'>Elevation</Text>,
                    placeholder: <Text id='input.elevation'>Elevation</Text>,
                    name: 'elevation',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.elevation,
                    showInList: true,
                },
                {
                    title: <Text id='input.raceClassTrack'>RaceClassTrack</Text>,
                    placeholder: <Text id='input.raceClassTrack'>RaceClassTrack</Text>,
                    name: 'raceClassTrack',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.raceClassTrack,
                    showInList: true,
                },
                {
                    title: <Text id='input.maxMushers'>maxMushers</Text>,
                    placeholder: <Text id='input.maxMushers'>maxMushers</Text>,
                    help: <Text id='input.maxMushers-help'>Max number of mushers in this class.</Text>,
                    name: 'maxMushers',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.maxMushers,
                    showInList: true,
                },
                {
                    title: <Text id='input.bibColor'>bibColor</Text>,
                    placeholder: <Text id='input.bibColor'>bibColor</Text>,
                    name: 'bibColor',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.bibColor,
                    showInList: false,
                },
                {
                    title: <Text id='input.startBib'>startBib</Text>,
                    placeholder: <Text id='input.startBib'>startBib</Text>,
                    help: <Text id='input.startBib-help'>Start bib number in this class</Text>,
                    name: 'startBib',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.startBib,
                    showInList: true,
                },
                {
                    title: <Text id='input.endBib'>endBib</Text>,
                    placeholder: <Text id='input.endBib'>endBib</Text>,
                    help: <Text id='input.endBib-help'>End bib number in this class</Text>,
                    name: 'endBib',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.endBib,
                    showInList: true,
                },
                {
                    title: <Text id='input.startInterval'>startInterval</Text>,
                    placeholder: <Text id='input.startInterval'>startInterval</Text>,
                    help: <Text id='input.startInterval-help'>Start interval in minutes</Text>,
                    name: 'startInterval',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.startInterval,
                    showInList: true,
                },
                {
                    title: <Text id='input.obligRest'>ObligRest</Text>,
                    placeholder: <Text id='input.obligRest'>ObligRest</Text>,
                    name: 'obligRest',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.obligRest,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },
            ],
        },
        {
            name: <Text id='input.race-class-fees-title'>Race Class fees</Text>,
            key: 'Race Class fees',
            fields: [
                {
                    title: <Text id='input.signupfee'>Signupfee</Text>,
                    placeholder: <Text id='input.signupfee'>Signupfee</Text>,
                    name: 'signupfee',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.signupfee,
                    showInList: true,
                },
                {
                    title: <Text id='input.signuppayment'>Signuppayment</Text>,
                    placeholder: <Text id='input.signuppayment'>Signuppayment</Text>,
                    name: 'signuppayment',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.signuppayment,
                    showInList: true,
                },
                {
                    title: <Text id='input.signuppaymentDelayed'>SignuppaymentDelayed</Text>,
                    placeholder: <Text id='input.signuppaymentDelayed'>SignuppaymentDelayed</Text>,
                    name: 'signuppaymentDelayed',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.signuppaymentDelayed,
                    showInList: true,
                },
                {
                    title: <Text id='input.seedingPaymentAmount'>seedingPaymentAmount</Text>,
                    placeholder: <Text id='input.seedingPaymentAmount'>seedingPaymentAmount</Text>,
                    name: 'seedingPaymentAmount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.seedingPaymentAmount,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.race-class-checkpoints-title'>Race Class checkpoints</Text>,
            key: 'Race Class checkpoints',
            fields: [
                {
                    title: <Text id='input.checkpoints'>checkpoints</Text>,
                    placeholder: <Text id='input.checkpoints'>checkpoints</Text>,
                    name: 'checkpoints',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.checkpoints,
                    displayArrayObject: obj => <div class='d-flex flex-column w-100'>

                        <div>{obj.sortOrder}: {obj.name}</div>
                        <div class='d-flex flex-row'><div class='w-50 text-right mr-1'><span class='font-weight-lighter'>fromPrev:</span></div><div class='w-50 text-left ml-1'>{obj.distanceFromPrev} km</div></div>
                        <div class='d-flex flex-row'><div class='w-50 text-right mr-1'><span class='font-weight-lighter'>rest:</span></div><div class='w-50 text-left ml-1'>{util.secToHms(obj.requiredRestMinutes * 60 || 0)}</div></div>
                        <div class='d-flex flex-row'><div class='w-50 text-right mr-1'><span class='font-weight-lighter'>timeEq:</span></div><div class='w-50 text-left ml-1'>{obj.timeEqualization ? <i class='fa-solid fa-square-check text-success' /> : <i class='fa-regular fa-square text-muted' />}</div></div>
                        <div class='d-flex flex-row'><div class='w-50 text-right mr-1'><span class='font-weight-lighter'>useRemainingRest:</span></div><div class='w-50 text-left ml-1'>{obj.useRemainingRest ? <i class='fa-solid fa-square-check text-success' /> : <i class='fa-regular fa-square text-muted' />}</div></div>
                        <div class='d-flex flex-row'><div class='w-50 text-right mr-1'><span class='font-weight-lighter'>isOptRest:</span></div><div class='w-50 text-left ml-1'>{obj.isOptionalRest ? <i class='fa-solid fa-square-check text-success' /> : <i class='fa-regular fa-square text-muted' />}</div></div>
                        <div class='d-flex flex-row'><div class='w-50 text-right mr-1'><span class='font-weight-lighter'>isStart:</span></div><div class='w-50 text-left ml-1'>{obj.isStartLine ? <i class='fa-solid fa-square-check text-success' /> : <i class='fa-regular fa-square text-muted' />}</div></div>
                        <div class='d-flex flex-row'><div class='w-50 text-right mr-1'><span class='font-weight-lighter'>isFinish:</span></div><div class='w-50 text-left ml-1'>{obj.isFinishLine ? <i class='fa-solid fa-square-check text-success' /> : <i class='fa-regular fa-square text-muted' />}</div></div>
                        <div class='d-flex flex-row'><div class='w-50 text-right mr-1'><span class='font-weight-lighter'>isPrologue:</span></div><div class='w-50 text-left ml-1'>{obj.isPrologue ? <i class='fa-solid fa-square-check text-success' /> : <i class='fa-regular fa-square text-muted' />}</div></div>
                        <div class='d-flex flex-row'><div class='w-50 text-right mr-1'><span class='font-weight-lighter'>lat:</span></div><div class='w-50 text-left ml-1'>{obj.lat}</div></div>
                        <div class='d-flex flex-row'><div class='w-50 text-right mr-1'><span class='font-weight-lighter'>lng:</span></div><div class='w-50 text-left ml-1'>{obj.lng}</div></div>
                        <div class='d-flex flex-row'><div class='w-50 text-right mr-1'><span class='font-weight-lighter'>altitude:</span></div><div class='w-50 text-left ml-1'>{obj.altitude}</div></div>
                        <div class='d-flex flex-row'><div class='w-50 text-right mr-1'><span class='font-weight-lighter'>rotateDeg:</span></div><div class='w-50 text-left ml-1'>{obj.rotateDeg}</div></div>
                    </div>,

                    datalist: [
                        'name', 'sortOrder', 'distanceFromPrev',
                        'requiredRestMinutes', 'useRemainingRest',
                        'timeEqualization', 'isStartLine', 'isFinishLine',
                        'estSpeed', 'estTimeToCpMinutes', 'estRestMinutes',
                        'requiredRestMinutesOptional', 'timeEqualizationOptional',
                        'isPrologue',
                        'isDogDrop', 'isOptionalRest',
                        'lat', 'lng', 'altitude',
                        'rotateDeg',
                    ],
                    datalistHelp: [
                        'Name of the checkpoint.',
                        'Sort order of the checkpoint. 1, 2, 3, etc.',
                        'Distance from previous checkpoint in km.',
                        'Estimated speed to checkpoint in km/h.',
                        'Estimated time to checkpoint in minutes.',
                        'Estimated rest time in minutes.',
                        'Required rest time in minutes.',
                        'Require remaining rest time for the race on this checkpoint. Use this everytime for the last checkpoint if rest is allowed.',
                        'Time equalization: yes = 1 or no = 0. Leave empty for default.',
                        'Is this the start line. yes = 1',
                        'Is this the finish line. yes = 1',
                        'Required rest time for optional rest in minutes.',
                        'If time equalization is optional for this checkpoint.',
                        'Is the stage to this checkpint is a prologue.',
                        'Is this a dog drop checkpoint.',
                        'Is this an optional rest checkpoint.',
                        'Latitude of the checkpoint.',
                        'Longitude of the checkpoint.',
                        'Altitude of the checkpoint.',
                        'Rotation of the checkpoint icon in degrees.',
                    ],
                    drawerSize: 'medium',
                    showInList: false,
                    displayHeight: '300px',
                    displayWidth: '280px',
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.waypoints'>waypoints</Text>,
                    placeholder: <Text id='input.waypoints'>waypoints</Text>,
                    name: 'waypoints',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-map-marker',
                    getValue: obj => obj.waypoints,
                    displayArrayObject: obj => <>
                        {obj.name}:<br /> {obj.lat},{obj.lng}`;
                    </>,
                    datalist: [
                        'name', 'lat', 'lng', 'rotateDeg', 'icon',
                    ],
                    showInList: false,
                    displayHeight: '100px',
                    hideOnCreate: true,
                },
            ],
        },
        {
            name: <Text id='input.race-class-media-title'>Race Class media</Text>,
            key: 'Race Class media',
            fields: [
                {
                    title: <Text id='input.sponsors'>sponsors</Text>,
                    placeholder: <Text id='input.sponsors'>sponsors</Text>,
                    help: <Text id='input.raceclass-sponsors-help'>Add sponsor images to promote your sponsors.</Text>,
                    name: 'sponsors',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.sponsors,
                    showInList: false,
                    addToImages: false,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },

            ],
        },
    ],
    raceClassContestant: [
        {
            name: <Text id='input.race-class-contestant-basics-title'>Race Class Contestant basics</Text>,
            key: 'Race Class Contestant basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                    csvField: true,
                },
                {
                    title: <Text id='input.signupDate'>signupDate</Text>,
                    placeholder: <Text id='input.signupDate'>signupDate</Text>,
                    name: 'signupDate',
                    type: 'date',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.signupDate,
                    displayValue: obj => obj ? util.formatDate(obj, { locale: 'nb-NO' }) : null,
                    showInList: true,
                    showInRaceAdminList: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.race'>Race</Text>,
                    placeholder: <Text id='input.race'>Race</Text>,
                    name: 'race',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.race,
                    showInList: true,
                    isAdminField: true,
                    csvField: true,
                },
                {
                    title: <Text id='input.raceClass'>RaceClass</Text>,
                    placeholder: <Text id='input.raceClass'>RaceClass</Text>,
                    name: 'raceClass',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.raceClass,
                    showInList: true,
                    isAdminField: true,
                    csvField: true,
                },
                {
                    title: <Text id='input.raceClass'>RaceClass</Text>,
                    placeholder: <Text id='input.raceClass'>RaceClass</Text>,
                    name: 'raceClass',
                    type: 'select',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj?.raceClass,
                    // displayValue: (val, obj) => {
                    //     return `${obj.firstname} ${obj.lastname}`;
                    // },
                    datalistName: 'raceClasses',
                    datalistFieldKey: e => e.id,
                    datalistFieldVal: e => `${e.title} (${e.distance}km)`,
                    showInList: false,
                    hideIfAdmin: false,
                },
                {
                    title: <Text id='input.raceClass'>RaceClass</Text>,
                    placeholder: <Text id='input.raceClass'>RaceClass</Text>,
                    name: 'raceClass',
                    type: 'select',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj?.raceClass,
                    // displayValue: (val, obj) => {
                    //     return `${obj.firstname} ${obj.lastname}`;
                    // },
                    datalistName: 'adminRaceClasses',
                    datalistFieldKey: e => e.id,
                    datalistFieldVal: e => `${e.title} (${e.distance}km)`,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.approved'>Approved</Text>,
                    placeholder: <Text id='input.approved'>Approved</Text>,
                    name: 'approved',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.approved ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.hasVaccines'>hasVaccines</Text>,
                    placeholder: <Text id='input.hasVaccines'>hasVaccines</Text>,
                    name: 'hasVaccines',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hasVaccines ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.hasSignedDeclaration'>hasSignedDeclaration</Text>,
                    placeholder: <Text id='input.hasSignedDeclaration'>hasSignedDeclaration</Text>,
                    name: 'hasSignedDeclaration',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hasSignedDeclaration ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.hasDeliveredChipList'>hasDeliveredChipList</Text>,
                    placeholder: <Text id='input.hasDeliveredChipList'>hasDeliveredChipList</Text>,
                    name: 'hasDeliveredChipList',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hasDeliveredChipList ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.hasReceivedBib'>hasReceivedBib</Text>,
                    placeholder: <Text id='input.hasReceivedBib'>hasReceivedBib</Text>,
                    name: 'hasReceivedBib',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hasReceivedBib ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.hasReceivedTracker'>hasReceivedTracker</Text>,
                    placeholder: <Text id='input.hasReceivedTracker'>hasReceivedTracker</Text>,
                    name: 'hasReceivedTracker',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hasReceivedTracker ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.hasPreCheck'>hasPreCheck</Text>,
                    placeholder: <Text id='input.hasPreCheck'>hasPreCheck</Text>,
                    name: 'hasPreCheck',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hasPreCheck ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.hasLicense'>hasLicense</Text>,
                    placeholder: <Text id='input.hasLicense'>hasLicense</Text>,
                    name: 'hasLicense',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hasLicense ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.hasWeighedEmergencyFood'>hasWeighedEmergencyFood</Text>,
                    placeholder: <Text id='input.hasWeighedEmergencyFood'>hasWeighedEmergencyFood</Text>,
                    name: 'hasWeighedEmergencyFood',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hasWeighedEmergencyFood ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.hasPreRaceCheck'>hasPreRaceCheck</Text>,
                    placeholder: <Text id='input.hasPreRaceCheck'>hasPreRaceCheck</Text>,
                    name: 'hasPreRaceCheck',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hasPreRaceCheck ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.isDNS'>isDNS</Text>,
                    placeholder: <Text id='input.isDNS'>isDNS</Text>,
                    name: 'isDNS',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isDNS ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.isScratched'>isScratched</Text>,
                    placeholder: <Text id='input.isScratched'>isScratched</Text>,
                    name: 'isScratched',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.isScratched ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.status'>Status</Text>,
                    placeholder: <Text id='input.status'>Status</Text>,
                    name: 'status',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.status,
                    showInList: true,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.raceStatus'>RaceStatus</Text>,
                    placeholder: <Text id='input.raceStatus'>RaceStatus</Text>,
                    name: 'raceStatus',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.raceStatus,
                    showInList: true,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.comment'>comment</Text>,
                    placeholder: <Text id='input.comment'>comment</Text>,
                    name: 'comment',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.comment,
                    showInList: false,
                    showInRaceAdminList: false,
                },
                {
                    title: <Text id='input.raceComment'>RaceComment</Text>,
                    placeholder: <Text id='input.raceComment'>RaceComment</Text>,
                    name: 'raceComment',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.raceComment,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                },
                {
                    title: <Text id='input.firstname'>Firstname</Text>,
                    placeholder: <Text id='input.firstname'>Firstname</Text>,
                    name: 'firstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.firstname,
                    showInList: true,
                    showInRaceAdminList: true,
                    noEdit: true,
                    csvField: true,
                },
                {
                    title: <Text id='input.lastname'>Lastname</Text>,
                    placeholder: <Text id='input.lastname'>Lastname</Text>,
                    name: 'lastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.lastname,
                    showInList: true,
                    showInRaceAdminList: true,
                    noEdit: true,
                    csvField: true,
                },
                {
                    title: <Text id='input.sportsClub'>SportsClub</Text>,
                    placeholder: <Text id='input.sportsClub'>SportsClub</Text>,
                    name: 'sportsClub',
                    type: 'text',
                    icon: 'fa-solid fa-people-group',
                    getValue: obj => obj.sportsClub,
                    showInList: true,
                    showInRaceAdminList: true,
                    csvField: true,
                },
                {
                    title: <Text id='input.licenseNumber'>License Number</Text>,
                    placeholder: <Text id='input.licenseNumber'>License Number</Text>,
                    name: 'licenseNumber',
                    type: 'text',
                    icon: 'fa-duotone fa-id-card',
                    getValue: obj => obj.licenseNumber,
                    showInList: false,
                    csvField: true,
                },
                {
                    title: <Text id='input.kennel'>kennel</Text>,
                    placeholder: <Text id='input.kennel'>kennel</Text>,
                    name: 'kennel',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.kennel,
                    showInList: true,
                    csvField: true,
                },
                {
                    title: <Text id='input.birth'>Birth</Text>,
                    placeholder: <Text id='input.birth'>Birth</Text>,
                    name: 'birth',
                    type: 'date',
                    min: '1900-01-01',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.birth,
                    displayValue: obj => obj ? util.formatDate(obj, { locale: 'nb-NO' }) : null,
                    showInList: false,
                    showInRaceAdminList: true,
                    csvField: true,
                },
            ],
        },
        {
            name: <Text id='input.race-class-contestant-handler-title'>Race Class Contestant handler</Text>,
            key: 'Race Class Contestant handler',
            fields: [
                {
                    title: <Text id='input.handler1Firstname'>Handler 1 Firstname</Text>,
                    placeholder: <Text id='input.handler1Firstname'>Handler 1 Firstname</Text>,
                    name: 'handler1Firstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.handler1Firstname,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.handler1Lastname'>Handler 1 Lastname</Text>,
                    placeholder: <Text id='input.handler1Lastname'>Handler 1 Lastname</Text>,
                    name: 'handler1Lastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.handler1Lastname,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.handler1Cellphone'>Handler 1 Cellphone</Text>,
                    placeholder: <Text id='input.handler1Cellphone'>Handler 1 Cellphone</Text>,
                    name: 'handler1Cellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: obj => obj.handler1Cellphone,
                    showInList: false,
                    showInRaceAdminList: true,
                    isInternalField: true,
                },
                {
                    title: <Text id='input.handler2Firstname'>Handler 2 Firstname</Text>,
                    placeholder: <Text id='input.handler2Firstname'>Handler 2 Firstname</Text>,
                    name: 'handler2Firstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.handler2Firstname,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.handler2Lastname'>Handler 2 Lastname</Text>,
                    placeholder: <Text id='input.handler2Lastname'>Handler 2 Lastname</Text>,
                    name: 'handler2Lastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.handler2Lastname,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.handler2Cellphone'>Handler 2 Cellphone</Text>,
                    placeholder: <Text id='input.handler2Cellphone'>Handler 2 Cellphone</Text>,
                    name: 'handler2Cellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: obj => obj.handler2Cellphone,
                    showInList: false,
                    showInRaceAdminList: true,
                    isInternalField: true,
                },
            ],
        },
        {
            name: <Text id='input.race-class-contestant-companion-title'>Race Class Contestant companion</Text>,
            key: 'Race Class Contestant companion',
            fields: [
                {
                    conditionFunc: obj => {
                        if (obj.birth) {
                            const diff = util.dateDiff(obj.birth, new Date());
                            if (diff.years < 15) {
                                return true;
                            }
                        }
                        return false;
                    },
                    title: <Text id='input.companionFirstname'>companionFirstname</Text>,
                    placeholder: <Text id='input.companionFirstname'>companionFirstname</Text>,
                    name: 'companionFirstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.companionFirstname,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    conditionFunc: obj => {
                        if (obj.birth) {
                            const diff = util.dateDiff(obj.birth, new Date());
                            if (diff.years < 15) {
                                return true;
                            }
                        }
                        return false;
                    },
                    title: <Text id='input.companionLastname'>companionLastname</Text>,
                    placeholder: <Text id='input.companionLastname'>companionLastname</Text>,
                    name: 'companionLastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.companionLastname,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    conditionFunc: obj => {
                        if (obj.birth) {
                            const diff = util.dateDiff(obj.birth, new Date());
                            if (diff.years < 15) {
                                return true;
                            }
                        }
                        return false;
                    },
                    title: <Text id='input.companionCellphone'>companionCellphone</Text>,
                    placeholder: <Text id='input.companionCellphone'>companionCellphone</Text>,
                    name: 'companionCellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: obj => obj.companionCellphone,
                    showInList: false,
                    showInRaceAdminList: true,
                    isInternalField: true,
                },
                {
                    conditionFunc: obj => {
                        if (obj.birth) {
                            const diff = util.dateDiff(obj.birth, new Date());
                            if (diff.years < 15) {
                                return true;
                            }
                        }
                        return false;
                    },
                    title: <Text id='input.companionBirth'>companionBirth</Text>,
                    placeholder: <Text id='input.companionBirth'>companionBirth</Text>,
                    name: 'companionBirth',
                    type: 'date',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.companionBirth,
                    displayValue: obj => obj ? util.formatDate(obj, { locale: 'nb-NO' }) : null,
                    showInList: false,
                    showInRaceAdminList: true,
                },
            ],
        },
        {
            name: <Text id='input.race-class-contestant-payment-title'>Race Class Contestant payment</Text>,
            key: 'Race Class Contestant payment',
            fields: [
                {
                    title: <Text id='input.signupfeeAmount'>signupfeeAmount</Text>,
                    placeholder: <Text id='input.signupfeeAmount'>signupfeeAmount</Text>,
                    name: 'signupfeeAmount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.signupfeeAmount,
                    showInList: true,
                    isAdminField: true,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.signuppaymentAmount'>signuppaymentAmount</Text>,
                    placeholder: <Text id='input.signuppaymentAmount'>signuppaymentAmount</Text>,
                    name: 'signuppaymentAmount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.signuppaymentAmount,
                    showInList: true,
                    isAdminField: true,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.restPaymentAmount'>restPaymentAmount</Text>,
                    placeholder: <Text id='input.restPaymentAmount'>restPaymentAmount</Text>,
                    name: 'restPaymentAmount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.restPaymentAmount,
                    showInList: true,
                    isAdminField: true,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.seedingPaymentAmount'>seedingPaymentAmount</Text>,
                    placeholder: <Text id='input.seedingPaymentAmount'>seedingPaymentAmount</Text>,
                    name: 'seedingPaymentAmount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.seedingPaymentAmount,
                    showInList: true,
                    isAdminField: true,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.paymentMethod'>paymentMethod</Text>,
                    placeholder: <Text id='input.paymentMethod'>paymentMethod</Text>,
                    name: 'paymentMethod',
                    type: 'select',
                    datalist: [['cash', 'Kontant'], ['card', 'Kort'], ['invoice', 'Faktura'], ['account', 'Konto'], ['creditNote', 'Kreditnota']],
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.paymentMethod,
                    showInList: true,
                    isAdminField: true,
                },

                {
                    title: <Text id='input.paidSignupfee'>PaidSignupfee</Text>,
                    placeholder: <Text id='input.paidSignupfee'>PaidSignupfee</Text>,
                    name: 'paidSignupfee',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.paidSignupfee,
                    showInList: true,
                },
                {
                    title: <Text id='input.paidSignup'>PaidSignup</Text>,
                    placeholder: <Text id='input.paidSignup'>PaidSignup</Text>,
                    name: 'paidSignup',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.paidSignup,
                    showInList: true,
                },
                {
                    title: <Text id='input.paidRest'>paidRest</Text>,
                    placeholder: <Text id='input.paidRest'>paidRest</Text>,
                    name: 'paidRest',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.paidRest,
                    showInList: true,
                },
                {
                    title: <Text id='input.paidSeeding'>paidSeeding</Text>,
                    placeholder: <Text id='input.paidSeeding'>paidSeeding</Text>,
                    name: 'paidSeeding',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.paidSeeding,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.race-class-contestant-raceinfo-title'>Race Class Contestant raceinfo</Text>,
            key: 'Race Class Contestant raceinfo',
            fields: [
                {
                    title: <Text id='input.bib'>Bib</Text>,
                    placeholder: <Text id='input.bib'>Bib</Text>,
                    name: 'bib',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.bib,
                    showInList: true,
                    showInRaceAdminList: true,
                    csvField: true,
                },
                {
                    title: <Text id='input.startTime'>StartTime</Text>,
                    placeholder: <Text id='input.startTime'>StartTime</Text>,
                    name: 'startTime',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.startTime,
                    showInList: true,
                    showInRaceAdminList: true,
                    csvField: true,
                },
                {
                    title: <Text id='input.declarationDate'>DeclarationDate</Text>,
                    placeholder: <Text id='input.declarationDate'>DeclarationDate</Text>,
                    name: 'declarationDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.declarationDate,
                    showInList: false,
                },
                {
                    title: <Text id='input.termsAndAgreement'>TermsAndAgreement</Text>,
                    placeholder: <Text id='input.termsAndAgreement'>TermsAndAgreement</Text>,
                    name: 'termsAndAgreement',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.termsAndAgreement,
                    showInList: false,
                },
                {
                    title: <Text id='input.declaration'>Declaration</Text>,
                    placeholder: <Text id='input.declaration'>Declaration</Text>,
                    name: 'declaration',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.declaration,
                    showInList: false,
                },
                {
                    title: <Text id='input.license'>License</Text>,
                    placeholder: <Text id='input.license'>License</Text>,
                    name: 'license',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.license,
                    showInList: false,
                },
                {
                    title: <Text id='input.preCheck'>PreCheck</Text>,
                    placeholder: <Text id='input.preCheck'>PreCheck</Text>,
                    name: 'preCheck',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.preCheck,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-class-contestant-name-address-title'>Race Class Contestant name & address</Text>,
            key: 'Race Class Contestant name & address',
            fields: [
                {
                    title: <Text id='input.address'>Address</Text>,
                    placeholder: <Text id='input.address'>Address</Text>,
                    name: 'address',
                    type: 'text',
                    icon: 'fa-solid fa-address-card',
                    getValue: obj => obj.address,
                    showInList: false,
                    isInternalField: true,
                    csvField: true,
                },
                {
                    title: <Text id='input.postalcode'>Postalcode</Text>,
                    placeholder: <Text id='input.postalcode'>Postalcode</Text>,
                    name: 'postalcode',
                    type: 'text',
                    icon: 'fa-solid fa-address-card',
                    getValue: obj => obj.postalcode,
                    showInList: false,
                    isInternalField: true,
                    csvField: true,
                    callback: async (obj, updateValue) => {
                        if (obj.postalcode && obj.postalcode.length === 4) {
                            const response = await postnummerLookup(obj.postalcode);

                            if (response) {
                                const place = response[0]?.poststed;
                                updateValue({
                                    name: 'postalplace',
                                    value: place,
                                    id: obj.id,
                                });
                            }
                        }
                    },
                },
                {
                    title: <Text id='input.postalplace'>Postalplace</Text>,
                    placeholder: <Text id='input.postalplace'>Postalplace</Text>,
                    name: 'postalplace',
                    type: 'text',
                    icon: 'fa-solid fa-address-card',
                    getValue: obj => obj.postalplace,
                    showInList: false,
                    csvField: true,
                },
                {
                    title: <Text id='input.country'>Country</Text>,
                    placeholder: <Text id='input.country'>Country</Text>,
                    name: 'country',
                    type: 'text',
                    icon: 'fa-solid fa-globe',
                    getValue: obj => obj.country,
                    showInList: false,
                    showInRaceAdminList: true,
                    csvField: true,
                },
            ],
        },
        {
            name: <Text id='input.race-class-contestant-contact-title'>Race Class Contestant contact info</Text>,
            key: 'Race Class Contestant contact info',
            fields: [
                {
                    title: <Text id='input.email'>Email</Text>,
                    placeholder: <Text id='input.email'>Email</Text>,
                    name: 'email',
                    type: 'text',
                    icon: 'fa-solid fa-envelope',
                    getValue: obj => obj.email,
                    showInList: true,
                    isInternalField: true,
                    csvField: true,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.email && !util.validateEmail(obj.email)) {
                            const invalidChars = findInvalidEmailChars(obj.email);
                            let messages = [];
                            if (invalidChars.length > 0) {
                                messages = invalidChars.map(obj => {
                                    return `Character "${obj.char}" at position ${obj.index} is not allowed.`
                                });
                            }
                            holdingStore.setFeedbackError('email', `Invalid email. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('email', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('email', '');
                        holdingStore.setFeedbackButton('email', null, null);
                    },
                },
                {
                    title: <Text id='input.cellphone'>Cellphone</Text>,
                    placeholder: <Text id='input.cellphone'>Cellphone</Text>,
                    name: 'cellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: obj => obj.cellphone,
                    showInList: true,
                    isInternalField: true,
                    noEdit: true,
                    csvField: true,
                },
                {
                    title: <Text id='input.cellphone'>Cellphone</Text>,
                    placeholder: <Text id='input.cellphone'>Cellphone</Text>,
                    name: 'cellphone',
                    type: 'text',
                    icon: 'fa-solid fa-mobile',
                    getValue: obj => obj.cellphone,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.facebook'>Facebook</Text>,
                    placeholder: <Text id='input.facebook'>Facebook</Text>,
                    name: 'facebook',
                    type: 'text',
                    icon: 'fa-brands fa-facebook',
                    getValue: obj => obj.facebook,
                    showInList: false,
                },
                {
                    title: <Text id='input.instagram'>Instagram</Text>,
                    placeholder: <Text id='input.instagram'>Instagram</Text>,
                    name: 'instagram',
                    type: 'text',
                    icon: 'fa-brands fa-instagram',
                    getValue: obj => obj.instagram,
                    showInList: false,
                },
                {
                    title: <Text id='input.web'>web</Text>,
                    placeholder: <Text id='input.web'>web</Text>,
                    name: 'web',
                    type: 'text',
                    icon: 'fa-solid fa-link',
                    getValue: obj => obj.web,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-class-contestant-settings-title'>Race Class Contestant settings</Text>,
            key: 'Race Class Contestant settings',
            fields: [
                {
                    title: <Text id='input.dogs'>Dogs</Text>,
                    placeholder: <Text id='input.dogs'>Dogs</Text>,
                    name: 'dogs',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.dogs,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.obligRest'>ObligRest</Text>,
                    placeholder: <Text id='input.obligRest'>ObligRest</Text>,
                    name: 'obligRest',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.obligRest,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.price'>Price</Text>,
                    placeholder: <Text id='input.price'>Price</Text>,
                    name: 'price',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.price,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.maxTrackIdxToSkip'>MaxTrackIdxToSkip</Text>,
                    placeholder: <Text id='input.maxTrackIdxToSkip'>MaxTrackIdxToSkip</Text>,
                    name: 'maxTrackIdxToSkip',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.maxTrackIdxToSkip,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-class-contestant-qualifying-title'>Race Class qualifying</Text>,
            key: 'Race Class qualifying',
            fields: [
                {
                    title: <Text id='input.veteranRookie'>VeteranRookie</Text>,
                    placeholder: <Text id='input.veteranRookie'>VeteranRookie</Text>,
                    name: 'veteranRookie',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.veteranRookie ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.qualifying'>Qualifying</Text>,
                    placeholder: <Text id='input.qualifying'>Qualifying</Text>,
                    name: 'qualifying',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.qualifying,
                    showInList: false,
                    showInRaceAdminList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-class-contestant-about-title'>Race Class About yourself</Text>,
            key: 'Race Class About yourself',
            fields: [
                {
                    title: <Text id='input.occupation'>Occupation</Text>,
                    placeholder: <Text id='input.occupation'>Occupation</Text>,
                    name: 'occupation',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.occupation,
                    showInList: false,
                },

                {
                    title: <Text id='input.hobbies'>Hobbies</Text>,
                    placeholder: <Text id='input.hobbies'>Hobbies</Text>,
                    name: 'hobbies',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hobbies,
                    showInList: false,
                },
                {
                    title: <Text id='input.funFacts'>FunFacts</Text>,
                    placeholder: <Text id='input.funFacts'>FunFacts</Text>,
                    name: 'funFacts',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.funFacts,
                    showInList: false,
                },
                {
                    title: <Text id='input.ambitions'>Ambitions</Text>,
                    placeholder: <Text id='input.ambitions'>Ambitions</Text>,
                    name: 'ambitions',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.ambitions,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-class-contestant-about-media-title'>Race Class About Media</Text>,
            key: 'Race Class About Media',
            fields: [
                {
                    title: <Text id='input.localNewsPaper'>LocalNewsPaper</Text>,
                    placeholder: <Text id='input.localNewsPaper'>LocalNewsPaper</Text>,
                    name: 'localNewsPaper',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.localNewsPaper,
                    showInList: false,
                },
                {
                    title: <Text id='input.okToShareInfoWithMedia'>OkToShareInfoWithMedia</Text>,
                    placeholder: <Text id='input.okToShareInfoWithMedia'>OkToShareInfoWithMedia</Text>,
                    name: 'okToShareInfoWithMedia',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.okToShareInfoWithMedia ? 1 : 0,
                    showInList: false,
                },
                {
                    title: <Text id='input.rnb'>Rnb</Text>,
                    placeholder: <Text id='input.rnb'>Rnb</Text>,
                    name: 'rnb',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.rnb ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.sponsors'>Sponsors</Text>,
                    placeholder: <Text id='input.sponsors'>Sponsors</Text>,
                    name: 'sponsors',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.sponsors,
                    showInList: false,
                },
                {
                    title: <Text id='input.leadDogs'>LeadDogs</Text>,
                    placeholder: <Text id='input.leadDogs'>LeadDogs</Text>,
                    name: 'leadDogs',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.leadDogs,
                    showInList: false,
                },
                {
                    title: <Text id='input.oldMerits'>OldMerits</Text>,
                    placeholder: <Text id='input.oldMerits'>OldMerits</Text>,
                    name: 'oldMerits',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.oldMerits,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-class-contestant-media-title'>Race Class Contestant media</Text>,
            key: 'Race Class Contestant media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                    showInRaceAdminList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: true,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                    hideOnCreate: true,
                },

            ],
        },
    ],
    raceClassSignup: [
        {
            name: <Text id='input.race-class-signup-basics-title'>Basic info</Text>,
            help: <Text id='input.race-class-signup-basics-help'>Basic info about you. Please fill in all the fields.</Text>,
            key: 'Basic info',
            hasAllRequiredFields: (obj, fields) => {
                const requiredFields = fields.filter(f => f.isRequired);
                return requiredFields.every(f => obj[f.name]);
            },
            fields: [
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.race'>Race</Text>,
                    placeholder: <Text id='input.race'>Race</Text>,
                    name: 'race',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.race,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.raceClass'>RaceClass</Text>,
                    placeholder: <Text id='input.raceClass'>RaceClass</Text>,
                    name: 'raceClass',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.raceClass,
                    showInList: true,
                    isAdminField: true,
                },

                {
                    title: <Text id='input.firstname'>Firstname</Text>,
                    placeholder: <Text id='input.firstname'>Firstname</Text>,
                    name: 'firstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.firstname,
                    showInList: true,
                    noEdit: true,
                    isRequired: true,
                },
                {
                    title: <Text id='input.lastname'>Lastname</Text>,
                    placeholder: <Text id='input.lastname'>Lastname</Text>,
                    name: 'lastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.lastname,
                    showInList: true,
                    noEdit: true,
                    isRequired: true,
                },
                {
                    title: <Text id='input.sportsClub'>SportsClub</Text>,
                    placeholder: <Text id='input.sportsClub'>SportsClub</Text>,
                    name: 'sportsClub',
                    type: 'text',
                    icon: 'fa-solid fa-people-group',
                    getValue: obj => obj.sportsClub,
                    showInList: true,
                    isRequired: true,
                },
                {
                    title: <Text id='input.licenseNumber'>License Number</Text>,
                    placeholder: <Text id='input.licenseNumber'>License Number</Text>,
                    name: 'licenseNumber',
                    type: 'text',
                    icon: 'fa-duotone fa-id-card',
                    getValue: obj => obj.licenseNumber,
                    showInList: true,
                },
                {
                    title: <Text id='input.kennel'>kennel</Text>,
                    placeholder: <Text id='input.kennel'>kennel</Text>,
                    name: 'kennel',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.kennel,
                    showInList: true,
                },
                {
                    title: <Text id='input.birth'>Birth</Text>,
                    placeholder: <Text id='input.birth'>Birth</Text>,
                    name: 'birth',
                    type: 'date',
                    min: '1900-01-01',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.birth,
                    displayValue: obj => obj ? util.formatDate(obj, { locale: 'nb-NO' }) : null,
                    showInList: false,
                    isRequired: true,
                },

                {
                    conditionFunc: obj => {
                        if (obj.birth) {
                            const diff = util.dateDiff(obj.birth, new Date());
                            if (diff.years < 15) {
                                return true;
                            }
                        }
                        return false;
                    },
                    title: <Text id='input.companionFirstname'>companionFirstname</Text>,
                    placeholder: <Text id='input.companionFirstname'>companionFirstname</Text>,
                    name: 'companionFirstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.companionFirstname,
                    showInList: true,
                    showInRaceAdminList: true,
                },
                {
                    conditionFunc: obj => {
                        if (obj.birth) {
                            const diff = util.dateDiff(obj.birth, new Date());
                            if (diff.years < 15) {
                                return true;
                            }
                        }
                        return false;
                    },
                    title: <Text id='input.companionLastname'>companionLastname</Text>,
                    placeholder: <Text id='input.companionLastname'>companionLastname</Text>,
                    name: 'companionLastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.companionLastname,
                    showInList: true,
                    showInRaceAdminList: true,
                },
                {
                    conditionFunc: obj => {
                        if (obj.birth) {
                            const diff = util.dateDiff(obj.birth, new Date());
                            if (diff.years < 15) {
                                return true;
                            }
                        }
                        return false;
                    },
                    title: <Text id='input.companionCellphone'>companionCellphone</Text>,
                    placeholder: <Text id='input.companionCellphone'>companionCellphone</Text>,
                    name: 'companionCellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: obj => obj.companionCellphone,
                    showInList: true,
                    showInRaceAdminList: true,
                },
                {
                    conditionFunc: obj => {
                        if (obj.birth) {
                            const diff = util.dateDiff(obj.birth, new Date());
                            if (diff.years < 15) {
                                return true;
                            }
                        }
                        return false;
                    },
                    title: <Text id='input.companionBirth'>companionBirth</Text>,
                    placeholder: <Text id='input.companionBirth'>companionBirth</Text>,
                    name: 'companionBirth',
                    type: 'date',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.companionBirth,
                    displayValue: obj => obj ? util.formatDate(obj, { locale: 'nb-NO' }) : null,
                    showInList: true,
                    showInRaceAdminList: true,
                },
            ],
        },
        {
            name: <Text id='input.race-class-signup-contact-title'>Contact info</Text>,
            help: <Text id='input.race-class-signup-contact-help'>Information about how we can get in contact with you. Also add information about your precense in social media.</Text>,
            key: 'Contact info',
            hasAllRequiredFields: (obj, fields) => {
                const requiredFields = fields.filter(f => f.isRequired);
                return requiredFields.every(f => obj[f.name]);
            },
            fields: [
                {
                    title: <Text id='input.address'>Address</Text>,
                    placeholder: <Text id='input.address'>Address</Text>,
                    name: 'address',
                    type: 'text',
                    icon: 'fa-solid fa-address-card',
                    getValue: obj => obj.address,
                    showInList: false,
                },
                {
                    title: <Text id='input.postalcode'>Postalcode</Text>,
                    placeholder: <Text id='input.postalcode'>Postalcode</Text>,
                    name: 'postalcode',
                    type: 'text',
                    icon: 'fa-solid fa-address-card',
                    getValue: obj => obj.postalcode,
                    showInList: false,
                    callback: async (obj, updateValue) => {
                        if (obj.postalcode && obj.postalcode.length === 4) {
                            const response = await postnummerLookup(obj.postalcode);

                            if (response) {
                                const place = response[0]?.poststed;
                                updateValue({
                                    name: 'postalplace',
                                    value: place,
                                    id: obj.id,
                                });
                            }
                        }
                    },
                },
                {
                    title: <Text id='input.postalplace'>Postalplace</Text>,
                    placeholder: <Text id='input.postalplace'>Postalplace</Text>,
                    name: 'postalplace',
                    type: 'text',
                    icon: 'fa-solid fa-address-card',
                    getValue: obj => obj.postalplace,
                    showInList: true,
                },
                {
                    title: <Text id='input.country'>Country</Text>,
                    placeholder: <Text id='input.country'>Country</Text>,
                    name: 'country',
                    type: 'text',
                    icon: 'fa-solid fa-globe',
                    getValue: obj => obj.country,
                    showInList: true,
                    isRequired: true,

                },
                {
                    title: <Text id='input.email'>Email</Text>,
                    placeholder: <Text id='input.email'>Email</Text>,
                    name: 'email',
                    type: 'text',
                    icon: 'fa-solid fa-envelope',
                    getValue: obj => obj.email,
                    showInList: true,
                    isRequired: true,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.email && !util.validateEmail(obj.email)) {
                            const invalidChars = findInvalidEmailChars(obj.email);
                            let messages = [];
                            if (invalidChars.length > 0) {
                                messages = invalidChars.map(obj => {
                                    return `Character "${obj.char}" at position ${obj.index} is not allowed.`
                                });
                            }
                            holdingStore.setFeedbackError('email', `Invalid email. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('email', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('email', '');
                        holdingStore.setFeedbackButton('email', null, null);
                    },
                },
                {
                    title: <Text id='input.cellphone'>Cellphone</Text>,
                    placeholder: <Text id='input.cellphone'>Cellphone</Text>,
                    name: 'cellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: obj => obj.cellphone,
                    showInList: true,
                    noEdit: true,
                    isRequired: true,
                },
                {
                    title: <Text id='input.facebook'>Facebook</Text>,
                    placeholder: <Text id='input.facebook'>Facebook</Text>,
                    name: 'facebook',
                    type: 'text',
                    icon: 'fa-brands fa-facebook',
                    getValue: obj => obj.facebook,
                    showInList: false,
                },
                {
                    title: <Text id='input.instagram'>Instagram</Text>,
                    placeholder: <Text id='input.instagram'>Instagram</Text>,
                    name: 'instagram',
                    type: 'text',
                    icon: 'fa-brands fa-instagram',
                    getValue: obj => obj.instagram,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.race-class-signup-qualifying-title'>Race qualifying</Text>,
            help: <Text id='input.race-class-signup-qualifying-help'>Are you a veteran in this race class? Describe your qualifying for this race.</Text>,
            key: 'Race qualifying',
            hasAllRequiredFields: (obj, fields) => {
                const requiredFields = fields.filter(f => f.isRequired);
                return requiredFields.every(f => obj[f.name]);
            },
            fields: [
                {
                    title: <Text id='input.veteranRookie'>VeteranRookie</Text>,
                    placeholder: <Text id='input.veteranRookie'>VeteranRookie</Text>,
                    help: <Text id='input.veteranRookie-help'>Did you complete the current race class before?</Text>,
                    name: 'veteranRookie',
                    type: 'toggle',
                    icon: 'fa-solid fa-shield-dog',
                    getValue: obj => obj.veteranRookie ? 1 : 0,
                    showInList: true,
                    showInRaceAdminList: true,
                    // isRequired: true,
                },
                {
                    title: <Text id='input.qualifying'>Qualifying</Text>,
                    placeholder: <Text id='input.qualifying'>Qualifying</Text>,
                    help: <Text id='input.qualifying-help'>Describe your qualifying for this race.</Text>,
                    name: 'qualifying',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.qualifying,
                    showInList: true,
                    isRequired: true,
                },
            ],
        },
        {
            name: <Text id='input.race-class-signup-about-media-title'>About Media</Text>,
            help: <Text id='input.race-class-signup-about-media-help'>Some information to help us promote the race in a better way. Choose if you allow us to share basic info with the media.</Text>,
            key: 'About Media',
            hasAllRequiredFields: (obj, fields) => {
                const requiredFields = fields.filter(f => f.isRequired);
                return requiredFields.every(f => obj[f.name]);
            },
            fields: [
                {
                    title: <Text id='input.localNewsPaper'>LocalNewsPaper</Text>,
                    placeholder: <Text id='input.localNewsPaper'>LocalNewsPaper</Text>,
                    name: 'localNewsPaper',
                    type: 'text',
                    icon: 'fa-solid fa-newspaper',
                    getValue: obj => obj.localNewsPaper,
                    showInList: true,
                },
                {
                    title: <Text id='input.okToShareInfoWithMedia'>Ok to share info with media</Text>,
                    placeholder: <Text id='input.okToShareInfoWithMedia'>Ok to share info with media</Text>,
                    help: <Text id='input.okToShareInfoWithMedia-help'>This will make it easier for the local press to write about you and your team.</Text>,
                    name: 'okToShareInfoWithMedia',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.okToShareInfoWithMedia ? 1 : 0,
                    showInList: true,
                    // isRequired: true,
                },
                {
                    title: <Text id='input.rnb'>RNB team</Text>,
                    placeholder: <Text id='input.rnb'>RNB team</Text>,
                    help: <Text id='input.rnb-help'>Are you racing a pure breed, then you should check this field.</Text>,
                    name: 'rnb',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.rnb ? 1 : 0,
                    showInList: true,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.hasPreRaceCheck'>hasPreRaceCheck</Text>,
                    placeholder: <Text id='input.hasPreRaceCheck'>hasPreRaceCheck</Text>,
                    name: 'hasPreRaceCheck',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hasPreRaceCheck ? 1 : 0,
                    showInList: false,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.sponsors'>Sponsors</Text>,
                    placeholder: <Text id='input.sponsors'>Sponsors</Text>,
                    help: <Text id='input.sponsors-help'>List your sponsors to promote them on your presentation page.</Text>,
                    name: 'sponsors',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.sponsors,
                    showInList: true,
                },
                {
                    title: <Text id='input.leadDogs'>lead Dogs</Text>,
                    placeholder: <Text id='input.leadDogs'>lead Dogs</Text>,
                    name: 'leadDogs',
                    type: 'text',
                    icon: 'fa-solid fa-dog',
                    getValue: obj => obj.leadDogs,
                    showInList: true,
                },
                {
                    title: <Text id='input.oldMerits'>old merits</Text>,
                    placeholder: <Text id='input.oldMerits'>old merits</Text>,
                    help: <Text id='input.oldMerits-help'>Describe your old merits and things you have achived in the sport.</Text>,
                    name: 'oldMerits',
                    type: 'textarea',
                    icon: 'fa-solid fa-medal',
                    getValue: obj => obj.oldMerits,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.race-class-signup-about-title'>About yourself</Text>,
            help: <Text id='input.race-class-signup-about-help'>Some information about you. This can be used by the speakers and in our presentation of the race.</Text>,
            key: 'About yourself',
            hasAllRequiredFields: (obj, fields) => {
                const requiredFields = fields.filter(f => f.isRequired);
                return requiredFields.every(f => obj[f.name]);
            },
            fields: [
                {
                    title: <Text id='input.occupation'>Occupation</Text>,
                    placeholder: <Text id='input.occupation'>Occupation</Text>,
                    name: 'occupation',
                    type: 'text',
                    icon: 'fa-solid fa-briefcase',
                    getValue: obj => obj.occupation,
                    showInList: true,
                },
                {
                    title: <Text id='input.hobbies'>Hobbies</Text>,
                    placeholder: <Text id='input.hobbies'>Hobbies</Text>,
                    help: <Text id='input.hobbies-help'>List your main hobbies.</Text>,
                    name: 'hobbies',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.hobbies,
                    showInList: true,
                },
                {
                    title: <Text id='input.funFacts'>fun facts</Text>,
                    placeholder: <Text id='input.funFacts'>fun facts</Text>,
                    help: <Text id='input.funFacts-help'>Describe some fun facts about yourself.</Text>,
                    name: 'funFacts',
                    type: 'textarea',
                    icon: 'fa-solid fa-face-smile',
                    getValue: obj => obj.funFacts,
                    showInList: true,
                },
                {
                    title: <Text id='input.ambitions'>Ambitions</Text>,
                    placeholder: <Text id='input.ambitions'>Ambitions</Text>,
                    help: <Text id='input.ambitions-help'>Your ambitions for this race.</Text>,
                    name: 'ambitions',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.ambitions,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.race-class-signup-images-file-links-title'>Images, files and links</Text>,
            help: <Text id='input.race-class-signup-images-file-links-help'>Upload some nice images of you. This is going to be used on the webpage and in other presentations of the race.</Text>,
            key: 'Images, files and links',
            hasAllRequiredFields: (obj, fields) => {
                const requiredFields = fields.filter(f => f.isRequired);
                return requiredFields.every(f => obj[f.name]);
            },
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    help: <Text id='input.images-help'>Upload a nice image of yourself.</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                    showInRaceAdminList: true,
                },
            ],
        },
        {
            name: <Text id='input.race-class-contestant-handler-title'>Race Class Contestant handler</Text>,
            help: <Text id='input.race-class-contestant-handler-help'>Add information about your handler. In some classes it is required to have 2 handlers. If you don't have this sorted out yet, you can add this later.</Text>,
            key: 'Race Class Contestant handler',
            hasAllRequiredFields: (obj, fields) => {
                const requiredFields = fields.filter(f => f.isRequired);
                return requiredFields.every(f => obj[f.name]);
            },
            fields: [
                {
                    title: <Text id='input.handler1Firstname'>Handler 1 Firstname</Text>,
                    placeholder: <Text id='input.handler1Firstname'>Handler 1 Firstname</Text>,
                    name: 'handler1Firstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.handler1Firstname,
                    showInList: true,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.handler1Lastname'>Handler 1 Lastname</Text>,
                    placeholder: <Text id='input.handler1Lastname'>Handler 1 Lastname</Text>,
                    name: 'handler1Lastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.handler1Lastname,
                    showInList: true,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.handler1Cellphone'>Handler 1 Cellphone</Text>,
                    placeholder: <Text id='input.handler1Cellphone'>Handler 1 Cellphone</Text>,
                    name: 'handler1Cellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: obj => obj.handler1Cellphone,
                    showInList: true,
                    showInRaceAdminList: true,
                    isInternalField: true,
                },
                {
                    title: <Text id='input.handler2Firstname'>Handler 2 Firstname</Text>,
                    placeholder: <Text id='input.handler2Firstname'>Handler 2 Firstname</Text>,
                    name: 'handler2Firstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.handler2Firstname,
                    showInList: true,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.handler2Lastname'>Handler 2 Lastname</Text>,
                    placeholder: <Text id='input.handler2Lastname'>Handler 2 Lastname</Text>,
                    name: 'handler2Lastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.handler2Lastname,
                    showInList: true,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.handler2Cellphone'>Handler 2 Cellphone</Text>,
                    placeholder: <Text id='input.handler2Cellphone'>Handler 2 Cellphone</Text>,
                    name: 'handler2Cellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: obj => obj.handler2Cellphone,
                    showInList: true,
                    showInRaceAdminList: true,
                    isInternalField: true,
                },
            ],
        },
        // {
        //     name: 'Dogs',
        //     fields: [
        //         {
        //             title: <Text id='input.dogs'>Dogs</Text>,
        //             placeholder: <Text id='input.dogs'>Dogs</Text>,
        //             name: 'dogs',
        //             type: 'arrayObject',
        //             icon: 'fa-solid fa-tag',
        //             getValue: obj => obj.dogs,
        //             datalist: [
        //                 'url', 'name', 'description'
        //             ],
        //             showInList: false,
        //             displayHeight: '60px',
        //             hideOnCreate: false,
        //         },
        //     ],
        // },
    ],
    raceResult: [
        {
            name: <Text id='input.raceresult-basics-title'>Race Result basics</Text>,
            key: 'Race Result basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                    csvField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.race'>race</Text>,
                    placeholder: <Text id='input.race'>race</Text>,
                    name: 'race',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.race,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.raceClass'>raceClass</Text>,
                    placeholder: <Text id='input.raceClass'>raceClass</Text>,
                    name: 'raceClass',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.raceClass,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.raceClassContestant'>raceClassContestant</Text>,
                    placeholder: <Text id='input.raceClassContestant'>raceClassContestant</Text>,
                    name: 'raceClassContestant',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.raceClassContestant,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.bib'>bib</Text>,
                    placeholder: <Text id='input.bib'>bib</Text>,
                    name: 'bib',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.bib,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.checkpoint'>checkpoint</Text>,
                    placeholder: <Text id='input.checkpoint'>checkpoint</Text>,
                    name: 'checkpoint',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.checkpoint,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.timestamp'>timestamp</Text>,
                    placeholder: <Text id='input.timestamp'>timestamp</Text>,
                    name: 'timestamp',
                    type: 'datetime-local',
                    step: 1,
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.timestamp,
                    displayValue: obj => obj ? util.formatDate(obj, { locale: 'nb-NO' }) : null,
                    showInList: true,
                    showInRaceAdminList: true,
                },
                {
                    title: <Text id='input.eventType'>eventType</Text>,
                    placeholder: <Text id='input.eventType'>eventType</Text>,
                    name: 'eventType',
                    type: 'select',
                    icon: 'fa-duotone fa-calendar-check',
                    getValue: obj => obj.eventType,
                    datalist: [['in', 'In'], ['out', 'Out'], ['scratch', 'Scratch']],
                    // datalistFieldKey: e => e.id,
                    // datalistFieldVal: e => `${e.firstname} ${e.lastname}`,
                    showInList: false,
                },
                {
                    title: <Text id='input.event'>event</Text>,
                    placeholder: <Text id='input.event'>event</Text>,
                    name: 'event',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.eventType,
                    displayValue: (val, obj) => {
                        return (<>
                            {obj.eventType}
                        </>);
                    },
                    showInList: true,
                    isReadOnly: true,
                },
                {
                    title: <Text id='input.dogs'>dogs</Text>,
                    placeholder: <Text id='input.dogs'>dogs</Text>,
                    name: 'dogs',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.dogs,
                    showInList: true,
                },
                {
                    title: <Text id='input.optionalRest'>optionalRest</Text>,
                    placeholder: <Text id='input.optionalRest'>optionalRest</Text>,
                    name: 'optionalRest',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.optionalRest,
                    showInList: true,
                },
                {
                    title: <Text id='input.description'>description</Text>,
                    placeholder: <Text id='input.description'>description</Text>,
                    name: 'description',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.description,
                    showInList: true,
                    isAdminField: true,
                },
            ],
        },
        {
            name: <Text id='input.raceresult-media-title'>RaceResult media</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'raceresult media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                }
            ],
        },
    ],
    timeTracker: [
        {
            name: <Text id='input.timetracker-basics-title'>TimeTracker basics</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'TimeTracker basics',
            fields: [
                {
                    title: <Text id='input.client'>Client</Text>,
                    placeholder: <Text id='input.client'>Client</Text>,
                    name: 'client',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.client,
                    showInList: true,
                },
                {
                    title: <Text id='input.project'>Project</Text>,
                    placeholder: <Text id='input.project'>Project</Text>,
                    name: 'project',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.project,
                    showInList: true,
                },
                {
                    title: <Text id='input.work'>Work</Text>,
                    placeholder: <Text id='input.work'>Work</Text>,
                    name: 'work',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.work,
                    showInList: true,
                },
                {
                    title: <Text id='input.description'>description</Text>,
                    placeholder: <Text id='input.description'>description</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.description,
                    showInList: false,
                },
                {
                    title: <Text id='input.startTime'>startTime</Text>,
                    placeholder: <Text id='input.startTime'>startTime</Text>,
                    name: 'startTime',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.startTime || null,
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.endTime'>endTime</Text>,
                    placeholder: <Text id='input.endTime'>endTime</Text>,
                    name: 'endTime',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.endTime || null,
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.totalTime'>totalTime</Text>,
                    placeholder: <Text id='input.totalTime'>totalTime</Text>,
                    name: 'totalTime',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => {
                        const timeUsed = util.dateDiff(obj.startTime, obj.endTime || new Date());
                        return `${util.secToHms(timeUsed.seconds, true)}`;
                    },
                    displayValue: (val, obj) => {
                        const timeUsed = util.dateDiff(obj.startTime, obj.endTime || new Date());
                        return <>
                            {!obj.endTime ? <i class='fa-duotone fa-stopwatch fa-beat-fade' /> : ''} {util.secToHms(timeUsed.seconds, true)}
                        </>;
                    },
                    showInList: true,
                    isReadOnly: true,
                },

                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    name: 'tags',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'tags',
                    getValue: obj => obj.tags,
                    datalist: [
                        'name',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
            ],
        },
        {
            name: <Text id='input.timetracker-media-title'>timetracker media</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'timetracker media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                }
            ],
        },
    ],

    contact: [
        {
            name: <Text id='input.contact-basics-title'>contact basics</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'contact basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: user => user.id || null,
                    showInList: true,
                    isReadOnly: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: user => user.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'offline',
                    placeholder: 'offline',
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.name'>name</Text>,
                    placeholder: <Text id='input.name'>name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.name || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.firstname'>Firstname</Text>,
                    placeholder: <Text id='input.firstname'>Firstname</Text>,
                    name: 'firstname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.firstname || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.lastname'>Lastname</Text>,
                    placeholder: <Text id='input.lastname'>Lastname</Text>,
                    name: 'lastname',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: user => user.lastname,
                    showInList: true,
                },
                {
                    title: <Text id='input.email'>Email</Text>,
                    placeholder: <Text id='input.email'>Email</Text>,
                    name: 'email',
                    type: 'text',
                    icon: 'fa-solid fa-envelope',
                    getValue: user => user.email,
                    showInList: true,
                    checkValue: (obj, holdingStore) => {
                        // console.log('checkValue', obj, holdingStore.name);
                        if (obj.email && !util.validateEmail(obj.email)) {
                            const invalidChars = findInvalidEmailChars(obj.email);
                            let messages = [];
                            if (invalidChars.length > 0) {
                                messages = invalidChars.map(obj => {
                                    return `Character "${obj.char}" at position ${obj.index} is not allowed.`
                                });
                            }
                            holdingStore.setFeedbackError('email', `Invalid email. ${messages.map(e => `- ${e}`).join('\n')}`);
                        } else {
                            holdingStore.setFeedbackError('email', '');
                        }
                    },
                    before: async (obj, holdingStore) => {
                        // console.log('before', obj, holdingStore.name);
                        holdingStore.setFeedback('email', '');
                        holdingStore.setFeedbackButton('email', null, null);
                    },
                },
                {
                    title: <Text id='input.cellphone'>Cellphone</Text>,
                    placeholder: <Text id='input.cellphone'>Cellphone</Text>,
                    name: 'cellphone',
                    type: 'phone',
                    icon: 'fa-solid fa-mobile',
                    getValue: user => user.cellphone,
                    showInList: true,
                },
                {
                    title: <Text id='input.description'>description</Text>,
                    placeholder: <Text id='input.description'>description</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-text',
                    getValue: user => user.description,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.contact-address-title'>contact address</Text>,
            key: 'contact address',
            fields: [
                {
                    title: <Text id='input.address'>Address</Text>,
                    placeholder: <Text id='input.address'>Address</Text>,
                    name: 'address',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.address,
                    showInList: false,
                },
                {
                    title: <Text id='input.postalcode'>Postalcode</Text>,
                    placeholder: <Text id='input.postalcode'>Postalcode</Text>,
                    name: 'postalcode',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.postalcode,
                    showInList: false,
                    callback: async (obj, updateValue) => {
                        if (obj.postalcode && obj.postalcode.length === 4) {
                            const response = await postnummerLookup(obj.postalcode);

                            if (response) {
                                const place = response[0]?.poststed;
                                updateValue({
                                    name: 'postalplace',
                                    value: place,
                                    id: obj.id,
                                });
                            }
                        }
                    },
                },
                {
                    title: <Text id='input.postalplace'>Postalplace</Text>,
                    placeholder: <Text id='input.postalplace'>Postalplace</Text>,
                    name: 'postalplace',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.postalplace,
                    showInList: true,
                },
                {
                    title: <Text id='input.country'>Country</Text>,
                    placeholder: <Text id='input.country'>Country</Text>,
                    name: 'country',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.country,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.contact-media-title'>contact media</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'contact media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                },
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    name: 'tags',
                    type: 'tags',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'tags',
                    foundListName: 'similarTags',
                    foundListKeysName: 'similarTagsKeys',
                    getValue: obj => obj.tags,
                    datalist: [
                        'name',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
            ],
        },
    ],

    calendar: [
        {
            name: <Text id='input.calendar-basics-title'>Calendar basics</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'Calendar basics',
            fields: [
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj?.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj?.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj?.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj?.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.sort'>sort</Text>,
                    placeholder: <Text id='input.sort'>sort</Text>,
                    name: 'sort',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.sort,
                    showInList: true,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.calendarId'>calendarId</Text>,
                    placeholder: <Text id='input.calendarId'>calendarId</Text>,
                    name: 'calendarId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj?.calendarId,
                    isReadOnly: true,
                    showInList: true,
                },
                {
                    title: <Text id='input.type'>type</Text>,
                    placeholder: <Text id='input.type'>type</Text>,
                    name: 'type',
                    type: 'select',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.type,
                    datalist: [['booking', 'Booking'], ['notification', 'Innkallinger']],
                    // datalistFieldKey: e => e.id,
                    // datalistFieldVal: e => `${e.firstname} ${e.lastname}`,
                    showInList: true,
                },
                // {
                //     title: <Text id='input.type'>type</Text>,
                //     placeholder: <Text id='input.type'>type</Text>,
                //     name: 'type',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj?.type,
                //     showInList: true,
                // },
                {
                    title: <Text id='input.useCalendarAvailability'>useCalendarAvailability</Text>,
                    placeholder: <Text id='input.useCalendarAvailability'>useCalendarAvailability</Text>,
                    name: 'useCalendarAvailability',
                    type: 'toggle',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj?.useCalendarAvailability ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.name'>Name</Text>,
                    placeholder: <Text id='input.name'>Name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                    callback: (obj, updateValue) => {
                        let calendarId = obj.name;
                        calendarId = calendarId.replace(/[æøå]/ig, match => mapLetter(match));
                        calendarId = calendarId.replace(/[^a-zA-Z0-9]/g, '_');
                        calendarId = calendarId.replace(/_+/g, '_');
                        calendarId = calendarId.replace(/^_/g, '');
                        calendarId = calendarId.replace(/_$/g, '');
                        updateValue({
                            name: 'calendarId',
                            value: calendarId,
                        });
                    },
                },
                {
                    title: <Text id='input.description'>description</Text>,
                    placeholder: <Text id='input.description'>description</Text>,
                    name: 'description',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.description,
                    showInList: true,
                },
                {
                    title: <Text id='input.isOnlineBooking'>isOnlineBooking</Text>,
                    placeholder: <Text id='input.isOnlineBooking'>isOnlineBooking</Text>,
                    name: 'isOnlineBooking',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.isOnlineBooking || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.displayCustomerInfo'>displayCustomerInfo</Text>,
                    placeholder: <Text id='input.displayCustomerInfo'>displayCustomerInfo</Text>,
                    name: 'displayCustomerInfo',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.displayCustomerInfo || null,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.calendar-color-title'>Calendar color</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'calendar color',
            fields: [
                {
                    title: <Text id='input.color'>color</Text>,
                    placeholder: <Text id='input.color'>color</Text>,
                    name: 'color',
                    type: 'select',
                    icon: 'fa-solid fa-input-text',
                    datalist: [
                        ['', 'Default'],
                        ['#ffffff', 'White'],
                        ['#000000', 'Black'],
                        ['#808080', 'Gray'],
                        ['#ff0000', 'Red'],
                        ['#00ff00', 'Green'],
                        ['#0000ff', 'blue'],
                    ],
                    useFirstValueAsBackgroundColorForOption: true,
                    getValue: obj => obj.color,
                    showInList: false,
                },
                {
                    title: <Text id='input.backgroundColor'>backgroundColor</Text>,
                    placeholder: <Text id='input.backgroundColor'>backgroundColor</Text>,
                    name: 'backgroundColor',
                    type: 'select',
                    icon: 'fa-solid fa-input-text',
                    datalist: [
                        ['#87CEEB', 'Sky Blue'],
                        ['#FF7F50', 'Coral'],
                        ['#90EE90', 'Light Green'],
                        ['#DDA0DD', 'Violet'],
                        ['#F4A460', 'Sandy Brown'],
                        ['#6495ED', 'Cornflower Blue'],
                        ['#AFEEEE', 'Pale Turquoise'],
                        ['#F0E68C', 'Khaki'],
                        ['#D8BFD8', 'Thistle'],
                        ['#FFA07A', 'Light Salmon'],
                        ['#DDA0DD', 'Plum'],
                        ['#5F9EA0', 'Cadet Blue'],
                        ['#FFDAB9', 'Peach Puff'],
                        ['#DA70D6', 'Orchid'],
                        ['#DEB887', 'Burlywood'],
                        ['#B0C4DE', 'Light Steel Blue'],
                        ['#7FFFD4', 'Aquamarine'],
                        ['#DAA520', 'Goldenrod'],
                        ['#7B68EE', 'Medium Slate Blue'],
                        ['#CD853F', 'Peru']
                    ],
                    useFirstValueAsBackgroundColorForOption: true,
                    getValue: obj => obj.backgroundColor,
                    showInList: false,
                },
                {
                    title: <Text id='input.dragBackgroundColor'>dragBackgroundColor</Text>,
                    placeholder: <Text id='input.dragBackgroundColor'>dragBackgroundColor</Text>,
                    name: 'dragBackgroundColor',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.dragBackgroundColor,
                    showInList: true,
                },
                {
                    title: <Text id='input.borderColor'>borderColor</Text>,
                    placeholder: <Text id='input.borderColor'>borderColor</Text>,
                    name: 'borderColor',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.borderColor,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.calendar-texttemplate-title'>Calendar text template</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'calendar text template',
            fields: [
                {
                    title: <Text id='input.smsTextBooking'>smsTextBooking</Text>,
                    placeholder: <Text id='input.smsTextBooking'>smsTextBooking</Text>,
                    help: <Text id='input.smsTextBooking-help'>This is the text that will be sent to the customer when a booking is made. Use these text replacements: [animal], [name], [eventDate], [clinic], [clinicName]</Text>,
                    name: 'smsTextBooking',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.smsTextBooking,
                    showInList: false,
                    showFieldInfo: ({ holdingStore, obj: object, field }) => {
                        const length = object[field] ? object[field].length : 0;
                        return (<>
                            <span class='font-weight-lighter'>Length:</span> {length}
                            {length > 160 ? <> <i class='fa-duotone fa-exclamation-triangle text-warning ml-2' /> <Text id='input.smsTextMoreThanOne'>More than 1 message.</Text></> : ''}
                        </>);
                    },
                },
                {
                    title: <Text id='input.smsTextNotification'>smsTextNotification</Text>,
                    placeholder: <Text id='input.smsTextNotification'>smsTextNotification</Text>,
                    name: 'smsTextNotification',
                    type: 'textarea',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.smsTextNotification,
                    showInList: false,
                    showFieldInfo: ({ holdingStore, obj: object, field }) => {
                        const length = object[field] ? object[field].length : 0;
                        return (<>
                            <span class='font-weight-lighter'>Length:</span> {length}
                            {length > 160 ? <> <i class='fa-duotone fa-exclamation-triangle text-warning ml-2' /> <Text id='input.smsTextMoreThanOne'>More than 1 message.</Text></> : ''}
                        </>);
                    },
                },
            ],
        },
        {
            name: <Text id='input.calendar-media-title'>Calendar media</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'calendar media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                }
            ],
        },
    ],
    calendarEvent: [
        {
            name: <Text id='input.calendarevent-basics-title'>Calendar event</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'Calendar event',
            fields: [
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.sale'>sale</Text>,
                    placeholder: <Text id='input.sale'>sale</Text>,
                    name: 'sale',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.sale,
                    showInList: true,
                    isAdminField: true,
                },
                // {
                //     title: <Text id='input.calendarId'>calendarId</Text>,
                //     placeholder: <Text id='input.calendarId'>calendarId</Text>,
                //     name: 'calendarId',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj.calendarId,
                //     showInList: true,
                // },
                {
                    title: <Text id='input.calendarId'>calendarId</Text>,
                    placeholder: <Text id='input.calendarId'>calendarId</Text>,
                    name: 'calendarId',
                    type: 'select',
                    icon: 'fa-solid fa-user-doctor',
                    getValue: obj => obj?.calendarId,
                    datalistName: 'calendars',
                    datalistFieldKey: e => e.calendarId,
                    datalistFieldVal: e => `${e.calendarId}`,
                    showInList: true,
                },
                {
                    title: <Text id='input.title'>Title</Text>,
                    placeholder: <Text id='input.title'>Title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                },
                {
                    title: <Text id='input.body'>Body</Text>,
                    placeholder: <Text id='input.body'>Body</Text>,
                    name: 'body',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.body,
                },
                {
                    title: <Text id='input.smsText'>smsText</Text>,
                    placeholder: <Text id='input.smsText'>smsText</Text>,
                    name: 'smsText',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.smsText,
                },
                {
                    title: <Text id='input.isAllday'>isAllday</Text>,
                    placeholder: <Text id='input.isAllday'>isAllday</Text>,
                    name: 'isAllday',
                    type: 'text',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.isAllday,
                    isAdminField: true,
                },
            ],
        },
        {
            name: <Text id='input.calendarevents-dates-title'>Calendar event dates</Text>,
            // help: <Text id='input.user-dates-help'>Basic settings for your profile.</Text>,
            key: 'Calendar event dates',
            fields: [
                {
                    title: <Text id='input.start'>Start</Text>,
                    placeholder: <Text id='input.start'>Start</Text>,
                    name: 'start',
                    type: 'datetime-local',
                    step: 900,
                    icon: 'fa-solid fa-calendar',
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                        // timeZoneName: 'short',
                    }) : null,
                    getValue: obj => obj.start,
                    callback: (obj, updateValue) => {
                        const start = new Date(obj.start);
                        const end = new Date(obj.end);
                        if (!obj.end || end < start) {
                            const end = new Date(obj.start);
                            end.setMinutes(end.getMinutes() + 15);
                            updateValue({
                                name: 'end',
                                value: util.isoDate(end),
                            });
                        }
                    },
                    showInList: true,
                },
                {
                    title: <Text id='input.end'>End</Text>,
                    placeholder: <Text id='input.end'>End</Text>,
                    name: 'end',
                    type: 'datetime-local',
                    step: 900,
                    icon: 'fa-solid fa-calendar',
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                        // timeZoneName: 'short',
                    }) : null,
                    getValue: obj => obj.end,
                    minFunc: obj => obj.start ? util.isoDate(obj.start, { dateTimeSep: 'T' }) : null,
                    showInList: true,
                },
                {
                    title: <Text id='input.goingDuration'>goingDuration</Text>,
                    placeholder: <Text id='input.goingDuration'>goingDuration</Text>,
                    name: 'goingDuration',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.goingDuration,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.comingDuration'>comingDuration</Text>,
                    placeholder: <Text id='input.comingDuration'>comingDuration</Text>,
                    name: 'comingDuration',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.comingDuration,
                    showInList: true,
                    isAdminField: true,
                },
            ],
        },
        {
            name: <Text id='input.calendarevents-recurring-title'>Calendar event recurring</Text>,
            // help: <Text id='input.user-dates-help'>Basic settings for your profile.</Text>,
            key: 'Calendar event recurring',
            isCollapsed: true,
            fields: [
                {
                    title: <Text id='input.isRecurring'>isRecurring</Text>,
                    placeholder: <Text id='input.isRecurring'>isRecurring</Text>,
                    name: 'isRecurring',
                    type: 'toggle',
                    icon: 'fa-solid fa-warning',
                    getValue: obj => obj?.isRecurring ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.recurringStart'>recurringStart</Text>,
                    placeholder: <Text id='input.recurringStart'>recurringStart</Text>,
                    name: 'recurringStart',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    getValue: obj => obj.recurringStart,
                    callback: (obj, updateValue) => {
                        const start = new Date(obj.recurringStart);
                        const end = new Date(obj.recurringEnd);
                        if (!obj.recurringEnd || end < start) {
                            const end = new Date(obj.recurringStart);
                            end.setDate(end.getDate() + 365);
                            updateValue({
                                name: 'recurringEnd',
                                value: util.isoDate(end),
                            });
                        }
                    },
                    showInList: true,
                },
                {
                    title: <Text id='input.recurringEnd'>recurringEnd</Text>,
                    placeholder: <Text id='input.recurringEnd'>recurringEnd</Text>,
                    name: 'recurringEnd',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    getValue: obj => obj.recurringEnd,
                    showInList: true,
                },
                {
                    title: <Text id='input.recurringFrequency'>recurringFrequency</Text>,
                    placeholder: <Text id='input.recurringFrequency'>recurringFrequency</Text>,
                    name: 'recurringFrequency',
                    type: 'select',
                    icon: 'fa-solid fa-input-text',
                    datalist: [
                        ['daily', 'Daily'],
                        ['weekly', 'Weekly'],
                        ['monthly', 'Monthly'],
                        ['yearly', 'Yearly'],
                    ],
                    getValue: obj => obj.recurringFrequency,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.calendarevent-color-title'>Calendar Event color</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'calendarevent color',
            isCollapsed: true,
            fields: [
                {
                    title: <Text id='input.color'>color</Text>,
                    placeholder: <Text id='input.color'>color</Text>,
                    name: 'color',
                    type: 'select',
                    icon: 'fa-solid fa-input-text',
                    datalist: [
                        ['', 'Default'],
                        ['#ffffff', 'White'],
                        ['#000000', 'Black'],
                        ['#808080', 'Gray'],
                        ['#ff0000', 'Red'],
                        ['#00ff00', 'Green'],
                        ['#0000ff', 'blue'],
                    ],
                    useFirstValueAsBackgroundColorForOption: true,
                    getValue: obj => obj.color,
                    showInList: false,
                },
                {
                    title: <Text id='input.backgroundColor'>backgroundColor</Text>,
                    placeholder: <Text id='input.backgroundColor'>backgroundColor</Text>,
                    name: 'backgroundColor',
                    type: 'select',
                    icon: 'fa-solid fa-input-text',
                    datalist: [
                        ['#87CEEB', 'Sky Blue'],
                        ['#FF7F50', 'Coral'],
                        ['#90EE90', 'Light Green'],
                        ['#DDA0DD', 'Violet'],
                        ['#F4A460', 'Sandy Brown'],
                        ['#6495ED', 'Cornflower Blue'],
                        ['#AFEEEE', 'Pale Turquoise'],
                        ['#F0E68C', 'Khaki'],
                        ['#D8BFD8', 'Thistle'],
                        ['#FFA07A', 'Light Salmon'],
                        ['#DDA0DD', 'Plum'],
                        ['#5F9EA0', 'Cadet Blue'],
                        ['#FFDAB9', 'Peach Puff'],
                        ['#DA70D6', 'Orchid'],
                        ['#DEB887', 'Burlywood'],
                        ['#B0C4DE', 'Light Steel Blue'],
                        ['#7FFFD4', 'Aquamarine'],
                        ['#DAA520', 'Goldenrod'],
                        ['#7B68EE', 'Medium Slate Blue'],
                        ['#CD853F', 'Peru']
                    ],
                    useFirstValueAsBackgroundColorForOption: true,
                    getValue: obj => obj.backgroundColor,
                    showInList: false,
                },
                {
                    title: <Text id='input.dragBackgroundColor'>dragBackgroundColor</Text>,
                    placeholder: <Text id='input.dragBackgroundColor'>dragBackgroundColor</Text>,
                    name: 'dragBackgroundColor',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.dragBackgroundColor,
                    showInList: true,
                },
                {
                    title: <Text id='input.borderColor'>borderColor</Text>,
                    placeholder: <Text id='input.borderColor'>borderColor</Text>,
                    name: 'borderColor',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.borderColor,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.calendarevents-meta-title'>Calendar event meta</Text>,
            // help: <Text id='input.user-dates-help'>Basic settings for your profile.</Text>,
            key: 'Calendar event meta',
            isCollapsed: false,
            fields: [
                {
                    title: <Text id='input.didNotShow'>didNotShow</Text>,
                    placeholder: <Text id='input.didNotShow'>didNotShow</Text>,
                    name: 'didNotShow',
                    type: 'toggle',
                    icon: 'fa-solid fa-warning',
                    getValue: obj => obj?.didNotShow ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.skipReminders'>Skip SMS Reminders</Text>,
                    placeholder: <Text id='input.skipReminders'>Skip SMS Reminders</Text>,
                    name: 'skipReminders',
                    type: 'toggle',
                    icon: 'fa-duotone fa-bell-slash',
                    getValue: obj => obj?.skipReminders ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.hasArrived'>Customer has arrived</Text>,
                    placeholder: <Text id='input.hasArrived'>Customer has arrived</Text>,
                    name: 'hasArrived',
                    type: 'toggle',
                    icon: 'fa-duotone fa-check',
                    getValue: obj => obj?.hasArrived ? 1 : 0,
                    showInList: true,
                },

                {
                    title: <Text id='input.arrivedTime'>arrivedTime</Text>,
                    placeholder: <Text id='input.arrivedTime'>arrivedTime</Text>,
                    name: 'arrivedTime',
                    type: 'datetime-local',
                    step: 60,
                    icon: 'fa-solid fa-calendar',
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                        // timeZoneName: 'short',
                    }) : null,
                    getValue: obj => obj.arrivedTime,
                    showInList: true,
                },
                {
                    title: <Text id='input.receivedTime'>receivedTime</Text>,
                    placeholder: <Text id='input.receivedTime'>receivedTime</Text>,
                    name: 'receivedTime',
                    type: 'datetime-local',
                    step: 60,
                    icon: 'fa-solid fa-calendar',
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                        // timeZoneName: 'short',
                    }) : null,
                    getValue: obj => obj.receivedTime,
                    showInList: true,
                },
                {
                    title: <Text id='input.doneTime'>doneTime</Text>,
                    placeholder: <Text id='input.doneTime'>doneTime</Text>,
                    name: 'doneTime',
                    type: 'datetime-local',
                    step: 60,
                    icon: 'fa-solid fa-calendar',
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                        // timeZoneName: 'short',
                    }) : null,
                    getValue: obj => obj.doneTime,
                    showInList: true,
                },

                {
                    title: <Text id='input.location'>location</Text>,
                    placeholder: <Text id='input.location'>location</Text>,
                    name: 'location',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.location,
                    showInList: true,
                },
                {
                    title: <Text id='input.calendar-category'>category</Text>,
                    placeholder: <Text id='input.calendar-category'>category</Text>,
                    help: <Text id='input.calendar-category-help'>Category of the event. Available categories are 'milestone', 'task', 'time' and 'allday'.</Text>,
                    name: 'category',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.category,
                    showInList: true,
                },
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    name: 'tags',
                    type: 'tags',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'tags',
                    foundListName: 'similarTags',
                    foundListKeysName: 'similarTagsKeys',
                    getValue: obj => obj.tags,
                    datalist: [
                        'name',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.animals'>animals</Text>,
                    placeholder: <Text id='input.animals'>animals</Text>,
                    name: 'animals',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'adminAnimals',
                    getValue: obj => {
                        if (obj.animals) {
                            return obj.animals?.map(m => m);
                        }
                        return [];
                    },
                    displayArrayObject: obj => `${obj.name} ${obj.species}, ${obj.breed}`,
                    datalist: [
                        'id', 'name', 'birth', 'breed', 'species',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                },
            ],
        },
    ],
    gpsDevice: [
        {
            name: <Text id='input.gpsDevice-basics-title'>GPS Device</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'GPS Device',
            fields: [
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.race'>race</Text>,
                    placeholder: <Text id='input.race'>race</Text>,
                    name: 'race',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.race,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.raceClass'>raceClass</Text>,
                    placeholder: <Text id='input.raceClass'>raceClass</Text>,
                    name: 'raceClass',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.raceClass,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.raceClassContestant'>raceClassContestant</Text>,
                    placeholder: <Text id='input.raceClassContestant'>raceClassContestant</Text>,
                    name: 'raceClassContestant',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.raceClassContestant,
                    showInList: true,
                    isAdminField: true,
                },
                // {
                //     title: <Text id='input.calendarId'>calendarId</Text>,
                //     placeholder: <Text id='input.calendarId'>calendarId</Text>,
                //     name: 'calendarId',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj.calendarId,
                //     showInList: true,
                // },
                {
                    title: <Text id='input.gpsSerial'>gpsSerial</Text>,
                    placeholder: <Text id='input.gpsSerial'>gpsSerial</Text>,
                    name: 'gpsSerial',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.gpsSerial,
                    showInList: true,
                },
                {
                    title: <Text id='input.gpsId'>gpsId</Text>,
                    placeholder: <Text id='input.gpsId'>gpsId</Text>,
                    name: 'gpsId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.gpsId,
                    showInList: true,
                },
                {
                    title: <Text id='input.name'>name</Text>,
                    placeholder: <Text id='input.name'>name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                },
                {
                    title: <Text id='input.description'>description</Text>,
                    placeholder: <Text id='input.description'>description</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.description,
                },
            ],
        },
        {
            name: <Text id='input.gpsDevice-dates-title'>gpsDevice dates</Text>,
            // help: <Text id='input.user-dates-help'>Basic settings for your profile.</Text>,
            key: 'gpsDevice dates',
            fields: [
                {
                    title: <Text id='input.start'>Start</Text>,
                    placeholder: <Text id='input.start'>Start</Text>,
                    name: 'start',
                    type: 'datetime-local',
                    step: 900,
                    icon: 'fa-solid fa-calendar',
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    getValue: obj => obj.start,
                    callback: (obj, updateValue) => {
                        const start = new Date(obj.start);
                        const end = new Date(obj.end);
                        if (!obj.end || end < start) {
                            const end = new Date(obj.start);
                            end.setMinutes(end.getMinutes() + 15);
                            updateValue({
                                name: 'end',
                                value: util.isoDate(end),
                            });
                        }
                    },
                    showInList: true,
                },
                {
                    title: <Text id='input.end'>End</Text>,
                    placeholder: <Text id='input.end'>End</Text>,
                    name: 'end',
                    type: 'datetime-local',
                    step: 900,
                    icon: 'fa-solid fa-calendar',
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    getValue: obj => obj.end,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.gpsDevice-media-title'>gpsDevice media</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'gpsDevice media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                }
            ],
        },
    ],
    stream: [
        {
            name: <Text id='input.stream-basics-title'>Stream</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'Stream',
            fields: [
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.active'>Active</Text>,
                    placeholder: <Text id='input.active'>Active</Text>,
                    name: 'active',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.active || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.frontpage'>frontpage</Text>,
                    placeholder: <Text id='input.frontpage'>frontpage</Text>,
                    name: 'frontpage',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.frontpage || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.race'>race</Text>,
                    placeholder: <Text id='input.race'>race</Text>,
                    name: 'race',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.race,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.streamId'>streamId</Text>,
                    placeholder: <Text id='input.streamId'>streamId</Text>,
                    name: 'streamId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.streamId,
                    showInList: true,
                },
                {
                    title: <Text id='input.streamUrl'>streamUrl</Text>,
                    placeholder: <Text id='input.streamUrl'>streamUrl</Text>,
                    name: 'streamUrl',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.streamUrl,
                    showInList: true,
                },
                {
                    title: <Text id='input.name'>name</Text>,
                    placeholder: <Text id='input.name'>name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                },
                {
                    title: <Text id='input.description'>description</Text>,
                    placeholder: <Text id='input.description'>description</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.description,
                },
            ],
        },
        {
            name: <Text id='input.stream-dates-title'>stream dates</Text>,
            // help: <Text id='input.user-dates-help'>Basic settings for your profile.</Text>,
            key: 'stream dates',
            fields: [
                {
                    title: <Text id='input.start'>Start</Text>,
                    placeholder: <Text id='input.start'>Start</Text>,
                    name: 'start',
                    type: 'datetime-local',
                    step: 900,
                    icon: 'fa-solid fa-calendar',
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    getValue: obj => obj.start,
                    callback: (obj, updateValue) => {
                        const start = new Date(obj.start);
                        const end = new Date(obj.end);
                        if (!obj.end || end < start) {
                            const end = new Date(obj.start);
                            end.setMinutes(end.getMinutes() + 15);
                            updateValue({
                                name: 'end',
                                value: util.isoDate(end),
                            });
                        }
                    },
                    showInList: true,
                },
                {
                    title: <Text id='input.end'>End</Text>,
                    placeholder: <Text id='input.end'>End</Text>,
                    name: 'end',
                    type: 'datetime-local',
                    step: 900,
                    icon: 'fa-solid fa-calendar',
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    getValue: obj => obj.end,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.stream-media-title'>stream media</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'stream media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                }
            ],
        },
    ],
    streamDevice: [
        {
            name: <Text id='input.streamDevice-basics-title'>streamDevice</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'streamDevice',
            fields: [
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.publicIp'>publicIp</Text>,
                    placeholder: <Text id='input.publicIp'>publicIp</Text>,
                    name: 'publicIp',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.publicIp,
                    showInList: true,
                },
                {
                    title: <Text id='input.ip'>ip</Text>,
                    placeholder: <Text id='input.ip'>ip</Text>,
                    name: 'ip',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.ip,
                    showInList: true,
                },
                {
                    title: <Text id='input.streamDeviceId'>streamDeviceId</Text>,
                    placeholder: <Text id='input.streamDeviceId'>streamDeviceId</Text>,
                    name: 'streamDeviceId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.streamDeviceId,
                    showInList: true,
                },
                {
                    title: <Text id='input.streamName'>streamName</Text>,
                    placeholder: <Text id='input.streamName'>streamName</Text>,
                    name: 'streamName',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.streamName,
                    showInList: true,
                },

                {
                    title: <Text id='input.videoSize'>videoSize</Text>,
                    placeholder: <Text id='input.videoSize'>videoSize</Text>,
                    name: 'videoSize',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.videoSize,
                    showInList: true,
                },
                {
                    title: <Text id='input.frameRate'>frameRate</Text>,
                    placeholder: <Text id='input.frameRate'>frameRate</Text>,
                    name: 'frameRate',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.frameRate,
                    showInList: true,
                },
                {
                    title: <Text id='input.videoBitRate'>videoBitRate</Text>,
                    placeholder: <Text id='input.videoBitRate'>videoBitRate</Text>,
                    name: 'videoBitRate',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.videoBitRate,
                    showInList: true,
                },
                {
                    title: <Text id='input.name'>name</Text>,
                    placeholder: <Text id='input.name'>name</Text>,
                    name: 'name',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                },
                {
                    title: <Text id='input.description'>description</Text>,
                    placeholder: <Text id='input.description'>description</Text>,
                    name: 'description',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.description,
                },
            ],
        },
        {
            name: <Text id='input.streamDevice-dates-title'>streamDevice dates</Text>,
            // help: <Text id='input.user-dates-help'>Basic settings for your profile.</Text>,
            key: 'streamDevice dates',
            fields: [
                {
                    title: <Text id='input.date'>date</Text>,
                    placeholder: <Text id='input.date'>date</Text>,
                    name: 'date',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: installation => installation.date,
                    showInList: true,
                    hideOnCreate: false,
                },
            ],
        },
        {
            name: <Text id='input.streamDevice-usercontrol-title'>streamDevice user control</Text>,
            // help: <Text id='input.user-dates-help'>Basic settings for your profile.</Text>,
            key: 'streamDevice user control',
            fields: [
                {
                    title: <Text id='input.brightness'>brightness</Text>,
                    placeholder: <Text id='input.brightness'>brightness</Text>,
                    name: 'brightness',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.brightness,
                    showInList: false,
                },
                {
                    title: <Text id='input.contrast'>contrast</Text>,
                    placeholder: <Text id='input.contrast'>contrast</Text>,
                    name: 'contrast',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.contrast,
                    showInList: false,
                },
                {
                    title: <Text id='input.saturation'>saturation</Text>,
                    placeholder: <Text id='input.saturation'>saturation</Text>,
                    name: 'saturation',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.saturation,
                    showInList: false,
                },
                {
                    title: <Text id='input.white_balance_automatic'>white_balance_automatic</Text>,
                    placeholder: <Text id='input.white_balance_automatic'>white_balance_automatic</Text>,
                    name: 'white_balance_automatic',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.white_balance_automatic,
                    showInList: false,
                },
                {
                    title: <Text id='input.gain'>gain</Text>,
                    placeholder: <Text id='input.gain'>gain</Text>,
                    name: 'gain',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.gain,
                    showInList: false,
                },
                {
                    title: <Text id='input.power_line_frequency'>power_line_frequency</Text>,
                    placeholder: <Text id='input.power_line_frequency'>power_line_frequency</Text>,
                    name: 'power_line_frequency',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.power_line_frequency,
                    showInList: false,
                },
                {
                    title: <Text id='input.white_balance_temperature'>white_balance_temperature</Text>,
                    placeholder: <Text id='input.brightness'>white_balance_temperature</Text>,
                    name: 'white_balance_temperature',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.white_balance_temperature,
                    showInList: false,
                },
                {
                    title: <Text id='input.sharpness'>sharpness</Text>,
                    placeholder: <Text id='input.sharpness'>sharpness</Text>,
                    name: 'sharpness',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.sharpness,
                    showInList: false,
                },
                {
                    title: <Text id='input.backlight_compensation'>backlight_compensation</Text>,
                    placeholder: <Text id='input.backlight_compensation'>backlight_compensation</Text>,
                    name: 'backlight_compensation',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.backlight_compensation,
                    showInList: false,
                },
            ],
        },
        {
            name: <Text id='input.streamDevice-cameracontrol-title'>streamDevice camera control</Text>,
            // help: <Text id='input.user-dates-help'>Basic settings for your profile.</Text>,
            key: 'streamDevice camera control',
            fields: [
                {
                    title: <Text id='input.auto_exposure'>auto_exposure</Text>,
                    placeholder: <Text id='input.auto_exposure'>auto_exposure</Text>,
                    name: 'auto_exposure',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.auto_exposure,
                    showInList: false,
                },
                {
                    title: <Text id='input.exposure_time_absolute'>exposure_time_absolute</Text>,
                    placeholder: <Text id='input.exposure_time_absolute'>exposure_time_absolute</Text>,
                    name: 'exposure_time_absolute',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.exposure_time_absolute,
                    showInList: false,
                },
                {
                    title: <Text id='input.exposure_dynamic_framerate'>exposure_dynamic_framerate</Text>,
                    placeholder: <Text id='input.exposure_dynamic_framerate'>exposure_dynamic_framerate</Text>,
                    name: 'exposure_dynamic_framerate',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.exposure_dynamic_framerate,
                    showInList: false,
                },
                {
                    title: <Text id='input.pan_absolute'>pan_absolute</Text>,
                    placeholder: <Text id='input.pan_absolute'>pan_absolute</Text>,
                    name: 'pan_absolute',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.pan_absolute,
                    showInList: false,
                },
                {
                    title: <Text id='input.tilt_absolute'>tilt_absolute</Text>,
                    placeholder: <Text id='input.tilt_absolute'>tilt_absolute</Text>,
                    name: 'tilt_absolute',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.tilt_absolute,
                    showInList: false,
                },
                {
                    title: <Text id='input.focus_absolute'>focus_absolute</Text>,
                    placeholder: <Text id='input.focus_absolute'>focus_absolute</Text>,
                    name: 'focus_absolute',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.focus_absolute,
                    showInList: false,
                },
                {
                    title: <Text id='input.focus_automatic_continuous'>focus_automatic_continuous</Text>,
                    placeholder: <Text id='input.focus_automatic_continuous'>focus_automatic_continuous</Text>,
                    name: 'focus_automatic_continuous',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.focus_automatic_continuous,
                    showInList: false,
                },
                {
                    title: <Text id='input.zoom_absolute'>zoom_absolute</Text>,
                    placeholder: <Text id='input.zoom_absolute'>zoom_absolute</Text>,
                    name: 'zoom_absolute',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.zoom_absolute,
                    showInList: false,
                },
            ]
        },
        {
            name: <Text id='input.streamDevice-media-title'>streamDevice media</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'streamDevice media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                }
            ],
        },
    ],
    convention: [
        {
            name: <Text id='input.convention-basics-title'>Convention basics</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'convention',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: user => user.id || null,
                    showInList: true,
                    isReadOnly: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.active'>Active</Text>,
                    placeholder: <Text id='input.active'>Active</Text>,
                    name: 'active',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.active || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.public'>public</Text>,
                    placeholder: <Text id='input.public'>public</Text>,
                    name: 'public',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.public || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.isCompetition'>isCompetition</Text>,
                    placeholder: <Text id='input.isCompetition'>isCompetition</Text>,
                    name: 'isCompetition',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.isCompetition || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.isApproved'>isApproved</Text>,
                    placeholder: <Text id='input.isApproved'>isApproved</Text>,
                    name: 'isApproved',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.isApproved || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.isAdminScoring'>isAdminScoring</Text>,
                    placeholder: <Text id='input.isAdminScoring'>isAdminScoring</Text>,
                    name: 'isAdminScoring',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.isAdminScoring || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.resultsLocked'>resultsLocked</Text>,
                    placeholder: <Text id='input.resultsLocked'>resultsLocked</Text>,
                    help: <Text id='input.resultsLocked-help'>Results are locked and approved.</Text>,
                    name: 'resultsLocked',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.resultsLocked ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.registrationIsOpen'>Registration Is Open</Text>,
                    placeholder: <Text id='input.registrationIsOpen'>Registration Is Open</Text>,
                    help: <Text id='input.registrationIsOpen-help'>Registration is open for all to signup.</Text>,
                    name: 'registrationIsOpen',
                    type: 'toggle',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.registrationIsOpen ? 1 : 0,
                    showInList: true,
                },
                {
                    title: <Text id='input.isNsfClassLeading'>isNsfClassLeading</Text>,
                    placeholder: <Text id='input.isNsfClassLeading'>isNsfClassLeading</Text>,
                    name: 'isNsfClassLeading',
                    type: 'toggle',
                    icon: 'fa-solid fa-eye',
                    getValue: obj => obj.isNsfClassLeading || null,
                    showInList: true,
                },
                {
                    title: <Text id='input.convention-nsfId'>NSF id</Text>,
                    placeholder: <Text id='input.convention-nsfId'>NSF ID</Text>,
                    help: <Text id='input.nsfId-help'>Unique identifier at NSF. Format is an uuidv4</Text>,
                    name: 'nsfId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.nsfId,
                    showInList: false,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.nsfNumber'>nsfNumber</Text>,
                    placeholder: <Text id='input.nsfNumber'>nsfNumber</Text>,
                    help: <Text id='input.nsfNumber-help'>Unique number at NSF. Format is an integer.</Text>,
                    name: 'nsfNumber',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.nsfNumber,
                    showInList: true,
                },
                {
                    title: <Text id='input.location'>location</Text>,
                    placeholder: <Text id='input.location'>location</Text>,
                    help: <Text id='input.location-help'>Location of the convention.</Text>,
                    name: 'location',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.location,
                    showInList: true,
                },
                {
                    title: <Text id='input.organizer'>organizer</Text>,
                    placeholder: <Text id='input.organizer'>organizer</Text>,
                    help: <Text id='input.organizer-help'>Name of the organizer.</Text>,
                    name: 'organizer',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.organizer,
                    showInList: true,
                },
                {
                    title: <Text id='input.judges'>judges</Text>,
                    placeholder: <Text id='input.judges'>judges</Text>,
                    help: <Text id='input.judges-help'>Name of the judges.</Text>,
                    name: 'judges',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.judges,
                    showInList: true,
                },
                {
                    title: 'title',
                    placeholder: 'title',
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                    isAdminField: false,
                },
                {
                    title: <Text id='input.ingress'>ingress</Text>,
                    placeholder: <Text id='input.ingress'>ingress</Text>,
                    name: 'ingress',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.ingress,
                    showInList: false,
                    // hideOnCreate: true,
                },
                {
                    title: <Text id='input.body'>body</Text>,
                    placeholder: <Text id='input.body'>body</Text>,
                    name: 'body',
                    type: 'textareaEditor',
                    viewHtml: false,
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.body,
                    showInList: false,
                    // hideOnCreate: true,
                },
            ]
        },
        {
            name: <Text id='input.convention-settings-title'>convention settings</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'convention settings',
            fields: [
                {
                    title: <Text id='input.convention-type'>Convention type</Text>,
                    placeholder: <Text id='input.convention-type'>Convention type</Text>,
                    name: 'type',
                    type: 'select',
                    icon: 'fa-duotone fa-face-thinking',
                    getValue: obj => obj.type,
                    datalist: [['felt', 'Felt'], ['norgesfelt', 'NorgesFelt'], ['hurtig', 'Hurtig'], ['nais', 'NAIS'], ['standard', 'Standard'], ['t-96', 'T-96']],
                    showInList: true,
                },
                {
                    title: <Text id='input.members'>Members</Text>,
                    placeholder: <Text id='input.members'>Members</Text>,
                    name: 'members',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'adminMembers',
                    getValue: obj => obj.members?.map(m => m),
                    datalist: [
                        'id', 'firstname', 'lastname', 'email', 'cellphone',
                    ],
                    datalistHelp: [
                        'User id', 'Firstname of shooting instructor.', 'Lastname of shooting instructor.',
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-left px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.firstname} {obj.lastname}<br />
                            <span class='font-weight-lighter'>Cellphone:</span> {obj.cellphone}<br />
                            <span class='font-weight-lighter'>Email:</span> {obj.email}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '60px',
                },
                {
                    title: <Text id='input.participants'>participants</Text>,
                    placeholder: <Text id='input.participants'>participants</Text>,
                    name: 'participants',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'allMembers',
                    getValue: obj => obj.participants?.map(m => m),
                    datalist: [
                        'id', 'firstname', 'lastname', 'email', 'cellphone', 'class'
                    ],
                    datalistHelp: [
                        'User id', 'Firstname of participant.', 'Lastname of participant.', 'Email of participant.', 'Cellphone of participant.', 'Shooting class of this round.',
                    ],
                    datalistValues: [
                        [], [], [], [], [], ['fin', 'grov', 'mil', 'rev', 'sp.rev', 'sp.pistol', 'mag1', 'mag2'],
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-left px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.firstname} {obj.lastname}<br />
                            <span class='font-weight-lighter'>Cellphone:</span> {obj.cellphone}<br />
                            <span class='font-weight-lighter'>Email:</span> {obj.email}<br />
                            <span class='font-weight-lighter'>Klasse:</span> {obj.class}<br />
                            <span class='font-weight-lighter'>Date:</span> {obj.signupDate ? util.isoDate(obj.signupDate) : ''}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '100px',
                },
                {
                    title: <Text id='input.participants1'>participants round 1</Text>,
                    placeholder: <Text id='input.participants1'>participants round 1</Text>,
                    name: 'participants1',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'allMembers',
                    getValue: obj => obj.participants1?.map(m => m),
                    datalist: [
                        'id', 'firstname', 'lastname', 'email', 'cellphone', 'class'
                    ],
                    datalistHelp: [
                        'User id', 'Firstname of participant.', 'Lastname of participant.', 'Email of participant.', 'Cellphone of participant.', 'Shooting class of this round.',
                    ],
                    datalistValues: [
                        [], [], [], [], [], ['fin', 'grov', 'mil', 'rev', 'sp.rev', 'sp.pistol', 'mag1', 'mag2'],
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-left px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.firstname} {obj.lastname}<br />
                            <span class='font-weight-lighter'>Cellphone:</span> {obj.cellphone}<br />
                            <span class='font-weight-lighter'>Email:</span> {obj.email}<br />
                            <span class='font-weight-lighter'>Klasse:</span> {obj.class}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '100px',
                },
                {
                    title: <Text id='input.participants2'>participants round 2</Text>,
                    placeholder: <Text id='input.participants2'>participants round 2</Text>,
                    name: 'participants2',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'allMembers',
                    getValue: obj => obj.participants2?.map(m => m),
                    datalist: [
                        'id', 'firstname', 'lastname', 'email', 'cellphone', 'class'
                    ],
                    datalistHelp: [
                        'User id', 'Firstname of participant.', 'Lastname of participant.', 'Email of participant.', 'Cellphone of participant.', 'Shooting class of this round.',
                    ],
                    datalistValues: [
                        [], [], [], [], [], ['fin', 'grov', 'mil', 'rev', 'sp.rev', 'sp.pistol', 'mag1', 'mag2'],
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-left px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.firstname} {obj.lastname}<br />
                            <span class='font-weight-lighter'>Cellphone:</span> {obj.cellphone}<br />
                            <span class='font-weight-lighter'>Email:</span> {obj.email}<br />
                            <span class='font-weight-lighter'>Klasse:</span> {obj.class}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '100px',
                },
                {
                    title: <Text id='input.participants3'>participants round 3</Text>,
                    placeholder: <Text id='input.participants3'>participants round 3</Text>,
                    name: 'participants3',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'allMembers',
                    getValue: obj => obj.participants3?.map(m => m),
                    datalist: [
                        'id', 'firstname', 'lastname', 'email', 'cellphone', 'class'
                    ],
                    datalistHelp: [
                        'User id', 'Firstname of participant.', 'Lastname of participant.', 'Email of participant.', 'Cellphone of participant.', 'Shooting class of this round.',
                    ],
                    datalistValues: [
                        [], [], [], [], [], ['fin', 'grov', 'mil', 'rev', 'sp.rev', 'sp.pistol', 'mag1', 'mag2'],
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-left px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.firstname} {obj.lastname}<br />
                            <span class='font-weight-lighter'>Cellphone:</span> {obj.cellphone}<br />
                            <span class='font-weight-lighter'>Email:</span> {obj.email}<br />
                            <span class='font-weight-lighter'>Klasse:</span> {obj.class}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '100px',
                },
                {
                    title: <Text id='input.participants4'>participants round 4</Text>,
                    placeholder: <Text id='input.participants4'>participants round 4</Text>,
                    name: 'participants4',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'allMembers',
                    getValue: obj => obj.participants4?.map(m => m),
                    datalist: [
                        'id', 'firstname', 'lastname', 'email', 'cellphone', 'class'
                    ],
                    datalistHelp: [
                        'User id', 'Firstname of participant.', 'Lastname of participant.', 'Email of participant.', 'Cellphone of participant.', 'Shooting class of this round.',
                    ],
                    datalistValues: [
                        [], [], [], [], [], ['fin', 'grov', 'mil', 'rev', 'sp.rev', 'sp.pistol', 'mag1', 'mag2'],
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-left px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.firstname} {obj.lastname}<br />
                            <span class='font-weight-lighter'>Cellphone:</span> {obj.cellphone}<br />
                            <span class='font-weight-lighter'>Email:</span> {obj.email}<br />
                            <span class='font-weight-lighter'>Klasse:</span> {obj.class}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '100px',
                },
                {
                    title: <Text id='input.participants5'>participants round 5</Text>,
                    placeholder: <Text id='input.participants5'>participants round 5</Text>,
                    name: 'participants5',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'allMembers',
                    getValue: obj => obj.participants5?.map(m => m),
                    datalist: [
                        'id', 'firstname', 'lastname', 'email', 'cellphone', 'class'
                    ],
                    datalistHelp: [
                        'User id', 'Firstname of participant.', 'Lastname of participant.', 'Email of participant.', 'Cellphone of participant.', 'Shooting class of this round.',
                    ],
                    datalistValues: [
                        [], [], [], [], [], ['fin', 'grov', 'mil', 'rev', 'sp.rev', 'sp.pistol', 'mag1', 'mag2'],
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-left px-2'>
                            <span class='font-weight-lighter'>Name:</span> {obj.firstname} {obj.lastname}<br />
                            <span class='font-weight-lighter'>Cellphone:</span> {obj.cellphone}<br />
                            <span class='font-weight-lighter'>Email:</span> {obj.email}<br />
                            <span class='font-weight-lighter'>Klasse:</span> {obj.class}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '100px',
                },
                {
                    title: <Text id='input.stages-rounds'>Rounds</Text>,
                    placeholder: <Text id='input.stages-rounds'>Rounds</Text>,
                    help: <Text id='input.stages-rounds-help'>Stages define the activity in this conventions. Types are: "hurtig", "nais", "norgesfelt", "felt", "standard".</Text>,
                    name: 'stages',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    getValue: obj => obj.stages?.map(m => m),
                    datalist: [
                        'type', 'name', 'stages', 'description',
                    ],
                    datalistHelp: [
                        'Rundetype', 'Navn på runden.', 'Antall standplasser på runden.', 'Beskrivelse av runden.',
                    ],
                    datalistValues: [
                        ['felt', 'norgesfelt', 'nais', 'hurtig', 'standard', 't-96'], ['Runde 1', 'Runde 2', 'Runde 3'],
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-left px-2'>
                            <span class='font-weight-lighter'>Type:</span> {obj.type}<br />
                            <span class='font-weight-lighter'>Name:</span> {obj.name}<br />
                            <span class='font-weight-lighter'>Stages:</span> {obj.stages}<br />
                            <span class='font-weight-lighter'>Description:</span> {obj.description}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '150px',
                },
            ],
        },
        {
            name: <Text id='input.convention-dates-title'>convention dates</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'convention dates',
            fields: [
                {
                    title: <Text id='input.startDate'>StartDate</Text>,
                    placeholder: <Text id='input.startDate'>StartDate</Text>,
                    name: 'startDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: installation => installation.startDate,
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    callback: (obj, updateValue) => {
                        const startDate = new Date(obj.startDate);
                        const endDate = new Date(obj.endDate);
                        if (!obj.endDate || endDate < startDate) {
                            const end = new Date(obj.startDate);
                            end.setMinutes(end.getMinutes() + 120);
                            updateValue({
                                name: 'endDate',
                                value: util.isoDate(end),
                            });
                        }
                    },
                    showInList: true,
                },
                {
                    title: <Text id='input.endDate'>EndDate</Text>,
                    placeholder: <Text id='input.endDate'>EndDate</Text>,
                    name: 'endDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: installation => installation.endDate,
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    minFunc: obj => obj.startDate ? util.isoDate(obj.startDate, { dateTimeSep: 'T' }) : null,
                    showInList: false,
                },
                {
                    title: <Text id='input.stageInterval'>stageInterval</Text>,
                    placeholder: <Text id='input.stageInterval'>stageInterval</Text>,
                    help: <Text id='input.stageInterval-help'>Minutes between each stage start.</Text>,
                    name: 'stageInterval',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.stageInterval,
                    showInList: true,
                },
                {
                    title: <Text id='input.stageSlots'>stageSlots</Text>,
                    placeholder: <Text id='input.stageSlots'>stageSlots</Text>,
                    help: <Text id='input.stageSlots-help'>Number of slots in each stage.</Text>,
                    name: 'stageSlots',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.stageSlots,
                    showInList: true,
                },
                {
                    title: <Text id='input.registrationStartDate'>registrationStartDate</Text>,
                    placeholder: <Text id='input.registrationStartDate'>registrationStartDate</Text>,
                    name: 'registrationStartDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: installation => installation.registrationStartDate,
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    callback: (obj, updateValue) => {
                        const registrationStartDate = new Date(obj.registrationStartDate);
                        const registrationEndDate = new Date(obj.registrationEndDate);
                        if (!obj.registrationEndDate || registrationEndDate < registrationStartDate) {
                            const end = new Date(obj.registrationStartDate);
                            end.setMinutes(end.getMinutes() + 120);
                            updateValue({
                                name: 'registrationEndDate',
                                value: util.isoDate(end),
                            });
                        }
                    },
                    showInList: true,
                },
                {
                    title: <Text id='input.registrationEndDate'>registrationEndDate</Text>,
                    placeholder: <Text id='input.registrationEndDate'>registrationEndDate</Text>,
                    name: 'registrationEndDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: installation => installation.registrationEndDate,
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    minFunc: obj => obj.registrationStartDate ? util.isoDate(obj.registrationStartDate, { dateTimeSep: 'T' }) : null,
                    showInList: false,
                },

                {
                    title: <Text id='input.stageStands'>stageStands</Text>,
                    placeholder: <Text id='input.stageStands'>stageStands</Text>,
                    name: 'stageStands',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-layer-group',
                    lookupArray: 'allMembers',
                    getValue: obj => obj.stageStands?.map(m => m),
                    datalist: [
                        'stand', 'scoreHits', 'scoreGroups', 'scoreTargets', 'scoreInner'
                    ],
                    datalistHelp: [
                        'stand #', 'Total hits', 'Total groups', 'Total targets.', 'Total iX.',
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-left px-2'>
                            <span class='font-weight-lighter'>Stand:</span> {obj.stand}<br />
                            <span class='font-weight-lighter'>scoreHits:</span> {obj.scoreHits}<br />
                            <span class='font-weight-lighter'>scoreGroups:</span> {obj.scoreGroups}<br />
                            <span class='font-weight-lighter'>scoreTargets:</span> {obj.scoreTargets}<br />
                            <span class='font-weight-lighter'>scoreInner:</span> {obj.scoreInner}<br />
                        </div>
                    </>,
                    showInList: false,
                    drawerSize: 'medium',
                    displayWidth: '200px',
                    displayHeight: '100px',
                },
                {
                    title: <Text id='input.stageStandImages'>Stand Images</Text>,
                    placeholder: <Text id='input.stageStandImages'>Stand Images</Text>,
                    name: 'stageStandImages',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.stageStandImages,
                    showInList: false,
                },


            ],
        },
        {
            name: <Text id='input.convention-media-title'>convention media</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'convention media',
            fields: [
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    help: <Text id='input.tags-help'>Tags are used to find similar news. Add as many as you like.</Text>,
                    name: 'tags',
                    type: 'tags',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'tags',
                    foundListName: 'similarTags',
                    foundListKeysName: 'similarTagsKeys',
                    getValue: obj => obj.tags,
                    datalist: [
                        'name',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: true,
                },

            ]
        },
        {
            name: <Text id='input.convention-sponsors-title'>convention sponsors</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'convention sponsors',
            fields: [
                {
                    title: <Text id='input.sponsors'>Sponsors</Text>,
                    placeholder: <Text id='input.sponsors'>Sponsors</Text>,
                    name: 'sponsors',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.sponsors,
                    showInList: false,
                },
                {
                    title: <Text id='input.sponsorsMeta'>Sponsors meta</Text>,
                    placeholder: <Text id='input.sponsorsMeta'>Sponsors meta</Text>,
                    name: 'sponsorsMeta',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.sponsorsMeta,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    displayArrayObject: obj => <>
                        <div class='text-left px-2'>
                            <span class='font-weight-lighter'>url:</span> {obj.url}<br />
                            <span class='font-weight-lighter'>name:</span> {obj.name}<br />
                            <span class='font-weight-lighter'>description:</span> {obj.description}<br />
                        </div>
                    </>,
                    showInList: false,
                    displayHeight: '100px',
                    displayWidth: '300px',
                },
            ],
        },
    ],
    conventionResult: [
        {
            name: <Text id='input.conventionResult-basics-title'>conventionResult basics</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'conventionResult',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id || null,
                    showInList: true,
                    isReadOnly: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.convention'>convention</Text>,
                    placeholder: <Text id='input.convention'>convention</Text>,
                    name: 'convention',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.convention,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.targetIndex'>targetIndex</Text>,
                    placeholder: <Text id='input.targetIndex'>targetIndex</Text>,
                    name: 'targetIndex',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.targetIndex,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.stage'>stage</Text>,
                    placeholder: <Text id='input.stage'>stage</Text>,
                    name: 'stage',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.stage,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.stand'>stand</Text>,
                    placeholder: <Text id='input.stand'>stand</Text>,
                    name: 'stand',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.stand,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.participant'>participant</Text>,
                    placeholder: <Text id='input.participant'>participant</Text>,
                    name: 'participant',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.participant,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'title',
                    placeholder: 'title',
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.name,
                    showInList: true,
                    isAdminField: false,
                },

                {
                    title: <Text id='input.scoreTotal'>scoreTotal</Text>,
                    placeholder: <Text id='input.scoreTotal'>scoreTotal</Text>,
                    name: 'scoreTotal',
                    type: 'forceNumber',
                    pattern: '[0-9]*',
                    inputmode: 'numeric',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.scoreTotal,
                    showInList: true,
                },
                {
                    title: <Text id='input.scoreHits'>scoreHits</Text>,
                    placeholder: <Text id='input.scoreHits'>scoreHits</Text>,
                    name: 'scoreHits',
                    type: 'forceNumber',
                    pattern: '[0-9]*',
                    inputmode: 'numeric',
                    icon: 'fa-solid fa-input-numeric',
                    step: 1,
                    min: 0,
                    max: 6,
                    getValue: obj => obj.scoreHits,
                    showInList: true,
                },
                {
                    title: <Text id='input.scoreTargets'>scoreTargets</Text>,
                    placeholder: <Text id='input.scoreTargets'>scoreTargets</Text>,
                    name: 'scoreTargets',
                    type: 'forceNumber',
                    pattern: '[0-9]*',
                    inputmode: 'numeric',
                    icon: 'fa-solid fa-input-numeric',
                    step: 1,
                    min: 0,
                    max: 6,
                    getValue: obj => obj.scoreTargets,
                    showInList: true,
                },
                {
                    title: <Text id='input.scoreInner'>scoreInner</Text>,
                    placeholder: <Text id='input.scoreInner'>scoreInner</Text>,
                    name: 'scoreInner',
                    type: 'forceNumber',
                    pattern: '[0-9]*',
                    inputmode: 'numeric',
                    icon: 'fa-solid fa-input-numeric',
                    step: 1,
                    min: 0,
                    max: 6,
                    getValue: obj => obj.scoreInner,
                    showInList: true,
                },
                {
                    title: <Text id='input.scorePenalty'>scorePenalty</Text>,
                    placeholder: <Text id='input.scorePenalty'>scorePenalty</Text>,
                    name: 'scorePenalty',
                    type: 'forceNumber',
                    icon: 'fa-solid fa-input-numeric',
                    step: 1,
                    min: 0,
                    max: 6,
                    getValue: obj => obj.scorePenalty,
                    showInList: true,
                },

            ]
        },
        {
            name: <Text id='input.conventionResult-media-title'>conventionResult media</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'conventionResult media',
            fields: [
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    help: <Text id='input.tags-help'>Tags are used to find similar news. Add as many as you like.</Text>,
                    name: 'tags',
                    type: 'tags',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'tags',
                    foundListName: 'similarTags',
                    foundListKeysName: 'similarTagsKeys',
                    getValue: obj => obj.tags,
                    datalist: [
                        'name',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: true,
                },

            ]
        },
    ],
    competitionResult: [
        {
            name: <Text id='input.competitionResult-basics-title'>competitionResult basics</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'competitionResult',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id || null,
                    showInList: true,
                    isReadOnly: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.date'>date</Text>,
                    placeholder: <Text id='input.date'>date</Text>,
                    name: 'date',
                    type: 'date',
                    min: '2020-01-01',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.date,
                    displayValue: obj => obj ? `${util.formatDate(obj, { locale: 'nb-NO' })}` : null,
                    showInList: false,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.title'>title</Text>,
                    placeholder: <Text id='input.title'>title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                },
                {
                    title: <Text id='input.competitionId'>competitionId</Text>,
                    placeholder: <Text id='input.competitionId'>competitionId</Text>,
                    name: 'competitionId',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.competitionId,
                    showInList: true,
                },
                {
                    title: <Text id='input.organizer'>organizer</Text>,
                    placeholder: <Text id='input.organizer'>organizer</Text>,
                    name: 'organizer',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.organizer,
                    showInList: true,
                },
                {
                    title: <Text id='input.place'>place</Text>,
                    placeholder: <Text id='input.place'>place</Text>,
                    name: 'place',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.place,
                    showInList: true,
                },

                {
                    title: <Text id='input.program'>program</Text>,
                    placeholder: <Text id='input.program'>program</Text>,
                    name: 'program',
                    type: 'select',
                    icon: 'fa-duotone fa-face-thinking',
                    getValue: obj => obj.type,
                    datalist: [
                        ['Fin', 'Fin'],
                        ['Grov', 'Grov'],
                        ['Militær', 'Militær'],
                        ['Revolver', 'Revolver'],
                        ['Sp.revolver', 'Sp.revolver'],
                        ['Sp.pistol', 'Sp.pistol'],
                        ['Mag 1', 'Mag 1'],
                        ['Mag 2', 'Mag 2'],
                        ['Militær RP', 'Militær RP'],
                        ['Revolver RP', 'Revolver RP'],
                    ],
                    showInList: true,
                },

                {
                    title: <Text id='input.class'>class</Text>,
                    placeholder: <Text id='input.class'>class</Text>,
                    name: 'class',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.class,
                    showInList: true,
                },
                {
                    title: <Text id='input.rank'>rank</Text>,
                    placeholder: <Text id='input.rank'>rank</Text>,
                    name: 'rank',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.rank,
                    showInList: true,
                },
                {
                    title: <Text id='input.score'>score</Text>,
                    placeholder: <Text id='input.score'>score</Text>,
                    name: 'score',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.score,
                    showInList: true,
                },
                {
                    title: <Text id='input.promotionClass'>promotionClass</Text>,
                    placeholder: <Text id='input.promotionClass'>promotionClass</Text>,
                    name: 'promotionClass',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.promotionClass,
                    showInList: true,
                },
                {
                    title: <Text id='input.country'>country</Text>,
                    placeholder: <Text id='input.country'>country</Text>,
                    name: 'country',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.country,
                    showInList: true,
                },
                {
                    title: <Text id='input.url'>url</Text>,
                    placeholder: <Text id='input.url'>url</Text>,
                    name: 'url',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.url,
                    showInList: true,
                },
            ]
        },
        {
            name: <Text id='input.competitionResult-media-title'>competitionResult media</Text>,
            // help: <Text id='input.part-basics-help'>Basic settings for your profile.</Text>,
            key: 'competitionResult media',
            fields: [
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    help: <Text id='input.tags-help'>Tags are used to find similar news. Add as many as you like.</Text>,
                    name: 'tags',
                    type: 'tags',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'tags',
                    foundListName: 'similarTags',
                    foundListKeysName: 'similarTagsKeys',
                    getValue: obj => obj.tags,
                    datalist: [
                        'name',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                    hideOnCreate: false,
                },

            ]
        },
    ],
    calendarAvailability: [
        {
            name: <Text id='input.calendarevent-basics-title'>Calendar event</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'Calendar availability',
            fields: [
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.calendar'>calendar</Text>,
                    placeholder: <Text id='input.calendar'>calendar</Text>,
                    name: 'calendar',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.calendar,
                    showInList: true,
                    isAdminField: true,
                },
                // {
                //     title: <Text id='input.calendarId'>calendarId</Text>,
                //     placeholder: <Text id='input.calendarId'>calendarId</Text>,
                //     name: 'calendarId',
                //     type: 'text',
                //     icon: 'fa-solid fa-input-text',
                //     getValue: obj => obj.calendarId,
                //     showInList: true,
                // },
                {
                    title: <Text id='input.title'>Title</Text>,
                    placeholder: <Text id='input.title'>Title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                },
                {
                    title: <Text id='input.body'>Body</Text>,
                    placeholder: <Text id='input.body'>Body</Text>,
                    name: 'body',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.body,
                },
                {
                    title: <Text id='input.isAllday'>isAllday</Text>,
                    placeholder: <Text id='input.isAllday'>isAllday</Text>,
                    name: 'isAllday',
                    type: 'text',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.isAllday,
                    isAdminField: true,
                },
            ],
        },
        {
            name: <Text id='input.calendarevents-dates-title'>Calendar event dates</Text>,
            // help: <Text id='input.user-dates-help'>Basic settings for your profile.</Text>,
            key: 'Calendar event dates',
            fields: [
                {
                    title: <Text id='input.start'>Start</Text>,
                    placeholder: <Text id='input.start'>Start</Text>,
                    name: 'start',
                    type: 'datetime-local',
                    step: 900,
                    icon: 'fa-solid fa-calendar',
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    getValue: obj => obj.start,
                    callback: (obj, updateValue) => {
                        const start = new Date(obj.start);
                        const end = new Date(obj.end);
                        if (!obj.end || end < start) {
                            const end = new Date(obj.start);
                            end.setMinutes(end.getMinutes() + 15);
                            updateValue({
                                name: 'end',
                                value: util.isoDate(end),
                            });
                        }
                    },
                    showInList: true,
                },
                {
                    title: <Text id='input.end'>End</Text>,
                    placeholder: <Text id='input.end'>End</Text>,
                    name: 'end',
                    type: 'datetime-local',
                    step: 900,
                    icon: 'fa-solid fa-calendar',
                    displayValue: val => val ? util.formatDate(val, {
                        hour12: false,
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: 'short',
                        year: '2-digit',
                        locale: 'nb-NO',
                    }) : null,
                    getValue: obj => obj.end,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.calendarevents-meta-title'>Calendar event meta</Text>,
            // help: <Text id='input.user-dates-help'>Basic settings for your profile.</Text>,
            key: 'Calendar event meta',
            fields: [
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    name: 'tags',
                    type: 'tags',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'tags',
                    foundListName: 'similarTags',
                    foundListKeysName: 'similarTagsKeys',
                    getValue: obj => obj.tags,
                    datalist: [
                        'name',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
            ],
        },
    ],
    textTemplate: [
        {
            name: <Text id='input.texttemplate-basics-title'>Text template basics</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'text template',
            fields: [
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.sort'>sort</Text>,
                    placeholder: <Text id='input.sort'>sort</Text>,
                    name: 'sort',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.sort,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'section',
                    placeholder: 'section',
                    name: 'section',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.section,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'field',
                    placeholder: 'field',
                    name: 'field',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.field,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'type',
                    placeholder: 'type',
                    name: 'type',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.type,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'title',
                    placeholder: 'title',
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.body'>Body</Text>,
                    placeholder: <Text id='input.body'>Body</Text>,
                    name: 'body',
                    type: 'textarea',
                    icon: 'fa-solid fa-pen-field',
                    getValue: obj => obj.body,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.texttemplate-meta-title'>Text template meta</Text>,
            // help: <Text id='input.user-dates-help'>Basic settings for your profile.</Text>,
            key: 'text template meta',
            fields: [
                {
                    title: <Text id='input.tags'>Tags</Text>,
                    placeholder: <Text id='input.tags'>Tags</Text>,
                    name: 'tags',
                    type: 'tags',
                    icon: 'fa-solid fa-tag',
                    lookupArray: 'tags',
                    foundListName: 'similarTags',
                    foundListKeysName: 'similarTagsKeys',
                    getValue: obj => obj.tags,
                    datalist: [
                        'name',
                    ],
                    showInList: false,
                    drawerSize: 'medium',
                    displayHeight: '60px',
                    hideOnCreate: false,
                },
            ],
        },
    ],
    sms: [
        {
            name: <Text id='input.sms-basics-title'>sms basics</Text>,
            // help: <Text id='input.user-basics-help'>Basic settings for your profile.</Text>,
            key: 'sms',
            fields: [
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer || null,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'initiator',
                    placeholder: 'initiator',
                    name: 'initiator',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.initiator,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'sender',
                    placeholder: 'sender',
                    name: 'sender',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.sender,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'cellphone',
                    placeholder: 'cellphone',
                    name: 'cellphone',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.cellphone,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'message',
                    placeholder: 'message',
                    name: 'message',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.message,
                    showInList: true,
                    isAdminField: true,
                },
            ],
        },
    ],
    payment: [
        {
            name: <Text id='input.payment-basics-title'>payment basics</Text>,
            key: 'payment basics',
            fields: [
                {
                    title: <Text id='input.id'>Id</Text>,
                    placeholder: <Text id='input.id'>Id</Text>,
                    name: 'id',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.id,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: 'uuidv4',
                    placeholder: 'uuidv4',
                    name: 'uuidv4',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.uuidv4,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.offline'>Offline</Text>,
                    placeholder: <Text id='input.offline'>Offline</Text>,
                    name: 'offline',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.offline,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.user'>User</Text>,
                    placeholder: <Text id='input.user'>User</Text>,
                    name: 'user',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.user,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.customer'>Customer</Text>,
                    placeholder: <Text id='input.customer'>Customer</Text>,
                    name: 'customer',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.customer,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.race'>race</Text>,
                    placeholder: <Text id='input.race'>race</Text>,
                    name: 'race',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.race,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.raceClassContestant'>raceClassContestant</Text>,
                    placeholder: <Text id='input.raceClassContestant'>raceClassContestant</Text>,
                    name: 'raceClassContestant',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.raceClassContestant,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.sale'>sale</Text>,
                    placeholder: <Text id='input.sale'>sale</Text>,
                    name: 'sale',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.sale,
                    showInList: true,
                    isAdminField: true,
                },
                {
                    title: <Text id='input.paidDate'>PaidDate</Text>,
                    placeholder: <Text id='input.paidDate'>PaidDate</Text>,
                    name: 'paidDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.paidDate,
                    showInList: true,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.capturedDate'>capturedDate</Text>,
                    placeholder: <Text id='input.capturedDate'>capturedDate</Text>,
                    name: 'capturedDate',
                    type: 'datetime-local',
                    icon: 'fa-solid fa-calendar',
                    getValue: obj => obj.capturedDate,
                    showInList: true,
                    hideOnCreate: false,
                },
                {
                    title: <Text id='input.title'>title</Text>,
                    placeholder: <Text id='input.title'>title</Text>,
                    name: 'title',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.title,
                    showInList: true,
                },
                {
                    title: <Text id='input.type'>type</Text>,
                    placeholder: <Text id='input.type'>type</Text>,
                    name: 'type',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.type,
                    showInList: true,
                },
                {
                    title: <Text id='input.description'>Description</Text>,
                    placeholder: <Text id='input.description'>Description</Text>,
                    name: 'description',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.description,
                    showInList: true,
                },
                {
                    title: <Text id='input.wantedPage'>wantedPage</Text>,
                    placeholder: <Text id='input.wantedPage'>wantedPage</Text>,
                    name: 'wantedPage',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.wantedPage,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.payment-price-payment-title'>payment price and payment</Text>,
            key: 'payment price and payment',
            fields: [
                {
                    title: <Text id='input.amount'>amount</Text>,
                    placeholder: <Text id='input.amount'>amount</Text>,
                    name: 'amount',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.amount,
                    showInList: true,
                },
                {
                    title: <Text id='input.vat'>Vat</Text>,
                    placeholder: <Text id='input.vat'>Vat</Text>,
                    name: 'vat',
                    type: 'number',
                    icon: 'fa-solid fa-input-numeric',
                    getValue: obj => obj.vat,
                    showInList: true,
                },
                {
                    title: <Text id='input.currency'>currency</Text>,
                    placeholder: <Text id='input.currency'>currency</Text>,
                    name: 'currency',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.currency,
                    showInList: true,
                },
                {
                    title: <Text id='input.paymentMethod'>paymentMethod</Text>,
                    placeholder: <Text id='input.paymentMethod'>paymentMethod</Text>,
                    name: 'paymentMethod',
                    type: 'text',
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.paymentMethod,
                    showInList: true,
                },
                {
                    title: <Text id='input.paymentStatus'>paymentStatus</Text>,
                    placeholder: <Text id='input.paymentStatus'>paymentStatus</Text>,
                    name: 'paymentStatus',
                    type: 'select',
                    /*
                        0: 'Not started',
                        1: 'Payment started',
                        2: 'Payment authorized',
                        3: 'Payment captured',
                        90: 'Payment canceled',
                        91: 'Payment aborted',
                        99: 'Payment failed',
                    */
                    datalist: [[0, 'Not started'], [1, 'Payment started'], [2, 'Payment authorized'], [3, 'Payment captured'], [90, 'Payment canceled'], [91, 'Payment aborted'], [99, 'Payment failed']],
                    icon: 'fa-solid fa-input-text',
                    getValue: obj => obj.paymentStatus,
                    showInList: true,
                },
            ],
        },
        {
            name: <Text id='input.payment-media-title'>payment media</Text>,
            key: 'payment media',
            fields: [
                {
                    title: <Text id='input.images'>Images</Text>,
                    placeholder: <Text id='input.images'>Images</Text>,
                    name: 'images',
                    type: 'image',
                    icon: 'fa-solid fa-images',
                    getValue: obj => obj.images,
                    showInList: false,
                },
                {
                    title: <Text id='input.files'>Files</Text>,
                    placeholder: <Text id='input.files'>Files</Text>,
                    name: 'files',
                    type: 'file',
                    icon: 'fa-solid fa-files',
                    getValue: obj => obj.files,
                    showInList: false,
                },
                {
                    title: <Text id='input.links'>Links</Text>,
                    placeholder: <Text id='input.links'>Links</Text>,
                    name: 'links',
                    type: 'arrayObject',
                    icon: 'fa-solid fa-tag',
                    getValue: obj => obj.links,
                    datalist: [
                        'url', 'name', 'description'
                    ],
                    showInList: false,
                    displayHeight: '60px',
                },
            ],
        },
    ],
};

class Fields {
    static get(type, section, skipFields = [], onlyFields = []) {
        if (FIELDS[type]) {
            let sections = FIELDS[type];
            if (Array.isArray(section)) {
                // Check if s.name or s.key exists in array section:
                sections = FIELDS[type].filter(s => section.includes(s.name) || section.includes(s.key));
            } else if (section) {
                sections = FIELDS[type].filter(s => s.name === section || s.key === section);
            }
            if (skipFields.length > 0) {
                sections.forEach(s => {
                    s.fields = s.fields.filter(f => !skipFields.includes(f.name));
                });
            }
            if (onlyFields.length > 0) {
                sections.forEach(s => {
                    s.fields = s.fields.filter(f => onlyFields.includes(f.name));
                });
            }
            return sections;
        }
        return [];
    }

    static getSection(type, section) {
        if (FIELDS[type]) {
            for (let i = 0, l = FIELDS[type].length; i < l; i += 1) {
                const sec = FIELDS[type][i];
                if (sec.key === section) {
                    return sec;
                }
            }
        }
        return null;
    }

    static getSections(type) {
        if (FIELDS[type]) {
            return FIELDS[type];
        }
        return null;
    }

    static getField(type, field) {
        if (FIELDS[type]) {
            for (let i = 0, l = FIELDS[type].length; i < l; i += 1) {
                const section = FIELDS[type][i];
                for (let j = 0, l2 = section.fields.length; j < l2; j += 1) {
                    const f = section.fields[j];
                    if (f.name === field) {
                        return f;
                    }
                }
            }
        }
        return null;
    }

    static getNumberFixedWidth(value, width = 4) {
        if (value === null || value === undefined) {
            return null;
        }
        const numberAsString = value.toString();
        const paddingNeeded = Math.max(width - numberAsString.length, 0);
        const paddedNumber = '0'.repeat(paddingNeeded) + numberAsString;
        return paddedNumber;
    }
}
export default Fields;
